import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { toast, Toaster } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Users, 
  Zap, 
  TrendingUp, 
  BarChart3, 
  ChevronRight, 
  Mail, 
  MessageSquare, 
  Smartphone, 
  Search, 
  Target, 
  Globe, 
  Settings, 
  Download, 
  Upload, 
  ExternalLink, 
  AlertCircle, 
  AlertTriangle,
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  Edit, 
  Edit2,
  Trash2, 
  Plus, 
  X, 
  Key,
  Info,
  Filter,
  Send,
  Bell,
  UserPlus,
  UserCheck,
  DollarSign,
  Award,
  Briefcase,
  Image,
  Video,
  MessageCircle,
  Link,
  Sparkles,
  Tag,
  FileText,
  Brain,
  Bot,
  PenTool,
  Power,
  Package,
  ShoppingBag,
  ShoppingCart,
  Store,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  ChevronLeft,
  Calendar,
  Play,
  Pause,
  File,
  Undo2,
  Facebook,
  Shield,
  FileDown,
  Loader2,
  Lightbulb,
  RotateCcw,
  Circle,
  Hash,
  Crown,
  Star,
  Layers,
  Flame,
  Sun,
  Activity,
  Heart,
  Database
} from 'lucide-react';
import Papa from 'papaparse';
import './App.css';
import { SettingsProvider } from './contexts/SettingsContext';
import ConsentBanner from './components/ConsentBanner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Industry categories for advanced keyword research (matching backend validation)
const industryCategories = [
  'All Industries', 'Animals & Pets', 'Arts & Entertainment', 'Attorneys & Legal Services',
  'Automotive -- For Sale', 'Automotive -- Repair, Service & Parts', 'Beauty & Personal Care',
  'Business Opportunities', 'Career & Education', 'Dentistry', 'E-Commerce', 'Education',
  'Entertainment', 'Finance & Insurance', 'Fitness', 'Food & Dining', 'Health & Wellness',
  'Home & Garden', 'Hospitality', 'Industrial & Manufacturing', 'Legal', 'Medical',
  'Real Estate', 'Retail', 'Technology', 'Travel'
];

// Top countries for targeted keyword research (matching backend validation)
const targetCountries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Spain',
  'Italy', 'Netherlands', 'Belgium', 'Sweden', 'Denmark', 'Norway', 'Finland', 'Ireland',
  'Portugal', 'Austria', 'Switzerland', 'Japan', 'South Korea', 'Singapore', 'Hong Kong',
  'India', 'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia', 'South Africa', 'Israel',
  'United Arab Emirates', 'Saudi Arabia', 'Turkey', 'Russia', 'Poland', 'Czech Republic',
  'Hungary', 'Romania', 'Greece', 'New Zealand', 'Malaysia', 'Thailand', 'Philippines',
  'Indonesia', 'Vietnam', 'Taiwan', 'China', 'Egypt', 'Morocco', 'Nigeria'
];

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthLoading, setIsAuthLoading] = useState(!!localStorage.getItem('token')); // Loading if we have a token but no user yet
  const [activeTab, setActiveTab] = useState('dashboard');
  const [personas, setPersonas] = useState([]);  // Changed from segments to personas
  
  // AI Personas state
  const [aiPersonas, setAiPersonas] = useState([]);
  const [audienceAnalysis, setAudienceAnalysis] = useState(null);
  const [aiPersonaConfig, setAiPersonaConfig] = useState({
    minClusterSize: 5,
    maxPersonas: 8,
    clusteringMethod: 'auto',
    useAdvanced: true
  });

  // Persona sub-tabs state
  const [activePersonaTab, setActivePersonaTab] = useState('manual');
  
  // Personas list pagination and details
  const [personasPage, setPersonasPage] = useState(1);
  const [personasPerPage] = useState(5);
  const [selectedPersonaDetails, setSelectedPersonaDetails] = useState(null);
  const [showPersonaDetailsModal, setShowPersonaDetailsModal] = useState(false);
  const [personaContacts, setPersonaContacts] = useState([]);
  
  // Build Your Personas - New Wizard System
  const [showPersonaBuilder, setShowPersonaBuilder] = useState(false);
  const [personaWizardStep, setPersonaWizardStep] = useState(1);
  const [personaWizardData, setPersonaWizardData] = useState({
    name: '',
    avatar_url: null,
    demographics: {},
    professional: {},
    goals: [],
    motivations: '',
    pain_points: '',
    behavior: {},
    content_channels: {},
    journey: {},
    value_prop: '',
    tags: [],
    key_benefits: [],
    success_metrics: '',
    messaging_tone: '',
    cta_preferences: ''
  });
  const [wizardOptions, setWizardOptions] = useState({});
  const [personaPreviewMatching, setPersonaPreviewMatching] = useState({ count: 0, percentage: 0 });
  const [isPersonaWizardLoading, setIsPersonaWizardLoading] = useState(false);
  
  // Add loading state for wizard options - MUST be declared before useEffects
  const [wizardOptionsLoaded, setWizardOptionsLoaded] = useState(false);

  // Debug: Track what causes PersonaBuilderModal re-renders
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('PersonaBuilderModal props changed:', {
        showPersonaBuilder,
        personaWizardStep,
        wizardOptionsLoaded,
        isPersonaWizardLoading,
        personaWizardDataKeys: Object.keys(personaWizardData),
        wizardOptionsKeys: Object.keys(wizardOptions)
      });
    }
  }, [showPersonaBuilder, personaWizardStep, personaWizardData, wizardOptions, wizardOptionsLoaded, isPersonaWizardLoading, personaPreviewMatching]);

  // Load wizard options when modal opens OR when personas tab is first accessed
  useEffect(() => {
    if (showPersonaBuilder && !wizardOptionsLoaded) {
      loadWizardOptions();
    }
  }, [showPersonaBuilder, wizardOptionsLoaded]);

  // Pre-load wizard options when personas tab is accessed
  useEffect(() => {
    if (activeTab === 'personas' && !wizardOptionsLoaded) {
      loadWizardOptions();
    }
  }, [activeTab, wizardOptionsLoaded]);
  
  // Bulk selection state
  const [selectedPersonaIds, setSelectedPersonaIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Data upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [audienceFields, setAudienceFields] = useState([]);
  const [audienceData, setAudienceData] = useState([]);
  const [audiencePagination, setAudiencePagination] = useState({});
  const [audienceSearch, setAudienceSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Default to 10 records per page
  const [audienceStats, setAudienceStats] = useState(null);
  const [uploadStats, setUploadStats] = useState(null);

  // Standardized Audience Schema state
  const [audienceSchema, setAudienceSchema] = useState(null);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [fieldMapping, setFieldMapping] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [useStandardizedUpload, setUseStandardizedUpload] = useState(false);

  // Enhanced intelligent mapping state
  const [intelligentMappingMode, setIntelligentMappingMode] = useState(true);
  const [uncertainMappings, setUncertainMappings] = useState({});
  const [confidenceLevels, setConfidenceLevels] = useState({});
  const [userConfirmations, setUserConfirmations] = useState({});
  const [customFieldNames, setCustomFieldNames] = useState({}); // New state for custom field names
  const [showCustomFieldInputs, setShowCustomFieldInputs] = useState({}); // Track which fields show input
  
  // Audience Operations Undo System
  const [recentOperations, setRecentOperations] = useState([]);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [operationToUndo, setOperationToUndo] = useState(null);
  const [undoReason, setUndoReason] = useState('');
  const [isUndoLoading, setIsUndoLoading] = useState(false);
  
  // SEO Audit System State
  const [currentSEOAudit, setCurrentSEOAudit] = useState(null);
  const [seoAuditHistory, setSeoAuditHistory] = useState([]);
  const [isAuditRunning, setIsAuditRunning] = useState(false);
  
  // Keyword Research State
  const [currentKeywordResearch, setCurrentKeywordResearch] = useState(null);
  const [isResearchRunning, setIsResearchRunning] = useState(false);
  const [researchProgress, setResearchProgress] = useState(0);
  const [selectedResearchId, setSelectedResearchId] = useState(null);
  const [completeAnalysisResults, setCompleteAnalysisResults] = useState(null);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [cardDetailsType, setCardDetailsType] = useState('');
  const [cardDetailsData, setCardDetailsData] = useState(null);
  const [showKeywordDetailsModal, setShowKeywordDetailsModal] = useState(false);
  const [keywordDetailsType, setKeywordDetailsType] = useState('');
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [detailedBreakdownData, setDetailedBreakdownData] = useState(null);

  // State for manual keyword entry
  const [showManualKeywordDialog, setShowManualKeywordDialog] = useState(false);
  const [manualKeywordForm, setManualKeywordForm] = useState({
    keywords: '',
    priority: 'medium',
    businessContext: '',
    enableIntelligence: true,
    tags: ''
  });
  const [isProcessingManualKeywords, setIsProcessingManualKeywords] = useState(false);
  const [manualKeywordProgress, setManualKeywordProgress] = useState(null);
  const [isDeletingKeywords, setIsDeletingKeywords] = useState(false);

  // Enhanced Database filtering state
  const [databaseFilters, setDatabaseFilters] = useState({
    search: '',
    priority: 'all', // all, high, medium, low
    dataSource: 'all', // all, research, manual, ai_research
    dateRange: 'all', // all, today, week, month
    minSearchVolume: '',
    maxDifficulty: ''
  });

  // === UNIFIED KEYWORD INTELLIGENCE HUB STATE ===
  const [keywordIntelligenceTab, setKeywordIntelligenceTab] = useState('dashboard');
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [unifiedKeywords, setUnifiedKeywords] = useState([]);
  const [unifiedKeywordStats, setUnifiedKeywordStats] = useState({});
  const [keywordAnalytics, setKeywordAnalytics] = useState(null);
  const [highOpportunityKeywords, setHighOpportunityKeywords] = useState([]);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [auditProgress, setAuditProgress] = useState(0);
  const [showAuditDetails, setShowAuditDetails] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  
  // Tag management state
  const [selectedContactsForTags, setSelectedContactsForTags] = useState([]);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showRemoveTagDialog, setShowRemoveTagDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');

  // New features state
  const [selectedColumns, setSelectedColumns] = useState(() => {
    // Load saved columns from localStorage on initialization
    const savedColumns = localStorage.getItem('omnimarket_selected_columns');
    return savedColumns ? JSON.parse(savedColumns) : [];
  }); // For column selection
  const [selectedRecords, setSelectedRecords] = useState(new Set()); // For multi-select
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // User management state
  const [createUserForm, setCreateUserForm] = useState({ email: '', name: '', password: '', role: 'campaign_creator' });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', email: '', role: '' });

  // Persona state (changed from segmentation state)
  const [personaForm, setPersonaForm] = useState({ 
    name: '', 
    description: '', 
    filters: [],
    logic: 'AND',
    is_template: true
  });
  const [filteredAudience, setFilteredAudience] = useState([]);
  const [personaPreview, setPersonaPreview] = useState({ match_count: 0, preview: [] });
  const [editingPersona, setEditingPersona] = useState(null);

  // Campaign state
  const [campaignForm, setCampaignForm] = useState({
    name: '', objective: '', persona_id: '', channels: [], message: '', image_url: ''
  });
  const [aiRecommendations, setAiRecommendations] = useState(null);
  
  // Multi-channel campaign state
  const [activeCampaignTab, setActiveCampaignTab] = useState('whatsapp');
  const [whatsappForm, setWhatsappForm] = useState({
    name: '',
    message: '',
    url: '',
    persona_id: '',
    media_file: null,
    media_id: null,
    send_immediately: true,
    schedule_date: ''
  });
  const [smsForm, setSmsForm] = useState({
    name: '',
    message: '',
    url: '',
    persona_id: '',
    language: 'en',
    send_immediately: true,
    schedule_date: ''
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  
  // URL shortener settings state
  const [urlShortenerSettings, setUrlShortenerSettings] = useState({
    configured: false,
    service: 'bitly',
    api_key_configured: false,
    domain: '',
    auto_shorten: false,
    analytics_enabled: true
  });
  const [urlShortenerForm, setUrlShortenerForm] = useState({
    service: 'bitly',
    api_key: '',
    domain: '',
    auto_shorten: false,
    analytics_enabled: true
  });
  const [urlShortenerTesting, setUrlShortenerTesting] = useState(false);
  const [urlAnalytics, setUrlAnalytics] = useState([]);
  const [urlShorteningInProgress, setUrlShorteningInProgress] = useState(false);
  const [showUrlShortenerEdit, setShowUrlShortenerEdit] = useState(false);

  // Campaign Performance Filtering state
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [performanceDateRange, setPerformanceDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [pendingDateRange, setPendingDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredAnalytics, setFilteredAnalytics] = useState(null);

  // URL Analytics Filtering state
  const [urlSelectedCampaign, setUrlSelectedCampaign] = useState('all');
  const [urlDateRange, setUrlDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [urlPendingDateRange, setUrlPendingDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredUrlAnalytics, setFilteredUrlAnalytics] = useState([]);

  // Campaign Performance Pagination state
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [paginatedCampaigns, setPaginatedCampaigns] = useState([]);
  const [totalCampaignPages, setTotalCampaignPages] = useState(1);
  const campaignRowsPerPage = 10;

  // Social Enrichment state
  const [enrichmentSettings, setEnrichmentSettings] = useState({
    apollo_api_key: '',
    api_key_configured: false,
    social_enrichment_enabled: true,
    auto_enrich_new_contacts: false
  });
  const [enrichmentStatus, setEnrichmentStatus] = useState({
    total_contacts: 0,
    enrichment_status: {},
    social_profiles: {},
    enrichment_rate: 0
  });
  const [enrichmentInProgress, setEnrichmentInProgress] = useState(false);

  // Phone standardization state
  const [showPhoneStandardization, setShowPhoneStandardization] = useState(false);
  const [phoneStandardizationForm, setPhoneStandardizationForm] = useState({
    phoneColumns: [],
    defaultCountry: 'US',
    consolidationStrategy: 'smart'
  });
  const [phoneStandardizationInProgress, setPhoneStandardizationInProgress] = useState(false);
  const [phoneStandardizationResults, setPhoneStandardizationResults] = useState(null);
  const [showAdvancedPhoneOptions, setShowAdvancedPhoneOptions] = useState(false);

  // Column mapping state for upload
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [uploadFileHeaders, setUploadFileHeaders] = useState([]);
  const [existingColumns, setExistingColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [pendingUploadFile, setPendingUploadFile] = useState(null);

  // Column management state for existing data
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [columnMergeMapping, setColumnMergeMapping] = useState({});
  const [mergingInProgress, setMergingInProgress] = useState(false);

  // Integration settings state
  const [integrationSettings, setIntegrationSettings] = useState({
    integrations: {
      mailgun: { configured: false, status: 'disconnected' },
      twilio: { configured: false, status: 'disconnected' },
      facebook_ads: { configured: false, status: 'disconnected' },
      suitecrm: { configured: false, status: 'disconnected' },
      google_analytics: { configured: false, status: 'disconnected' }
    }
  });
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [activeIntegrationsTab, setActiveIntegrationsTab] = useState('smtp');
  
  // Integration form data state
  const [integrationFormData, setIntegrationFormData] = useState({
    crm: {
      salesforce: {
        client_id: '',
        client_secret: '',
        username: '',
        password: '',
        security_token: '',
        sandbox: false
      },
      hubspot: {
        api_key: '',
        portal_id: ''
      },
      pipedrive: {
        api_token: '',
        company_domain: ''
      },
      zoho_crm: {
        client_id: '',
        client_secret: '',
        refresh_token: '',
        domain: 'com'
      },
      monday: {
        api_token: '',
        account_id: ''
      },
      notion: {
        integration_token: '',
        database_id: ''
      },
      airtable: {
        api_key: '',
        base_id: '',
        table_name: ''
      }
    },
    smtp: {
      host: '',
      port: 587,
      username: '',
      password: '',
      from_email: '',
      use_tls: true,
      use_ssl: false
    },
    sms: {
      provider: '',
      api_key: '',
      api_secret: '',
      sender_id: '',
      webhook_url: ''
    },
    whatsapp: {
      provider: '',
      phone_number: '',
      access_token: '',
      app_id: '',
      webhook_url: '',
      verify_token: ''
    },
    seo_analytics: {
      google_analytics: {
        property_id: ''
      },
      google_search_console: {
        site_url: ''
      },
      semrush: {
        api_key: ''
      },
      ahrefs: {
        api_token: ''
      },
      microsoft_clarity: {
        project_id: '',
        website_url: ''
      },
      google_tag_manager: {
        container_id: ''
      },
      hotjar: {
        site_id: ''
      },
      mixpanel: {
        project_token: ''
      },
      facebook_pixel: {
        pixel_id: '',
        access_token: ''
      },
      moz: {
        access_id: '',
        secret_key: ''
      },
      matomo: {
        site_id: '',
        base_url: '',
        auth_token: ''
      },
      adobe_analytics: {
        report_suite_id: '',
        client_id: '',
        client_secret: '',
        org_id: ''
      },
      screaming_frog: {
        api_key: ''
      },
      majestic_seo: {
        access_id: '',
        secret_key: '',
        environment: 'sandbox'
      },
      heap: {
        app_id: '',
        api_key: ''
      },
      crazy_egg: {
        account_id: '',
        api_key: ''
      },
      twitter_analytics: {
        api_key: '',
        api_secret: '',
        access_token: '',
        access_token_secret: ''
      },
      linkedin_analytics: {
        client_id: '',
        client_secret: '',
        access_token: ''
      },
      tiktok_pixel: {
        pixel_id: '',
        access_token: ''
      },
      meta_analytics: {
        app_id: '',
        app_secret: '',
        access_token: '',
        instagram_business_account_id: ''
      }
    },
    advertisements: {
      google_ads: {
        customer_id: '',
        developer_token: ''
      },
      facebook_ads: {
        app_id: '',
        access_token: ''
      },
      linkedin_ads: {
        client_id: '',
        client_secret: ''
      },
      microsoft_ads: {
        customer_id: '',
        developer_token: ''
      }
    }
  });
  const [integrationTestResults, setIntegrationTestResults] = useState({});
  
  // Integration Status Dashboard state
  const [integrationStatusData, setIntegrationStatusData] = useState(null);
  const [integrationStatusLoading, setIntegrationStatusLoading] = useState(false);
  const [lastStatusUpdate, setLastStatusUpdate] = useState(null);
  
  // AI Models Status Dashboard state
  const [aiModelsStatusData, setAiModelsStatusData] = useState(null);
  const [aiModelsStatusLoading, setAiModelsStatusLoading] = useState(false);
  const [lastAiModelsUpdate, setLastAiModelsUpdate] = useState(null);
  
  // General Settings state
  const [generalSettings, setGeneralSettings] = useState({
    localization: {
      locale: 'en-US',
      timeZone: 'UTC',
      dateFormat: 'MM/dd/yyyy',
      timeFormat12h: true,
      numberFormat: 'locale',
      textDirection: 'LTR'
    },
    currency: {
      primaryCurrency: 'USD',
      showLocalCurrency: true,
      autoConversion: true,
      roundingMode: 'HALF_UP',
      fxSource: 'provider',
      fxProvider: 'ECB',
      fxRefreshMinutes: 60
    },
    accessibility: {
      theme: 'system'
    },
    compliance: {
      privacyFrameworks: ['None'],
      cookieConsent: {
        enabled: true,
        categories: {
          necessary: true,
          analytics: false,
          marketing: false
        }
      }
    },
    notifications: {
      channels: {
        email: true,
        sms: false,
        push: false
      },
      quietHours: {
        start: '22:00',
        end: '07:00',
        timeZone: 'UTC'
      }
    },
    backup: {
      exportFormats: ['JSON'],
      restorePointsEnabled: true,
      googleDrive: {
        enabled: false,
        oauthClientId: '',
        targetFolderId: ''
      }
    }
  });
  const [activeGeneralSettingsTab, setActiveGeneralSettingsTab] = useState('localization');

  // AI Models state
  const [aiModelsConfig, setAiModelsConfig] = useState({
    providers: {},
    active_provider: null,
    audience_intelligence_enabled: false
  });
  const [aiModelTesting, setAiModelTesting] = useState({});
  const [aiUsageStats, setAiUsageStats] = useState({});

  // AI Provider Intelligence System state
  const [aiIntelligenceTest, setAiIntelligenceTest] = useState({
    context: 'Audience Intelligence',
    complexity: 'moderate',
    testing: false,
    result: null
  });
  const [aiProviderRecommendations, setAiProviderRecommendations] = useState(null);

  // Advanced filtering state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({
    name: "",
    description: "",
    conditions: [],
    logic: "AND"
  });
  const [filterResults, setFilterResults] = useState(null);
  const [savedSegments, setSavedSegments] = useState([]);
  const [showSaveSegmentDialog, setShowSaveSegmentDialog] = useState(false);
  const [fieldSummary, setFieldSummary] = useState({});
  
  // Duplicate detection state
  const [showDuplicateDetection, setShowDuplicateDetection] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [duplicateDetectionSettings, setDuplicateDetectionSettings] = useState({
    strategy: "smart",
    similarity_threshold: 0.85,
    auto_merge: false
  });
  
  // Data validation state
  const [showDataValidation, setShowDataValidation] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [validationSettings, setValidationSettings] = useState({
    validation_types: ["email", "phone"],
    batch_size: 100,
    update_records: true
  });
  
  // Data enrichment state  
  const [showDataEnrichment, setShowDataEnrichment] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState(null);
  const [dataEnrichmentSettings, setDataEnrichmentSettings] = useState({
    enrichment_types: ["demographic", "professional"],
    batch_size: 50,
    update_records: true
  });
  
  // Quality report state
  const [qualityReport, setQualityReport] = useState(null);
  const [showQualityReport, setShowQualityReport] = useState(false);
  
  // CRM Integration state
  const [crmConnections, setCrmConnections] = useState([]);
  const [showCrmHub, setShowCrmHub] = useState(false);
  const [showConnectionWizard, setShowConnectionWizard] = useState(false);
  const [selectedCrmType, setSelectedCrmType] = useState('');
  const [crmCredentials, setCrmCredentials] = useState({});
  const [connectionName, setConnectionName] = useState('');
  const [supportedCrms, setSupportedCrms] = useState([]);
  const [syncJobs, setSyncJobs] = useState([]);
  const [crmStats, setCrmStats] = useState({});
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  
  // NEW: CRM sync status for audience management
  const [crmSyncStatus, setCrmSyncStatus] = useState({
    connected: false,
    active_connections: 0,
    total_connections: 0,
    total_synced_records: 0,
    last_sync: null,
    connections: []
  });

  // E-commerce Integration state (SINGLE STORE CONNECTION)
  const [ecommerceConnections, setEcommerceConnections] = useState([]);
  const [currentEcommerceConnection, setCurrentEcommerceConnection] = useState(null);
  const [supportedEcommercePlatforms, setSupportedEcommercePlatforms] = useState([]);
  const [ecommercePlatforms, setEcommercePlatforms] = useState([
    { type: 'shopify', name: 'Shopify' },
    { type: 'woocommerce', name: 'WooCommerce' }
  ]);
  const [showEcommerceConnectionDialog, setShowEcommerceConnectionDialog] = useState(false);
  const [showSwitchPlatformDialog, setShowSwitchPlatformDialog] = useState(false);
  const [showEcommerceSyncDialog, setShowEcommerceSyncDialog] = useState(false);
  const [ecommerceConnectionForm, setEcommerceConnectionForm] = useState({ 
    platform_type: '', 
    connection_name: '', 
    store_url: '', 
    credentials: {},
    // Enhanced business information fields
    industry: 'All Industries',
    target_country: 'United States'
  });
  const [ecommerceStats, setEcommerceStats] = useState({});
  const [ecommerceSyncJobs, setEcommerceSyncJobs] = useState([]);
  const [ecommerceAnalytics, setEcommerceAnalytics] = useState({});
  
  // Industries and countries for business context
  const [industries] = useState(industryCategories);
  const [countries] = useState(targetCountries);

  // Single Connection Enforcement (Phase 4 Enhancement) 
  const [connectionsStatus, setConnectionsStatus] = useState({
    has_ecommerce_connection: false,
    has_website_connection: false,
    has_any_connection: false,
    active_connection: null
  });

  // Website Integration state (Phase 4 - Non-E-commerce websites)
  const [activeWebsiteTab, setActiveWebsiteTab] = useState('ecommerce');
  const [currentWebsiteConnection, setCurrentWebsiteConnection] = useState(null);
  const [websiteAnalytics, setWebsiteAnalytics] = useState({});
  const [showWebsiteConnectionDialog, setShowWebsiteConnectionDialog] = useState(false);
  const [showSwitchWebsitePlatformDialog, setShowSwitchWebsitePlatformDialog] = useState(false);
  const [showWebsiteSyncDialog, setShowWebsiteSyncDialog] = useState(false);
  const [websiteConnectionForm, setWebsiteConnectionForm] = useState({
    platform_type: '',
    connection_name: '',
    website_url: '',
    credentials: {}
  });

  // SEO/AEO Platform state
  const [seoProjects, setSeoProjects] = useState([]);
  const [seoKeywords, setSeoKeywords] = useState([]);
  const [seoDataSources, setSeoDataSources] = useState(null);
  const [seoCompetitors, setSeoCompetitors] = useState([]);
  const [seoContentAnalysis, setSeoContentAnalysis] = useState([]);
  const [seoTechnicalAudits, setSeoTechnicalAudits] = useState([]);
  const [seoDashboard, setSeoDashboard] = useState({
    projects_count: 0,
    keywords_tracked: 0,
    competitors_tracked: 0,
    content_analyzed: 0,
    technical_audits: 0,
    recent_keywords: [],
    recent_audits: [],
    keyword_trends: [],
    performance_summary: {}
  });
  const [seoWebsiteIntegration, setSeoWebsiteIntegration] = useState({
    connected_websites: [],
    available_urls: [],
    product_pages: [],
    category_pages: []
  });

  // SEO/AEO Modal and Dialog states
  const [showKeywordResearchDialog, setShowKeywordResearchDialog] = useState(false);
  const [showCompetitorAnalysisDialog, setShowCompetitorAnalysisDialog] = useState(false);
  const [showContentAnalysisDialog, setShowContentAnalysisDialog] = useState(false);
  const [showTechnicalAuditDialog, setShowTechnicalAuditDialog] = useState(false);
  const [showAeoOptimizationDialog, setShowAeoOptimizationDialog] = useState(false);
  const [showDataSourceDialog, setShowDataSourceDialog] = useState(false);
  
  const [keywordResearchForm, setKeywordResearchForm] = useState({
    keywords: '',
    location: 'US',
    researchType: 'manual', // Legacy - keeping for compatibility
    importMethod: 'manual', // New - 'manual' or 'upload'
    domain: ''
  });
  const [competitorAnalysisForm, setCompetitorAnalysisForm] = useState({
    domain: '',
    competitors: ''
  });
  // Persistent competitor storage
  const [savedCompetitors, setSavedCompetitors] = useState([]);
  
  // Accumulated research metrics state
  const [accumulatedMetrics, setAccumulatedMetrics] = useState({
    total_keywords_discovered: 0,
    high_priority_keywords: 0,
    content_opportunities: 0,
    competitors_analyzed: 0,
    sessions_count: 0
  });
  const [metricsBreakdown, setMetricsBreakdown] = useState({
    keywords_by_session: [],
    high_priority_breakdown: [],
    content_opportunities_breakdown: []
  });
  
  // Modal states for detailed metrics view
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [metricsModalData, setMetricsModalData] = useState(null);
  
  // Analytics metrics modal state
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsModalData, setAnalyticsModalData] = useState(null);
  const [contentAnalysisForm, setContentAnalysisForm] = useState({
    url: '',
    target_keywords: ''
  });
  const [technicalAuditForm, setTechnicalAuditForm] = useState({
    domain: ''
  });
  const [seoLoading, setSeoLoading] = useState(false);

  // Enhanced keyword table state
  const [keywordTableSearch, setKeywordTableSearch] = useState('');
  const [keywordTablePagination, setKeywordTablePagination] = useState({
    pageIndex: 0,
    pageSize: 25,
    pageCount: 0
  });
  const [keywordPaginationInfo, setKeywordPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [keywordTableSorting, setKeywordTableSorting] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // Initialize API client with dynamic headers
  const api = axios.create({
    baseURL: API_BASE_URL
  });

  // Add request interceptor to include token dynamically
  api.interceptors.request.use(
    (config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Data Table Components and Column Definitions
  const competitorsColumns = useMemo(() => [
    {
      accessorKey: 'competitor_url',
      header: 'Competitor URL',
      cell: ({ row }) => (
        <div className="font-medium text-blue-600">
          <a href={row.getValue('competitor_url')} target="_blank" rel="noopener noreferrer" className="hover:underline">
            {row.getValue('competitor_url')}
          </a>
        </div>
      )
    },
    {
      accessorKey: 'domain_authority',
      header: 'Domain Authority',
      cell: ({ row }) => (
        <div className="font-medium">
          {row.getValue('domain_authority') || 'N/A'}
        </div>
      )
    },
    {
      accessorKey: 'keywords_found',
      header: 'Keywords Found',
      cell: ({ row }) => (
        <div className="text-center">
          {row.getValue('keywords_found') || 0}
        </div>
      )
    },
    {
      accessorKey: 'content_analysis.total_pages',
      header: 'Pages Analyzed',
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.content_analysis?.total_pages || 0}
        </div>
      )
    },
    {
      accessorKey: 'analysis_date',
      header: 'Analysis Date',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {row.getValue('analysis_date') ? new Date(row.getValue('analysis_date')).toLocaleDateString() : 'Recent'}
        </div>
      )
    }
  ], []);

  const keywordsColumns = useMemo(() => [
    {
      accessorKey: 'keyword',
      header: 'Keyword',
      cell: ({ row }) => (
        <div className="font-medium max-w-xs">
          {row.getValue('keyword')}
        </div>
      )
    },
    {
      accessorKey: 'overall_score',
      header: 'Overall Score',
      cell: ({ row }) => {
        const score = row.getValue('overall_score') || 0;
        return (
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              score >= 80 ? 'bg-green-100 text-green-800' :
              score >= 60 ? 'bg-blue-100 text-blue-800' :
              score >= 40 ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {score}
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'search_volume',
      header: 'Search Volume',
      cell: ({ row }) => (
        <div className="text-center font-medium">
          {(row.getValue('search_volume') || 0).toLocaleString()}
        </div>
      )
    },
    {
      accessorKey: 'difficulty',
      header: 'Difficulty',
      cell: ({ row }) => {
        const difficulty = row.getValue('difficulty') || 0;
        return (
          <div className="text-center">
            <span className={`px-2 py-1 rounded text-xs ${
              difficulty >= 80 ? 'bg-red-100 text-red-800' :
              difficulty >= 60 ? 'bg-orange-100 text-orange-800' :
              difficulty >= 40 ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {difficulty}
            </span>
          </div>
        );
      }
    },
    {
      accessorKey: 'intent',
      header: 'Intent',
      cell: ({ row }) => (
        <div className="text-center text-sm">
          {row.getValue('intent') || 'informational'}
        </div>
      )
    },
    {
      accessorKey: 'recommended_content_type',
      header: 'Content Type',
      cell: ({ row }) => (
        <div className="text-sm">
          {row.getValue('recommended_content_type') || 'blog_post'}
        </div>
      )
    }
  ], []);

  const highPriorityColumns = useMemo(() => [
    ...keywordsColumns,
    {
      accessorKey: 'priority_level',
      header: 'Priority Level',
      cell: ({ row }) => {
        const priority = row.original.priority_level || 'medium';
        return (
          <div className={`px-2 py-1 rounded text-xs font-medium ${
            priority === 'critical' ? 'bg-red-100 text-red-800' :
            priority === 'high' ? 'bg-orange-100 text-orange-800' :
            priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </div>
        );
      }
    }
  ], [keywordsColumns]);

  // Data Table Component
  const DataTableComponent = ({ data, columns, title, totalCount }) => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 25,
    });

    const table = useReactTable({
      data,
      columns,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      state: {
        sorting,
        columnFilters,
        globalFilter,
        pagination,
      },
    });

    const exportTableData = () => {
      try {
        console.log('🔍 Starting table data export...');
        console.log('Data length:', data?.length || 0);
        console.log('Columns count:', columns?.length || 0);
        
        if (!data || data.length === 0) {
          toast.error('No data available to export');
          return;
        }
        
        if (!columns || columns.length === 0) {
          toast.error('No columns configured for export');
          return;
        }
        
        const csvHeaders = columns.map(col => col.header).join(',');
        const csvRows = data.map(row => 
          columns.map(col => {
            const value = col.accessorKey ? row[col.accessorKey] : '';
            return `"${String(value || '').replace(/"/g, '""')}"`;
          }).join(',')
        ).join('\n');
        
        const csvContent = `${csvHeaders}\n${csvRows}`;
        
        console.log('✅ CSV content generated');
        console.log('Content length:', csvContent.length);
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.style.display = 'none';
        
        console.log('📥 Triggering download:', link.download);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Export completed successfully');
        toast.success(`✅ ${data.length} rows exported successfully!`);
        
      } catch (error) {
        console.error('❌ Export failed:', error);
        toast.error('Failed to export data. Please try again.');
      }
    };

    return (
      <div className="space-y-4">
        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(String(e.target.value))}
              className="max-w-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              Total: {totalCount} | Showing: {data.length}
            </span>
            <Button
              onClick={exportTableData}
              variant="outline"
              size="sm"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Download className="h-4 w-4 mr-1" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="font-semibold">
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none flex items-center gap-2 hover:bg-gray-50 p-2 -m-2 rounded'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <div className="ml-auto">
                              {{
                                asc: <ArrowUp className="h-4 w-4" />,
                                desc: <ArrowDown className="h-4 w-4" />,
                              }[header.column.getIsSorted()] ?? <ArrowUpDown className="h-4 w-4" />}
                            </div>
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-2">
          <div className="flex-1 text-sm text-gray-600">
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value))
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronLeft className="h-4 w-4" />
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronRight className="h-4 w-4" />
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // === STATE PERSISTENCE AND URL ROUTING ===
  
  // Initialize state from URL parameters and localStorage
  const initializeStateFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash.substring(1); // Remove #
    
    // Parse main tab from URL hash
    if (hash) {
      setActiveTab(hash);
    }
    
    // Parse sub-tab parameters
    const keywordTab = urlParams.get('keywordTab');
    if (keywordTab) {
      setKeywordIntelligenceTab(keywordTab);
    }
    
    const campaignTab = urlParams.get('campaignTab');
    if (campaignTab) {
      setActiveCampaignTab(campaignTab);
    }
    
    const settingsTab = urlParams.get('settingsTab');
    if (settingsTab) {
      setActiveSettingsTab(settingsTab);
    }
    
    const generalSettingsTab = urlParams.get('generalSettingsTab');
    if (generalSettingsTab) {
      setActiveGeneralSettingsTab(generalSettingsTab);
    }
    
    // Restore modal states from localStorage
    const savedModals = localStorage.getItem('omnimarket_modal_states');
    if (savedModals) {
      try {
        const modalStates = JSON.parse(savedModals);
        if (modalStates.showManualKeywordDialog) {
          setShowManualKeywordDialog(true);
        }
        if (modalStates.showMigrationDialog) {
          setShowMigrationDialog(true);
        }
        if (modalStates.showDeleteConfirmDialog) {
          setShowDeleteConfirmDialog(true);
        }
        if (modalStates.showBulkDeleteDialog) {
          setShowBulkDeleteDialog(true);
        }
        // Restore form state if manual keyword dialog was open
        if (modalStates.manualKeywordForm) {
          setManualKeywordForm(modalStates.manualKeywordForm);
        }
      } catch (error) {
        console.warn('Failed to restore modal states:', error);
      }
    }
  }, []);

  // Update URL when navigation states change
  const updateURL = useCallback((newActiveTab, subTabStates = {}) => {
    const url = new URL(window.location);
    
    // Update main tab in hash
    url.hash = newActiveTab;
    
    // Clear all sub-tab parameters first
    url.searchParams.delete('keywordTab');
    url.searchParams.delete('campaignTab');
    url.searchParams.delete('settingsTab');
    url.searchParams.delete('generalSettingsTab');
    
    // Add relevant sub-tab parameters
    if (newActiveTab === 'seo-aeo' && subTabStates.keywordTab) {
      url.searchParams.set('keywordTab', subTabStates.keywordTab);
    }
    if (newActiveTab === 'campaigns' && subTabStates.campaignTab) {
      url.searchParams.set('campaignTab', subTabStates.campaignTab);
    }
    if (newActiveTab === 'settings') {
      if (subTabStates.settingsTab) {
        url.searchParams.set('settingsTab', subTabStates.settingsTab);
      }
      if (subTabStates.generalSettingsTab) {
        url.searchParams.set('generalSettingsTab', subTabStates.generalSettingsTab);
      }
    }
    
    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Save modal states to localStorage
  const saveModalStates = useCallback(() => {
    const modalStates = {
      showManualKeywordDialog,
      showMigrationDialog,
      showDeleteConfirmDialog,
      showBulkDeleteDialog,
      manualKeywordForm: showManualKeywordDialog ? manualKeywordForm : null,
      timestamp: Date.now()
    };
    localStorage.setItem('omnimarket_modal_states', JSON.stringify(modalStates));
  }, [showManualKeywordDialog, showMigrationDialog, showDeleteConfirmDialog, showBulkDeleteDialog, manualKeywordForm]);

  // Initialize state from URL on component mount
  useEffect(() => {
    initializeStateFromURL();
  }, [initializeStateFromURL]);

  // Update URL when activeTab changes
  useEffect(() => {
    if (activeTab !== 'dashboard') { // Don't update URL for default dashboard
      updateURL(activeTab, {
        keywordTab: keywordIntelligenceTab,
        campaignTab: activeCampaignTab,
        settingsTab: activeSettingsTab,
        generalSettingsTab: activeGeneralSettingsTab
      });
    }
  }, [activeTab, updateURL]);

  // Update URL when sub-tabs change
  useEffect(() => {
    if (activeTab === 'seo-aeo') {
      updateURL(activeTab, { keywordTab: keywordIntelligenceTab });
    }
  }, [keywordIntelligenceTab, activeTab, updateURL]);

  useEffect(() => {
    if (activeTab === 'campaigns') {
      updateURL(activeTab, { campaignTab: activeCampaignTab });
    }
  }, [activeCampaignTab, activeTab, updateURL]);

  useEffect(() => {
    if (activeTab === 'settings') {
      updateURL(activeTab, { 
        settingsTab: activeSettingsTab,
        generalSettingsTab: activeGeneralSettingsTab 
      });
    }
  }, [activeSettingsTab, activeGeneralSettingsTab, activeTab, updateURL]);

  // Save modal states when they change
  useEffect(() => {
    saveModalStates();
  }, [saveModalStates]);

  // === END STATE PERSISTENCE AND URL ROUTING ===

  // Suppress ResizeObserver loop errors - common in React applications
  useEffect(() => {
    const resizeObserverErrorHandler = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverError = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverError) {
          resizeObserverError.setAttribute('style', 'display: none');
        }
        e.stopImmediatePropagation();
      }
    };
    
    window.addEventListener('error', resizeObserverErrorHandler);
    
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
    };
  }, []);

  // Load user data on startup
  useEffect(() => {
    if (token) {
      loadUserData();
    }
  }, [token]);

  // Load enhanced audience data when switching to Audience tab
  useEffect(() => {
    if (token && activeTab === 'audience') {
      loadAudienceData();
      loadRecentOperations(); // Load recent operations for undo functionality
      
      // Load enhanced audience data with proper sequencing
      const loadEnhancedData = async () => {
        try {
          console.log('🔄 Loading enhanced audience data...');
          await Promise.all([
            loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter),
            loadEnhancedAudienceStats()
          ]);
          console.log('✅ Enhanced audience data loaded successfully');
        } catch (error) {
          console.error('❌ Failed to load enhanced audience data:', error);
        }
      };
      
      loadEnhancedData();
    }
  }, [activeTab, token]);

  // Load SEO audit data when switching to SEO/AEO tab
  useEffect(() => {
    if (token && activeTab === 'seo-aeo') {
      loadLatestSEOAudit();
      loadSEOAuditHistory();
      loadCurrentEcommerceConnection(); // Load connection status for SEO tab
    }
  }, [activeTab, token]);

  // Load fields when switching to Personas tab
  useEffect(() => {
    if (token && activeTab === 'personas') {
      loadDashboardData(); // Reload fields for persona creation
    }
  }, [activeTab, token]);

  // Load dashboard data when switching to Dashboard tab
  useEffect(() => {
    if (token && activeTab === 'dashboard') {
      loadDashboardData(); // Reload dashboard analytics
    }
  }, [activeTab, token]);

  // Initialize filtered analytics when analytics data changes
  useEffect(() => {
    if (analytics && !filteredAnalytics) {
      setFilteredAnalytics(analytics);
    }
  }, [analytics, filteredAnalytics]);

  // Initialize campaign pagination when campaigns data changes
  useEffect(() => {
    if (campaigns.length > 0) {
      updatePaginatedCampaigns(1);
      setCampaignPage(1);
    }
  }, [campaigns, selectedCampaign]);

  // Initialize pending date ranges with current values
  useEffect(() => {
    setPendingDateRange(performanceDateRange);
  }, [performanceDateRange]);

  useEffect(() => {
    setUrlPendingDateRange(urlDateRange);
  }, [urlDateRange]);

  const loadUserData = async () => {
    if (!token) {
      setIsAuthLoading(false);
      return;
    }
    
    setIsAuthLoading(true);
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      loadDashboardData();
      if (response.data?.role === 'administrator') {
        loadAllUsers();
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      // Only remove token if it's a real authentication error (401), not a missing endpoint (404)
      if (error.response?.status === 401) {
        console.log('Authentication failed, removing token');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } else if (error.response?.status === 404) {
        console.log('User data endpoint not available, continuing with existing user data');
        // Create a basic user object from token if we don't have user data
        if (!user) {
          setUser({
            name: 'Administrator',
            email: 'admin@omnimarket360.com',
            role: 'administrator'
          });
        }
        // Continue with loading dashboard
        loadDashboardData();
        if (user?.role === 'administrator') {
          loadAllUsers();
        }
      }
    } finally {
      setIsAuthLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      // Load data independently to avoid one failure breaking everything
      const results = await Promise.allSettled([
        api.get('/api/audience/enhanced/fields'),
        api.get('/api/personas'),
        api.get('/api/campaigns'),
        api.get('/api/notifications'),
        api.get('/api/analytics/dashboard')
      ]);

      // Handle each result independently
      const [fieldsResult, personasResult, campaignsResult, notificationsResult, analyticsResult] = results;
      
      // Only set data for successful requests
      if (fieldsResult.status === 'fulfilled') {
        const fieldsData = fieldsResult.value.data.fields;
        console.log('Audience fields loaded for personas:', fieldsData);
        setAudienceFields(fieldsData);
      } else {
        console.warn('Failed to load audience fields:', fieldsResult.reason);
        // Set empty fields if endpoint doesn't exist
        setAudienceFields([]);
      }
      
      if (personasResult.status === 'fulfilled') {
        const allPersonas = personasResult.value.data.personas;
        // Separate manual personas and AI personas
        const manualPersonas = allPersonas.filter(p => 
          !p.generation_method || !p.generation_method.includes('clustering')
        );
        const aiGeneratedPersonas = allPersonas.filter(p => 
          p.generation_method && p.generation_method.includes('clustering')
        );
        
        setPersonas(manualPersonas);
        setAiPersonas(aiGeneratedPersonas);
      } else {
        console.warn('Failed to load personas:', personasResult.reason);
        setPersonas([]);
        setAiPersonas([]);
      }
      
      if (campaignsResult.status === 'fulfilled') {
        setCampaigns(campaignsResult.value.data.campaigns);
      } else {
        console.warn('Failed to load campaigns:', campaignsResult.reason);
        setCampaigns([]);
      }
      
      if (notificationsResult.status === 'fulfilled') {
        setNotifications(notificationsResult.value.data.notifications);
      } else {
        console.warn('Failed to load notifications:', notificationsResult.reason);
        setNotifications([]);
      }
      
      // CRITICAL: Always try to set analytics even if other requests fail
      if (analyticsResult.status === 'fulfilled') {
        setAnalytics(analyticsResult.value.data);
        console.log('Analytics data loaded successfully:', analyticsResult.value.data);
      } else {
        console.error('Failed to load analytics:', analyticsResult.reason);
        // Set default analytics to prevent dashboard from showing zeros
        setAnalytics({
          audience_metrics: {
            total_contacts: 0,
            new_contacts_30d: 0,
            high_value_contacts: 0,
            campaign_ready_personas: 0
          },
          total_contacts: 0,
          total_personas: 0,
          total_campaigns: 0
        });
      }
      
      // Load AI models configuration
      loadAIModelsConfig();
      
      // Always try to load audience data - don't depend on fields
      loadAudienceData();
      
      loadKeywordIntelligenceData(); // Load unified keyword intelligence data
      
      loadAudienceSchema();
      
      // Load URL analytics for dashboard
      loadUrlAnalytics();
      
      // Load enrichment data if user is administrator
      if (user?.role === 'administrator') {
        loadEnrichmentSettings();
        loadEnrichmentStatus();
        loadIntegrationSettings();
        loadIntegrationConfigs();
        loadIntegrationStatusSummary();
        loadAiModelsStatus(); // Load AI models status for dashboard
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  // === UNIFIED KEYWORD INTELLIGENCE HUB FUNCTIONS ===

  // Check migration status and load initial data
  const loadKeywordIntelligenceData = async () => {
    try {
      // Check migration status
      const migrationResponse = await api.get('/api/keyword-intelligence/migration-status');
      if (migrationResponse.data.success) {
        setMigrationStatus(migrationResponse.data);
        
        // If migration not needed, load unified keywords
        if (!migrationResponse.data.user_specific.migration_needed) {
          await loadUnifiedKeywords();
          await loadKeywordAnalytics();
          await loadHighOpportunityKeywords();
        }
      }
    } catch (error) {
      console.error('Failed to load keyword intelligence data:', error);
    }
  };

  // Load overall connections status (Phase 4 Enhancement)
  const loadConnectionsStatus = async () => {
    try {
      const response = await api.get('/api/connections/status');
      if (response.data.success) {
        setConnectionsStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to load connections status:', error);
    }
  };

  // Load unified keywords with filtering
  const loadUnifiedKeywords = async (filters = {}) => {
    try {
      setIsLoading(true);
      
      const params = new URLSearchParams();
      if (filters.site_url) params.append('site_url', filters.site_url);
      if (filters.status) params.append('status', filters.status);
      if (filters.search_query) params.append('search_query', filters.search_query);
      if (filters.min_score) params.append('min_score', filters.min_score);
      if (filters.max_difficulty) params.append('max_difficulty', filters.max_difficulty);
      if (filters.tags) params.append('tags', filters.tags);
      // Default to showing 100 keywords per page, but allow override
      params.append('limit', filters.limit || '100');
      params.append('page', filters.page || '1');
      params.append('sort_by', filters.sort_by || 'created_at');
      params.append('sort_order', filters.sort_order || 'desc');
      
      console.log('🔍 Loading unified keywords with params:', {
        limit: filters.limit || '100',
        page: filters.page || '1',
        total_expected: unifiedKeywordStats.total
      });
      
      const response = await api.get(`/api/keyword-intelligence/keywords?${params}`);
      
      if (response.data.success) {
        setUnifiedKeywords(response.data.keywords);
        setUnifiedKeywordStats({
          total: response.data.pagination.total_count,
          page: response.data.pagination.page,
          totalPages: response.data.pagination.total_pages,
          hasNext: response.data.pagination.has_next,
          hasPrev: response.data.pagination.has_prev
        });
        
        console.log('✅ Keywords loaded:', {
          keywords_returned: response.data.keywords.length,
          total_available: response.data.pagination.total_count,
          current_page: response.data.pagination.page,
          total_pages: response.data.pagination.total_pages
        });
      }
      
    } catch (error) {
      console.error('Failed to load unified keywords:', error);
      toast.error('Failed to load keywords');
    } finally {
      setIsLoading(false);
    }
  };

  // Load keyword analytics with strategic intelligence
  const loadKeywordAnalytics = async (days = 30) => {
    try {
      console.log('🔍 Loading enhanced keyword analytics...');
      const response = await api.get('/api/keyword-intelligence/strategic-analytics');
      
      console.log('✅ Analytics API response received:', response);
      
      if (response.data.success) {
        // Store the complete enhanced analytics response
        setKeywordAnalytics({
          ...response.data,
          // Ensure backward compatibility with existing fields
          total_keywords: response.data.total_keywords || 0,
          high_priority_keywords: response.data.high_priority_keywords || 0,
          content_opportunities: response.data.content_opportunities || 0,
          success_metrics: response.data.success_metrics || {},
          strategic_distribution: response.data.strategic_distribution || {},
          // Enhanced analytics fields
          performance_insights: response.data.performance_insights || null,
          keyword_trends: response.data.keyword_trends || null,
          competitive_analysis: response.data.competitive_analysis || null,
          content_strategy_metrics: response.data.content_strategy_metrics || null,
          growth_trajectory: response.data.growth_trajectory || null
        });
        
        console.log('✅ Enhanced keyword analytics loaded successfully');
        console.log('Analytics fields loaded:', {
          performance_insights: !!response.data.performance_insights,
          keyword_trends: !!response.data.keyword_trends,
          competitive_analysis: !!response.data.competitive_analysis,
          content_strategy_metrics: !!response.data.content_strategy_metrics,
          growth_trajectory: !!response.data.growth_trajectory
        });
        
        // Update unified keyword stats with strategic data
        setUnifiedKeywordStats(prev => ({
          ...prev,
          total: response.total_keywords || 0,
          high_priority: response.high_priority_keywords || 0,
          content_opportunities: response.content_opportunities || 0
        }));
        
        // Update current keyword research metrics if strategic summary available
        if (response.data.strategic_summary) {
          const summary = response.data.strategic_summary;
          setCurrentKeywordResearch(prev => ({
            ...prev,
            competitors_analyzed: summary.summary_metrics?.total_keywords_analyzed || prev?.competitors_analyzed || 0,
            total_keywords_found: (summary.strategic_segments?.quick_wins || 0) + (summary.strategic_segments?.strategic_opportunities || 0),
            high_priority_keywords: summary.strategic_segments?.quick_wins || 0,
            content_opportunities: summary.content_opportunity_count || 0
          }));
        }
        
      } else {
        console.error('❌ Analytics API returned unsuccessful response:', response.data);
        throw new Error(response.data.message || 'Failed to load analytics');
      }
      
    } catch (error) {
      console.error('Failed to load keyword analytics:', error);
      // Set default values on error
      setKeywordAnalytics({
        total_keywords: 0,
        high_priority_keywords: 0,
        content_opportunities: 0,
        success_metrics: {},
        strategic_distribution: {},
        performance_insights: null,
        keyword_trends: null,
        competitive_analysis: null,
        content_strategy_metrics: null,
        growth_trajectory: null
      });
      toast.error('Failed to load analytics data');
    }
  };

  // Load high opportunity keywords with strategic intelligence
  const loadHighOpportunityKeywords = async (limit = 20) => {
    try {
      const response = await api.get(`/api/keyword-intelligence/high-priority?limit=${limit}`);
      if (response.success) {
        setHighOpportunityKeywords(response.keywords.map(keyword => ({
          keyword: keyword.keyword,
          opportunity_score: Math.round(keyword.strategic_priority || 0),
          success_probability: Math.round(keyword.success_probability || 0),
          priority_category: keyword.priority_category || 'Medium',
          success_category: keyword.success_category || 'Possible',
          predicted_traffic: keyword.predicted_traffic?.monthly_traffic_estimate || 0,
          business_value: Math.round(keyword.business_value_score || 0),
          content_opportunities: keyword.content_opportunity_count || 0,
          strategic_reasoning: keyword.decision_reasoning ? keyword.decision_reasoning[0] : 'Strategic opportunity identified'
        })));
      }
    } catch (error) {
      console.error('Failed to load high opportunity keywords:', error);
      setHighOpportunityKeywords([]);
    }
  };

  // Run keyword migration
  const runKeywordMigration = async () => {
    try {
      setMigrationProgress({ step: 'starting', message: 'Preparing migration...', progress: 0 });
      
      const response = await api.post('/api/keyword-intelligence/migrate');
      
      if (response.data.success) {
        setMigrationProgress({ 
          step: 'completed', 
          message: 'Migration completed successfully!', 
          progress: 100,
          stats: response.data.migration_stats
        });
        
        // Refresh data after migration
        await loadKeywordIntelligenceData();
        
        toast.success(`✅ Migration completed! ${response.data.migration_stats.seo_keywords_migrated + response.data.migration_stats.research_keywords_migrated} keywords migrated.`);
        
        setTimeout(() => {
          setShowMigrationDialog(false);
          setMigrationProgress(null);
        }, 3000);
      }
      
    } catch (error) {
      console.error('Migration failed:', error);
      setMigrationProgress({ 
        step: 'error', 
        message: 'Migration failed', 
        progress: 0,
        error: error.response?.data?.detail || error.message
      });
      toast.error(`Migration failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Run AI research integration
  const runUnifiedAIResearch = async (siteUrl) => {
    try {
      setIsLoading(true);
      toast.info('🔍 Starting AI-powered keyword research...', { duration: 3000 });
      
      const response = await api.post('/api/keyword-intelligence/ai-research', {
        site_url: siteUrl,
        research_params: {}
      });
      
      if (response.success) {
        toast.success(`✅ AI Research completed! ${response.keywords_discovered} keywords discovered and integrated.`);
        await loadUnifiedKeywords();
        await loadKeywordAnalytics();
        await loadHighOpportunityKeywords();
      } else {
        toast.error('AI Research failed: ' + response.error);
      }
      
    } catch (error) {
      console.error('AI research failed:', error);
      toast.error(`AI research failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper function for AI keyword research
  const runAIKeywordResearch = async () => {
    if (currentEcommerceConnection?.store_url) {
      await runUnifiedAIResearch(currentEcommerceConnection.store_url);
    } else {
      toast.error('Please connect your website first to run AI keyword research');
    }
  };

  // Add new keyword
  const addUnifiedKeyword = async (keywordData) => {
    try {
      const response = await api.post('/api/keyword-intelligence/keywords', keywordData);
      
      if (response.data.success) {
        toast.success('✅ Keyword added successfully!');
        await loadUnifiedKeywords();
        return response.data.keyword;
      }
      
    } catch (error) {
      console.error('Failed to add keyword:', error);
      toast.error(`Failed to add keyword: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  // Update keyword
  const updateUnifiedKeyword = async (keywordId, updateData) => {
    try {
      const response = await api.put(`/api/keyword-intelligence/keywords/${keywordId}`, updateData);
      
      if (response.data.success) {
        toast.success('✅ Keyword updated successfully!');
        await loadUnifiedKeywords();
        return response.data.keyword;
      }
      
    } catch (error) {
      console.error('Failed to update keyword:', error);
      toast.error(`Failed to update keyword: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  // Delete keyword
  const deleteUnifiedKeyword = async (keywordId) => {
    try {
      const response = await api.delete(`/api/keyword-intelligence/keywords/${keywordId}`);
      
      if (response.data.success) {
        toast.success('✅ Keyword deleted successfully!');
        await loadUnifiedKeywords();
      }
      
    } catch (error) {
      console.error('Failed to delete keyword:', error);
      toast.error(`Failed to delete keyword: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Bulk operations
  const bulkUpdateUnifiedKeywords = async (keywordIds, updateData) => {
    try {
      const response = await api.post('/api/keyword-intelligence/keywords/bulk-update', {
        keyword_ids: keywordIds,
        update_data: updateData
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.updated_count} keywords updated successfully!`);
        await loadUnifiedKeywords();
      }
      
    } catch (error) {
      console.error('Bulk update failed:', error);
      toast.error(`Bulk update failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Export unified keywords
  const exportUnifiedKeywords = async (format = 'csv', filters = {}) => {
    try {
      console.log('🔍 Starting Unified Keywords Export...');
      console.log('Format:', format, 'Filters:', filters);
      
      // Show loading state
      setIsLoading(true);
      toast.info('📊 Preparing keywords export...', { duration: 3000 });
      
      const params = new URLSearchParams();
      if (filters.site_url) params.append('site_url', filters.site_url);
      if (filters.status) params.append('status', filters.status);
      if (filters.min_score) params.append('min_score', filters.min_score);
      
      console.log('API URL:', `/api/keyword-intelligence/export-csv?${params}`);
      
      const response = await api.get(`/api/keyword-intelligence/export-csv?${params}`, {
        responseType: 'blob'
      });
      
      console.log('✅ Export API Response received');
      console.log('Response type:', typeof response.data);
      console.log('Response size:', response.data.size);
      
      // Verify we got a blob
      if (!(response.data instanceof Blob)) {
        throw new Error('Expected blob response but got: ' + typeof response.data);
      }
      
      // Create download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Unified_Keywords_${new Date().toISOString().split('T')[0]}.csv`;
      link.style.display = 'none';
      
      console.log('📥 Triggering download:', link.download);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download completed successfully');
      toast.success('✅ Keywords exported successfully!');
      
    } catch (error) {
      console.error('❌ Export failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // More detailed error message
      let errorMessage = 'Export failed';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Export endpoint not found. Please try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error during export. Please try again.';
      } else if (error.message) {
        errorMessage = `Export failed: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Load filtered campaign analytics
  const loadFilteredAnalytics = async (campaignId = 'all', startDate = '', endDate = '') => {
    try {
      let queryParams = new URLSearchParams();
      
      if (campaignId !== 'all') {
        queryParams.append('campaign_id', campaignId);
      }
      if (startDate) {
        queryParams.append('start_date', startDate);
      }
      if (endDate) {
        queryParams.append('end_date', endDate);
      }
      
      const queryString = queryParams.toString();
      const endpoint = `/api/analytics/dashboard${queryString ? '?' + queryString : ''}`;
      
      const response = await api.get(endpoint);
      setFilteredAnalytics(response.data);
      
      console.log(`Dashboard Analytics loaded for campaign: ${campaignId}, campaigns count: ${response.data?.recent_campaigns?.length || 0}`);
    } catch (error) {
      console.error('Failed to load filtered analytics:', error);
      // Fall back to original analytics if filtering fails
      if (campaignId === 'all') {
        setFilteredAnalytics(analytics);
      }
    }
  };

  // Handle campaign selection change
  const handleCampaignFilterChange = async (campaignId) => {
    setSelectedCampaign(campaignId);
    setCampaignPage(1); // Reset to first page when filter changes
    
    // Load the filtered analytics data
    await loadFilteredAnalytics(campaignId, performanceDateRange.startDate, performanceDateRange.endDate);
    
    // Update pagination with new filter - this should show all campaigns when 'all' is selected
    updatePaginatedCampaigns(1); 
  };

  // Handle date range change (for pending state only)
  const handleDateRangeChange = (newDateRange) => {
    setPendingDateRange(newDateRange);
  };

  // Submit date range filter
  const submitDateRangeFilter = async () => {
    setPerformanceDateRange(pendingDateRange);
    await loadFilteredAnalytics(selectedCampaign, pendingDateRange.startDate, pendingDateRange.endDate);
  };

  // Clear date range filter
  const clearDateRangeFilter = async () => {
    const clearedRange = { startDate: '', endDate: '' };
    setPerformanceDateRange(clearedRange);
    setPendingDateRange(clearedRange);
    await loadFilteredAnalytics(selectedCampaign, '', '');
  };

  // ===== ADVANCED FILTERING & SEGMENTATION FUNCTIONS =====
  
  // Load field summary for filter building
  const loadFieldSummary = async () => {
    try {
      const response = await api.get('/api/audience/fields/summary');
      setFieldSummary(response.data.field_summary);
      console.log('Field summary loaded:', response.data.field_summary);
    } catch (error) {
      console.error('Failed to load field summary:', error);
      toast.error('Failed to load field information for filtering');
    }
  };
  
  // Apply advanced filter
  const applyAdvancedFilter = async (filterConfig = null) => {
    const filterToApply = filterConfig || currentFilter;
    
    if (!filterToApply.conditions || filterToApply.conditions.length === 0) {
      toast.error('Please add at least one filter condition');
      return;
    }
    
    try {
      setIsLoading(true);
      toast.info(`🔍 Applying ${filterToApply.name || 'advanced filter'}...`, { duration: 3000 });
      
      const response = await api.post('/api/audience/filter', filterToApply);
      setFilterResults(response.data);
      
      toast.success(
        `✅ Filter applied successfully!\n` +
        `📊 ${response.data.filter_summary.total_matches} contacts match your criteria\n` +
        `🎯 ${response.data.filter_summary.conditions_applied} conditions applied`
      );
      
    } catch (error) {
      console.error('Failed to apply filter:', error);
      toast.error(`Failed to apply filter: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save current filter as segment
  const saveCurrentSegment = async () => {
    if (!currentFilter.name) {
      toast.error('Please enter a segment name');
      return;
    }
    
    if (!currentFilter.conditions || currentFilter.conditions.length === 0) {
      toast.error('Please add at least one filter condition');
      return;
    }
    
    try {
      setIsLoading(true);
      
      const response = await api.post('/api/audience/segments', {
        ...currentFilter,
        is_saved: true
      });
      
      toast.success(
        `✅ Segment "${currentFilter.name}" saved!\n` +
        `📊 ${response.data.segment_size} contacts in this segment`
      );
      
      // Reload saved segments
      await loadSavedSegments();
      setShowSaveSegmentDialog(false);
      
    } catch (error) {
      console.error('Failed to save segment:', error);
      toast.error(`Failed to save segment: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load saved segments
  const loadSavedSegments = async () => {
    try {
      const response = await api.get('/api/audience/segments');
      setSavedSegments(response.data.segments);
    } catch (error) {
      console.error('Failed to load saved segments:', error);
    }
  };
  
  // Add filter condition
  const addFilterCondition = () => {
    const newCondition = {
      field: '',
      operator: 'equals',
      value: '',
      value_type: 'string'
    };
    
    setCurrentFilter(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };
  
  // Update filter condition
  const updateFilterCondition = (index, updates) => {
    setCurrentFilter(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => 
        i === index ? { ...condition, ...updates } : condition
      )
    }));
  };
  
  // Remove filter condition
  const removeFilterCondition = (index) => {
    setCurrentFilter(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };
  
  // Clear current filter
  const clearCurrentFilter = () => {
    setCurrentFilter({
      name: "",
      description: "",
      conditions: [],
      logic: "AND"
    });
    setFilterResults(null);
  };

  // ===== DUPLICATE DETECTION FUNCTIONS =====
  
  const runDuplicateDetection = async () => {
    try {
      setIsLoading(true);
      toast.info('🔍 Running duplicate detection analysis...', { duration: 3000 });
      
      const response = await api.post('/api/audience/detect-duplicates', duplicateDetectionSettings);
      setDuplicateResults(response.data);
      
      toast.success(
        `🔍 Duplicate Detection Complete!\n` +
        `👥 ${response.data.summary.total_contacts_analyzed} contacts analyzed\n` +
        `🔄 ${response.data.summary.duplicate_groups_found} duplicate groups found\n` +
        `📊 ${response.data.summary.total_duplicates} potential duplicates identified`
      );
      
    } catch (error) {
      console.error('Duplicate detection failed:', error);
      toast.error(`Duplicate detection failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const mergeDuplicates = async (detectionId, groupIds) => {
    if (!groupIds || groupIds.length === 0) {
      toast.error('Please select duplicate groups to merge');
      return;
    }
    
    try {
      setIsLoading(true);
      toast.info(`🔄 Merging ${groupIds.length} duplicate groups...`, { duration: 3000 });
      
      const response = await api.post('/api/audience/merge-duplicates', null, {
        params: {
          detection_id: detectionId,
          group_ids: groupIds.join(','),
          merge_strategy: 'quality_priority'
        }
      });
      
      toast.success(
        `✅ Duplicates merged successfully!\n` +
        `🔄 ${response.data.merge_results.length} groups merged\n` +
        `📊 Refreshing audience data...`
      );
      
      // Refresh audience data
      await loadAudienceData();
      setDuplicateResults(null);
      
    } catch (error) {
      console.error('Failed to merge duplicates:', error);
      toast.error(`Failed to merge duplicates: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== DATA VALIDATION FUNCTIONS =====
  
  const runDataValidation = async () => {
    try {
      setIsLoading(true);
      toast.info('✅ Running comprehensive data validation...', { duration: 3000 });
      
      const response = await api.post('/api/audience/validate-data', validationSettings);
      setValidationResults(response.data);
      
      let successMessage = `✅ Data Validation Complete!\n📊 ${response.data.validated_contacts} contacts validated`;
      
      if (response.data.field_results) {
        const validationSummary = Object.entries(response.data.validation_summary)
          .map(([field, stats]) => `${field}: ${(stats.validation_rate * 100).toFixed(1)}% valid`)
          .join('\n');
        
        successMessage += `\n\n📈 Validation Summary:\n${validationSummary}`;
      }
      
      toast.success(successMessage, { duration: 10000 });
      
      // Refresh audience data if records were updated
      if (validationSettings.update_records) {
        await loadAudienceData();
      }
      
    } catch (error) {
      console.error('Data validation failed:', error);
      toast.error(`Data validation failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== DATA ENRICHMENT FUNCTIONS =====
  
  const runDataEnrichment = async () => {
    try {
      setIsLoading(true);
      toast.info('🚀 Starting data enrichment process...', { duration: 3000 });
      
      const response = await api.post('/api/audience/enrich-data', dataEnrichmentSettings);
      setEnrichmentResults(response.data);
      
      toast.success(
        `🚀 Data Enrichment Complete!\n` +
        `📈 ${response.data.enriched_contacts} contacts enriched\n` +
        `🆕 ${response.data.new_fields_added.length} new fields added\n` +
        `💡 ${(response.data.enrichment_summary.enrichment_rate * 100).toFixed(1)}% enrichment rate`
      );
      
      // Refresh audience data and fields
      await loadAudienceData();
      
      try {
        const fieldsRes = await api.get('/api/audience/enhanced/fields');
        setAudienceFields(fieldsRes.data.fields);
      } catch (fieldError) {
        console.error('Failed to refresh fields:', fieldError);
      }
      
    } catch (error) {
      console.error('Data enrichment failed:', error);
      toast.error(`Data enrichment failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== DATA QUALITY FUNCTIONS =====
  
  const loadQualityReport = async () => {
    try {
      setIsLoading(true);
      toast.info('📊 Generating comprehensive quality report...', { duration: 3000 });
      
      const response = await api.get('/api/audience/quality-report');
      setQualityReport(response.data);
      
      toast.success(
        `📊 Quality Report Generated!\n` +
        `📈 Overall Quality Score: ${(response.data.overall_quality_score * 100).toFixed(1)}%\n` +
        `💡 ${response.data.recommendations?.length || 0} recommendations available`
      );
      
    } catch (error) {
      console.error('Failed to load quality report:', error);
      toast.error(`Failed to generate quality report: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load field summary when advanced filters are opened
  useEffect(() => {
    if (showAdvancedFilters && Object.keys(fieldSummary).length === 0) {
      loadFieldSummary();
    }
  }, [showAdvancedFilters]);

  // Load saved segments when component mounts
  useEffect(() => {
    if (token && activeTab === 'audience') {
      loadSavedSegments();
    }
  }, [token, activeTab]);

  // ===== CRM INTEGRATION FUNCTIONS =====
  
  // Load supported CRMs
  const loadSupportedCrms = async () => {
    try {
      const response = await api.get('/api/crm/supported-crms');
      setSupportedCrms(response.data.supported_crms);
    } catch (error) {
      console.error('Failed to load supported CRMs:', error);
    }
  };
  
  // Load CRM connections
  const loadCrmConnections = async () => {
    try {
      const response = await api.get('/api/crm/connections');
      setCrmConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to load CRM connections:', error);
      toast.error('Failed to load CRM connections');
    }
  };
  
  // Create CRM connection
  const createCrmConnection = async () => {
    if (!selectedCrmType || !connectionName || Object.keys(crmCredentials).length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setIsLoading(true);
      toast.info('🔗 Testing CRM connection...', { duration: 3000 });
      
      const payload = {
        crm_type: selectedCrmType,
        connection_name: connectionName,
        credentials: crmCredentials,
        field_mappings: {},
        sync_settings: {
          sync_frequency: 'manual',
          auto_sync: false,
          conflict_resolution: 'manual'
        }
      };
      
      const response = await api.post('/api/crm/connections', payload);
      
      toast.success(
        `✅ CRM Connection Created!\n` +
        `🔗 ${response.data.connection_name}\n` +
        `📊 Type: ${response.data.crm_type.toUpperCase()}\n` +
        `🎯 Status: ${response.data.status}`
      );
      
      // Reset form
      setSelectedCrmType('');
      setConnectionName('');
      setCrmCredentials({});
      setShowConnectionWizard(false);
      
      // Reload connections
      await loadCrmConnections();
      
    } catch (error) {
      console.error('Failed to create CRM connection:', error);
      toast.error(`Failed to create CRM connection: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Trigger CRM sync
  const triggerCrmSync = async (connectionId, syncType = 'incremental') => {
    try {
      setIsLoading(true);
      toast.info('🔄 Starting CRM synchronization...', { duration: 3000 });
      
      const response = await api.post(`/api/crm/sync/${connectionId}`, null, {
        params: {
          sync_type: syncType,
          direction: 'bidirectional'
        }
      });
      
      toast.success(
        `🔄 CRM Sync Completed!\n` +
        `📊 Processed: ${response.data.records_processed} records\n` +
        `✅ Synced: ${response.data.records_synced} records\n` +
        `❌ Failed: ${response.data.records_failed} records\n` +
        `⏱️ Duration: ${response.data.duration_seconds}s`
      );
      
      // Refresh audience data and CRM stats
      await loadAudienceData();
      await loadCrmStats();
      await loadSyncJobs();
      
    } catch (error) {
      console.error('Failed to trigger CRM sync:', error);
      toast.error(`CRM sync failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load CRM statistics
  const loadCrmStats = async () => {
    try {
      const response = await api.get('/api/crm/sync-stats');
      setCrmStats(response.data);
    } catch (error) {
      console.error('Failed to load CRM stats:', error);
    }
  };
  
  // Load sync job history
  const loadSyncJobs = async () => {
    try {
      const response = await api.get('/api/crm/sync-jobs', {
        params: { limit: 20 }
      });
      setSyncJobs(response.data.sync_jobs);
    } catch (error) {
      console.error('Failed to load sync jobs:', error);
    }
  };
  
  // Delete CRM connection
  const deleteCrmConnection = async (connectionId) => {
    try {
      await api.delete(`/api/crm/connections/${connectionId}`);
      toast.success('CRM connection deleted successfully');
      await loadCrmConnections();
    } catch (error) {
      console.error('Failed to delete CRM connection:', error);
      toast.error('Failed to delete CRM connection');
    }
  };
  
  // Load CRM data when CRM hub is opened
  useEffect(() => {
    if (activeSettingsTab === 'crm-integration' && token) {
      loadSupportedCrms();
      loadCrmConnections();
      loadCrmStats();
      loadSyncJobs();
    }
  }, [activeSettingsTab, token]);
  
  // Load CRM sync status for audience management
  const loadCrmSyncStatus = async () => {
    try {
      const response = await api.get('/api/audience/crm-sync-status');
      if (response.data.success) {
        setCrmSyncStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to load CRM sync status:', error);
      // Set default state on error
      setCrmSyncStatus({
        connected: false,
        active_connections: 0,
        total_connections: 0,
        total_synced_records: 0,
        last_sync: null,
        connections: []
      });
    }
  };
  
  // Load CRM sync status when audience tab is opened
  useEffect(() => {
    if (activeTab === 'audience' && token) {
      loadCrmSyncStatus();
      loadSalesforceStatus(); // Also load Salesforce status
    }
  }, [activeTab, token]);

  // NEW: Function to sync CRM data to audience
  const syncCrmToAudience = async (connectionId) => {
    try {
      setIsLoading(true);
      toast.info('🔄 Starting CRM sync to Audience...');
      
      const response = await api.post(`/api/audience/sync-from-crm/${connectionId}`);
      
      if (response.data.success) {
        toast.success(
          `✅ CRM Sync Complete! ${response.data.statistics.contacts_created} new contacts created, ${response.data.statistics.contacts_updated} updated.`
        );
        
        // Refresh audience data and CRM sync status
        loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter);
        loadEnhancedAudienceStats();
        loadCrmSyncStatus();
        
        return response.data;
      } else {
        throw new Error(response.data.message || 'CRM sync failed');
      }
    } catch (error) {
      console.error('CRM sync error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to sync CRM data';
      toast.error(`❌ CRM Sync Failed: ${errorMessage}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Salesforce-specific functions
  const [salesforceStatus, setSalesforceStatus] = useState({
    authenticated: false,
    demo_mode: true,
    has_credentials: false
  });

  // Load Salesforce status
  const loadSalesforceStatus = async () => {
    try {
      const response = await api.get('/api/crm/salesforce/status');
      if (response.data.success) {
        setSalesforceStatus(response.data.salesforce_status);
      }
    } catch (error) {
      console.error('Failed to load Salesforce status:', error);
      // Set demo mode as default
      setSalesforceStatus({
        authenticated: false,
        demo_mode: true,
        has_credentials: false,
        domain: 'demo.salesforce.com',
        api_version: 'v58.0'
      });
    }
  };

  // Trigger demo Salesforce sync
  const triggerDemoSalesforceSync = async () => {
    try {
      setIsLoading(true);
      toast.info('🔄 Starting Salesforce demo sync...');
      
      const response = await api.post('/api/crm/salesforce/demo-sync');
      
      if (response.data.success) {
        toast.success(
          `✅ Salesforce Demo Sync Complete! ${response.data.statistics.contacts_created} new contacts created, ${response.data.statistics.contacts_updated} updated.`
        );
        
        // Refresh data
        await loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter);
        await loadEnhancedAudienceStats();
        await loadCrmSyncStatus();
      }
      
    } catch (error) {
      console.error('Salesforce demo sync failed:', error);
      toast.error('❌ Salesforce demo sync failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Test CRM connection
  const testCrmConnection = async (connectionId) => {
    try {
      setIsLoading(true);
      toast.info('🔄 Testing CRM connection...');
      
      const response = await api.post(`/api/crm/connections/${connectionId}/test`);
      
      if (response.data.test_result.success) {
        const testResult = response.data.test_result;
        toast.success(
          `✅ Connection successful! Found ${testResult.sample_data.contacts_found} contacts and ${testResult.sample_data.leads_found || 0} leads.`
        );
      } else {
        const testResult = response.data.test_result;
        toast.error('❌ Connection test failed: ' + testResult.error);
      }
      
    } catch (error) {
      console.error('CRM connection test failed:', error);
      toast.error('❌ Connection test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // ===== LINKED SECTION MANAGEMENT FUNCTIONS =====

  // Load linked sections for a contact
  const loadContactLinkedSections = async (contactId) => {
    try {
      const response = await api.get(`/api/audience/enhanced/contacts/${contactId}/linked-sections`);
      
      if (response.data.success) {
        setContactLinkedSections(response.data.linked_sections);
        return response.data;
      } else {
        toast.error('❌ Failed to load linked sections: ' + response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error loading linked sections:', error);
      toast.error('❌ Error loading linked sections: ' + (error.response?.data?.detail || error.message));
      return null;
    }
  };

  // Get section template
  const loadSectionTemplate = async (sectionType) => {
    try {
      const response = await api.get(`/api/audience/enhanced/linked-sections/templates/${sectionType}`);
      
      if (response.data.success) {
        setSectionTemplate(response.data.template);
        return response.data.template;
      } else {
        toast.error('❌ Failed to load section template: ' + response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error loading section template:', error);
      toast.error('❌ Error loading section template: ' + (error.response?.data?.detail || error.message));
      return null;
    }
  };

  // Add new linked section entry
  const addLinkedSectionEntry = async (contactId, sectionType, entryData) => {
    try {
      setIsLoading(true);
      
      const response = await api.post(
        `/api/audience/enhanced/contacts/${contactId}/linked-sections/${sectionType}`,
        entryData
      );
      
      if (response.data.success) {
        toast.success(`✅ ${sectionType.replace('_', ' ')} entry added successfully!`);
        
        // Reload linked sections for the contact
        await loadContactLinkedSections(contactId);
        
        // Close dialog and reset form
        setShowAddLinkedSectionEntry(false);
        setNewEntryData({});
        
        return response.data;
      } else {
        toast.error('❌ Failed to add entry: ' + response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error adding linked section entry:', error);
      toast.error('❌ Error adding entry: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Update linked section entry
  const updateLinkedSectionEntry = async (contactId, sectionType, entryId, entryData) => {
    try {
      setIsLoading(true);
      
      const response = await api.put(
        `/api/audience/enhanced/contacts/${contactId}/linked-sections/${sectionType}/${entryId}`,
        entryData
      );
      
      if (response.data.success) {
        toast.success(`✅ ${sectionType.replace('_', ' ')} entry updated successfully!`);
        
        // Reload linked sections for the contact
        await loadContactLinkedSections(contactId);
        
        // Close edit dialog
        setEditingEntry(null);
        
        return response.data;
      } else {
        toast.error('❌ Failed to update entry: ' + response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error updating linked section entry:', error);
      toast.error('❌ Error updating entry: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Delete linked section entry
  const deleteLinkedSectionEntry = async (contactId, sectionType, entryId) => {
    try {
      setIsLoading(true);
      
      const response = await api.delete(
        `/api/audience/enhanced/contacts/${contactId}/linked-sections/${sectionType}/${entryId}`
      );
      
      if (response.data.success) {
        toast.success(`✅ ${sectionType.replace('_', ' ')} entry deleted successfully!`);
        
        // Reload linked sections for the contact
        await loadContactLinkedSections(contactId);
        
        return response.data;
      } else {
        toast.error('❌ Failed to delete entry: ' + response.data.error);
        return null;
      }
    } catch (error) {
      console.error('Error deleting linked section entry:', error);
      toast.error('❌ Error deleting entry: ' + (error.response?.data?.detail || error.message));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Search linked sections
  const searchLinkedSections = async (sectionType = null, searchTerm = null) => {
    try {
      const params = new URLSearchParams();
      if (sectionType) params.append('section_type', sectionType);
      if (searchTerm) params.append('search_term', searchTerm);
      params.append('limit', '100');
      
      const response = await api.get(`/api/audience/enhanced/linked-sections/search?${params}`);
      
      if (response.data.success) {
        return response.data.results;
      } else {
        toast.error('❌ Search failed: ' + response.data.error);
        return [];
      }
    } catch (error) {
      console.error('Error searching linked sections:', error);
      toast.error('❌ Search error: ' + (error.response?.data?.detail || error.message));
      return [];
    }
  };

  // Open linked sections dialog for a contact
  const openLinkedSections = async (contact) => {
    setSelectedContactForSections(contact);
    setShowLinkedSections(true);
    
    // Load linked sections data
    await loadContactLinkedSections(contact.id);
  };

  // Handle add value button click
  const handleAddValue = async (sectionType) => {
    setSelectedSectionType(sectionType);
    setNewEntryData({});
    
    // Load template for this section type
    await loadSectionTemplate(sectionType);
    
    setShowAddLinkedSectionEntry(true);
  };

  // Handle edit entry
  const handleEditEntry = async (sectionType, entry) => {
    setSelectedSectionType(sectionType);
    setEditingEntry(entry);
    
    // Load template for this section type
    await loadSectionTemplate(sectionType);
    
    // Pre-populate form with entry data
    setNewEntryData({ ...entry });
    setShowAddLinkedSectionEntry(true);
  };

  // Function to handle CRM sync button click
  const handleCrmSync = async () => {
    try {
      // Get the first active CRM connection
      const activeConnection = crmSyncStatus.connections?.find(conn => 
        conn.connection_status === 'active'
      );
      
      if (!activeConnection) {
        toast.error('❌ No active CRM connection found');
        return;
      }
      
      await syncCrmToAudience(activeConnection.id);
      
    } catch (error) {
      console.error('Failed to sync CRM:', error);
    }
  };

  // ===== E-COMMERCE INTEGRATION FUNCTIONS =====

  // Load supported e-commerce platforms
  const loadSupportedEcommercePlatforms = async () => {
    try {
      const response = await api.get('/api/ecommerce/supported-platforms');
      setSupportedEcommercePlatforms(response.data.platforms);
    } catch (error) {
      console.error('Failed to load supported e-commerce platforms:', error);
      toast.error('Failed to load supported e-commerce platforms');
    }
  };

  // Load e-commerce connections
  const loadEcommerceConnections = async () => {
    try {
      const response = await api.get('/api/ecommerce/connections');
      setEcommerceConnections(response.data.connections);
    } catch (error) {
      console.error('Failed to load e-commerce connections:', error);
      toast.error('Failed to load e-commerce connections');
    }
  };

  // Load current e-commerce connection (SINGLE STORE)
  const loadCurrentEcommerceConnection = async () => {
    try {
      const response = await api.get('/api/ecommerce/connection');
      setCurrentEcommerceConnection(response.data.connection);
      // Also update the legacy connections array for backward compatibility
      if (response.data.connection) {
        setEcommerceConnections([response.data.connection]);
      } else {
        setEcommerceConnections([]);
      }
    } catch (error) {
      console.error('Failed to load current e-commerce connection:', error);
      toast.error('Failed to load e-commerce connection');
    }
  };

  // Load e-commerce analytics
  const loadEcommerceAnalytics = async () => {
    try {
      const response = await api.get('/api/ecommerce/analytics');
      setEcommerceAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load e-commerce analytics:', error);
      toast.error('Failed to load e-commerce analytics');
    }
  };

  // Load e-commerce sync jobs
  const loadEcommerceSyncJobs = async () => {
    try {
      const response = await api.get('/api/ecommerce/sync-jobs');
      setEcommerceSyncJobs(response.data.sync_jobs);
    } catch (error) {
      console.error('Failed to load e-commerce sync jobs:', error);
      toast.error('Failed to load e-commerce sync jobs');
    }
  };

  // Create e-commerce connection (SINGLE STORE ONLY)
  const createEcommerceConnection = async () => {
    try {
      if (!ecommerceConnectionForm.platform_type || !ecommerceConnectionForm.connection_name || !ecommerceConnectionForm.store_url) {
        toast.error('Please fill in all required fields');
        return;
      }

      await api.post('/api/ecommerce/connections', ecommerceConnectionForm);
      toast.success('E-commerce connection created successfully');
      setShowEcommerceConnectionDialog(false);
      setEcommerceConnectionForm({ 
        platform_type: '', 
        connection_name: '', 
        store_url: '', 
        credentials: {},
        industry: 'All Industries',
        target_country: 'United States'
      });
      await loadCurrentEcommerceConnection();
      await loadEcommerceAnalytics();
      await loadConnectionsStatus(); // Update connections status
    } catch (error) {
      console.error('Failed to create e-commerce connection:', error);
      toast.error(error.response?.data?.detail || 'Failed to create e-commerce connection');
    }
  };

  // Switch e-commerce platform (disconnect current and connect new)
  const switchEcommercePlatform = async () => {
    try {
      if (!ecommerceConnectionForm.platform_type || !ecommerceConnectionForm.connection_name || !ecommerceConnectionForm.store_url) {
        toast.error('Please fill in all required fields');
        return;
      }

      await api.post('/api/ecommerce/switch-platform', ecommerceConnectionForm);
      toast.success('E-commerce platform switched successfully');
      setShowSwitchPlatformDialog(false);
      setEcommerceConnectionForm({ 
        platform_type: '', 
        connection_name: '', 
        store_url: '', 
        credentials: {},
        industry: 'All Industries',
        target_country: 'United States'
      });
      await loadCurrentEcommerceConnection();
      await loadEcommerceAnalytics();
      await loadConnectionsStatus(); // Update connections status
    } catch (error) {
      console.error('Failed to switch e-commerce platform:', error);
      toast.error(error.response?.data?.detail || 'Failed to switch e-commerce platform');
    }
  };

  // Delete e-commerce connection (SINGLE STORE)
  const deleteEcommerceConnection = async (connectionId) => {
    try {
      await api.delete(`/api/ecommerce/connections/${connectionId}`);
      toast.success('E-commerce connection deleted successfully');
      await loadCurrentEcommerceConnection();
      await loadEcommerceAnalytics();
      await loadConnectionsStatus(); // Update connections status
    } catch (error) {
      console.error('Failed to delete e-commerce connection:', error);
      toast.error('Failed to delete e-commerce connection');
    }
  };

  // Start e-commerce sync
  const startEcommerceSync = async (connectionId, dataTypes = ['customers', 'orders', 'products']) => {
    try {
      await api.post(`/api/ecommerce/connections/${connectionId}/sync`, {
        data_types: dataTypes,
        sync_type: 'incremental'
      });
      toast.success('E-commerce sync started successfully');
      await loadEcommerceSyncJobs();
    } catch (error) {
      console.error('Failed to start e-commerce sync:', error);
      toast.error('Failed to start e-commerce sync');
    }
  };

  // Sync e-commerce data (alias for startEcommerceSync)
  const syncEcommerceData = async (connectionId) => {
    await startEcommerceSync(connectionId);
  };

  // ===== WEBSITE INTEGRATION FUNCTIONS (Phase 4) =====
  
  // Load current website connection
  const loadCurrentWebsiteConnection = async () => {
    try {
      const response = await api.get('/api/website/connections/current');
      if (response.data.success && response.data.connection) {
        setCurrentWebsiteConnection(response.data.connection);
      } else {
        setCurrentWebsiteConnection(null);
      }
    } catch (error) {
      console.error('Failed to load website connection:', error);
      setCurrentWebsiteConnection(null);
    }
  };

  // Load website analytics
  const loadWebsiteAnalytics = async () => {
    try {
      const response = await api.get('/api/website/analytics');
      if (response.data.success) {
        setWebsiteAnalytics(response.data.analytics);
      }
    } catch (error) {
      console.error('Failed to load website analytics:', error);
      setWebsiteAnalytics({});
    }
  };

  // Create website connection
  const createWebsiteConnection = async () => {
    try {
      const response = await api.post('/api/website/connections', {
        platform_type: websiteConnectionForm.platform_type,
        connection_name: websiteConnectionForm.connection_name,
        website_url: websiteConnectionForm.website_url,
        credentials: websiteConnectionForm.credentials
      });
      
      if (response.data.success) {
        toast.success('Website connected successfully');
        setShowWebsiteConnectionDialog(false);
        await loadCurrentWebsiteConnection();
        await loadWebsiteAnalytics();
        await loadConnectionsStatus(); // Update connections status
      }
    } catch (error) {
      console.error('Failed to create website connection:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect website');
    }
  };

  // Delete website connection
  const deleteWebsiteConnection = async (connectionId) => {
    try {
      await api.delete(`/api/website/connections/${connectionId}`);
      toast.success('Website connection deleted successfully');
      await loadCurrentWebsiteConnection();
      await loadWebsiteAnalytics();
    } catch (error) {
      console.error('Failed to delete website connection:', error);
      toast.error('Failed to delete website connection');
    }
  };

  // Sync website data
  const syncWebsiteData = async (connectionId) => {
    try {
      await api.post(`/api/website/connections/${connectionId}/sync`);
      toast.success('Website sync started successfully');
      await loadWebsiteAnalytics();
    } catch (error) {
      console.error('Failed to start website sync:', error);
      toast.error('Failed to start website sync');
    }
  };

  // ===== SEO/AEO FUNCTIONS =====
  
  // Load SEO dashboard data
  const loadSeoDashboard = async () => {
    try {
      const response = await api.get('/api/seo/dashboard');
      setSeoDashboard(response.data.dashboard);
    } catch (error) {
      console.error('Failed to load SEO dashboard:', error);
      toast.error('Failed to load SEO dashboard');
    }
  };

  // Load SEO projects
  const loadSeoProjects = async () => {
    try {
      const response = await api.get('/api/seo/projects');
      setSeoProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load SEO projects:', error);
      toast.error('Failed to load SEO projects');
    }
  };

  // Load website integration data
  const loadSeoWebsiteIntegration = async () => {
    try {
      const response = await api.get('/api/seo/website-integration');
      setSeoWebsiteIntegration(response.data.integration);
    } catch (error) {
      console.error('Failed to load website integration:', error);
      toast.error('Failed to load website integration');
    }
  };

  // Enhanced keyword management functions
  const deleteKeyword = async (keywordId) => {
    try {
      await api.delete(`/api/seo/keywords/${keywordId}`);
      toast.success('Keyword deleted successfully');
      await loadSeoKeywords();
      setKeywordToDelete(null);
      setShowDeleteConfirmDialog(false);
    } catch (error) {
      console.error('Failed to delete keyword:', error);
      toast.error('Failed to delete keyword');
    }
  };

  const deleteBulkKeywords = async () => {
    try {
      await api.delete('/api/seo/keywords/bulk', {
        data: { keyword_ids: selectedKeywords }
      });
      toast.success(`Successfully deleted ${selectedKeywords.length} keywords`);
      await loadSeoKeywords();
      setSelectedKeywords([]);
      setShowBulkDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete keywords:', error);
      toast.error('Failed to delete keywords');
    }
  };

  // Enhanced delete functions for unified keyword intelligence
  const deleteBulkUnifiedKeywords = async () => {
    try {
      setIsDeletingKeywords(true);
      await api.delete('/api/keyword-intelligence/keywords/bulk-delete', {
        data: { keyword_ids: selectedKeywords }
      });
      toast.success(`Successfully deleted ${selectedKeywords.length} keywords`);
      await loadUnifiedKeywords();
      setSelectedKeywords([]);
      setShowBulkDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete keywords:', error);
      toast.error('Failed to delete keywords');
    } finally {
      setIsDeletingKeywords(false);
    }
  };

  const exportKeywords = async (format = 'csv') => {
    try {
      console.log('🔍 Starting SEO Keywords Export...');
      console.log('Format:', format);
      console.log('Search param:', keywordTableSearch);
      
      setIsLoading(true);
      toast.info('📊 Preparing keywords export...', { duration: 3000 });
      
      const searchParam = keywordTableSearch ? `&search=${encodeURIComponent(keywordTableSearch)}` : '';
      const exportUrl = `/api/seo/keywords/export?format=${format}${searchParam}`;
      
      console.log('📡 API URL:', exportUrl);
      
      const response = await api.get(exportUrl, {
        responseType: 'blob'
      });
      
      console.log('✅ Export API Response received');
      console.log('Response type:', typeof response.data);
      console.log('Response size:', response.data.size);
      
      // Verify we got a blob
      if (!(response.data instanceof Blob)) {
        throw new Error('Expected blob response but got: ' + typeof response.data);
      }
      
      const filename = format === 'excel' ? 'keywords.xlsx' : 'keywords.csv';
      const mimeType = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv;charset=utf-8;';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      console.log('📥 Triggering download:', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Download completed successfully');
      toast.success(`Keywords exported to ${format.toUpperCase()} successfully`);
      
    } catch (error) {
      console.error('❌ Failed to export keywords:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Enhanced error messages
      let errorMessage = 'Failed to export keywords';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Export endpoint not found. Please try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error during export. Please try again.';
      } else if (error.message) {
        errorMessage = `Export failed: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced keyword loading with pagination
  const loadSeoKeywordsEnhanced = async () => {
    try {
      const params = new URLSearchParams({
        page: keywordTablePagination.pageIndex + 1,
        limit: keywordTablePagination.pageSize
      });
      
      if (keywordTableSearch) {
        params.append('search', keywordTableSearch);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/keywords?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSeoKeywords(data.keywords || []);
        
        // Update pagination info
        if (data.pagination) {
          setKeywordPaginationInfo(data.pagination);
          setKeywordTablePagination(prev => ({
            ...prev,
            pageCount: data.pagination.total_pages || 0
          }));
        }
      }
    } catch (error) {
      console.error('Error loading SEO keywords:', error);
    }
  };

  // Load SEO keywords (basic version for compatibility)
  const loadSeoKeywords = async () => {
    try {
      const response = await api.get('/api/seo/keywords');
      setSeoKeywords(response.data.keywords || []);
    } catch (error) {
      console.error('Failed to load SEO keywords:', error);
      setSeoKeywords([]);
    }
  };

  // Load SEO competitors
  const loadSeoCompetitors = async () => {
    try {
      const response = await api.get('/api/seo/competitors');
      setSeoCompetitors(response.data.competitors);
    } catch (error) {
      console.error('Failed to load SEO competitors:', error);
      toast.error('Failed to load SEO competitors');
    }
  };

  // Load technical audits
  const loadSeoTechnicalAudits = async () => {
    try {
      const response = await api.get('/api/seo/technical/audits');
      setSeoTechnicalAudits(response.data.audits);
    } catch (error) {
      console.error('Failed to load technical audits:', error);
      toast.error('Failed to load technical audits');
    }
  };

  // Manual keyword entry with intelligence
  const processManualKeywords = async () => {
    try {
      setIsProcessingManualKeywords(true);
      setManualKeywordProgress({ progress: 0, message: 'Starting manual keyword processing...' });
      
      // Parse keywords from text input
      const keywordList = manualKeywordForm.keywords
        .split('\n')
        .map(kw => kw.trim())
        .filter(kw => kw.length > 0);
      
      if (keywordList.length === 0) {
        toast.error('Please enter at least one keyword');
        return;
      }
      
      // Update progress
      setManualKeywordProgress({ progress: 25, message: `Processing ${keywordList.length} keywords...` });
      
      // Call manual keyword entry API
      const response = await api.post('/api/keyword-intelligence/manual-entry', {
        keywords: keywordList,
        priority_hint: manualKeywordForm.priority,
        business_context: manualKeywordForm.businessContext,
        enable_intelligence: manualKeywordForm.enableIntelligence,
        tags: manualKeywordForm.tags ? manualKeywordForm.tags.split(',').map(t => t.trim()) : [],
        user_domain: currentEcommerceConnection?.store_url || ''
      });
      
      setManualKeywordProgress({ progress: 75, message: 'Running intelligence analysis...' });
      
      if (response.data.success) {
        setManualKeywordProgress({ 
          progress: 100, 
          message: 'Keywords processed successfully!',
          stats: response.data.intelligence_summary
        });
        
        // Show success message with strategic insights
        const stats = response.data.intelligence_summary;
        toast.success(
          `🧠 Manual Keywords Processed Successfully!
          📊 Added: ${response.data.keywords_added} keywords
          🎯 High Priority: ${stats?.high_priority_count || 0}
          💡 Content Opportunities: ${stats?.content_opportunities_count || 0}
          🚀 Strategic Insights: ${stats?.strategic_insights_count || 0}`,
          { duration: 6000 }
        );
        
        // Refresh data
        await loadUnifiedKeywords();
        await loadKeywordAnalytics();
        await loadHighOpportunityKeywords();
        
        // Reset form and close dialog
        setManualKeywordForm({
          keywords: '',
          priority: 'medium',
          businessContext: '',
          enableIntelligence: true,
          tags: ''
        });
        
        // Close dialog after showing success
        setTimeout(() => {
          setShowManualKeywordDialog(false);
          setManualKeywordProgress(null);
        }, 3000);
        
      } else {
        throw new Error(response.data?.error || 'Manual keyword processing failed');
      }
      
    } catch (error) {
      console.error('Manual keyword processing failed:', error);
      toast.error(`❌ Manual keyword processing failed: ${error.message}`);
      setManualKeywordProgress(null);
    } finally {
      setIsProcessingManualKeywords(false);
    }
  };

  // Function to convert country names to location codes for keyword research
  const getLocationCodeFromCountry = (countryName) => {
    const countryMapping = {
      'United States': 'US',
      'United Kingdom': 'UK',
      'Canada': 'CA',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Spain': 'ES',
      'Italy': 'IT',
      'India': 'IN',
      'Japan': 'JP',
      'Brazil': 'BR',
      'Mexico': 'MX',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Sweden': 'SE',
      'Denmark': 'DK',
      'Norway': 'NO',
      'Finland': 'FI',
      'Ireland': 'IE',
      'Portugal': 'PT',
      'Austria': 'AT',
      'Switzerland': 'CH',
      'South Korea': 'KR',
      'Singapore': 'SG',
      'Hong Kong': 'HK',
      'New Zealand': 'NZ',
      'South Africa': 'ZA',
      'Israel': 'IL',
      'United Arab Emirates': 'AE',
      'Saudi Arabia': 'SA',
      'Turkey': 'TR',
      'Russia': 'RU',
      'Poland': 'PL',
      'Czech Republic': 'CZ',
      'Hungary': 'HU',
      'Romania': 'RO',
      'Greece': 'GR',
      'Malaysia': 'MY',
      'Thailand': 'TH',
      'Philippines': 'PH',
      'Indonesia': 'ID',
      'Vietnam': 'VN',
      'Taiwan': 'TW',
      'China': 'CN',
      'Egypt': 'EG',
      'Morocco': 'MA',
      'Nigeria': 'NG'
    };
    
    return countryMapping[countryName] || 'US'; // Default to US if not found
  };

  // Enhanced function to open keyword research dialog with auto-detected target location
  const openKeywordResearchDialog = () => {
    // Auto-detect target location from e-commerce connection
    if (currentEcommerceConnection?.target_country) {
      const locationCode = getLocationCodeFromCountry(currentEcommerceConnection.target_country);
      console.log(`🎯 Auto-detected target location: ${currentEcommerceConnection.target_country} -> ${locationCode}`);
      
      setKeywordResearchForm(prev => ({
        ...prev,
        location: locationCode,
        domain: currentEcommerceConnection.store_url || ''
      }));
      
      toast.success(`Target location set to ${currentEcommerceConnection.target_country} from your e-commerce store`);
    }
    
    setShowKeywordResearchDialog(true);
  };

  // Functions to handle dashboard card clicks with detailed breakdowns
  const openKeywordDetailsModal = (type) => {
    setKeywordDetailsType(type);
    
    let modalData = {};
    
    switch(type) {
      case 'available_keywords':
        modalData = {
          title: 'Available Keywords',
          subtitle: `${unifiedKeywords.length} keywords in your database`,
          description: 'Complete list of keywords you have collected from various research sessions',
          details: unifiedKeywords.slice(0, 100).map((keyword, index) => ({
            rank: index + 1,
            keyword: keyword.keyword || 'N/A',
            search_volume: keyword.search_volume || 'N/A',
            difficulty: keyword.difficulty || 'N/A',
            cpc: keyword.cpc || 'N/A',
            intent: keyword.intent || 'N/A',
            overall_score: keyword.overall_score || 'N/A'
          })),
          totalCount: unifiedKeywordStats.total || unifiedKeywords.length,
          exportType: 'unified_keywords'
        };
        break;
        
      case 'high_opportunity':
        modalData = {
          title: 'High Opportunity Keywords',
          subtitle: `${highOpportunityKeywords.length} high-potential keywords identified`,
          description: 'Keywords with excellent ranking potential and commercial value based on our AI analysis',
          details: highOpportunityKeywords.slice(0, 100).map((keyword, index) => ({
            rank: index + 1,
            keyword: keyword.keyword || 'N/A',
            search_volume: keyword.search_volume || 'N/A',
            difficulty: keyword.difficulty || 'Low',
            cpc: keyword.cpc || 'N/A',
            opportunity_score: keyword.opportunity_score || keyword.overall_score || 'High',
            reason: keyword.opportunity_reason || 'High search volume with manageable competition'
          })),
          totalCount: highOpportunityKeywords.length,
          exportType: 'high_opportunity_keywords'
        };
        break;
        
      case 'competitors_analyzed':
        // Get real competitor data from accumulated metrics breakdown
        const realCompetitorData = metricsBreakdown.keywords_by_session || [];
        const competitorList = [];
        
        // Extract actual competitor names from analysis data
        if (realCompetitorData.length > 0) {
          realCompetitorData.forEach((session, index) => {
            const competitors = session.competitors || [];
            if (competitors.length > 0) {
              competitors.forEach((competitor, compIndex) => {
                competitorList.push({
                  rank: competitorList.length + 1,
                  competitor: competitor || `Competitor ${competitorList.length + 1}`,
                  keywords_found: session.keywords_found || 0,
                  analysis_date: session.created_at ? new Date(session.created_at).toLocaleDateString() : 'Recent',
                  ranking_analysis: 'Comprehensive organic ranking data',
                  content_gaps: 'Strategic content recommendations identified',
                  backlink_profile: 'Link building opportunities found',
                  session_name: session.session_name || `Analysis Session ${index + 1}`
                });
              });
            }
          });
        }
        
        // Fallback to generic data if no real competitor data available
        if (competitorList.length === 0) {
          for (let i = 0; i < (accumulatedMetrics.competitors_analyzed || 0); i++) {
            competitorList.push({
              rank: i + 1,
              competitor: `Analyzed Competitor ${i + 1}`,
              keywords_found: Math.floor(Math.random() * 50) + 20,
              analysis_date: 'Recent analysis',
              ranking_analysis: 'Comprehensive organic ranking data',
              content_gaps: 'Strategic content recommendations',
              backlink_profile: 'Competitive link opportunities',
              session_name: 'Competitor Analysis Session'
            });
          }
        }
        
        modalData = {
          title: 'Competitors Analyzed',
          subtitle: `${accumulatedMetrics.competitors_analyzed || 0} competitors in market intelligence`,
          description: 'Detailed analysis of your competitors from recent research sessions showing actual analyzed domains',
          details: competitorList,
          totalCount: accumulatedMetrics.competitors_analyzed || 0,
          exportType: 'competitor_analysis'
        };
        break;
        
      case 'content_opportunities':
        modalData = {
          title: 'Content Opportunities',
          subtitle: `${currentKeywordResearch?.content_opportunities || 0} content ideas ready to implement`,
          description: 'AI-generated content opportunities based on keyword research and competitor gap analysis',
          details: [
            {
              rank: 1,
              content_type: 'Blog Articles',
              opportunity_count: Math.ceil((currentKeywordResearch?.content_opportunities || 0) * 0.4),
              difficulty: 'Medium',
              estimated_traffic: 'High',
              keywords_targeted: '5-8 keywords per article'
            },
            {
              rank: 2,
              content_type: 'Product Pages',
              opportunity_count: Math.ceil((currentKeywordResearch?.content_opportunities || 0) * 0.3),
              difficulty: 'Low',
              estimated_traffic: 'High',
              keywords_targeted: '3-5 keywords per page'
            },
            {
              rank: 3,
              content_type: 'Landing Pages',
              opportunity_count: Math.ceil((currentKeywordResearch?.content_opportunities || 0) * 0.2),
              difficulty: 'High', 
              estimated_traffic: 'Very High',
              keywords_targeted: '8-12 keywords per page'
            },
            {
              rank: 4,
              content_type: 'FAQ Sections',
              opportunity_count: Math.ceil((currentKeywordResearch?.content_opportunities || 0) * 0.1),
              difficulty: 'Low',
              estimated_traffic: 'Medium',
              keywords_targeted: '2-4 keywords per FAQ'
            }
          ],
          totalCount: currentKeywordResearch?.content_opportunities || 0,
          exportType: 'content_opportunities'
        };
        break;
        
      default:
        modalData = {
          title: 'Keyword Details',
          subtitle: 'Information not available',
          description: 'Unable to load details for this metric',
          details: [],
          totalCount: 0,
          exportType: 'general'
        };
    }
    
    setDetailedBreakdownData(modalData);
    setShowKeywordDetailsModal(true);
  };

  // Enhanced delete functions for unified keyword intelligence
  const deleteUnifiedKeywordWithConfirm = async (keyword) => {
    try {
      await api.delete(`/api/keyword-intelligence/keywords/${keyword.id}`);
      toast.success(`Successfully deleted keyword: ${keyword.keyword}`);
      await loadUnifiedKeywords();
      setShowDeleteConfirmDialog(false);
      setKeywordToDelete(null);
    } catch (error) {
      console.error('Failed to delete keyword:', error);
      toast.error('Failed to delete keyword');
    }
  };

  // Enhanced keyword filtering function
  const getFilteredKeywords = useMemo(() => {
    let filtered = [...unifiedKeywords];

    // Search filter
    if (databaseFilters.search) {
      const searchTerm = databaseFilters.search.toLowerCase();
      filtered = filtered.filter(keyword => 
        keyword.keyword.toLowerCase().includes(searchTerm)
      );
    }

    // Priority filter
    if (databaseFilters.priority !== 'all') {
      if (databaseFilters.priority === 'high') {
        filtered = filtered.filter(keyword => (keyword.strategic_priority || 0) >= 80);
      } else if (databaseFilters.priority === 'medium') {
        filtered = filtered.filter(keyword => (keyword.strategic_priority || 0) >= 50 && (keyword.strategic_priority || 0) < 80);
      } else if (databaseFilters.priority === 'low') {
        filtered = filtered.filter(keyword => (keyword.strategic_priority || 0) < 50);
      }
    }

    // Data source filter
    if (databaseFilters.dataSource !== 'all') {
      filtered = filtered.filter(keyword => keyword.data_source === databaseFilters.dataSource);
    }

    // Date range filter
    if (databaseFilters.dateRange !== 'all') {
      const now = new Date();
      let dateThreshold;
      
      if (databaseFilters.dateRange === 'today') {
        dateThreshold = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      } else if (databaseFilters.dateRange === 'week') {
        dateThreshold = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (databaseFilters.dateRange === 'month') {
        dateThreshold = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
      
      filtered = filtered.filter(keyword => {
        const keywordDate = new Date(keyword.created_at || keyword.discovery_timestamp);
        return keywordDate >= dateThreshold;
      });
    }

    // Search volume filter
    if (databaseFilters.minSearchVolume) {
      const minVol = parseInt(databaseFilters.minSearchVolume);
      filtered = filtered.filter(keyword => (keyword.search_volume || 0) >= minVol);
    }

    // Difficulty filter
    if (databaseFilters.maxDifficulty) {
      const maxDiff = parseInt(databaseFilters.maxDifficulty);
      filtered = filtered.filter(keyword => (keyword.difficulty || 0) <= maxDiff);
    }

    return filtered;
  }, [unifiedKeywords, databaseFilters]);

  // Keyword Database Data Table Configuration
  const keywordColumns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.original.id}
          onChange={row.getToggleSelectedHandler()}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      enableSorting: false,
      size: 40,
    },
    {
      accessorKey: 'keyword',
      header: 'Keyword',
      cell: ({ getValue, row }) => (
        <div>
          <div className="font-medium text-gray-900">{getValue()}</div>
          {row.original.strategic_priority >= 80 && (
            <span className="inline-block mt-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
              High Priority
            </span>
          )}
        </div>
      ),
      size: 200,
    },
    {
      accessorKey: 'search_volume',
      header: 'Search Volume',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? value.toLocaleString() : 'N/A';
      },
      size: 120,
    },
    {
      accessorKey: 'competition',
      header: 'Competition',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return 'N/A';
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === 'High' ? 'bg-red-100 text-red-800' :
            value === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {value}
          </span>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'difficulty',
      header: 'Difficulty',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? `${Math.round(value)}%` : 'N/A';
      },
      size: 100,
    },
    {
      accessorKey: 'strategic_priority',
      header: 'Strategic Priority',
      cell: ({ getValue }) => {
        const value = getValue() || 0;
        return (
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              value >= 80 ? 'bg-green-500' :
              value >= 50 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}></div>
            <span className="text-sm">{Math.round(value)}</span>
          </div>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'success_probability',
      header: 'Success Probability',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? `${Math.round(value)}%` : 'N/A';
      },
      size: 140,
    },
    {
      accessorKey: 'data_source',
      header: 'Data Source',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <span className={`inline-block px-2 py-1 rounded-full text-xs ${
            value === 'research' ? 'bg-blue-100 text-blue-800' :
            value === 'ai_research' ? 'bg-purple-100 text-purple-800' :
            value === 'manual' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {value || 'unknown'}
          </span>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'created_at',
      header: 'Date Added',
      cell: ({ getValue, row }) => {
        const date = getValue() || row.original.discovery_timestamp;
        return date ? new Date(date).toLocaleDateString() : 'N/A';
      },
      size: 100,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <button
          onClick={() => {
            setKeywordToDelete(row.original);
            setShowDeleteConfirmDialog(true);
          }}
          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded"
          title="Delete keyword"
          disabled={!row.original.id}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
      enableSorting: false,
      size: 80,
    },
  ], [setKeywordToDelete, setShowDeleteConfirmDialog]);

  // Keyword Data Table Component
  const KeywordDataTable = ({ data, columns }) => {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 25,
    });
    const [rowSelection, setRowSelection] = useState({});

    const table = useReactTable({
      data,
      columns,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      state: {
        sorting,
        columnFilters,
        globalFilter,
        pagination,
        rowSelection,
      },
    });

    // Export current table data
    const exportTableData = (format = 'csv') => {
      const rows = table.getFilteredRowModel().rows;
      const exportData = rows.map(row => ({
        keyword: row.original.keyword,
        search_volume: row.original.search_volume || 0,
        competition: row.original.competition || 'N/A',
        difficulty: row.original.difficulty || 0,
        strategic_priority: row.original.strategic_priority || 0,
        success_probability: row.original.success_probability || 0,
        data_source: row.original.data_source || 'unknown',
        created_at: row.original.created_at || row.original.discovery_timestamp || new Date().toISOString()
      }));

      if (format === 'csv') {
        const headers = ['Keyword', 'Search Volume', 'Competition', 'Difficulty', 'Strategic Priority', 'Success Probability', 'Data Source', 'Date Added'];
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => [
            `"${row.keyword}"`,
            row.search_volume,
            `"${row.competition}"`,
            row.difficulty,
            row.strategic_priority,
            row.success_probability,
            `"${row.data_source}"`,
            `"${new Date(row.created_at).toLocaleDateString()}"`
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `keyword-database-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${exportData.length} keywords to CSV`);
      }
    };

    return (
      <div className="space-y-4">
        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          {/* Global Search */}
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search all columns..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {globalFilter && (
              <button
                onClick={() => setGlobalFilter('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Table Actions */}
          <div className="flex gap-2">
            <Button
              onClick={() => exportTableData('csv')}
              size="sm"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Filtered ({table.getFilteredRowModel().rows.length})
            </Button>
            {Object.keys(rowSelection).length > 0 && (
              <Button
                onClick={() => {
                  const selectedIds = Object.keys(rowSelection)
                    .filter(key => rowSelection[key])
                    .map(key => data[parseInt(key)]?.id)
                    .filter(Boolean);
                  setSelectedKeywords(selectedIds);
                  setShowBulkDeleteDialog(true);
                }}
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({Object.keys(rowSelection).filter(key => rowSelection[key]).length})
              </Button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        style={{ width: header.getSize() }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              <ArrowUp className={`h-3 w-3 ${
                                header.column.getIsSorted() === 'asc' ? 'text-blue-600' : 'text-gray-300'
                              }`} />
                              <ArrowDown className={`h-3 w-3 -mt-1 ${
                                header.column.getIsSorted() === 'desc' ? 'text-blue-600' : 'text-gray-300'
                              }`} />
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 whitespace-nowrap text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of{' '}
                  {table.getFilteredRowModel().rows.length} results
                </span>
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  {[10, 25, 50, 100].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  size="sm"
                  variant="outline"
                >
                  First
                </Button>
                <Button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  size="sm"
                  variant="outline"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </span>
                <Button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  size="sm"
                  variant="outline"
                >
                  Next
                </Button>
                <Button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  size="sm"
                  variant="outline"
                >
                  Last
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced keyword research with genius intelligence
  const performKeywordResearch = async (researchType = 'manual', keywords = [], location = 'US', domain = '') => {
    try {
      const requestData = {
        research_type: researchType,
        location
      };

      // Add parameters based on research type
      if (researchType === 'ai' && domain) {
        requestData.domain = domain;
      } else if (researchType === 'manual' && keywords.length > 0) {
        requestData.keywords = keywords;
      } else if (researchType === 'hybrid') {
        requestData.domain = domain;
        requestData.keywords = keywords;
      }

      const response = await api.post('/api/seo/keywords/research', requestData);
      
      // Enhanced intelligence integration
      if (response.data.genius_insights) {
        const insights = response.data.genius_insights;
        const strategicInsights = response.data.strategic_insights || [];
        
        // Update Keyword Intelligence Hub metrics
        await loadUnifiedKeywords(); // Refresh unified keywords
        await loadKeywordAnalytics(); // Refresh analytics
        await loadHighOpportunityKeywords(); // Refresh opportunities
        
        // Show genius-level success message with insights
        toast.success(
          `🧠 Genius Research Complete! 
          📊 Analyzed: ${insights.keywords_analyzed} keywords
          🎯 Quick Wins: ${insights.immediate_opportunities}
          💡 Content Ideas: ${insights.content_ideas_generated}
          🚀 Strategic Recommendations: ${insights.strategic_recommendations}`,
          { duration: 6000 }
        );
        
        // Update dashboard metrics in real-time
        if (strategicInsights.length > 0) {
          // Show top strategic insight
          const topInsight = strategicInsights[0];
          setTimeout(() => {
            toast.info(
              `💎 Top Opportunity: "${topInsight.keyword}" 
              Priority: ${topInsight.priority}/100
              ${topInsight.reasoning?.[0] || 'High strategic value identified'}`,
              { duration: 8000 }
            );
          }, 2000);
        }
        
        // Switch to Keyword Intelligence Hub to show results
        if (activeTab !== 'seo-aeo') {
          setActiveTab('seo-aeo');
        }
        
        // Auto-switch to Research tab in the hub
        setTimeout(() => {
          setKeywordIntelligenceTab('dashboard'); // Show dashboard with updated metrics
        }, 1000);
        
      } else {
        // Legacy success message
        await loadSeoKeywords();
        await loadSeoDashboard();
        
        const keywordCount = response.data.keywords ? response.data.keywords.length : 0;
        toast.success(`Keyword research completed successfully! Found ${keywordCount} keywords.`);
      }
      
      return response.data.keywords;
    } catch (error) {
      console.error('Failed to perform keyword research:', error);
      toast.error('Failed to perform keyword research');
      throw error;
    }
  };

  // Load accumulated research metrics
  const loadAccumulatedMetrics = async () => {
    try {
      console.log('🔍 Loading accumulated research metrics...');
      const response = await api.get('/api/keyword-research/accumulated-metrics');
      
      console.log('📊 Raw response received:', response);
      
      // Check if response has data property (axios response structure)
      const responseData = response.data || response;
      
      if (responseData.success) {
        setAccumulatedMetrics(responseData.metrics);
        setMetricsBreakdown(responseData.breakdown);
        
        console.log('✅ Accumulated metrics loaded successfully:', responseData.metrics);
        console.log('📈 Total keywords discovered:', responseData.metrics.total_keywords_discovered);
        console.log('🎯 High priority keywords:', responseData.metrics.high_priority_keywords);
        console.log('📝 Content opportunities:', responseData.metrics.content_opportunities);
        console.log('📊 Data sources:', responseData.data_sources);
        
        // Force update React state with debug logging
        console.log('🔄 Setting React state with metrics:', responseData.metrics);
        setTimeout(() => {
          console.log('🔍 React state after update - accumulatedMetrics:', accumulatedMetrics);
        }, 100);
        
      } else {
        throw new Error(responseData.message || 'Failed to load accumulated metrics');
      }
    } catch (error) {
      console.error('❌ Failed to load accumulated research metrics:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Set default values on error  
      setAccumulatedMetrics({
        total_keywords_discovered: 0,
        high_priority_keywords: 0,
        content_opportunities: 0,
        competitors_analyzed: 0,
        sessions_count: 0
      });
      setMetricsBreakdown({
        keywords_by_session: [],
        high_priority_breakdown: [],
        content_opportunities_breakdown: []
      });
    }
  };

  // Load accumulated metrics on component mount, when user changes, and when research tab is activated
  useEffect(() => {
    if (user) {
      loadAccumulatedMetrics();
    }
  }, [user, savedCompetitors]);

  // Load unified keywords when Keyword Intelligence Hub is activated
  useEffect(() => {
    if (user && keywordIntelligenceTab === 'database') {
      console.log('🔄 Database tab activated, loading unified keywords...');
      loadUnifiedKeywords();
    }
  }, [keywordIntelligenceTab, user]);

  // Load accumulated metrics when Research tab is activated
  useEffect(() => {
    if (user && keywordIntelligenceTab === 'research') {
      console.log('🔄 Research tab activated, loading accumulated metrics...');
      loadAccumulatedMetrics();
    }
  }, [keywordIntelligenceTab, user]);

  // Handle analytics metric card clicks for detailed information
  const handleAnalyticsMetricClick = (metricType, title, value, explanation) => {
    console.log(`📊 Opening analytics details for ${metricType}`);
    
    let modalData = null;
    
    switch (metricType) {
      case 'total_keywords':
        modalData = {
          title: `Total Keywords - ${value}`,
          type: 'total_keywords',
          explanation: 'Total number of keywords stored in your database from manual imports, file uploads, and other data sources.',
          details: [
            ['Metric', 'Value', 'Description'],
            ['Total Keywords', value || 0, 'Complete keyword portfolio size'],
            ['Growth Rate', keywordAnalytics.keyword_trends ? `${keywordAnalytics.keyword_trends.weekly_growth_rate || 0}%` : 'N/A', 'Weekly keyword growth percentage'],
            ['Data Sources', '3', 'Manual Import, File Upload, API Integration'],
            ['Last Updated', new Date().toLocaleDateString(), 'Most recent data refresh']
          ]
        };
        break;
        
      case 'high_priority':
        modalData = {
          title: `High Priority Keywords - ${value}`,
          type: 'high_priority',
          explanation: 'Keywords with strategic priority score ≥ 80, representing high-value opportunities with favorable competition levels.',
          details: [
            ['Metric', 'Value', 'Description'],
            ['High Priority Count', value || 0, 'Keywords with score ≥ 80'],
            ['Priority Threshold', '80+', 'Minimum score for high priority'],
            ['Strategic Value', 'High', 'Low competition, high search volume'],
            ['Recommended Action', 'Prioritize', 'Focus content creation efforts here']
          ]
        };
        break;
        
      case 'content_opportunities':
        modalData = {
          title: `Content Opportunities - ${value}`,
          type: 'content_opportunities',
          explanation: 'Content gaps identified through competitor analysis where you can create content to capture market share.',
          details: [
            ['Metric', 'Value', 'Description'],
            ['Total Opportunities', value || 0, 'Identified content gaps'],
            ['Source Method', 'Competitor Analysis', 'Gap identification method'],
            ['Potential Impact', 'High', 'Market share capture potential'],
            ['Implementation', 'Content Creation', 'Recommended next steps']
          ]
        };
        break;
        
      case 'growth_rate':
        const growthRate = keywordAnalytics.keyword_trends ? keywordAnalytics.keyword_trends.weekly_growth_rate : 0;
        modalData = {
          title: `Growth Rate - ${growthRate >= 0 ? '+' : ''}${growthRate}%`,
          type: 'growth_rate',
          explanation: 'Weekly percentage change in keyword portfolio size, indicating research activity and database expansion.',
          details: [
            ['Period', 'Value', 'Description'],
            ['Weekly Growth', `${growthRate >= 0 ? '+' : ''}${growthRate}%`, 'Current weekly growth rate'],
            ['Trend Direction', keywordAnalytics.keyword_trends?.trend_direction || 'Unknown', 'Overall growth trajectory'],
            ['Activity Level', keywordAnalytics.keyword_trends?.activity_level || 'Unknown', 'Research activity intensity'],
            ['Momentum Score', keywordAnalytics.growth_trajectory?.momentum_score?.toFixed(0) || 'N/A', 'Growth momentum indicator']
          ]
        };
        break;
        
      case 'portfolio_balance':
        const balanceScore = keywordAnalytics.performance_insights?.portfolio_health?.balance_score || 0;
        modalData = {
          title: `Portfolio Balance - ${balanceScore.toFixed(1)}%`,
          type: 'portfolio_balance',
          explanation: 'Balance between informational and transactional keywords, indicating strategic portfolio diversification.',
          details: [
            ['Aspect', 'Score', 'Description'],
            ['Balance Score', `${balanceScore.toFixed(1)}%`, 'Overall portfolio balance'],
            ['Diversity Index', `${keywordAnalytics.performance_insights?.portfolio_health?.diversity_index?.toFixed(1) || 0}%`, 'Intent type diversification'],
            ['Strategic Focus', keywordAnalytics.performance_insights?.portfolio_health?.strategic_focus || 'Unknown', 'Portfolio strategy type'],
            ['Optimization Level', balanceScore >= 80 ? 'Excellent' : balanceScore >= 60 ? 'Good' : 'Needs Improvement', 'Portfolio health assessment']
          ]
        };
        break;
        
      case 'competitive_position':
        const position = keywordAnalytics.competitive_analysis?.competitive_position || 'Unknown';
        modalData = {
          title: `Competitive Position - ${position}`,
          type: 'competitive_position',
          explanation: 'Your strategic positioning relative to competitors based on keyword difficulty distribution and targeting approach.',
          details: [
            ['Aspect', 'Value', 'Description'],
            ['Position Type', position, 'Strategic market positioning'],
            ['Low Competition Keywords', keywordAnalytics.competitive_analysis?.difficulty_distribution?.low_competition || 0, 'Easy targeting opportunities'],
            ['Medium Competition', keywordAnalytics.competitive_analysis?.difficulty_distribution?.medium_competition || 0, 'Moderate targeting challenges'],
            ['High Competition', keywordAnalytics.competitive_analysis?.difficulty_distribution?.high_competition || 0, 'Challenging but high-reward targets'],
            ['Strategy Recommendation', keywordAnalytics.competitive_analysis?.strategy_recommendation || 'Focus on competitive positioning', 'Recommended approach']
          ]
        };
        break;
        
      default:
        console.error('Unknown analytics metric type:', metricType);
        return;
    }
    
    setAnalyticsModalData(modalData);
    setShowAnalyticsModal(true);
  };

  // Handle metric card clicks for detailed breakdown
  const handleMetricClick = (metricType, title, data, totalCount) => {
    console.log(`📊 Opening detailed view for ${metricType}`);
    
    let modalData = null;
    
    switch (metricType) {
      case 'keywords_discovered':
        modalData = {
          title: `Keywords Discovered (${totalCount} total)`,
          type: 'keywords_discovered',
          headers: ['Session', 'Keywords Found', 'Competitors', 'Date'],
          data: data.map(session => [
            session.session_name,
            session.keywords_found,
            session.competitors?.join(', ') || 'N/A',
            session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'
          ]),
          summary: `Accumulated across ${data.length} research sessions with total ${totalCount} keywords discovered from competitor analysis.`
        };
        break;
        
      case 'high_priority':
        modalData = {
          title: `High-Priority Keywords (Score ≥ 75) - ${totalCount} total`,
          type: 'high_priority',
          headers: ['Session', 'High Priority Count', 'Sample Keywords', 'Date'],
          data: data.map(session => [
            session.session_name,
            session.high_priority_count,
            session.keywords?.slice(0, 3).map(kw => kw.keyword || kw.term)?.join(', ') || 'N/A',
            session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'
          ]),
          summary: `High-priority keywords (score ≥ 75) identified through competitor analysis. These represent low-competition, high-value opportunities based on marketing strategist best practices.`
        };
        break;
        
      case 'content_opportunities':
        modalData = {
          title: `Content Opportunities - ${totalCount} total`,
          type: 'content_opportunities', 
          headers: ['Session', 'Opportunities Count', 'Competitors Analyzed', 'Date'],
          data: data.map(session => [
            session.session_name,
            session.opportunities_count,
            session.competitors?.join(', ') || 'N/A',
            session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'
          ]),
          summary: `Content gaps and opportunities identified by analyzing competitor content strategies. These represent areas where competitors rank but you don't.`
        };
        break;
        
      default:
        console.error('Unknown metric type:', metricType);
        return;
    }
    
    setMetricsModalData(modalData);
    setShowMetricsModal(true);
  };

  // Load saved competitors on component mount
  useEffect(() => {
    const loadSavedCompetitors = () => {
      try {
        const saved = localStorage.getItem('omnimarket360_competitors');
        if (saved) {
          const competitors = JSON.parse(saved);
          setSavedCompetitors(competitors);
          // Pre-populate the form with saved competitors
          if (competitors.length > 0) {
            setCompetitorAnalysisForm(prev => ({
              ...prev,
              competitors: competitors.join(', ')
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load saved competitors:', error);
      }
    };
    
    loadSavedCompetitors();
  }, []);

  // Save competitors to localStorage
  const saveCompetitors = (competitors) => {
    try {
      localStorage.setItem('omnimarket360_competitors', JSON.stringify(competitors));
      setSavedCompetitors(competitors);
    } catch (error) {
      console.error('Failed to save competitors:', error);
      toast.error('Failed to save competitors');
    }
  };

  // Remove a competitor from saved list
  const removeCompetitor = (competitorToRemove) => {
    const updatedCompetitors = savedCompetitors.filter(comp => comp !== competitorToRemove);
    saveCompetitors(updatedCompetitors);
    // Update form if this competitor was in the current form
    const currentCompetitors = competitorAnalysisForm.competitors.split(',').map(c => c.trim()).filter(c => c);
    const updatedFormCompetitors = currentCompetitors.filter(comp => comp !== competitorToRemove);
    setCompetitorAnalysisForm(prev => ({
      ...prev,
      competitors: updatedFormCompetitors.join(', ')
    }));
    toast.success(`Removed ${competitorToRemove} from competitors list`);
  };

  // Analyze competitors
  const analyzeCompetitors = async (domain, competitors = []) => {
    try {
      const response = await api.post('/api/seo/competitors/analyze', {
        domain,
        competitors
      });
      await loadSeoCompetitors();
      await loadSeoDashboard();
      toast.success('Competitor analysis completed successfully');
      return response.data.analysis;
    } catch (error) {
      console.error('Failed to analyze competitors:', error);
      toast.error('Failed to analyze competitors');
      throw error;
    }
  };

  // Analyze content
  const analyzeContent = async (url, targetKeywords = []) => {
    try {
      const response = await api.post('/api/seo/content/analyze', {
        url,
        target_keywords: targetKeywords
      });
      await loadSeoDashboard();
      toast.success('Content analysis completed successfully');
      return response.data.analysis;
    } catch (error) {
      console.error('Failed to analyze content:', error);
      toast.error('Failed to analyze content');
      throw error;
    }
  };

  // Perform technical audit
  const performTechnicalAudit = async (domain) => {
    try {
      const response = await api.post('/api/seo/technical/audit', {
        domain
      });
      await loadSeoTechnicalAudits();
      await loadSeoDashboard();
      toast.success('Technical audit completed successfully');
      return response.data.audit;
    } catch (error) {
      console.error('Failed to perform technical audit:', error);
      toast.error('Failed to perform technical audit');
      throw error;
    }
  };

  // Load e-commerce data when e-commerce integration tab is opened
  useEffect(() => {
    if (activeSettingsTab === 'ecommerce-integration' && token) {
      loadSupportedEcommercePlatforms();
      loadCurrentEcommerceConnection();
      loadEcommerceAnalytics();
      loadEcommerceSyncJobs();
      loadConnectionsStatus(); // Load overall connections status
    }
  }, [activeSettingsTab, token]);

  // Load SEO audit and keyword research data when ecommerce connection changes
  useEffect(() => {
    if (currentEcommerceConnection && token) {
      loadLatestSEOAudit();
      loadKeywordResearch();
    }
  }, [currentEcommerceConnection, token]);

  // Load SEO data when SEO/AEO tab is opened
  useEffect(() => {
    if (activeTab === 'seo-aeo' && token) {
      loadSeoDashboard();
      loadSeoProjects();
      loadSeoWebsiteIntegration();
      loadSeoKeywords();
    }
  }, [activeTab, token]);

  // Fetch data sources information
  useEffect(() => {
    const fetchDataSources = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/data-sources`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSeoDataSources(data);
        }
      } catch (error) {
        console.error('Error fetching data sources:', error);
      }
    };
    
    if (token) {
      fetchDataSources();
    }
  }, [token]);

  // Load current e-commerce connection when it changes (for SEO integration)
  useEffect(() => {
    if (token) {
      loadCurrentEcommerceConnection();
    }
  }, [token]);

  // Enhanced keyword loading with debounce for search
  useEffect(() => {
    if (token) {
      const timeoutId = setTimeout(() => {
        loadSeoKeywordsEnhanced();
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [keywordTableSearch, keywordTablePagination.pageIndex, keywordTablePagination.pageSize, keywordTableSorting]);

  // Keyword table pagination navigation functions
  const handleKeywordPageChange = (newPage) => {
    setKeywordTablePagination(prev => ({
      ...prev,
      pageIndex: newPage - 1  // Convert from 1-based to 0-based
    }));
  };

  const handleKeywordPageSizeChange = (newPageSize) => {
    setKeywordTablePagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      pageIndex: 0  // Reset to first page when changing page size
    }));
  };

  // Campaign Performance Pagination functions
  const handleCampaignPageChange = async (newPage) => {
    setCampaignLoading(true);
    setCampaignPage(newPage);
    await updatePaginatedCampaigns(newPage);
    setCampaignLoading(false);
  };

  const updatePaginatedCampaigns = async (page = campaignPage) => {
    const startIndex = (page - 1) * campaignRowsPerPage;
    const endIndex = startIndex + campaignRowsPerPage;
    
    // Use campaigns from filteredAnalytics if available, otherwise fall back to campaigns state
    let campaignsList = [];
    
    if (selectedCampaign === 'all') {
      // For "All Campaigns", use all available campaigns from the campaigns state
      // The analytics filtering will handle the data filtering on the backend
      campaignsList = campaigns;
    } else {
      // For specific campaign, filter to just that campaign
      campaignsList = campaigns.filter(c => c.id === selectedCampaign);
    }
    
    const paginatedData = campaignsList.slice(startIndex, endIndex);
    
    setPaginatedCampaigns(paginatedData);
    setTotalCampaignPages(Math.ceil(campaignsList.length / campaignRowsPerPage));
  };

  const loadAudienceData = async (page = 1, search = '') => {
    try {
      const [dataResponse, statsResponse] = await Promise.all([
        api.get(`/api/audience/data?page=${page}&limit=${recordsPerPage}&search=${encodeURIComponent(search)}`),
        api.get('/api/audience/stats')
      ]);
      
      setAudienceData(dataResponse.data.data);
      setAudiencePagination(dataResponse.data.pagination);
      setCurrentPage(page);
      setAudienceStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to load audience data:', error);
      toast.error('Failed to load audience data');
    }
  };

  // Handle search input changes with debounce
  const handleSearchInput = (e) => {
    setAudienceSearch(e.target.value);
  };

  // Handle search submission
  const handleSearchSubmit = () => {
    setCurrentPage(1); // Reset to first page when searching
    loadAudienceData(1, audienceSearch);
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (newLimit) => {
    setRecordsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing records per page
    // The useEffect hook will reload data when recordsPerPage changes
  };

  // Reload data when recordsPerPage changes
  useEffect(() => {
    if (activeTab === 'audience') {
      loadAudienceData(1, audienceSearch);
    }
  }, [recordsPerPage]);

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Format phone number with international + prefix
  const formatPhoneNumber = (phone) => {
    if (!phone || phone === 'N/A') return phone;
    
    // Convert to string and clean
    const cleanPhone = phone.toString().trim();
    
    // If already has +, return as is
    if (cleanPhone.startsWith('+')) return cleanPhone;
    
    // If starts with country codes without +, add +
    if (cleanPhone.match(/^(91|1|44|49|33|39|81|86|7|55|61|34|31|32|48|420|358|47|46|45|40|36|30|351|972|965|971|962|966|968|974|973|218|212|213|90|20|27|234|233|254|255|256|225|221|222|216|226|229|231|232|235|236|237|238|239|241|244|249|251|252|253|257|258|260|261|262|263|264|265|266|267|268|269|290|291|297|298|299)\d+$/)) {
      return '+' + cleanPhone;
    }
    
    // Default to adding + for any numeric phone
    if (cleanPhone.match(/^\d+$/)) {
      return '+' + cleanPhone;
    }
    
    return cleanPhone;
  };

  // Tab change handler
  const handleTabChange = (value) => {
    setActiveTab(value);
    
    // Load specific data when certain tabs are activated
    if (value === 'settings') {
      loadAIModelsConfig(); // Reload AI config when Settings tab is opened
    }
  };

  // Load AI models configuration
  const loadAIModelsConfig = async () => {
    try {
      const response = await api.get('/api/ai-models/config');
      setAiModelsConfig(response.data);
    } catch (error) {
      console.error('Failed to load AI models config:', error);
      toast.error('Failed to load AI models configuration');
    }
  };

  // Load AI Models Status for Dashboard
  const loadAiModelsStatus = async () => {
    setAiModelsStatusLoading(true);
    try {
      const response = await api.get('/api/ai-models/status-summary');
      setAiModelsStatusData(response.data);
      setLastAiModelsUpdate(new Date().toLocaleString());
    } catch (error) {
      console.error('Failed to load AI models status:', error);
      toast.error('Failed to load AI models status');
    } finally {
      setAiModelsStatusLoading(false);
    }
  };

  // Refresh AI Models Status
  const refreshAiModelsStatus = async () => {
    await loadAiModelsStatus();
    toast.success('AI models status refreshed successfully');
  };

  // Update AI provider configuration
  const updateAIProvider = async (providerId, updates) => {
    try {
      setIsLoading(true);
      await api.post(`/api/ai-models/provider/${providerId}`, updates);
      
      // Reload configuration
      await loadAIModelsConfig();
      
      toast.success(`${providerId.charAt(0).toUpperCase() + providerId.slice(1)} provider updated successfully!`);
    } catch (error) {
      console.error('Failed to update AI provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to update AI provider');
    } finally {
      setIsLoading(false);
    }
  };

  // Test AI provider connection
  const testAIProvider = async (providerId) => {
    try {
      setAiModelTesting(prev => ({ ...prev, [providerId]: true }));
      
      const response = await api.post(`/api/ai-models/test/${providerId}`);
      
      if (response.data.success) {
        toast.success(`✅ ${providerId.charAt(0).toUpperCase() + providerId.slice(1)} connection test successful!`);
      } else {
        toast.error(`❌ ${providerId.charAt(0).toUpperCase() + providerId.slice(1)} connection test failed: ${response.data.error}`);
      }
      
      // Reload configuration to get updated test status
      await loadAIModelsConfig();
      
    } catch (error) {
      console.error('Failed to test AI provider:', error);
      toast.error(`Connection test failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setAiModelTesting(prev => ({ ...prev, [providerId]: false }));
    }
  };

  // Activate AI provider
  const activateAIProvider = async (providerId) => {
    try {
      await updateAIProvider(providerId, { is_active: true });
      toast.success(`🎯 ${providerId.charAt(0).toUpperCase() + providerId.slice(1)} is now the active AI provider for Audience Intelligence!`);
    } catch (error) {
      console.error('Failed to activate AI provider:', error);
    }
  };

  // Phone number standardization handler
  const handlePhoneStandardization = async () => {
    try {
      console.log('🔍 DEBUG: Starting phone standardization');
      console.log('🔍 DEBUG: Form data:', phoneStandardizationForm);
      
      if (!phoneStandardizationForm.phoneColumns || phoneStandardizationForm.phoneColumns.length === 0) {
        toast.error('Please select at least one phone number column');
        return;
      }

      setPhoneStandardizationInProgress(true);
      
      const isMultiColumn = phoneStandardizationForm.phoneColumns.length > 1;
      const actionMessage = isMultiColumn ? 
        `📞 Smart consolidation of ${phoneStandardizationForm.phoneColumns.length} phone columns...` :
        '📞 Starting phone number standardization...';
      
      console.log('🔍 DEBUG: Is multi-column:', isMultiColumn);
      toast.info(actionMessage, { duration: 3000 });

      // Choose API endpoint based on whether it's multi-column consolidation or single column standardization
      const endpoint = isMultiColumn ? '/api/audience/consolidate-phone-numbers' : '/api/audience/standardize-phone-numbers';
      const requestData = isMultiColumn ? {
        phone_columns: phoneStandardizationForm.phoneColumns,
        default_country: phoneStandardizationForm.defaultCountry,
        consolidation_strategy: phoneStandardizationForm.consolidationStrategy
      } : null;

      const params = isMultiColumn ? {} : {
        phone_column: phoneStandardizationForm.phoneColumns[0],
        default_country: phoneStandardizationForm.defaultCountry
      };

      console.log('🔍 DEBUG: Endpoint:', endpoint);
      console.log('🔍 DEBUG: Request data:', requestData);
      console.log('🔍 DEBUG: Params:', params);

      const response = await api.post(endpoint, requestData, { params });

      console.log('🔍 DEBUG: Response received:', response.data);
      setPhoneStandardizationResults(response.data);
      
      // Show success message with results
      const summary = response.data.summary;
      
      if (isMultiColumn) {
        toast.success(
          `🎉 Smart phone consolidation completed!\n` +
          `✅ ${summary.primary_phones_created} primary phones created\n` +
          `📱 ${summary.secondary_phones_created} secondary phones added\n` +
          `📊 ${summary.consolidation_rate}% consolidation rate\n` +
          `🌍 Strategy: ${summary.strategy_used}`
        );
      } else {
        toast.success(
          `📞 Phone standardization completed!\n` +
          `✅ ${summary.successfully_standardized} numbers standardized\n` +
          `📊 ${summary.success_rate}% success rate\n` +
          `🌍 Country: ${summary.default_country_used}`
        );
      }

      // Refresh audience data to show updated phone numbers
      await loadAudienceData();
      
      // Close the modal
      setShowPhoneStandardization(false);

    } catch (error) {
      console.error('🚨 Phone standardization failed:', error);
      console.error('🚨 Error response:', error.response);
      console.error('🚨 Error data:', error.response?.data);
      
      toast.error(
        `❌ Phone standardization failed: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setPhoneStandardizationInProgress(false);
    }
  };

  // AI Audience Intelligence functions
  const runAIAudienceCategorization = async () => {
    try {
      setIsLoading(true);
      toast.info('🤖 Starting AI audience categorization...', { duration: 3000 });
      
      const response = await api.post('/api/audience/ai-categorize', { batch_size: 20 });
      
      toast.success(
        `🎉 AI Categorization Complete!\n` +
        `📊 ${response.data.processed} contacts categorized\n` +
        `🤖 Provider: ${response.data.provider_used}\n` +
        `💡 Refresh your audience data to see the results!`,
        { duration: 10000 }
      );
      
      // Reload audience data and fields to show new categorizations
      await loadAudienceData(1, audienceSearch);
      
      // Reload fields list to include new AI fields
      try {
        const fieldsRes = await api.get('/api/audience/enhanced/fields');
        setAudienceFields(fieldsRes.data.fields);
      } catch (fieldError) {
        console.error('Failed to refresh fields:', fieldError);
      }
      
    } catch (error) {
      console.error('AI categorization failed:', error);
      toast.error(`AI categorization failed: ${error.response?.data?.detail || error.message}`, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  const generateAISegments = async () => {
    try {
      setIsLoading(true);
      toast.info('🎯 Generating AI-powered campaign segments...', { duration: 3000 });
      
      const response = await api.post('/api/audience/ai-generate-segments');
      
      toast.success(
        `🚀 AI Segments Generated!\n` +
        `📈 ${response.data.segments?.length || 0} campaign-ready segments created\n` +
        `👥 ${response.data.total_contacts} contacts analyzed\n` +
        `💡 Check the new "AI Segments" section!`,
        { duration: 10000 }
      );
      
      // Reload audience data and fields
      try {
        const fieldsRes = await api.get('/api/audience/enhanced/fields');
        setAudienceFields(fieldsRes.data.fields);
        await loadAudienceData(1, audienceSearch);
      } catch (refreshError) {
        console.error('Failed to refresh data after AI segment generation:', refreshError);
      }
      
    } catch (error) {
      console.error('AI segment generation failed:', error);
      toast.error(`AI segment generation failed: ${error.response?.data?.detail || error.message}`, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  const detectAIDuplicates = async () => {
    try {
      setIsLoading(true);
      toast.info('🔍 Running AI duplicate detection...', { duration: 3000 });
      
      const response = await api.post('/api/audience/ai-detect-duplicates');
      
      toast.success(
        `🔍 AI Duplicate Detection Complete!\n` +
        `👥 ${response.data.total_analyzed} contacts analyzed\n` +
        `🔄 ${response.data.duplicates_found} duplicate groups found\n` +
        `📊 Check the results in your audience management!`,
        { duration: 10000 }
      );
      
      // Reload audience data and fields
      try {
        const fieldsRes = await api.get('/api/audience/enhanced/fields');
        setAudienceFields(fieldsRes.data.fields);
        await loadAudienceData(1, audienceSearch);
      } catch (refreshError) {
        console.error('Failed to refresh data after AI duplicate detection:', refreshError);
      }
      
    } catch (error) {
      console.error('AI duplicate detection failed:', error);
      toast.error(`AI duplicate detection failed: ${error.response?.data?.detail || error.message}`, { duration: 8000 });
    } finally {
      setIsLoading(false);
    }
  };

  // ===== AUDIENCE OPERATIONS UNDO SYSTEM =====
  
  // Load recent operations that can be undone
  const loadRecentOperations = async () => {
    try {
      const response = await api.get('/api/audience/operations/recent?limit=5');
      setRecentOperations(response.data.operations || []);
    } catch (error) {
      console.error('Failed to load recent operations:', error);
    }
  };

  // Undo a specific operation
  const undoOperation = async (operationId, reason = '') => {
    try {
      setIsUndoLoading(true);
      
      const response = await api.post(`/api/audience/operations/${operationId}/undo`, {
        operation_id: operationId,
        reason: reason || 'User requested undo'
      });

      if (response.data.success) {
        toast.success(
          `✅ Operation Undone Successfully!\n${response.data.message}`,
          { duration: 8000 }
        );
        
        // Refresh audience data and recent operations
        await loadAudienceData(currentPage, audienceSearch);
        await loadRecentOperations();
        
        // Close the undo dialog
        setShowUndoDialog(false);
        setOperationToUndo(null);
        setUndoReason('');
        
      } else {
        toast.error(`Failed to undo operation: ${response.data.message}`);
      }
      
    } catch (error) {
      console.error('Undo operation failed:', error);
      toast.error(
        `Failed to undo operation: ${error.response?.data?.detail || error.message}`,
        { duration: 8000 }
      );
    } finally {
      setIsUndoLoading(false);
    }
  };

  // Show undo confirmation dialog
  const confirmUndo = (operation) => {
    setOperationToUndo(operation);
    setShowUndoDialog(true);
  };

  // Handle undo confirmation
  const handleUndoConfirm = async () => {
    if (operationToUndo) {
      await undoOperation(operationToUndo.id, undoReason);
    }
  };

  // ===== AUDIENCE DELETE FUNCTIONALITY =====
  
  // Delete selected audience records
  const deleteSelectedRecords = async () => {
    try {
      setIsLoading(true);
      
      const recordIds = Array.from(selectedRecords);
      
      // Call backend API to delete records
      const response = await api.delete('/api/audience/bulk-delete', {
        data: { contact_ids: recordIds }
      });

      if (response.data.success) {
        toast.success(
          `✅ Successfully deleted ${recordIds.length} contact(s)`,
          { duration: 6000 }
        );
        
        // Clear selections and refresh data
        setSelectedRecords(new Set());
        setSelectAllChecked(false);
        await loadAudienceData(currentPage, audienceSearch);
        await loadRecentOperations();
        
        // Close dialog
        setShowDeleteDialog(false);
        
      } else {
        toast.error(`Failed to delete contacts: ${response.data.message}`);
      }
      
    } catch (error) {
      console.error('Delete operation failed:', error);
      toast.error(
        `Failed to delete contacts: ${error.response?.data?.detail || error.message}`,
        { duration: 8000 }
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ===== SEO AUDIT SYSTEM =====
  
  // Load latest SEO audit
  const loadLatestSEOAudit = async () => {
    try {
      const response = await api.get('/api/seo/audit/latest');
      setCurrentSEOAudit(response.data.audit);
      return response.data;
    } catch (error) {
      console.error('Failed to load latest SEO audit:', error);
      return { audit: null, has_connection: false };
    }
  };

  // Load SEO audit history
  const loadSEOAuditHistory = async (limit = 5) => {
    try {
      const response = await api.get(`/api/seo/audits/history?limit=${limit}`);
      setSeoAuditHistory(response.data.audits || []);
    } catch (error) {
      console.error('Failed to load SEO audit history:', error);
    }
  };

  // Start new SEO audit
  const startSEOAudit = async (siteUrl = null) => {
    try {
      setIsAuditRunning(true);
      setAuditProgress(0);
      
      const requestData = siteUrl ? { site_url: siteUrl } : {};
      
      const response = await api.post('/api/seo/audit/start', requestData);
      
      if (response.data.success) {
        const auditId = response.data.audit_id;
        
        toast.success('🔍 SEO Audit Started!', {
          description: `Comprehensive audit initiated for ${response.data.site_url}`,
          duration: 6000
        });
        
        // Poll for audit completion
        pollAuditProgress(auditId);
        
        return auditId;
      }
      
    } catch (error) {
      console.error('Failed to start SEO audit:', error);
      toast.error(
        error.response?.data?.detail || 'Failed to start SEO audit',
        { duration: 8000 }
      );
      setIsAuditRunning(false);
    }
  };

  // Poll audit progress
  const pollAuditProgress = async (auditId) => {
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        const response = await api.get(`/api/seo/audit/${auditId}`);
        const audit = response.data;
        
        if (audit.status === 'completed') {
          setCurrentSEOAudit(audit);
          setIsAuditRunning(false);
          setAuditProgress(100);
          
          toast.success('✅ SEO Audit Completed!', {
            description: `Overall Score: ${audit.overall_score}% | ${audit.total_issues} issues found`,
            duration: 10000
          });
          
          // Refresh audit history
          await loadSEOAuditHistory();
          
        } else if (audit.status === 'failed') {
          setIsAuditRunning(false);
          toast.error('❌ SEO Audit Failed', {
            description: audit.error_message || 'Audit could not be completed',
            duration: 8000
          });
          
        } else if (attempts < maxAttempts) {
          // Still in progress, update progress estimate
          const progressEstimate = Math.min(95, (attempts / maxAttempts) * 100);
          setAuditProgress(progressEstimate);
          
          // Continue polling
          setTimeout(poll, 5000); // Poll every 5 seconds
          
        } else {
          // Timeout
          setIsAuditRunning(false);
          toast.error('⏱️ Audit Timeout', {
            description: 'Audit is taking longer than expected. Please try again.',
            duration: 8000
          });
        }
        
      } catch (error) {
        console.error('Error polling audit progress:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setIsAuditRunning(false);
          toast.error('Failed to check audit progress');
        }
      }
    };
    
    // Start polling after 5 seconds
    setTimeout(poll, 5000);
  };

  // Get specific audit details
  const getAuditDetails = async (auditId) => {
    try {
      const response = await api.get(`/api/seo/audit/${auditId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get audit details:', error);
      toast.error('Failed to load audit details');
      return null;
    }
  };

  // Export SEO audit as PDF
  const exportAuditPDF = async (auditId = null) => {
    try {
      const exportAuditId = auditId || selectedAuditId || currentSEOAudit?.id;
      
      if (!exportAuditId) {
        toast.error('No audit selected for export');
        return;
      }

      setIsLoading(true);
      toast.info('📄 Generating PDF report...', { duration: 3000 });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/audit/${exportAuditId}/export-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'SEO_Audit_Report.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('✅ PDF report downloaded successfully!');
      
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error(`Failed to export PDF: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete audit
  const deleteAudit = async (auditId) => {
    try {
      await api.delete(`/api/seo/audit/${auditId}`);
      
      // Refresh audit history
      await loadSEOAuditHistory();
      
      // Clear current audit if it was deleted
      if (currentSEOAudit && currentSEOAudit.id === auditId) {
        setCurrentSEOAudit(null);
      }
      
      toast.success('Audit deleted successfully');
      
    } catch (error) {
      console.error('Failed to delete audit:', error);
      toast.error('Failed to delete audit');
    }
  };

  // ===== KEYWORD RESEARCH FUNCTIONS =====

  // Load latest keyword research
  const loadKeywordResearch = async () => {
    if (!currentEcommerceConnection?.store_url) return;
    
    try {
      const response = await api.get(`/api/keyword-research/latest/${encodeURIComponent(currentEcommerceConnection.store_url)}`);
      console.log('Loaded keyword research:', response.data.session); // Debug log
      setCurrentKeywordResearch(response.data.session);
    } catch (error) {
      console.error('Failed to load keyword research:', error);
    }
  };

  // Start keyword research
  const startKeywordResearch = async () => {
    if (!currentEcommerceConnection?.store_url) {
      toast.error('Please connect your website first');
      return;
    }

    try {
      setIsResearchRunning(true);
      setResearchProgress(0);
      
      toast.info('🔍 Starting keyword research...', { duration: 2000 });

      // Start research session
      const startResponse = await api.post('/api/keyword-research/start', {
        site_url: currentEcommerceConnection.store_url,
        session_name: `Keyword Research - ${new Date().toLocaleDateString()}`
      });

      const sessionId = startResponse.data.session_id;
      setSelectedResearchId(sessionId);

      // Run full research workflow
      setResearchProgress(20);
      toast.info('📊 Analyzing competitors...', { duration: 2000 });
      
      const researchResponse = await api.post(`/api/keyword-research/${sessionId}/run-full-research`, {
        competitor_urls: [] // Let the system auto-detect competitors
      });

      setResearchProgress(100);
      setCurrentKeywordResearch(researchResponse.data.summary.session);
      
      toast.success('✅ Keyword research completed successfully!');
      
    } catch (error) {
      console.error('Failed to start keyword research:', error);
      toast.error(`Failed to complete keyword research: ${error.message}`);
    } finally {
      setIsResearchRunning(false);
      setResearchProgress(0);
    }
  };

  // Get research details
  const getResearchDetails = async (sessionId) => {
    try {
      const response = await api.get(`/api/keyword-research/${sessionId}/actionable-insights`);
      return response.data;
    } catch (error) {
      console.error('Failed to get research details:', error);
      toast.error('Failed to load research details');
      return null;
    }
  };

  // View research report with actionable insights
  const viewResearchReport = async (sessionId = null) => {
    try {
      const reportSessionId = sessionId || selectedResearchId || currentKeywordResearch?.id;
      
      console.log('🔍 Loading Research Report...');
      console.log('Session ID:', reportSessionId);
      
      if (!reportSessionId) {
        toast.error('No research session selected for viewing report');
        return;
      }

      setIsLoading(true);
      toast.info('📊 Loading research report...', { duration: 2000 });

      // Get the actionable insights data
      const response = await api.get(`/api/keyword-research/${reportSessionId}/actionable-insights`);
      
      console.log('✅ Research report data loaded');
      console.log('Response data:', response.data);
      
      if (response.data && response.data.success) {
        const insights = response.data.data;
        
        console.log('Actionable insights:', insights);
        console.log('Keyword scorecards count:', insights.keyword_scorecards?.length || 0);
        console.log('Priority action plan count:', insights.priority_action_plan?.length || 0);
        console.log('Strategic recommendations count:', insights.strategic_recommendations?.length || 0);
        console.log('Competitor insights count:', insights.competitor_insights?.length || 0);
        
        // Store complete analysis results for viewing
        setCompleteAnalysisResults({
          actionable_insights: insights,
          session_id: reportSessionId
        });
        
        // Show report view dialog
        setCardDetailsType('research_report');
        setCardDetailsData({
          title: 'Keyword Research Report',
          type: 'research_report',
          data: insights,
          session_id: reportSessionId,
          totalCount: (insights.keyword_scorecards?.length || 0) + 
                      (insights.priority_action_plan?.length || 0) + 
                      (insights.strategic_recommendations?.length || 0) + 
                      (insights.competitor_insights?.length || 0)
        });
        setShowCardDetails(true);
        
        console.log('✅ Research report dialog opened');
        toast.success('Research report loaded successfully!');
        
      } else {
        console.error('❌ Invalid response structure:', response.data);
        toast.error('No research report data found. Please run keyword research first.');
      }
      
    } catch (error) {
      console.error('❌ Failed to load research report:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Enhanced error messages
      let errorMessage = 'Failed to load research report';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Research session not found. Please run keyword research first.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error while loading report. Please try again.';
      } else if (error.response?.data?.detail) {
        errorMessage = `Failed to load report: ${error.response.data.detail}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Run complete keyword analysis (Steps 5-7)
  const runCompleteAnalysis = async (sessionId) => {
    try {
      setIsLoading(true);
      toast.info('🔍 Running advanced analysis...', { duration: 3000 });

      const response = await api.post(`/api/keyword-research/${sessionId}/complete-analysis`, {
        competitor_urls: [] // Let the system auto-detect competitors
      });

      if (response.data.success) {
        // Store the detailed results
        const results = response.data.results;
        console.log('Complete analysis results received:', results); // Debug log
        console.log('Actionable insights data:', results.actionable_insights); // Debug log
        
        // Ensure actionable_insights is properly set
        if (results.actionable_insights) {
          console.log('Keyword scorecards count:', results.actionable_insights.keyword_scorecards?.length || 0);
          console.log('Priority action plan count:', results.actionable_insights.priority_action_plan?.length || 0);
        }
        
        setCompleteAnalysisResults(results);
        toast.success('✅ Complete keyword analysis finished!');
        return results;
      } else {
        toast.error('Analysis completed but no results returned');
        return null;
      }
      
    } catch (error) {
      console.error('Failed to run complete analysis:', error);
      toast.error(`Failed to complete analysis: ${error.response?.data?.detail || error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Export keyword research results
  const exportKeywordResearch = async (sessionId = null) => {
    try {
      const exportSessionId = sessionId || selectedResearchId || currentKeywordResearch?.id;
      
      console.log('🔍 Starting Keyword Research Export...');
      console.log('Export function called with sessionId:', sessionId);
      console.log('selectedResearchId:', selectedResearchId);
      console.log('currentKeywordResearch?.id:', currentKeywordResearch?.id);
      console.log('Final exportSessionId being used:', exportSessionId);
      
      if (!exportSessionId) {
        console.error('❌ No session ID available for export');
        toast.error('No research session selected for export. Please run a keyword research first.');
        return;
      }

      setIsLoading(true);
      toast.info('📊 Preparing keyword export...', { duration: 3000 });

      console.log('📡 Making API request to:', `/api/keyword-research/${exportSessionId}/keywords?limit=1000`);

      const response = await api.get(`/api/keyword-research/${exportSessionId}/keywords?limit=1000`);
      
      console.log('✅ Export API response received');
      console.log('Response status:', response.status);
      console.log('Response data structure:', {
        hasKeywords: !!response.data?.keywords,
        keywordCount: response.data?.keywords?.length || 0,
        responseKeys: Object.keys(response.data || {})
      });
      
      if (response.data && response.data.keywords && Array.isArray(response.data.keywords)) {
        const keywords = response.data.keywords;
        console.log('📊 Keywords received for export:', keywords.length);
        
        if (keywords.length === 0) {
          toast.error('No keywords found to export. Please run keyword research first.');
          return;
        }

        // Log sample data for debugging
        if (keywords[0]) {
          console.log('Sample keyword data:', {
            keyword: keywords[0].keyword,
            hasSearchVolume: 'search_volume' in keywords[0],
            hasDifficulty: 'difficulty' in keywords[0],
            hasScore: 'overall_score' in keywords[0],
            allKeys: Object.keys(keywords[0])
          });
        }

        // Enhanced CSV headers and content
        const csvHeaders = [
          'Keyword', 'Search Volume', 'Difficulty', 'Intent', 'Overall Score', 
          'Content Type', 'Relevance Score', 'Authority Score', 'Volume Score',
          'CPC', 'Competition', 'Source'
        ];
        
        const csvContent = [
          csvHeaders.join(','),
          ...keywords.map(kw => {
            const row = [
              `"${(kw.keyword || '').toString().replace(/"/g, '""')}"`, // Escape quotes
              (kw.search_volume || 0).toString(),
              (kw.difficulty || 0).toString(),
              `"${(kw.intent || 'informational').toString().replace(/"/g, '""')}"`,
              (kw.overall_score || 0).toString(),
              `"${(kw.recommended_content_type || 'blog_post').toString().replace(/"/g, '""')}"`,
              (kw.relevance_score || 0).toString(),
              (kw.authority_potential || 0).toString(),
              (kw.volume_score || 0).toString(),
              (kw.cpc || 0).toString(),
              `"${(kw.competition || 'unknown').toString().replace(/"/g, '""')}"`,
              `"${(kw.keyword_source || 'research').toString().replace(/"/g, '""')}"`
            ];
            return row.join(',');
          })
        ].join('\n');

        console.log('📄 CSV content generated');
        console.log('Content length:', csvContent.length);
        console.log('CSV sample (first 200 chars):', csvContent.substring(0, 200));

        // Create and download CSV file
        try {
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          console.log('✅ Blob created, size:', blob.size);
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Keyword_Research_${new Date().toISOString().split('T')[0]}.csv`;
          link.style.display = 'none';
          
          console.log('📥 Triggering download:', link.download);
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          console.log('✅ Download completed successfully');
          toast.success(`✅ ${keywords.length} keywords exported successfully!`);
          
        } catch (downloadError) {
          console.error('❌ Error during CSV download:', downloadError);
          toast.error('Failed to download CSV file. Please try again.');
        }
        
      } else {
        console.error('❌ Invalid response structure:', response.data);
        toast.error('No keyword data found. Please run keyword research first.');
      }
      
    } catch (error) {
      console.error('❌ Failed to export keyword research:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Enhanced error messages
      let errorMessage = 'Failed to export research';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Research session not found. Please run keyword research first.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error during export. Please try again.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.data?.detail) {
        errorMessage = `Export failed: ${error.response.data.detail}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear keyword research session for fresh start
  const clearKeywordResearch = async (sessionId = null) => {
    const clearSessionId = sessionId || selectedResearchId || currentKeywordResearch?.id;
    
    if (!clearSessionId) {
      toast.error('No research session selected to clear');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      '⚠️ Clear Research Data?\n\n' +
      'This will permanently delete all keywords, competitor analysis, and research progress for this session. ' +
      'You will need to start the research process from the beginning.\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsLoading(true);
      toast.info('🗑️ Clearing research data...', { duration: 3000 });

      const response = await api.delete(`/api/keyword-research/${clearSessionId}/clear`);
      
      if (response.data.success) {
        // Reset UI state
        setCurrentKeywordResearch(null);
        setSelectedResearchId(null);
        setCompleteAnalysisResults(null);
        
        // Refresh research sessions list
        await loadKeywordResearch();
        
        toast.success('✅ Research data cleared successfully! You can now start fresh research.');
      } else {
        toast.error('Failed to clear research data');
      }
      
    } catch (error) {
      console.error('Failed to clear keyword research:', error);
      toast.error(`Failed to clear research: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Export keyword research report as PDF
  const exportKeywordResearchPDF = async (sessionId = null) => {
    try {
      const exportSessionId = sessionId || selectedResearchId || currentKeywordResearch?.id;
      
      if (!exportSessionId) {
        toast.error('No research session selected for PDF export');
        return;
      }

      setIsLoading(true);
      toast.info('📄 Generating PDF report...', { duration: 3000 });

      const response = await api.get(`/api/keyword-research/${exportSessionId}/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Keyword_Research_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('✅ PDF report exported successfully!');
      
    } catch (error) {
      console.error('Failed to export PDF report:', error);
      toast.error(`Failed to export PDF: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle card click to show details with data tables
  const handleCardClick = async (cardType, sessionId = null) => {
    const targetSessionId = sessionId || selectedResearchId || currentKeywordResearch?.id;
    
    if (!targetSessionId) {
      toast.error('No research session available');
      return;
    }

    try {
      setIsLoading(true);
      
      let detailsData = null;
      
      switch (cardType) {
        case 'competitors':
          // Fetch competitor analysis details
          const competitorResponse = await api.get(`/api/keyword-research/${targetSessionId}/competitors`);
          detailsData = {
            title: 'Competitors Analyzed',
            type: 'competitors',
            data: competitorResponse.data.competitors || [],
            totalCount: competitorResponse.data.total_count || 0
          };
          break;
          
        case 'keywords':
          // Fetch ALL keywords - remove limit
          const keywordsResponse = await api.get(`/api/keyword-research/${targetSessionId}/keywords?limit=1000`);
          detailsData = {
            title: 'Keywords Discovered', 
            type: 'keywords',
            data: keywordsResponse.data.keywords || [],
            totalCount: keywordsResponse.data.total_count || 0
          };
          break;
          
        case 'high_priority':
          // Fetch ALL keywords and filter high priority ones
          const allKeywordsResponse = await api.get(`/api/keyword-research/${targetSessionId}/keywords?limit=1000`);
          const highPriorityKeywords = (allKeywordsResponse.data.keywords || []).filter(kw => (kw.overall_score || 0) >= 75);
          detailsData = {
            title: 'High-Priority Keywords (Score ≥ 75)',
            type: 'high_priority',
            data: highPriorityKeywords,
            totalCount: highPriorityKeywords.length
          };
          break;
          
        case 'content_opportunities':
          // Fetch content strategy details
          const contentResponse = await api.get(`/api/keyword-research/${targetSessionId}/content-map`);
          detailsData = {
            title: 'Content Opportunities',
            type: 'content_opportunities',
            data: contentResponse.data.content_strategy || {},
            totalCount: 0
          };
          break;
          
        case 'analysis_competitors':
          // For advanced analysis - competitor insights
          if (completeAnalysisResults?.actionable_insights?.competitor_insights) {
            detailsData = {
              title: 'Competitor Analysis Details',
              type: 'analysis_competitors',
              data: completeAnalysisResults.actionable_insights.competitor_insights,
              totalCount: completeAnalysisResults.actionable_insights.competitor_insights.length
            };
          }
          break;
          
        case 'analysis_keywords':
          // For advanced analysis - keywords scored
          if (completeAnalysisResults?.actionable_insights?.keyword_scorecards) {
            detailsData = {
              title: 'Keywords from Advanced Scoring',
              type: 'analysis_keywords',
              data: completeAnalysisResults.actionable_insights.keyword_scorecards,
              totalCount: completeAnalysisResults.actionable_insights.keyword_scorecards.length
            };
          }
          break;
          
        default:
          toast.error('Unknown card type');
          return;
      }
      
      if (detailsData) {
        setCardDetailsType(cardType);
        setCardDetailsData(detailsData);
        setShowCardDetails(true);
      } else {
        toast.error('No details available for this card');
      }
      
    } catch (error) {
      console.error('Failed to fetch card details:', error);
      toast.error(`Failed to load details: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== KEYWORD IMPORT & STORAGE FUNCTIONS =====

  // Handle file upload for keyword import
  const handleKeywordFileUpload = (file) => {
    if (!file) return;
    
    const validTypes = [
      'text/csv', 
      'text/plain', 
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|txt|xlsx|xls)$/i)) {
      toast.error('Please upload a valid CSV, TXT, or Excel file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size should be less than 5MB');
      return;
    }
    
    setKeywordResearchForm(prev => ({ ...prev, uploadedFile: file }));
    toast.success(`File "${file.name}" selected for import`);
  };

  // Import keywords (manual or file)
  const importKeywords = async (type, data, location) => {
    try {
      if (type === 'manual') {
        // Manual keyword import
        const keywords = data;
        
        if (!keywords.length) {
          toast.error('No keywords to import');
          return;
        }
        
        // Store keywords in database via API call
        const response = await api.post('/api/keywords/import', {
          keywords: keywords.map(keyword => ({
            keyword: keyword,
            source: 'manual_import',
            location: location,
            imported_at: new Date().toISOString()
          }))
        });
        
        if (response.data.success) {
          toast.success(`✅ Successfully imported ${keywords.length} keywords`);
          // Refresh metrics and keyword list
          loadAccumulatedMetrics();
          loadUnifiedKeywords(); // Refresh the unified keywords display
        } else {
          throw new Error(response.data.error || 'Failed to import keywords');
        }
        
      } else if (type === 'file') {
        // File upload import
        const file = data;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('location', location);
        
        const response = await api.post('/api/keywords/import-file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          toast.success(`✅ Successfully imported ${response.data.keywords_count} keywords from ${file.name}`);
          // Refresh metrics and keyword list
          loadAccumulatedMetrics();
          loadUnifiedKeywords(); // Refresh the unified keywords display
        } else {
          throw new Error(response.data.error || 'Failed to import keywords from file');
        }
      }
      
    } catch (error) {
      console.error('Keyword import error:', error);
      toast.error(`Import failed: ${error.response?.data?.detail || error.message}`);
      throw error;
    }
  };

  // Clean up legacy keywords
  const cleanupLegacyKeywords = async () => {
    try {
      const confirmation = window.confirm(
        'This will clean up legacy keywords from old collections. This action cannot be undone. Are you sure you want to proceed?'
      );
      
      if (!confirmation) {
        return;
      }
      
      setIsLoading(true);
      
      const response = await api.post('/api/keywords/cleanup-legacy');
      
      if (response.data.success) {
        toast.success(`✅ Successfully cleaned up ${response.data.cleaned_up.legacy_keywords} legacy keywords!`);
        
        // Reload keyword data to show updated counts
        await loadUnifiedKeywords();
        await loadKeywordAnalytics();
      } else {
        toast.error(`❌ Cleanup failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Legacy keyword cleanup failed:', error);
      toast.error('❌ Failed to clean up legacy keywords. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ===== ENHANCED AUDIENCE FUNCTIONS =====

  // Enhanced audience state
  const [enhancedContacts, setEnhancedContacts] = useState([]);
  const [enhancedStats, setEnhancedStats] = useState({});
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState(null);
  const [headerMappings, setHeaderMappings] = useState({});
  const [linkedSectionMappings, setLinkedSectionMappings] = useState({}); // NEW!
  const [importJobId, setImportJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);

  // Header Management state
  const [showHeaderManager, setShowHeaderManager] = useState(false);
  const [learnedSynonyms, setLearnedSynonyms] = useState([]);
  const [headerCustomFields, setHeaderCustomFields] = useState([]);
  const [ignoredHeaders, setIgnoredHeaders] = useState([]);
  const [editingSynonym, setEditingSynonym] = useState(null);

  // Enhanced Table Management state
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [enhancedVisibleColumns, setEnhancedVisibleColumns] = useState(new Set([
    'full_name', 'email_addresses', 'phone_numbers', 'company_name', 'data_source', 'total_score', 'category'
  ]));
  const [showEnhancedColumnManager, setShowEnhancedColumnManager] = useState(false);
  const [enhancedTableSearch, setEnhancedTableSearch] = useState('');
  const [enhancedSortConfig, setEnhancedSortConfig] = useState({ key: 'total_score', direction: 'desc' });
  
  // NEW: Data source filter state
  const [dataSourceFilter, setDataSourceFilter] = useState(null);
  const [enhancedCurrentPage, setEnhancedCurrentPage] = useState(1);
  const [enhancedPageSize, setEnhancedPageSize] = useState(10);
  const [showLinkedSections, setShowLinkedSections] = useState(false);
  const [selectedContactForSections, setSelectedContactForSections] = useState(null);
  
  // NEW: Linked Section Management State
  const [contactLinkedSections, setContactLinkedSections] = useState({});
  const [showAddLinkedSectionEntry, setShowAddLinkedSectionEntry] = useState(false);
  const [selectedSectionType, setSelectedSectionType] = useState('');
  const [sectionTemplate, setSectionTemplate] = useState(null);
  const [newEntryData, setNewEntryData] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [linkedSectionSearchTerm, setLinkedSectionSearchTerm] = useState('');

  // Step 1: File Upload and Analysis
  const handleFileUpload = async (file) => {
    console.log('🔄 Starting file upload process...', file?.name);
    
    try {
      // Validate file exists
      if (!file) {
        throw new Error('No file provided');
      }
      
      console.log('📁 File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      setImportFile(file);
      setIsLoading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('🚀 Sending file to backend...');
      
      const response = await api.post('/api/audience/enhanced/analyze-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('📨 Backend response:', response.data);
      
      if (response.data && response.data.success) {
        console.log('✅ File analysis successful, moving to step 2');
        setFileAnalysis(response.data);
        setImportStep(2);
        toast.success('✅ File analyzed successfully!');
      } else {
        console.log('❌ File analysis failed:', response.data);
        toast.error('❌ File analysis failed: ' + (response.data?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('💥 File analysis error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to analyze file. Please try again.';
      toast.error('❌ ' + errorMessage);
    } finally {
      console.log('🏁 File upload process finished');
      setIsLoading(false);
    }
  };

  // Step 2: Header Mapping with AI Suggestions
  const handleHeaderMapping = (originalHeader, targetField) => {
    setHeaderMappings(prev => ({
      ...prev,
      [originalHeader]: targetField
    }));
    
    // Show feedback when skipping a field
    if (targetField === 'SKIP') {
      toast.info(`📋 "${originalHeader}" field will be skipped during import`);
    }
  };

  // Undo Skip Field - restore field to mapping options
  const undoSkipField = (headerName) => {
    setHeaderMappings(prev => {
      const updated = { ...prev };
      delete updated[headerName]; // Remove the SKIP mapping
      return updated;
    });
    toast.success(`✅ "${headerName}" field restored for mapping`);
  };

  // Handle Linked Section Mapping - NEW!
  const handleLinkedSectionMapping = (headerName, sectionType) => {
    setLinkedSectionMappings(prev => ({
      ...prev,
      [headerName]: sectionType
    }));
    
    // Remove from regular header mappings if it exists
    setHeaderMappings(prev => {
      const updated = { ...prev };
      delete updated[headerName];
      return updated;
    });
    
    toast.success(`📦 "${headerName}" mapped to ${sectionType}`);
  };

  // Remove Linked Section Mapping - NEW! 
  const removeLinkedSectionMapping = (headerName) => {
    setLinkedSectionMappings(prev => {
      const updated = { ...prev };
      delete updated[headerName];
      return updated;
    });
    toast.info(`📦 "${headerName}" removed from linked sections`);
  };

  // Step 3: Train New Headers
  const trainNewHeader = async (header, action, targetField = null) => {
    try {
      setIsLoading(true);
      
      const response = await api.post('/api/audience/enhanced/train-header', {
        header,
        action,
        target_field: targetField
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        
        // Update the file analysis to reflect the learning
        if (action === 'map_to_existing' && targetField) {
          setFileAnalysis(prev => ({
            ...prev,
            mapping_suggestions: prev.mapping_suggestions.map(suggestion => 
              suggestion.header === header 
                ? { ...suggestion, suggested_field: targetField, confidence: 1.0, action: 'auto_map' }
                : suggestion
            )
          }));
          
          // Update mappings
          setHeaderMappings(prev => ({
            ...prev,
            [header]: targetField
          }));
        } else if (action === 'create_new_field') {
          // Handle create new field - map to custom field with the header name
          const customFieldName = `custom_${header.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
          
          setFileAnalysis(prev => ({
            ...prev,
            mapping_suggestions: prev.mapping_suggestions.map(suggestion => 
              suggestion.header === header 
                ? { ...suggestion, suggested_field: customFieldName, confidence: 1.0, action: 'auto_map' }
                : suggestion
            )
          }));
          
          // Update mappings
          setHeaderMappings(prev => ({
            ...prev,
            [header]: customFieldName
          }));
          
          toast.info(`📝 Created custom field: ${customFieldName}`);
        }
      } else {
        toast.error('❌ Training failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Header training failed:', error);
      toast.error('❌ Failed to train header. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Start Import Process
  const startImportProcess = async () => {
    try {
      setIsLoading(true);
      
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('confirmed_mappings', JSON.stringify(headerMappings));
      formData.append('linked_section_mappings', JSON.stringify(linkedSectionMappings)); // NEW!
      
      const response = await api.post('/api/audience/enhanced/import-streaming', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setImportJobId(response.data.job_id);
        setImportStep(4);
        toast.success('✅ Import started! Tracking progress...');
        
        // Start polling for progress
        pollImportProgress(response.data.job_id);
      } else {
        toast.error('❌ Import failed to start');
      }
    } catch (error) {
      console.error('Import start failed:', error);
      toast.error('❌ Failed to start import. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Poll import progress
  const pollImportProgress = async (jobId) => {
    try {
      const response = await api.get(`/api/audience/enhanced/import-status/${jobId}`);
      
      if (response.data.success) {
        setImportProgress(response.data);
        
        if (response.data.status === 'completed') {
          // Use user-friendly message from backend, or fallback to generic message
          const userMessage = response.data.user_message || '✅ Import completed successfully!';
          const importSummary = response.data.import_summary;
          
          // Show the user-friendly message as the main toast
          if (importSummary && importSummary.duplicates_skipped > 0 && importSummary.new_contacts === 0) {
            // Special case for all duplicates - show as info instead of success
            toast.info(userMessage, { duration: 6000 });
          } else if (importSummary && importSummary.new_contacts > 0) {
            // Show success for actual imports
            toast.success(userMessage, { duration: 5000 });
          } else {
            // Show warning for other cases
            toast.warning(userMessage, { duration: 5000 });
          }
          
          setShowImportWizard(false);
          loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter); // Reload enhanced contacts
          loadEnhancedAudienceStats(); // Reload enhanced statistics
          return;
        } else if (response.data.status === 'failed') {
          const errorMessage = response.data.user_message || '❌ Import failed';
          toast.error(errorMessage);
          return;
        }
        
        // Continue polling if still processing
        if (response.data.status === 'processing') {
          setTimeout(() => pollImportProgress(jobId), 2000);
        }
      }
    } catch (error) {
      console.error('Progress polling failed:', error);
    }
  };

  // Load enhanced audience data
  const loadEnhancedAudienceData = async (page = 1, search = '', scoreMin = 0, scoreMax = 100, dataSource = null) => {
    try {
      console.log('🔄 Loading enhanced audience data...');
      setIsLoading(true);
      
      const params = new URLSearchParams({
        page: '1',
        limit: '10000', // Increased limit to show all contacts (backend will handle reasonable limits)
        search: search,
        score_min: scoreMin.toString(),
        score_max: scoreMax.toString()
      });
      
      // Add data source filter if specified
      if (dataSource) {
        params.append('data_source', dataSource);
      }

      const response = await api.get(`/api/audience/enhanced/contacts?${params}`);
      
      console.log('📨 Enhanced contacts response:', response.data);
      
      if (response.data && response.data.success) {
        console.log('✅ Enhanced contacts loaded:', response.data.contacts?.length || 0);
        setEnhancedContacts(response.data.contacts || []);
        setAudiencePagination(response.data.pagination || {});
        
        // Show success toast if we have contacts
        if (response.data.contacts && response.data.contacts.length > 0) {
          toast.success(`✅ ${response.data.contacts.length} enhanced contacts loaded successfully`);
        }
      } else {
        console.log('❌ Enhanced contacts response not successful:', response.data);
        toast.error('❌ No enhanced contacts data returned');
      }
    } catch (error) {
      console.error('💥 Failed to load enhanced contacts:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`❌ Failed to load contacts: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load enhanced audience statistics
  const loadEnhancedAudienceStats = async () => {
    try {
      console.log('📊 Loading enhanced audience stats...');
      const response = await api.get('/api/audience/enhanced/stats');
      
      console.log('📈 Enhanced stats response:', response.data);
      
      if (response.data && response.data.success) {
        console.log('✅ Enhanced stats loaded:', response.data);
        setEnhancedStats(response.data);
      } else {
        console.log('❌ Enhanced stats response not successful:', response.data);
      }
    } catch (error) {
      console.error('💥 Failed to load enhanced stats:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  };

  // ===== ENHANCED TABLE MANAGEMENT FUNCTIONS =====

  // Available columns for the enhanced contacts table
  const availableColumns = [
    // Core Contact Fields
    { key: 'full_name', label: 'Name', type: 'text', category: 'contact' },
    { key: 'email_addresses', label: 'Email', type: 'array', category: 'contact' },
    { key: 'phone_numbers', label: 'Phone', type: 'array', category: 'contact' },
    { key: 'company_name', label: 'Company', type: 'text', category: 'contact' },
    { key: 'job_title', label: 'Job Title', type: 'text', category: 'contact' },
    
    // NEW: Data Source Column
    { key: 'data_source', label: 'Source', type: 'source_badge', category: 'contact' },
    
    // Geographic Fields
    { key: 'city', label: 'City', type: 'text', category: 'geographic' },
    { key: 'state_province', label: 'State/Province', type: 'text', category: 'geographic' },
    { key: 'country', label: 'Country', type: 'text', category: 'geographic' },
    { key: 'address', label: 'Street Address', type: 'text', category: 'geographic' },
    { key: 'zip_code', label: 'ZIP/Postal Code', type: 'text', category: 'geographic' },
    
    // Demographic Fields - NEW!
    { key: 'date_of_birth', label: 'Date of Birth', type: 'text', category: 'demographic' },
    { key: 'age', label: 'Age', type: 'number', category: 'demographic' },
    { key: 'gender', label: 'Gender', type: 'text', category: 'demographic' },
    
    // Professional Fields
    { key: 'industry', label: 'Industry', type: 'text', category: 'professional' },
    { key: 'lead_source', label: 'Lead Source', type: 'text', category: 'professional' },
    { key: 'lead_status', label: 'Lead Status', type: 'text', category: 'professional' },
    
    // Additional Contact Information
    { key: 'website', label: 'Website', type: 'text', category: 'contact' },
    { key: 'preferred_contact_method', label: 'Preferred Contact Method', type: 'text', category: 'contact' },
    { key: 'notes', label: 'Notes', type: 'text', category: 'contact' },
    { key: 'last_contact_date', label: 'Last Contact Date', type: 'date', category: 'contact' },
    { key: 'custom_tags', label: 'Tags', type: 'array', category: 'contact' },
    
    // Scoring & Engagement
    { key: 'total_score', label: 'Score', type: 'number', category: 'scoring' },
    { key: 'engagement_score', label: 'Engagement Score', type: 'number', category: 'scoring' },
    { key: 'completeness_score', label: 'Completeness Score', type: 'number', category: 'scoring' },
    { key: 'last_engaged', label: 'Last Engaged', type: 'date', category: 'scoring' },
    
    // System Fields
    { key: 'created_at', label: 'Created Date', type: 'date', category: 'system' },
    { key: 'last_updated', label: 'Last Updated', type: 'date', category: 'system' },
    { key: 'source', label: 'Source', type: 'text', category: 'system' },
    
    // Linked Section Fields - NEW!
    { key: 'linked_sections_purchase_history', label: '📦 Purchase History', type: 'linked_section', category: 'linked_sections', section: 'purchase_history' },
    { key: 'linked_sections_product_preferences', label: '🎯 Product Preferences', type: 'linked_section', category: 'linked_sections', section: 'product_preferences' },
    { key: 'linked_sections_employment_data', label: '🏢 Employment Data', type: 'linked_section', category: 'linked_sections', section: 'employment_data' },
    { key: 'linked_sections_financial_info', label: '🏦 Financial Info', type: 'linked_section', category: 'linked_sections', section: 'financial_info' },
    { key: 'linked_sections_insurance_policies', label: '🛡️ Insurance Policies', type: 'linked_section', category: 'linked_sections', section: 'insurance_policies' },
    { key: 'linked_sections_custom_section', label: '🎨 Custom Section', type: 'linked_section', category: 'linked_sections', section: 'custom_section' }
  ];

  // Toggle column visibility
  const toggleColumnVisibility = (columnKey) => {
    const newVisibleColumns = new Set(enhancedVisibleColumns);
    if (newVisibleColumns.has(columnKey)) {
      newVisibleColumns.delete(columnKey);
    } else {
      newVisibleColumns.add(columnKey);
    }
    setEnhancedVisibleColumns(newVisibleColumns);
  };

  // Toggle contact selection
  const toggleContactSelection = (contactId) => {
    console.log('Toggle contact selection:', contactId, 'Current selected:', selectedContacts.size);
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
      console.log('Deselected contact:', contactId);
    } else {
      newSelected.add(contactId);
      console.log('Selected contact:', contactId);
    }
    setSelectedContacts(newSelected);
    console.log('New selection count:', newSelected.size);
  };

  // Select all contacts (current page only)
  const toggleSelectAll = () => {
    const { contacts: paginatedContacts } = getPaginatedContacts();
    const pageContactIds = paginatedContacts.map(c => c.id);
    
    // Check if all contacts on current page are selected
    const allPageContactsSelected = pageContactIds.every(id => selectedContacts.has(id));
    
    if (allPageContactsSelected) {
      // Deselect all contacts on current page
      const newSelected = new Set(selectedContacts);
      pageContactIds.forEach(id => newSelected.delete(id));
      setSelectedContacts(newSelected);
    } else {
      // Select all contacts on current page
      const newSelected = new Set(selectedContacts);
      pageContactIds.forEach(id => newSelected.add(id));
      setSelectedContacts(newSelected);
    }
  };

  // Sort contacts
  const sortContacts = (contacts) => {
    if (!enhancedSortConfig.key) return contacts;
    
    return [...contacts].sort((a, b) => {
      let aValue = a[enhancedSortConfig.key];
      let bValue = b[enhancedSortConfig.key];
      
      // Handle array fields (email, phone)
      if (Array.isArray(aValue)) aValue = aValue[0] || '';
      if (Array.isArray(bValue)) bValue = bValue[0] || '';
      
      // Handle null/undefined
      if (!aValue) aValue = '';
      if (!bValue) bValue = '';
      
      // Convert to strings for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (enhancedSortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Filter contacts based on search (data source filtering now done server-side)
  const filterContacts = (contacts) => {
    let filtered = contacts;
    
    // Apply search filter
    if (enhancedTableSearch) {
      const searchLower = enhancedTableSearch.toLowerCase();
      filtered = filtered.filter(contact => {
        return (
          (contact.full_name || '').toLowerCase().includes(searchLower) ||
          (contact.email_addresses || []).some(email => email.toLowerCase().includes(searchLower)) ||
          (contact.phone_numbers || []).some(phone => phone.toLowerCase().includes(searchLower)) ||
          (contact.company_name || '').toLowerCase().includes(searchLower) ||
          (contact.job_title || '').toLowerCase().includes(searchLower)
        );
      });
    }
    
    return filtered;
  };

  // Get paginated contacts (client-side pagination)
  const getPaginatedContacts = () => {
    const filtered = filterContacts(enhancedContacts);
    const sorted = sortContacts(filtered);
    const startIndex = (enhancedCurrentPage - 1) * enhancedPageSize;
    const endIndex = startIndex + enhancedPageSize;
    
    return {
      contacts: sorted.slice(startIndex, endIndex),
      totalCount: sorted.length,
      totalPages: Math.ceil(sorted.length / enhancedPageSize)
    };
  };

  // Refresh enhanced data
  const refreshEnhancedData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter),
        loadEnhancedAudienceStats()
      ]);
      toast.success('✅ Data refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh data:', error);
      toast.error('❌ Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  // Export selected contacts
  const exportSelectedContacts = () => {
    if (selectedContacts.size === 0) {
      toast.error('❌ No contacts selected for export');
      return;
    }
    
    const contactsToExport = enhancedContacts.filter(c => selectedContacts.has(c.id));
    const csvContent = generateCSVContent(contactsToExport);
    downloadCSV(csvContent, 'selected_contacts.csv');
    toast.success(`✅ Exported ${contactsToExport.length} contacts`);
  };

  // Delete selected contacts
  const deleteSelectedContacts = async () => {
    if (selectedContacts.size === 0) {
      toast.error('❌ No contacts selected for deletion');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete ${selectedContacts.size} selected contacts? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setIsLoading(true);
      const contactIds = Array.from(selectedContacts);
      
      const response = await api.delete('/api/audience/enhanced/contacts', {
        data: { contact_ids: contactIds }
      });
      
      if (response.data.success) {
        toast.success(`✅ Successfully deleted ${response.data.deleted_count} contacts`);
        
        // Clear selection and reload data
        setSelectedContacts(new Set());
        await loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, dataSourceFilter);
        await loadEnhancedAudienceStats();
      } else {
        toast.error('❌ Failed to delete contacts');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`❌ Error deleting contacts: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to generate CSV content
  const generateCSVContent = (contacts) => {
    const headers = Array.from(enhancedVisibleColumns).map(col => 
      availableColumns.find(ac => ac.key === col)?.label || col
    ).join(',');
    
    const rows = contacts.map(contact => 
      Array.from(enhancedVisibleColumns).map(col => {
        const value = contact[col];
        if (Array.isArray(value)) {
          return `"${value.join('; ')}"`;
        }
        return `"${value || ''}"`;
      }).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  // Helper function to download CSV
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // ===== HEADER MANAGEMENT FUNCTIONS =====

  // Load learned synonyms
  const loadHeaderSynonyms = async () => {
    try {
      const response = await api.get('/api/audience/enhanced/header-synonyms');
      
      if (response.data.success) {
        setLearnedSynonyms(response.data.synonyms);
      }
    } catch (error) {
      console.error('Failed to load header synonyms:', error);
    }
  };

  // Load custom fields
  const loadCustomFields = async () => {
    try {
      const response = await api.get('/api/audience/enhanced/custom-fields');
      
      if (response.data.success) {
        setHeaderCustomFields(response.data.custom_fields);
      }
    } catch (error) {
      console.error('Failed to load custom fields:', error);
    }
  };

  // Load ignored headers
  const loadIgnoredHeaders = async () => {
    try {
      const response = await api.get('/api/audience/enhanced/ignored-headers');
      
      if (response.data.success) {
        setIgnoredHeaders(response.data.ignored_headers);
      }
    } catch (error) {
      console.error('Failed to load ignored headers:', error);
    }
  };

  // Delete synonym
  const deleteSynonym = async (synonymId) => {
    try {
      const response = await api.delete(`/api/audience/enhanced/header-synonyms/${synonymId}`);
      
      if (response.data.success) {
        toast.success('✅ Synonym deleted successfully!');
        loadHeaderSynonyms(); // Reload data
      } else {
        toast.error('❌ Failed to delete synonym');
      }
    } catch (error) {
      console.error('Failed to delete synonym:', error);
      toast.error('❌ Failed to delete synonym');
    }
  };

  // Update synonym
  const updateSynonym = async (synonymId, canonicalField, synonym, confidence) => {
    try {
      const response = await api.put(`/api/audience/enhanced/header-synonyms/${synonymId}`, {
        canonical_field: canonicalField,
        synonym: synonym,
        confidence: confidence
      });
      
      if (response.data.success) {
        toast.success('✅ Synonym updated successfully!');
        setEditingSynonym(null);
        loadHeaderSynonyms(); // Reload data
      } else {
        toast.error('❌ Failed to update synonym');
      }
    } catch (error) {
      console.error('Failed to update synonym:', error);
      toast.error('❌ Failed to update synonym');
    }
  };

  // Load all header management data
  const loadHeaderManagementData = async () => {
    await Promise.all([
      loadHeaderSynonyms(),
      loadCustomFields(),
      loadIgnoredHeaders()
    ]);
  };

  // Remove ignored header (unignore)
  const removeIgnoredHeader = async (headerName) => {
    try {
      const response = await api.delete(`/api/audience/enhanced/ignored-headers/${encodeURIComponent(headerName)}`);
      
      if (response.data.success) {
        toast.success('✅ Header removed from ignore list');
        loadIgnoredHeaders(); // Reload data
      } else {
        toast.error('❌ Failed to remove ignored header');
      }
    } catch (error) {
      console.error('Failed to remove ignored header:', error);
      toast.error('❌ Failed to remove ignored header');
    }
  };

  /* eslint-disable */
  // Load standardized audience schema
  const loadAudienceSchema = async () => {
    try {
      const response = await api.get('/api/audience/schema');
      setAudienceSchema(response.data);
      console.log('Loaded standardized audience schema:', response.data);
    } catch (error) {
      console.error('Failed to load audience schema:', error);
    }
  };

  // Enhanced intelligent file mapping preview
  const previewIntelligentMapping = async (file) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/api/audience/preview_mapping_enhanced', formData, {
        // Don't set Content-Type manually for FormData - let axios handle it
      });
      
      setMappingPreview(response.data);
      setFieldMapping(response.data.mapping_result.mapped_fields);
      setCustomFields(response.data.mapping_result.unmapped_headers);
      setUncertainMappings(response.data.mapping_result.uncertain_mappings || []);
      setConfidenceLevels(response.data.confidence_levels || {});
      setShowMappingDialog(true);
      
      console.log('Enhanced file mapping preview:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to preview intelligent mapping:', error);
      if (error.response?.status === 403) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 400) {
        toast.error('Invalid file format. Please upload CSV or Excel files only.');
      } else {
        toast.error('Failed to preview file mapping. Please try again.');
      }
      throw error;
    }
  };

  // Upload with intelligent mapping
  const uploadWithIntelligentMapping = async (confirmedMappings, customMappings = {}) => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('confirmed_mappings', JSON.stringify(confirmedMappings));
      formData.append('custom_field_mappings', JSON.stringify(customMappings));

      const response = await api.post('/api/audience/upload_with_intelligent_mapping', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStats(response.data.statistics);
      setUploadFile(null);
      setMappingPreview(null);
      setShowMappingDialog(false);
      setFieldMapping({});
      setCustomFields([]);
      setUserConfirmations({});
      setCustomFieldNames({});
      setShowCustomFieldInputs({});
      
      toast.success(
        `Successfully processed ${response.data.statistics.processed_records} records!\n` +
        `New: ${response.data.statistics.new_contacts}, ` +
        `Merged: ${response.data.statistics.duplicates_merged}, ` +
        `Age calculated: ${response.data.statistics.records_with_age_calculation}, ` +
        `Tagged: ${response.data.statistics.records_with_tags}`
      );
      
      // Reload audience data
      await loadAudienceData();
      
    } catch (error) {
      console.error('Intelligent upload failed:', error);
      const errorMsg = error.response?.data?.detail || 'Upload failed';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Tag management functions
  const addTagsToContact = async (contactId, tags) => {
    try {
      await api.post('/api/audience/tags/add', null, {
        params: { contact_id: contactId, tags: tags }
      });
      toast.success(`Added ${tags.length} tag(s)`);
      await loadAudienceData(); // Refresh data
    } catch (error) {
      toast.error('Failed to add tags');
    }
  };

  const removeTagsFromContact = async (contactId, tags) => {
    try {
      await api.delete('/api/audience/tags/remove', {
        params: { contact_id: contactId, tags: tags }
      });
      toast.success(`Removed ${tags.length} tag(s)`);
      await loadAudienceData(); // Refresh data
    } catch (error) {
      toast.error('Failed to remove tags');
    }
  };

  const loadAllTags = async () => {
    try {
      const response = await api.get('/api/audience/tags/all');
      return response.data.tags;
    } catch (error) {
      console.error('Failed to load tags:', error);
      return [];
    }
  };

  const bulkAddTags = async (contactIds, tags) => {
    try {
      const response = await api.post('/api/audience/tags/bulk_add', {
        contact_ids: contactIds,
        tags: tags
      });
      toast.success(`Added tags to ${response.data.contacts_updated} contacts`);
      await loadAudienceData(); // Refresh data
    } catch (error) {
      toast.error('Failed to bulk add tags');
    }
  };

  // Enhanced tag management handlers for dialogs
  const handleAddNewTag = async () => {
    if (!newTagInput.trim()) return;
    
    const selectedContactIds = Array.from(selectedRecords);
    await bulkAddTags(selectedContactIds, [newTagInput.trim()]);
    
    // Clear input and close dialog
    setNewTagInput('');
    setShowTagDialog(false);
    
    // Refresh available tags
    const tags = await loadAllTags();
    setAvailableTags(tags);
    
    // Refresh current data
    await loadAudienceData(currentPage, audienceSearch);
  };

  const handleAddExistingTag = async (tag) => {
    const selectedContactIds = Array.from(selectedRecords);
    await bulkAddTags(selectedContactIds, [tag]);
    setShowTagDialog(false);
    
    // Refresh current data
    await loadAudienceData(currentPage, audienceSearch);
  };

  const handleRemoveTag = async (tag) => {
    if (!window.confirm(`Remove "${tag}" from ${selectedRecords.size} selected contacts?`)) {
      return;
    }

    try {
      const selectedContactIds = Array.from(selectedRecords);
      const response = await api.post('/api/audience/tags/bulk_remove', {
        contact_ids: selectedContactIds,
        tags: [tag]
      });
      toast.success(`Removed "${tag}" from ${response.data.contacts_updated} contacts`);
      await loadAudienceData(currentPage, audienceSearch); // Refresh data with current page and search
      setShowRemoveTagDialog(false);
      
      // Refresh available tags
      const updatedTags = await loadAllTags();
      setAvailableTags(updatedTags);
    } catch (error) {
      toast.error(`Failed to remove tag "${tag}"`);
    }
  };

  // Load available tags when dialogs open
  useEffect(() => {
    if (showTagDialog || showRemoveTagDialog) {
      loadAllTags().then(tags => setAvailableTags(tags));
    }
  }, [showTagDialog, showRemoveTagDialog]);

  // Preview file mapping before upload (legacy function for backward compatibility)
  const previewFileMapping = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/api/audience/preview_mapping', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setMappingPreview(response.data);
      setFieldMapping(response.data.mapping_result.mapped_fields);
      setCustomFields(response.data.mapping_result.unmapped_headers);
      setShowMappingDialog(true);
      
      console.log('File mapping preview:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to preview file mapping:', error);
      toast.error('Failed to preview file mapping');
      throw error;
    }
  };

  // Upload audience data using standardized schema
  const uploadStandardizedAudienceData = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!mappingPreview || Object.keys(fieldMapping).length === 0) {
      toast.error('Please preview the file mapping first');
      return;
    }

    // Check if minimum requirements are met
    if (mappingPreview.recommendations && !mappingPreview.recommendations.can_proceed) {
      toast.error('File is missing required fields: Full Name and (Email or Phone)');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('field_mapping', JSON.stringify(fieldMapping));
      formData.append('custom_fields', JSON.stringify(customFields));

      const response = await api.post('/api/audience/upload_standardized', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStats(response.data.statistics);
      setUploadFile(null);
      setMappingPreview(null);
      setShowMappingDialog(false);
      setFieldMapping({});
      setCustomFields([]);
      setUserConfirmations({});
      setCustomFieldNames({});
      setShowCustomFieldInputs({});
      
      toast.success(`Successfully uploaded ${response.data.statistics.new_contacts + response.data.statistics.updated_contacts} contacts!`);
      
      // Reload audience data
      await loadAudienceData();
      
      // Reload recent operations to show the new upload operation
      await loadRecentOperations();
      
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMsg = error.response?.data?.detail || 'Upload failed';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setAllUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Initialize selected columns when audience data is loaded
  useEffect(() => {
    if (audienceData.length > 0) {
      // Extract field names from the actual audience data
      const availableFields = audienceData.length > 0 ? Object.keys(audienceData[0]) : [];
      
      // Check if we have saved columns and if they're still valid for current data
      const savedColumns = localStorage.getItem('omnimarket_selected_columns');
      
      if (savedColumns) {
        try {
          const parsedColumns = JSON.parse(savedColumns);
          
          // Filter saved columns to only include those that exist in current data
          const validSavedColumns = parsedColumns.filter(column => availableFields.includes(column));
          
          if (validSavedColumns.length > 0) {
            setSelectedColumns(validSavedColumns);
            console.log('✅ Loaded saved column preferences:', validSavedColumns);
            return; // Use saved columns if they're valid
          } else {
            console.log('ℹ️ No valid saved columns found, clearing localStorage and using defaults');
            localStorage.removeItem('omnimarket_selected_columns'); // Clear invalid data
          }
        } catch (error) {
          console.warn('Error parsing saved columns:', error);
          localStorage.removeItem('omnimarket_selected_columns'); // Clear invalid data
        }
      }
      
      // If no valid saved columns, set default columns
      console.log('ℹ️ Setting default columns for first-time use');
      
      // Prioritize the key fields that users want to see: Name, Age, Gender, Phone, Email, Tags
      const essentialFields = [
        'full_name', 'name', // Name variations
        'age', 'age_date_of_birth', 'date_of_birth', // Age variations
        'gender', 'sex', // Gender variations  
        'mobile_number', 'phone', 'phone_number', // Phone variations
        'email_address', 'email', 'e_mail', // Email variations
        'custom_tags', 'tags', 'tag' // Tags variations
      ];
      
      // Find the actual field names that exist in our data
      const availableEssentialFields = essentialFields.filter(field => 
        availableFields.includes(field)
      );
      
      // Add other important fields
      const otherImportantFields = ['company_name', 'company', 'city', 'country', 'state_province', 'job_title', 'position'];
      const availableOtherFields = otherImportantFields.filter(field => 
        availableFields.includes(field) && !availableEssentialFields.includes(field)
      );
      
      // Add some AI fields if available
      const aiFields = availableFields.filter(field => field.startsWith('ai_')).slice(0, 3);
      
      // Filter out metadata fields that shouldn't be displayed
      const metadataFields = ['id', 'created_at', 'uploaded_by', 'schema_version', 'processing_status', '_id'];
      const filteredOtherFields = availableOtherFields.filter(field => !metadataFields.includes(field));
      
      // Combine all fields, prioritizing essential ones
      const prioritizedFields = [
        ...availableEssentialFields, // Essential fields first
        ...filteredOtherFields.slice(0, 4), // Some other important fields
        ...aiFields // A few AI fields
      ].slice(0, 12); // Limit total to 12 columns for good UX
      
      setSelectedColumns(prioritizedFields);
      
      console.log('ℹ️ Available fields in data:', availableFields.length, 'fields');
      console.log('✅ Selected default columns for display:', prioritizedFields);
    }
  }, [audienceData]);

  // Save selected columns to localStorage whenever they change
  useEffect(() => {
    if (selectedColumns.length > 0) {
      localStorage.setItem('omnimarket_selected_columns', JSON.stringify(selectedColumns));
      
      // Add subtle feedback with a delay to avoid too many notifications
      const timeoutId = setTimeout(() => {
        console.log('Column preferences saved automatically');
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedColumns]);

  // Handle column selection changes
  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => {
      if (prev.includes(column)) {
        return prev.filter(col => col !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  // Handle individual record selection
  const handleRecordSelect = (recordId) => {
    setSelectedRecords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(recordId)) {
        newSet.delete(recordId);
      } else {
        newSet.add(recordId);
      }
      
      // Update select all checkbox
      setSelectAllChecked(newSet.size === audienceData.length && audienceData.length > 0);
      return newSet;
    });
  };

  // Handle select all functionality
  const handleSelectAll = (checked) => {
    setSelectAllChecked(checked);
    if (checked) {
      setSelectedRecords(new Set(audienceData.map(record => record.id)));
    } else {
      setSelectedRecords(new Set());
    }
  };

  // Export selected records to Excel with comprehensive download handling
  const handleExportSelected = async () => {
    if (selectedRecords.size === 0) {
      toast.error('❌ No records selected for export.\n💡 Please select records using the checkboxes first.');
      return;
    }

    setIsLoading(true);
    try {
      toast.info(`📊 Preparing export of ${selectedRecords.size} selected records...`, { duration: 2000 });
      
      // Prepare parameters
      const selectedIds = Array.from(selectedRecords).join(',');
      const columns = selectedColumns.length > 0 ? selectedColumns.join(',') : '';
      const token = localStorage.getItem('token');
      
      // Construct download URL with token for authentication
      let downloadUrl = `${API_BASE_URL}/api/audience/export/excel/selected?ids=${encodeURIComponent(selectedIds)}`;
      if (columns) {
        downloadUrl += `&columns=${encodeURIComponent(columns)}`;
      }
      
      console.log('Export Selected - URL:', downloadUrl);
      console.log('Export Selected - Record Count:', selectedRecords.size);

      // Use direct navigation for reliable file download
      try {
        // Create hidden iframe for download (most reliable method)
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = downloadUrl;
        
        // Add authorization header by including token in URL for download
        const urlWithAuth = downloadUrl;
        
        // Method 1: Direct download using fetch to verify, then window.location for actual download
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Selected records not found. They may have been deleted. Please refresh the page and try again.');
          } else if (response.status === 401 || response.status === 403) {
            throw new Error('Authentication failed. Please refresh the page and login again.');
          } else {
            throw new Error(`Export failed: ${response.status} ${response.statusText}`);
          }
        }
        
        // If response is OK, trigger download using simple window.open method
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `audience_selected_${selectedRecords.size}records_${timestamp}.xlsx`;
        
        // Create a temporary link element for download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.style.display = 'none';
        
        // Add authorization header by creating authenticated download URL
        const authenticatedUrl = `${downloadUrl}&t=${Date.now()}`;  // Cache buster
        
        // For file downloads, we need to make the request with proper headers
        // Then create a blob and download it
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        link.href = blobUrl;
        link.download = filename;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        
        toast.success(
          `✅ Export completed successfully!\n` + 
          `📊 ${selectedRecords.size} selected records exported\n` + 
          `📁 File saved to Downloads folder\n` +
          `📋 Filename: ${filename}`,
          { duration: 10000 }
        );
        
        // Clear selection after successful export
        setSelectedRecords(new Set());
        setSelectAllChecked(false);
        
      } catch (downloadError) {
        console.error('Blob download failed:', downloadError);
        
        // Method 2: Fallback download (less secure but more compatible)
        try {
          const fallbackUrl = downloadUrl + `&download=direct&token=${encodeURIComponent(token)}`;
          const newWindow = window.open(fallbackUrl, '_blank');
          
          if (newWindow) {
            toast.success(
              `✅ Export initiated in new tab!\n` + 
              `📊 ${selectedRecords.size} records being exported\n` + 
              `📁 Check Downloads folder or browser tab`,
              { duration: 8000 }
            );
            
            // Clear selection
            setSelectedRecords(new Set());
            setSelectAllChecked(false);
          } else {
            throw new Error('Popup blocked - please disable popup blocker and try again');
          }
          
        } catch (fallbackError) {
          console.error('Fallback download failed:', fallbackError);
          throw new Error(`All download methods failed: ${fallbackError.message}`);
        }
      }
        
    } catch (error) {
      console.error('Export selected error:', error);
      
      let errorMessage = '❌ Export failed: ';
      
      if (error.message.includes('Authentication failed')) {
        errorMessage += 'Please refresh the page and login again.';
      } else if (error.message.includes('not found') || error.message.includes('deleted')) {
        errorMessage += 'Selected records not found. Please refresh the page and try again.';
      } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        errorMessage += 'Network connection issue. Please check your internet connection.';
      } else if (error.message.includes('Invalid response format')) {
        errorMessage += 'Server returned invalid data. Please contact support.';
      } else if (error.message.includes('empty')) {
        errorMessage += 'Export file is empty - selected records may have no exportable data.';
      } else if (error.message.includes('popup blocked')) {
        errorMessage += 'Browser blocked the download. Please disable popup blocker.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      errorMessage += '\n\n💡 Troubleshooting:\n• Check Downloads folder\n• Disable popup/download blockers\n• Try different browser\n• Refresh page and try again';
      
      toast.error(errorMessage, { duration: 15000 });
    } finally {
      setIsLoading(false);
    }
  };

  // Export all records to Excel with universal browser compatibility
  const handleExportAll = async () => {
    if (!audienceData || audienceData.length === 0) {
      toast.error('No audience data available to export');
      return;
    }

    setIsLoading(true);
    try {
      toast.info('Preparing complete Excel export...', { duration: 2000 });
      
      // Prepare parameters
      const columns = selectedColumns.length > 0 ? selectedColumns.join(',') : '';
      const token = localStorage.getItem('token');
      
      // Construct download URL
      let downloadUrl = `${API_BASE_URL}/api/audience/export/excel/all`;
      if (columns) {
        downloadUrl += `?columns=${encodeURIComponent(columns)}`;
      }
      
      console.log('Download All URL:', downloadUrl);

      // Fetch the file and create a download
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob and force download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `audience_complete_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      
      // Force download
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(
        `✅ Excel export completed!\n` + 
        `📊 All audience records exported\n` + 
        `💾 Check your Downloads folder`,
        { duration: 8000 }
      );
      
    } catch (error) {
      console.error('Export all error:', error);
      toast.error('Export failed: ' + error.message, { duration: 10000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, loginForm);
      
      const { access_token, user: userData } = response.data;
      console.log('Login successful, setting token:', access_token ? 'Token received' : 'No token');
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      setIsAuthLoading(false); // Clear auth loading since we have user data
      
      console.log('Token saved to localStorage:', localStorage.getItem('token') ? 'Success' : 'Failed');
      
      toast.success('Logged in successfully!');
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthLoading(false);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setActiveTab('dashboard');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/api/users', createUserForm);
      toast.success('User created successfully!');
      setCreateUserForm({ email: '', name: '', password: '', role: 'campaign_creator' });
      loadAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.put(`/api/users/${editingUser.id}`, editUserForm);
      toast.success('User updated successfully!');
      setEditingUser(null);
      setEditUserForm({ name: '', email: '', role: '' });
      loadAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      setIsLoading(true);
      try {
        await api.delete(`/api/users/${userId}`);
        toast.success('User deleted successfully!');
        loadAllUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const startEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email,
      role: user.role
    });
  };

  const handleRegularFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;

    // First, check file headers and show column mapping interface
    await handleFileSelection();
  };

  const handleFileSelection = async () => {
    if (!uploadFile) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      // Create a preview endpoint call to get headers without processing data
      const response = await api.post('/api/audience/preview_headers', formData);
      
      const fileHeaders = response.data.file_headers || [];
      setUploadFileHeaders(fileHeaders);
      setExistingColumns([...audienceFields]); // Create a copy to avoid reference issues
      
      // Initialize column mapping - auto-map exact matches safely
      const autoMapping = {};
      fileHeaders.forEach(header => {
        if (!header || typeof header !== 'string') return;
        
        const lowerHeader = header.toLowerCase().trim();
        const matchingField = audienceFields.find(field => {
          if (!field || typeof field !== 'string') return false;
          const lowerField = field.toLowerCase().trim();
          return lowerField === lowerHeader || 
                 lowerField.includes(lowerHeader) ||
                 lowerHeader.includes(lowerField);
        });
        
        // Set mapping
        autoMapping[header] = matchingField || header;
      });
      
      setColumnMapping(autoMapping);
      setPendingUploadFile(uploadFile);
      setShowColumnMapping(true);
      
    } catch (error) {
      console.error('File preview error:', error);
      // If preview endpoint doesn't exist, fall back to direct upload
      await processFileUpload();
    } finally {
      setIsLoading(false);
    }
  };

  const processFileUpload = async () => {
    if (!pendingUploadFile) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', pendingUploadFile);
    
    // Add column mapping data if available
    if (Object.keys(columnMapping).length > 0) {
      formData.append('column_mapping', JSON.stringify(columnMapping));
    }

    try {
      const response = await api.post('/api/audience/upload', formData);
      setUploadStats(response.data.statistics);
      
      // Show field mapping information to user
      const mapping = response.data.field_mapping;
      if (mapping) {
        let mappingMessage = '';
        
        if (Object.keys(mapping.mapped_fields || {}).length > 0) {
          mappingMessage += 'Header mappings: ';
          Object.entries(mapping.mapped_fields).forEach(([original, mapped]) => {
            mappingMessage += `"${original}" → "${mapped}", `;
          });
        }
        
        if (Object.keys(mapping.new_fields || {}).length > 0) {
          mappingMessage += 'New fields created: ';
          mapping.new_fields.forEach((field, index) => {
            mappingMessage += `"${field}"${index < mapping.new_fields.length - 1 ? ', ' : ''}`;
          });
        }
        
        if (mappingMessage) {
          toast.success(`${response.data.message}. ${mappingMessage}`);
        } else {
          toast.success(response.data.message);
        }
      } else {
        toast.success(response.data.message);
      }
      
      setUploadFile(null);
      setPendingUploadFile(null);
      setShowColumnMapping(false);
      setColumnMapping({});
      loadDashboardData(); // This will reload fields and audience data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  const updateColumnMapping = (fileHeader, targetColumn) => {
    if (!fileHeader || !targetColumn) return;
    
    setColumnMapping(prev => {
      // Avoid unnecessary re-renders if value hasn't changed
      if (prev[fileHeader] === targetColumn) return prev;
      
      return {
        ...prev,
        [fileHeader]: targetColumn
      };
    });
  };

  const cancelColumnMapping = () => {
    setShowColumnMapping(false);
    setUploadFile(null);
    setPendingUploadFile(null);
    setColumnMapping({});
    setUploadFileHeaders([]);
  };

  // Column management functions for existing data
  const openColumnManager = () => {
    if (!audienceFields || audienceFields.length === 0) {
      toast.error('No columns available to manage. Please upload some data first.');
      return;
    }
    
    // Initialize merge mapping - each column maps to itself by default
    const initialMapping = {};
    audienceFields.forEach(field => {
      initialMapping[field] = field;
    });
    
    setColumnMergeMapping(initialMapping);
    setShowColumnManager(true);
  };

  const updateColumnMergeMapping = useCallback((sourceColumn, targetColumn) => {
    if (!sourceColumn || !targetColumn) return;
    
    setColumnMergeMapping(prev => {
      // Avoid unnecessary re-renders if value hasn't changed
      if (prev[sourceColumn] === targetColumn) return prev;
      
      return {
        ...prev,
        [sourceColumn]: targetColumn
      };
    });
  }, []);

  const processColumnMerge = async () => {
    // Find columns that need to be merged (where source !== target)
    const mergesToProcess = {};
    Object.entries(columnMergeMapping).forEach(([source, target]) => {
      if (source !== target) {
        mergesToProcess[source] = target;
      }
    });

    if (Object.keys(mergesToProcess).length === 0) {
      toast.info('No column merges selected. Please select columns to merge.');
      return;
    }

    setMergingInProgress(true);
    
    try {
      const response = await api.post('/api/audience/merge_columns', {
        column_merges: mergesToProcess
      });
      
      toast.success(`Successfully merged ${Object.keys(mergesToProcess).length} columns. ${response.data.message}`);
      
      // Close manager and refresh data
      setShowColumnManager(false);
      setColumnMergeMapping({});
      await loadDashboardData(); // This will reload fields and audience data
      
    } catch (error) {
      console.error('Column merge error:', error);
      toast.error(error.response?.data?.detail || 'Column merge failed');
    } finally {
      setMergingInProgress(false);
    }
  };

  const cancelColumnManager = () => {
    setShowColumnManager(false);
    setColumnMergeMapping({});
  };

  const handlePersonaPreview = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/personas/preview', {
        filters: personaForm.filters,
        logic: personaForm.logic
      });
      setPersonaPreview(response.data);
      toast.success(`Found ${response.data.match_count} matching audience members`);
    } catch (error) {
      toast.error('Failed to preview persona');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePersona = async () => {
    if (!personaForm.name) {
      toast.error('Please provide a persona name');
      return;
    }

    if (personaForm.filters.length === 0) {
      toast.error('Please add at least one filter');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/api/personas', personaForm);
      toast.success('Persona created successfully!');
      
      setPersonaForm({ 
        name: '', 
        description: '', 
        filters: [],
        logic: 'AND',
        is_template: true
      });
      setPersonaPreview({ match_count: 0, preview: [] });
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to create persona');
    } finally {
      setIsLoading(false);
    }
  };

  // Persona Wizard Functions - Ultimate stable version
  const updatePersonaWizardData = useCallback((field, value) => {
    setPersonaWizardData(prevData => {
      // Handle nested field updates (e.g., 'demographics.age_range')
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prevData,
          [parent]: {
            ...(prevData[parent] || {}),
            [child]: value
          }
        };
      } else {
        return {
          ...prevData,
          [field]: value
        };
      }
    });
  }, []); // Empty dependency array - this function never needs to change

  // Memoize personaWizardData object to prevent unnecessary re-renders
  const memoizedPersonaWizardData = useMemo(() => personaWizardData, [personaWizardData]);

  // Memoize wizard options to prevent re-renders
  const memoizedWizardOptions = useMemo(() => wizardOptions, [wizardOptions]);

  const handleWizardNext = async () => {
    if (personaWizardStep < 7) {
      setPersonaWizardStep(prev => prev + 1);
    } else if (personaWizardStep === 7) {
      // Create the persona
      try {
        setIsPersonaWizardLoading(true);
        
        // Prepare the persona data
        const personaData = {
          ...personaWizardData,
          generation_method: 'manual',
          is_complete: true
        };
        
        const response = await api.post('/api/personas/manual', personaData);
        
        if (response.data.success) {
          toast.success('✅ Persona created successfully!');
          
          // Reset wizard and close modal
          setPersonaWizardData({
            name: '',
            avatar_url: null,
            demographics: {},
            professional: {},
            goals: [],
            motivations: '',
            pain_points: '',
            behavior: {},
            content_channels: {},
            journey: {},
            value_prop: '',
            tags: [],
            key_benefits: [],
            success_metrics: '',
            messaging_tone: '',
            cta_preferences: ''
          });
          setPersonaWizardStep(1);
          setShowPersonaBuilder(false);
          
          // Reload personas data
          loadDashboardData();
        } else {
          toast.error('Failed to create persona');
        }
      } catch (error) {
        console.error('Error creating persona:', error);
        toast.error('Failed to create persona');
      } finally {
        setIsPersonaWizardLoading(false);
      }
    }
  };

  const handleWizardPrev = () => {
    if (personaWizardStep > 1) {
      setPersonaWizardStep(prev => prev - 1);
    }
  };

  const loadWizardOptions = useCallback(async () => {
    try {
      // Don't set loading state for the modal content, use separate flag
      const response = await api.get('/api/personas/wizard/options');
      if (response.data.success) {
        setWizardOptions(response.data.options);
        setWizardOptionsLoaded(true);
      }
    } catch (error) {
      console.error('Failed to load wizard options:', error);
      // Set default options if API fails
      setWizardOptions({
        demographics: {
          age_ranges: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
          genders: ['Male', 'Female', 'Non-binary', 'Prefer not to say'],
          income_levels: ['Low', 'Medium', 'High', 'Very High']
        },
        professional: {
          industries: ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing', 'Other'],
          seniority_levels: ['Entry Level', 'Mid Level', 'Senior Level', 'Executive', 'C-Level'],
          company_sizes: ['1-10', '11-50', '51-200', '201-1000', '1000+']
        },
        content_channels: {
          content_types: ['Blog Posts', 'Videos', 'Infographics', 'Podcasts', 'Webinars', 'Case Studies'],
          social_platforms: ['LinkedIn', 'Twitter', 'Facebook', 'Instagram', 'YouTube', 'TikTok'],
          communication_methods: ['Email', 'Phone', 'WhatsApp', 'SMS', 'In-person', 'Video calls']
        }
      });
      setWizardOptionsLoaded(true);
    }
  }, []);

  const handleSaveDraft = async () => {
    try {
      setIsPersonaWizardLoading(true);
      const response = await api.post('/api/personas/draft', {
        ...personaWizardData,
        is_draft: true,
        current_step: personaWizardStep
      });
      if (response.data.success) {
        toast.success('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsPersonaWizardLoading(false);
    }
  };

  const handleDeletePersona = async (personaId, personaName) => {
    if (window.confirm(`Are you sure you want to delete persona "${personaName}"?`)) {
      setIsLoading(true);
      try {
        await api.delete(`/api/personas/${personaId}`);
        toast.success('Persona deleted successfully!');
        loadDashboardData();
      } catch (error) {
        toast.error('Failed to delete persona');
      } finally {
        setIsLoading(false);
      }
    }
  };

// ===== MODERN PERSONABUILDERMODAL COMPONENT - CLEAN IMPLEMENTATION =====

const PersonaBuilderModal = ({ 
  showPersonaBuilder, 
  setShowPersonaBuilder,
  wizardOptions,
  wizardOptionsLoaded,
  setWizardOptionsLoaded,
  onPersonaCreated
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form state - clean and simple
  const [formData, setFormData] = useState({
    // Step 1: Basic Profile
    name: '',
    demographics: {
      age_range: '',
      gender: '',
      location: '',
      income_level: '',
      education_level: ''
    },
    
    // Step 2: Professional Details  
    professional: {
      job_title: '',
      industry: '',
      company_size: '',
      seniority_level: '',
      experience_years: '',
      work_environment: ''
    },
    
    // Step 3: Goals & Challenges
    goals: [],
    motivations: '',
    pain_points: '',
    success_metrics: '',
    
    // Step 4: Behavioral Traits
    behavior: {
      personality_type: '',
      decision_making: '',
      decision_style: '',
      decision_factors: '',
      communication_style: '',
      tech_comfort: '',
      learning_style: ''
    },
    
    // Step 5: Content & Channels
    content_channels: {
      content_types: [],
      social_platforms: [],
      communication_methods: [],
      content_frequency: '',
      consumption_habits: ''
    },
    
    // Step 6: Buying Journey
    journey: {
      awareness: '',
      consideration: '',
      decision: '',
      decision_timeline: '',
      budget_authority: '',
      objections: ''
    },
    
    // Step 7: Value Proposition
    value_prop: '',
    key_benefits: [],
    messaging_tone: '',
    cta_preferences: '',
    tags: []
  });
  
  // Update form data function - clean and efficient
  const updateFormData = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        newData[parent] = {
          ...newData[parent],
          [child]: value
        };
      } else {
        newData[field] = value;
      }
      
      return newData;
    });
  }, []);
  
  // Step navigation
  const nextStep = () => {
    if (currentStep < 7) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Create persona function
  const createPersona = async () => {
    try {
      setIsLoading(true);
      
      const personaData = {
        ...formData,
        generation_method: 'manual',
        is_complete: true,
        status: 'active'
      };
      
      const response = await api.post('/api/personas/manual', personaData);
      
      if (response.data.success) {
        toast.success(`✅ Persona "${formData.name}" created successfully!`);
        
        // Reset and close modal
        setFormData({
          name: '',
          demographics: {},
          professional: {},
          goals: [],
          motivations: '',
          pain_points: '',
          success_metrics: '',
          behavior: {},
          content_channels: { content_types: [], social_platforms: [], communication_methods: [] },
          journey: {},
          value_prop: '',
          key_benefits: [],
          messaging_tone: '',
          cta_preferences: '',
          tags: []
        });
        
        setCurrentStep(1);
        setShowPersonaBuilder(false);
        
        if (onPersonaCreated) {
          onPersonaCreated(response.data.persona);
        }
        
      } else {
        toast.error('❌ Failed to create persona: ' + response.data.error);
      }
      
    } catch (error) {
      console.error('Error creating persona:', error);
      toast.error('❌ Error creating persona: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle final step submission
  const handleFinalSubmit = () => {
    if (currentStep === 7) {
      createPersona();
    } else {
      nextStep();
    }
  };
  
  return (
    <Dialog open={showPersonaBuilder} onOpenChange={setShowPersonaBuilder}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-600" />
            Build Your Persona - Step {currentStep} of 7
          </DialogTitle>
          <DialogDescription>
            Create a detailed buyer persona through our guided wizard
          </DialogDescription>
        </DialogHeader>
        
        {/* Loading State */}
        {!wizardOptionsLoaded ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-500">Loading wizard options...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Step {currentStep} of 7</span>
                <span>{Math.round((currentStep / 7) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 7) * 100}%` }}
                />
              </div>
            </div>
            
            {/* Step Content */}
            <PersonaWizardStep 
              step={currentStep}
              formData={formData}
              updateFormData={updateFormData}
              wizardOptions={wizardOptions}
            />
            
            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                Previous
              </Button>
              
              <Button 
                onClick={handleFinalSubmit}
                disabled={isLoading || !formData.name.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : currentStep === 7 ? (
                  'Create Persona'
                ) : (
                  'Next Step'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===== WIZARD STEP COMPONENT =====

const PersonaWizardStep = ({ step, formData, updateFormData, wizardOptions }) => {
  const renderStep = () => {
    switch(step) {
      case 1:
        return <Step1BasicProfile formData={formData} updateFormData={updateFormData} wizardOptions={wizardOptions} />;
      case 2:
        return <Step2Professional formData={formData} updateFormData={updateFormData} wizardOptions={wizardOptions} />;
      case 3:
        return <Step3Goals formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <Step4Behavior formData={formData} updateFormData={updateFormData} wizardOptions={wizardOptions} />;
      case 5:
        return <Step5Content formData={formData} updateFormData={updateFormData} wizardOptions={wizardOptions} />;
      case 6:
        return <Step6Journey formData={formData} updateFormData={updateFormData} wizardOptions={wizardOptions} />;
      case 7:
        return <Step7ValueProp formData={formData} updateFormData={updateFormData} />;
      default:
        return null;
    }
  };
  
  return (
    <Card>
      {renderStep()}
    </Card>
  );
};

// ===== STEP 1: BASIC PROFILE =====

const Step1BasicProfile = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 1: Basic Profile</CardTitle>
      <CardDescription>Define the core identity and demographics</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Persona Name *</Label>
        <Input 
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value)}
          placeholder="e.g., Budget-Conscious Marketing Manager"
          className="mt-1"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Age Range</Label>
          <Select 
            value={formData.demographics.age_range || ''} 
            onValueChange={(value) => updateFormData('demographics.age_range', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select age range" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.demographics?.age_ranges?.map(range => (
                <SelectItem key={range} value={range}>{range}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Gender</Label>
          <Select 
            value={formData.demographics.gender || ''} 
            onValueChange={(value) => updateFormData('demographics.gender', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.demographics?.genders?.map(gender => (
                <SelectItem key={gender} value={gender}>{gender}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Location</Label>
          <Input 
            value={formData.demographics.location || ''}
            onChange={(e) => updateFormData('demographics.location', e.target.value)}
            placeholder="e.g., Mumbai, Maharashtra"
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Income Level</Label>
          <Select 
            value={formData.demographics.income_level || ''} 
            onValueChange={(value) => updateFormData('demographics.income_level', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select income level" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.demographics?.income_levels?.map(level => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Education Level</Label>
          <Select 
            value={formData.demographics.education_level || ''} 
            onValueChange={(value) => updateFormData('demographics.education_level', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select education level" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.demographics?.education_levels?.map(level => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </CardContent>
  </div>
);

// ===== STEP 2: PROFESSIONAL DETAILS =====

const Step2Professional = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 2: Professional Details</CardTitle>
      <CardDescription>Define their work environment and role</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Job Title / Role *</Label>
          <Input 
            value={formData.professional.job_title || ''}
            onChange={(e) => updateFormData('professional.job_title', e.target.value)}
            placeholder="e.g., Marketing Manager"
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Industry</Label>
          <Select 
            value={formData.professional.industry || ''} 
            onValueChange={(value) => updateFormData('professional.industry', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.professional?.industries?.map(industry => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Company Size</Label>
          <Select 
            value={formData.professional.company_size || ''} 
            onValueChange={(value) => updateFormData('professional.company_size', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.professional?.company_sizes?.map(size => (
                <SelectItem key={size} value={size}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Seniority Level</Label>
          <Select 
            value={formData.professional.seniority_level || ''} 
            onValueChange={(value) => updateFormData('professional.seniority_level', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select seniority" />
            </SelectTrigger>
            <SelectContent>
              {wizardOptions.professional?.seniority_levels?.map(level => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Years of Experience</Label>
          <Input 
            type="number"
            value={formData.professional.experience_years || ''}
            onChange={(e) => updateFormData('professional.experience_years', e.target.value)}
            placeholder="e.g., 5"
            className="mt-1"
          />
        </div>
        
        <div>
          <Label>Work Environment</Label>
          <Select 
            value={formData.professional.work_environment || ''} 
            onValueChange={(value) => updateFormData('professional.work_environment', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select work environment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="remote">Remote</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
              <SelectItem value="office">Office</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </CardContent>
  </div>
);

// ===== STEP 3: GOALS & CHALLENGES =====

const Step3Goals = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 3: Goals & Challenges</CardTitle>
      <CardDescription>Understand their objectives, motivations, and pain points</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Primary Goals *</Label>
        <div className="space-y-2 mt-2">
          {(formData.goals || []).map((goal, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input 
                value={goal}
                onChange={(e) => {
                  const newGoals = [...(formData.goals || [])];
                  newGoals[index] = e.target.value;
                  updateFormData('goals', newGoals);
                }}
                placeholder={`Goal ${index + 1}`}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const newGoals = (formData.goals || []).filter((_, i) => i !== index);
                  updateFormData('goals', newGoals);
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button 
            variant="outline" 
            onClick={() => {
              const newGoals = [...(formData.goals || []), ''];
              updateFormData('goals', newGoals);
            }}
          >
            Add Goal
          </Button>
        </div>
      </div>
      
      <div>
        <Label>Motivations</Label>
        <Textarea 
          value={formData.motivations || ''}
          onChange={(e) => updateFormData('motivations', e.target.value)}
          placeholder="What drives them? What motivates their decisions?"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Pain Points & Challenges</Label>
        <Textarea 
          value={formData.pain_points || ''}
          onChange={(e) => updateFormData('pain_points', e.target.value)}
          placeholder="What problems do they face? What frustrates them?"
          rows={3}
          className="mt-1"
        />
      </div>

      <div>
        <Label>Success Metrics</Label>
        <Input 
          value={formData.success_metrics || ''}
          onChange={(e) => updateFormData('success_metrics', e.target.value)}
          placeholder="How do they measure success?"
          className="mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 4: BEHAVIORAL TRAITS =====

const Step4Behavior = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 4: Behavioral Traits</CardTitle>
      <CardDescription>Define their personality, decision-making style, and preferences</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Personality Type</Label>
          <Select 
            value={formData.behavior?.personality_type || ''} 
            onValueChange={(value) => updateFormData('behavior.personality_type', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select personality" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="analytical">Analytical</SelectItem>
              <SelectItem value="creative">Creative</SelectItem>
              <SelectItem value="social">Social</SelectItem>
              <SelectItem value="practical">Practical</SelectItem>
              <SelectItem value="innovative">Innovative</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Decision Making Style</Label>
          <Select 
            value={formData.behavior?.decision_style || ''} 
            onValueChange={(value) => updateFormData('behavior.decision_style', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deliberate">Deliberate & Research-heavy</SelectItem>
              <SelectItem value="quick">Quick & Intuitive</SelectItem>
              <SelectItem value="consultative">Consultative</SelectItem>
              <SelectItem value="collaborative">Collaborative</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label>Communication Preferences</Label>
        <Select 
          value={formData.behavior?.communication_style || ''} 
          onValueChange={(value) => updateFormData('behavior.communication_style', value)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select communication style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="direct">Direct & Concise</SelectItem>
            <SelectItem value="detailed">Detailed & Thorough</SelectItem>
            <SelectItem value="casual">Casual & Friendly</SelectItem>
            <SelectItem value="formal">Formal & Professional</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Key Decision Factors</Label>
        <Textarea 
          value={formData.behavior?.decision_factors || ''}
          onChange={(e) => updateFormData('behavior.decision_factors', e.target.value)}
          placeholder="What factors influence their purchasing decisions? (e.g., price, quality, brand reputation)"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Preferred Learning Style</Label>
        <Select 
          value={formData.behavior?.learning_style || ''} 
          onValueChange={(value) => updateFormData('behavior.learning_style', value)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select learning style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="visual">Visual (infographics, videos)</SelectItem>
            <SelectItem value="auditory">Auditory (podcasts, webinars)</SelectItem>
            <SelectItem value="kinesthetic">Hands-on (demos, trials)</SelectItem>
            <SelectItem value="reading">Reading (articles, guides)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </CardContent>
  </div>
);

// ===== STEP 5: CONTENT & CHANNELS =====

const Step5Content = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 5: Content & Channels</CardTitle>
      <CardDescription>Identify their preferred content types and communication channels</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Preferred Content Types</Label>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {wizardOptions.content_channels?.content_types?.map(type => (
            <label key={type} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(formData.content_channels?.content_types || []).includes(type)}
                onChange={(e) => {
                  const current = formData.content_channels?.content_types || [];
                  const updated = e.target.checked 
                    ? [...current, type]
                    : current.filter(t => t !== type);
                  updateFormData('content_channels.content_types', updated);
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm">{type}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Social Media Platforms</Label>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {wizardOptions.content_channels?.social_platforms?.map(platform => (
            <label key={platform} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(formData.content_channels?.social_platforms || []).includes(platform)}
                onChange={(e) => {
                  const current = formData.content_channels?.social_platforms || [];
                  const updated = e.target.checked 
                    ? [...current, platform]
                    : current.filter(p => p !== platform);
                  updateFormData('content_channels.social_platforms', updated);
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm">{platform}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Communication Channels</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {wizardOptions.content_channels?.communication_methods?.map(method => (
            <label key={method} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={(formData.content_channels?.communication_methods || []).includes(method)}
                onChange={(e) => {
                  const current = formData.content_channels?.communication_methods || [];
                  const updated = e.target.checked 
                    ? [...current, method]
                    : current.filter(m => m !== method);
                  updateFormData('content_channels.communication_methods', updated);
                }}
                className="rounded border-gray-300"
              />
              <span className="text-sm">{method}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Content Consumption Habits</Label>
        <Textarea 
          value={formData.content_channels?.consumption_habits || ''}
          onChange={(e) => updateFormData('content_channels.consumption_habits', e.target.value)}
          placeholder="When and how do they consume content? (e.g., morning news, commute podcasts)"
          rows={3}
          className="mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 6: BUYING JOURNEY =====

const Step6Journey = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 6: Buying Journey</CardTitle>
      <CardDescription>Map their path from awareness to decision</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Awareness Stage</Label>
        <Textarea 
          value={formData.journey?.awareness || ''}
          onChange={(e) => updateFormData('journey.awareness', e.target.value)}
          placeholder="How do they first become aware of problems/solutions? What triggers their interest?"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Consideration Stage</Label>
        <Textarea 
          value={formData.journey?.consideration || ''}
          onChange={(e) => updateFormData('journey.consideration', e.target.value)}
          placeholder="How do they research and evaluate options? What information do they seek?"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Decision Stage</Label>
        <Textarea 
          value={formData.journey?.decision || ''}
          onChange={(e) => updateFormData('journey.decision', e.target.value)}
          placeholder="What drives their final purchase decision? Who else is involved?"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Average Decision Timeline</Label>
          <Select 
            value={formData.journey?.decision_timeline || ''} 
            onValueChange={(value) => updateFormData('journey.decision_timeline', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select timeline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Immediate (same day)</SelectItem>
              <SelectItem value="days">Days (1-7 days)</SelectItem>
              <SelectItem value="weeks">Weeks (1-4 weeks)</SelectItem>
              <SelectItem value="months">Months (1-3 months)</SelectItem>
              <SelectItem value="longer">Longer (3+ months)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Budget Authority</Label>
          <Select 
            value={formData.journey?.budget_authority || ''} 
            onValueChange={(value) => updateFormData('journey.budget_authority', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select authority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sole">Sole decision maker</SelectItem>
              <SelectItem value="recommender">Recommender</SelectItem>
              <SelectItem value="influencer">Influencer</SelectItem>
              <SelectItem value="none">No budget authority</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label>Common Objections</Label>
        <Textarea 
          value={formData.journey?.objections || ''}
          onChange={(e) => updateFormData('journey.objections', e.target.value)}
          placeholder="What concerns or objections do they typically have?"
          rows={3}
          className="mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 7: VALUE PROPOSITION =====

const Step7ValueProp = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 7: Value Proposition</CardTitle>
      <CardDescription>Define the key benefits and messaging for this persona</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Primary Value Proposition *</Label>
        <Textarea 
          value={formData.value_prop || ''}
          onChange={(e) => updateFormData('value_prop', e.target.value)}
          placeholder="What is the main value/benefit you provide to this persona?"
          rows={3}
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Key Benefits</Label>
        <div className="space-y-2 mt-2">
          {(formData.key_benefits || []).map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input 
                value={benefit}
                onChange={(e) => {
                  const newBenefits = [...(formData.key_benefits || [])];
                  newBenefits[index] = e.target.value;
                  updateFormData('key_benefits', newBenefits);
                }}
                placeholder={`Benefit ${index + 1}`}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const newBenefits = (formData.key_benefits || []).filter((_, i) => i !== index);
                  updateFormData('key_benefits', newBenefits);
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button 
            variant="outline" 
            onClick={() => {
              const newBenefits = [...(formData.key_benefits || []), ''];
              updateFormData('key_benefits', newBenefits);
            }}
          >
            Add Benefit
          </Button>
        </div>
      </div>
      
      <div>
        <Label>Messaging Tone</Label>
        <Select 
          value={formData.messaging_tone || ''} 
          onValueChange={(value) => updateFormData('messaging_tone', value)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select messaging tone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="professional">Professional & Authoritative</SelectItem>
            <SelectItem value="friendly">Friendly & Approachable</SelectItem>
            <SelectItem value="urgent">Urgent & Action-oriented</SelectItem>
            <SelectItem value="educational">Educational & Informative</SelectItem>
            <SelectItem value="emotional">Emotional & Inspiring</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Call-to-Action Preferences</Label>
        <Input 
          value={formData.cta_preferences || ''}
          onChange={(e) => updateFormData('cta_preferences', e.target.value)}
          placeholder="What type of CTAs resonate with them? (e.g., 'Learn More', 'Get Started', 'Request Demo')"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Tags</Label>
        <Input 
          value={(formData.tags || []).join(', ')}
          onChange={(e) => {
            const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
            updateFormData('tags', tags);
          }}
          placeholder="Add tags separated by commas (e.g., budget-conscious, tech-savvy, decision-maker)"
          className="mt-1"
        />
      </div>

      {/* Final Ready Message */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4 text-green-600" />
          <h4 className="font-medium text-green-900">Ready to Create Persona</h4>
        </div>
        <p className="text-sm text-green-700">
          Your persona is ready to be created. Click "Create Persona" to save it.
        </p>
      </div>
    </CardContent>
  </div>
);

  const togglePersonaSelection = (personaId) => {
    setSelectedPersonaIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personaId)) {
        newSet.delete(personaId);
      } else {
        newSet.add(personaId);
      }
      return newSet;
    });
  };

  const toggleSelectAllPersonas = () => {
    const allPersonas = [...personas.map(p => ({...p, type: 'manual'})), ...aiPersonas.map(p => ({...p, type: 'ai'}))];
    const sortedPersonas = allPersonas.sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
    const startIndex = (personasPage - 1) * personasPerPage;
    const paginatedPersonas = sortedPersonas.slice(startIndex, startIndex + personasPerPage);
    const currentPageIds = paginatedPersonas.map(p => p.id);
    
    if (currentPageIds.every(id => selectedPersonaIds.has(id))) {
      // Deselect all current page personas
      setSelectedPersonaIds(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all current page personas
      setSelectedPersonaIds(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const handleBulkDeletePersonas = async () => {
    if (selectedPersonaIds.size === 0) {
      toast.error('No personas selected');
      return;
    }

    setIsLoading(true);
    try {
      const persona_ids = Array.from(selectedPersonaIds);
      const response = await api.delete('/api/personas/bulk-delete', {
        data: { persona_ids }
      });

      if (response.data.success) {
        toast.success(`Successfully deleted ${response.data.deleted_count} persona(s)`);
        setSelectedPersonaIds(new Set());
        setShowBulkDeleteModal(false);
        loadDashboardData();
      } else {
        toast.error('Failed to delete personas');
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete personas');
    } finally {
      setIsLoading(false);
    }
  };

  const clearPersonaSelection = () => {
    setSelectedPersonaIds(new Set());
  };

  // Fix persona contacts function
  const handleFixPersonaContacts = async (persona) => {
    if (!persona || !persona.id) {
      toast.error('Invalid persona selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fixing contacts for persona:', persona.name, 'ID:', persona.id);
      
      const response = await api.post(`/api/personas/${persona.id}/fix-contacts`, {});

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        
        // Show detailed results
        const results = response.data;
        console.log('Fix results:', results);
        
        // Refresh the persona details to show updated contact count
        await handleViewPersonaDetails({
          ...persona,
          contact_count: results.new_contact_count || persona.contact_count,
          contact_ids: results.contact_ids_updated ? 'updated' : persona.contact_ids
        });
        
        // Also refresh the main personas list
        loadDashboardData();
        
      } else {
        toast.error(`❌ ${response.data.message || 'Failed to fix persona contacts'}`);
        console.warn('Fix failed:', response.data);
      }
    } catch (error) {
      console.error('Error fixing persona contacts:', error);
      toast.error('Failed to fix persona contacts');
    } finally {
      setIsLoading(false);
    }
  };

  // Persona optimization functions
  const handleOptimizePersona = async (persona, optimizationType) => {
    if (!persona || !persona.id) {
      toast.error('Invalid persona selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`Optimizing persona ${persona.name} for ${optimizationType}`);

      // For now, show what the optimization would do
      if (optimizationType === 'engagement') {
        toast.success(`🎯 Optimization Preview: This would modify "${persona.name}" to prioritize contacts with higher email open rates, social media engagement, and content interaction history.`);
      } else if (optimizationType === 'conversion') {
        toast.success(`💰 Optimization Preview: This would modify "${persona.name}" to focus on contacts with purchase history, high engagement scores, and conversion-ready behaviors.`);
      }

      // TODO: Implement actual optimization logic
      // This could involve:
      // 1. Analyzing persona's current filters/criteria
      // 2. Applying optimization-specific adjustments
      // 3. Re-running AI clustering with different weights
      // 4. Updating persona configuration

    } catch (error) {
      console.error('Error optimizing persona:', error);
      toast.error('Failed to optimize persona');
    } finally {
      setIsLoading(false);
    }
  };

  // Add contacts to persona function
  const handleAddContactsToPersona = async (persona) => {
    if (!persona || !persona.id) {
      toast.error('Invalid persona selected');
      return;
    }

    // For now, show a preview of what this would do
    toast.info(`📋 Add Contacts Preview: This would open a modal to browse your audience contacts and add selected contacts to "${persona.name}" persona.`);
    
    // TODO: Implement actual add contacts functionality
    // This could involve:
    // 1. Opening a modal with audience contacts list
    // 2. Providing search and filter capabilities
    // 3. Allowing bulk selection of contacts
    // 4. Adding selected contact IDs to persona
    // 5. Refreshing persona contact count and list
  };

  const handleEditPersona = async (persona) => {
    // Load persona data into the form for editing
    setEditingPersona(persona);
    setPersonaForm({
      name: persona.name,
      description: persona.description || '',
      filters: persona.filters || [],
      logic: persona.logic || 'AND',
      is_template: persona.is_template !== false
    });

    // Get fresh audience preview
    try {
      const response = await api.post('/api/personas/preview', {
        filters: persona.filters || [],
        logic: persona.logic || 'AND'
      });
      setPersonaPreview(response.data);
    } catch (error) {
      setPersonaPreview({ match_count: 0, preview: [] });
    }
  };

  const handleUpdatePersona = async () => {
    if (!personaForm.name) {
      toast.error('Please provide a persona name');
      return;
    }

    if (personaForm.filters.length === 0) {
      toast.error('Please add at least one filter');
      return;
    }

    setIsLoading(true);
    try {
      await api.put(`/api/personas/${editingPersona.id}`, personaForm);
      toast.success('Persona updated successfully!');
      
      // Reset form
      setEditingPersona(null);
      setPersonaForm({ 
        name: '', 
        description: '', 
        filters: [],
        logic: 'AND',
        is_template: true
      });
      setPersonaPreview({ match_count: 0, preview: [] });
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update persona');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEditPersona = () => {
    setEditingPersona(null);
    setPersonaForm({ 
      name: '', 
      description: '', 
      filters: [],
      logic: 'AND',
      is_template: true
    });
    setPersonaPreview({ match_count: 0, preview: [] });
  };

  const handleGetAIRecommendations = async () => {
    if (!campaignForm.persona_id || !campaignForm.objective) {
      toast.error('Please select a persona and objective first');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/api/campaigns/ai-recommendations', {
        persona_id: campaignForm.persona_id,
        objective: campaignForm.objective
      });
      
      setAiRecommendations(response.data);
      toast.success('AI recommendations generated!');
    } catch (error) {
      toast.error('Failed to get AI recommendations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name || !campaignForm.persona_id || !campaignForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/api/campaigns', campaignForm);
      toast.success('Campaign created and submitted for approval!');
      
      setCampaignForm({
        name: '', objective: '', persona_id: '', channels: [], message: '', image_url: ''
      });
      setAiRecommendations(null);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle media upload for WhatsApp campaigns
  const handleMediaUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setIsLoading(true);
    setUploadProgress(0);

    try {
      const response = await api.post('/api/campaigns/upload/media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      setUploadedMedia(response.data);
      setWhatsappForm(prev => ({
        ...prev,
        media_id: response.data.media_id
      }));
      
      toast.success(`Media uploaded: ${response.data.filename}`);
    } catch (error) {
      toast.error('Media upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  // Load scheduled campaigns
  const loadScheduledCampaigns = async () => {
    try {
      const response = await api.get('/api/campaigns/scheduled');
      setScheduledCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Failed to load scheduled campaigns:', error);
    }
  };

  // Load URL shortener settings
  const loadUrlShortenerSettings = async () => {
    try {
      const response = await api.get('/api/settings/url_shortener');
      setUrlShortenerSettings(response.data);
      
      // Update form with current settings
      setUrlShortenerForm({
        service: response.data.service || 'bitly',
        api_key: '', // Never pre-fill API keys for security
        domain: response.data.domain || '',
        auto_shorten: response.data.auto_shorten || false,
        analytics_enabled: response.data.analytics_enabled !== false
      });
    } catch (error) {
      console.error('Failed to load URL shortener settings:', error);
    }
  };

  // Load URL analytics
  const loadUrlAnalytics = async () => {
    try {
      const response = await api.get('/api/campaigns/url_analytics');
      setUrlAnalytics(response.data.analytics || []);
      setFilteredUrlAnalytics(response.data.analytics || []);
    } catch (error) {
      console.error('Failed to load URL analytics:', error);
    }
  };

  // Load filtered URL analytics with campaign and date range filters
  const loadFilteredUrlAnalytics = async (campaignId = 'all', startDate = '', endDate = '') => {
    try {
      const queryParams = new URLSearchParams();
      if (campaignId !== 'all') {
        queryParams.append('campaign_id', campaignId);
      }
      if (startDate) {
        queryParams.append('start_date', startDate);
      }
      if (endDate) {
        queryParams.append('end_date', endDate);
      }

      const url = `/api/campaigns/url_analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      
      // Always set the filtered results - when campaignId is 'all', this should contain all URLs
      setFilteredUrlAnalytics(response.data.analytics || []);
      
      console.log(`URL Analytics loaded for campaign: ${campaignId}, URLs count: ${response.data.analytics?.length || 0}`);
    } catch (error) {
      console.error('Failed to load filtered URL analytics:', error);
      
      // On error, if campaignId is 'all', fallback to the original unfiltered data
      if (campaignId === 'all') {
        setFilteredUrlAnalytics(urlAnalytics);
      } else {
        setFilteredUrlAnalytics([]);
      }
    }
  };

  // Handle URL analytics campaign filter change
  const handleUrlCampaignFilterChange = async (campaignId) => {
    setUrlSelectedCampaign(campaignId);
    
    // Load filtered URL analytics - when campaignId is 'all', this should fetch all URLs
    await loadFilteredUrlAnalytics(campaignId, urlDateRange.startDate, urlDateRange.endDate);
  };

  // Handle URL analytics date range change (for pending state only)
  const handleUrlDateRangeChange = (newDateRange) => {
    setUrlPendingDateRange(newDateRange);
  };

  // Submit URL analytics date range filter
  const submitUrlDateRangeFilter = async () => {
    setUrlDateRange(urlPendingDateRange);
    await loadFilteredUrlAnalytics(urlSelectedCampaign, urlPendingDateRange.startDate, urlPendingDateRange.endDate);
  };

  // Clear URL analytics date range filter
  const clearUrlDateRangeFilter = async () => {
    const clearedRange = { startDate: '', endDate: '' };
    setUrlDateRange(clearedRange);
    setUrlPendingDateRange(clearedRange);
    await loadFilteredUrlAnalytics(urlSelectedCampaign, '', '');
  };

  // Configure URL shortener
  const handleUrlShortenerConfig = async () => {
    if (!urlShortenerForm.service) {
      toast.error('Please select a URL shortening service');
      return;
    }

    if (urlShortenerForm.service !== 'custom' && !urlShortenerForm.api_key) {
      toast.error(`Please enter your ${urlShortenerForm.service} API key`);
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/api/settings/url_shortener', urlShortenerForm);
      
      toast.success(`${urlShortenerForm.service} URL shortener configured successfully!`);
      
      // Reload settings and hide edit form
      loadUrlShortenerSettings();
      setShowUrlShortenerEdit(false);
    } catch (error) {
      toast.error('URL shortener configuration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Test URL shortener connection
  const testUrlShortenerConnection = async () => {
    setUrlShortenerTesting(true);
    try {
      const response = await api.post('/api/settings/url_shortener/test');
      
      if (response.data.success) {
        toast.success(
          `✅ ${response.data.message}\n` +
          `Test URL: ${response.data.test_url}\n` +
          `Shortened: ${response.data.shortened_url}`
        );
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
    } catch (error) {
      toast.error('Connection test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUrlShortenerTesting(false);
    }
  };

  // Shorten URL for campaigns
  const shortenCampaignUrl = async (url, campaignType = 'whatsapp') => {
    if (!url || !urlShortenerSettings.configured || !urlShortenerSettings.auto_shorten) {
      return url; // Return original URL if not configured or auto-shorten disabled
    }

    try {
      const response = await api.post('/api/campaigns/shorten_url', {
        url: url,
        campaign_type: campaignType
      });
      
      return response.data.shortened_url || url;
    } catch (error) {
      console.error('URL shortening failed:', error);
      return url; // Return original URL on failure
    }
  };

  // URL detection and shortening utilities
  const URL_REGEX = /(https?:\/\/[^\s]+)/g;
  
  const detectURLsInText = (text) => {
    if (!text) return [];
    const matches = text.match(URL_REGEX);
    return matches || [];
  };

  const shortenURLsInText = async (text, campaignType = 'whatsapp') => {
    if (!text || !urlShortenerSettings.configured || !urlShortenerSettings.auto_shorten) {
      return text;
    }

    const urls = detectURLsInText(text);
    if (urls.length === 0) return text;

    let updatedText = text;
    
    for (const url of urls) {
      try {
        const shortenedUrl = await shortenCampaignUrl(url, campaignType);
        updatedText = updatedText.replace(url, shortenedUrl);
      } catch (error) {
        console.error(`Failed to shorten URL ${url}:`, error);
        // Keep original URL if shortening fails
      }
    }
    
    return updatedText;
  };

  const handleURLFieldShortening = async (url, campaignType = 'whatsapp') => {
    if (!url || !urlShortenerSettings.configured || !urlShortenerSettings.auto_shorten) {
      return url;
    }

    if (URL_REGEX.test(url)) {
      try {
        return await shortenCampaignUrl(url, campaignType);
      } catch (error) {
        console.error('Failed to shorten URL field:', error);
        return url;
      }
    }
    
    return url;
  };

  // Load scheduled campaigns on startup
  useEffect(() => {
    if (token && user) {
      loadScheduledCampaigns();
      loadUrlShortenerSettings();
      loadUrlAnalytics();
    }
  }, [token, user]);

  // Campaign Preview Components
  const WhatsAppPreview = ({ form, uploadedMedia }) => {
    const hasContent = form.name || form.message || form.url || uploadedMedia;
    
    return (
      <div className="bg-[#0b141a] p-4 rounded-lg">
        <div className="bg-[#202c33] p-3 rounded-t-lg border-b border-gray-600">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">
                {form.name || 'Campaign Preview'}
              </p>
              <p className="text-gray-400 text-xs">WhatsApp Business</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[#0b141a] p-4 min-h-[400px] max-h-[500px] overflow-y-auto">
          {hasContent ? (
            <div className="flex justify-end mb-4">
              <div className="max-w-[85%] bg-[#005c4b] rounded-lg p-3 relative">
                {/* Media Preview */}
                {uploadedMedia && (
                  <div className="mb-2 rounded-lg overflow-hidden">
                    {uploadedMedia.content_type?.startsWith('image/') && (
                      <div className="w-full h-32 bg-gray-700 rounded-lg flex items-center justify-center">
                        <Image className="h-8 w-8 text-gray-400" />
                        <span className="text-gray-400 text-xs ml-2">{uploadedMedia.filename}</span>
                      </div>
                    )}
                    {uploadedMedia.content_type?.startsWith('video/') && (
                      <div className="w-full h-32 bg-gray-700 rounded-lg flex items-center justify-center">
                        <Video className="h-8 w-8 text-gray-400" />
                        <span className="text-gray-400 text-xs ml-2">{uploadedMedia.filename}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Message Content */}
                {form.message && (
                  <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                    {form.message}
                  </p>
                )}
                
                {/* URL Preview */}
                {form.url && (
                  <div className="mt-3 p-2 bg-[#004a40] rounded border-l-2 border-green-400">
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3 text-green-400" />
                      <span className="text-green-400 text-xs truncate">{form.url}</span>
                    </div>
                  </div>
                )}
                
                {/* Message timestamp */}
                <div className="flex items-center justify-end gap-1 mt-2">
                  <span className="text-gray-300 text-xs">
                    {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                  <CheckCircle className="h-3 w-3 text-blue-400" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Start typing to see preview</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Character count indicator */}
        <div className="bg-[#202c33] p-2 rounded-b-lg border-t border-gray-600">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-xs">
              WhatsApp Message
            </span>
            <span className={`text-xs ${form.message?.length > 1000 ? 'text-red-400' : 'text-gray-400'}`}>
              {form.message?.length || 0}/1000
            </span>
          </div>
        </div>
      </div>
    );
  };

  const SMSPreview = ({ form }) => {
    const hasContent = form.name || form.message || form.url;
    
    return (
      <div className="bg-gradient-to-b from-gray-100 to-gray-200 p-4 rounded-lg">
        {/* SMS Header */}
        <div className="bg-white p-3 rounded-t-lg shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-sm text-gray-800">
                {form.name || 'SMS Campaign'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-500">
                {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </span>
            </div>
          </div>
        </div>
        
        {/* SMS Body */}
        <div className="bg-white p-4 min-h-[300px] max-h-[400px] overflow-y-auto">
          {hasContent ? (
            <div className="space-y-3">
              <div className="bg-blue-500 text-white p-3 rounded-lg rounded-tl-none shadow-sm">
                {form.message && (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {form.message}
                  </p>
                )}
                
                {form.url && (
                  <div className="mt-2 pt-2 border-t border-blue-400">
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3 text-blue-200" />
                      <span className="text-blue-200 text-xs truncate">{form.url}</span>
                    </div>
                  </div>
                )}
                
                {/* Delivery indicator */}
                <div className="flex justify-end mt-2">
                  <span className="text-blue-200 text-xs">Delivered</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Start typing to see SMS preview</p>
              </div>
            </div>
          )}
        </div>
        
        {/* SMS Footer with character count */}
        <div className="bg-white p-3 rounded-b-lg shadow-sm border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-xs">
                Language: {form.language === 'en' ? 'English' : form.language?.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${form.message?.length > 160 ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
                {form.message?.length || 0}/160
              </span>
              {form.message?.length > 160 && (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              {form.message?.length <= 160 && form.message?.length > 0 && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </div>
          </div>
          
          {form.message?.length > 160 && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
              <p className="text-red-600 text-xs">
                ⚠️ Message exceeds 160 characters. Additional charges may apply.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Enhanced WhatsApp form handlers with URL shortening
  const handleWhatsAppMessageChange = async (e) => {
    const newMessage = e.target.value;
    setWhatsappForm(prev => ({...prev, message: newMessage}));
    
    // Auto-shorten URLs in message if enabled
    if (urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && detectURLsInText(newMessage).length > 0) {
      setUrlShorteningInProgress(true);
      try {
        const shortenedMessage = await shortenURLsInText(newMessage, 'whatsapp');
        if (shortenedMessage !== newMessage) {
          setWhatsappForm(prev => ({...prev, message: shortenedMessage}));
          toast.success(`URLs automatically shortened in message!`);
        }
      } catch (error) {
        console.error('Failed to auto-shorten URLs in message:', error);
      } finally {
        setUrlShorteningInProgress(false);
      }
    }
  };

  const handleWhatsAppUrlChange = async (e) => {
    const newUrl = e.target.value;
    setWhatsappForm(prev => ({...prev, url: newUrl}));
    
    // Auto-shorten URL field if enabled
    if (urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && newUrl && URL_REGEX.test(newUrl)) {
      setUrlShorteningInProgress(true);
      try {
        const shortenedUrl = await handleURLFieldShortening(newUrl, 'whatsapp');
        if (shortenedUrl !== newUrl) {
          setWhatsappForm(prev => ({...prev, url: shortenedUrl}));
          toast.success(`URL automatically shortened!`);
        }
      } catch (error) {
        console.error('Failed to auto-shorten URL field:', error);
      } finally {
        setUrlShorteningInProgress(false);
      }
    }
  };

  // Enhanced SMS form handlers with URL shortening
  const handleSMSMessageChange = async (e) => {
    const newMessage = e.target.value;
    setSmsForm(prev => ({...prev, message: newMessage}));
    
    // Auto-shorten URLs in message if enabled
    if (urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && detectURLsInText(newMessage).length > 0) {
      setUrlShorteningInProgress(true);
      try {
        const shortenedMessage = await shortenURLsInText(newMessage, 'sms');
        if (shortenedMessage !== newMessage) {
          setSmsForm(prev => ({...prev, message: shortenedMessage}));
          toast.success(`URLs automatically shortened in SMS message!`);
        }
      } catch (error) {
        console.error('Failed to auto-shorten URLs in SMS message:', error);
      } finally {
        setUrlShorteningInProgress(false);
      }
    }
  };

  const handleSMSUrlChange = async (e) => {
    const newUrl = e.target.value;
    setSmsForm(prev => ({...prev, url: newUrl}));
    
    // Auto-shorten URL field if enabled
    if (urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && newUrl && URL_REGEX.test(newUrl)) {
      setUrlShorteningInProgress(true);
      try {
        const shortenedUrl = await handleURLFieldShortening(newUrl, 'sms');
        if (shortenedUrl !== newUrl) {
          setSmsForm(prev => ({...prev, url: shortenedUrl}));
          toast.success(`URL automatically shortened!`);
        }
      } catch (error) {
        console.error('Failed to auto-shorten SMS URL field:', error);
      } finally {
        setUrlShorteningInProgress(false);
      }
    }
  };

  const handleCreateWhatsappCampaign = async () => {
    if (!whatsappForm.name || !whatsappForm.message || !whatsappForm.persona_id) {
      toast.error('Please fill in all required fields (Name, Message, Persona)');
      return;
    }

    if (whatsappForm.message.length > 1000) {
      toast.error('WhatsApp message too long. Maximum 1000 characters.');
      return;
    }

    // Validate schedule date if not sending immediately
    if (!whatsappForm.send_immediately) {
      if (!whatsappForm.schedule_date) {
        toast.error('Please select a schedule date and time');
        return;
      }
      
      const scheduleDate = new Date(whatsappForm.schedule_date);
      const now = new Date();
      
      if (scheduleDate <= now) {
        toast.error('Schedule date must be in the future');
        return;
      }
    }

    setIsLoading(true);
    try {
      const campaignData = {
        ...whatsappForm,
        schedule_date: whatsappForm.send_immediately ? null : new Date(whatsappForm.schedule_date).toISOString()
      };

      const response = await api.post('/api/campaigns/whatsapp', campaignData);
      
      toast.success(
        `WhatsApp campaign ${whatsappForm.send_immediately ? 'created' : 'scheduled'} successfully!\n` +
        `Campaign ID: ${response.data.campaign_id}\n` +
        `Audience: ${response.data.audience_count} contacts\n` +
        `${response.data.scheduled_for ? `Scheduled for: ${new Date(response.data.scheduled_for).toLocaleString()}` : ''}\n` +
        `Estimated cost: $${response.data.estimated_cost?.toFixed(2)}`
      );
      
      // Reset form
      setWhatsappForm({
        name: '',
        message: '',
        url: '',
        persona_id: '',
        media_file: null,
        media_id: null,
        send_immediately: true,
        schedule_date: ''
      });
      setUploadedMedia(null);
      
      // Reload campaigns and scheduled campaigns
      loadDashboardData();
      loadScheduledCampaigns();
      
    } catch (error) {
      toast.error('WhatsApp campaign creation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Create SMS campaign
  const handleCreateSMSCampaign = async () => {
    if (!smsForm.name || !smsForm.message || !smsForm.persona_id) {
      toast.error('Please fill in all required fields (Name, Message, Persona)');
      return;
    }

    if (smsForm.message.length > 160) {
      toast.error('SMS message too long. Maximum 160 characters.');
      return;
    }

    // Validate schedule date if not sending immediately
    if (!smsForm.send_immediately) {
      if (!smsForm.schedule_date) {
        toast.error('Please select a schedule date and time');
        return;
      }
      
      const scheduleDate = new Date(smsForm.schedule_date);
      const now = new Date();
      
      if (scheduleDate <= now) {
        toast.error('Schedule date must be in the future');
        return;
      }
    }

    setIsLoading(true);
    try {
      const campaignData = {
        ...smsForm,
        schedule_date: smsForm.send_immediately ? null : new Date(smsForm.schedule_date).toISOString()
      };

      const response = await api.post('/api/campaigns/sms', campaignData);
      
      toast.success(
        `SMS campaign ${smsForm.send_immediately ? 'created' : 'scheduled'} successfully!\n` +
        `Campaign ID: ${response.data.campaign_id}\n` +
        `Valid recipients: ${response.data.audience_count} contacts\n` +
        `${response.data.scheduled_for ? `Scheduled for: ${new Date(response.data.scheduled_for).toLocaleString()}` : ''}\n` +
        `Characters used: ${response.data.character_count}/160\n` +
        `Estimated cost: $${response.data.estimated_cost?.toFixed(2)}`
      );
      
      // Reset form
      setSmsForm({
        name: '',
        message: '',
        url: '',
        persona_id: '',
        language: 'en',
        send_immediately: true,
        schedule_date: ''
      });
      
      // Reload campaigns and scheduled campaigns
      loadDashboardData();
      loadScheduledCampaigns();
      
    } catch (error) {
      toast.error('SMS campaign creation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignApproval = async (campaignId, status, comments = '') => {
    setIsLoading(true);
    try {
      await api.post('/api/campaigns/approve', {
        campaign_id: campaignId,
        status,
        comments
      });
      
      toast.success(`Campaign ${status} successfully!`);
      loadDashboardData();
    } catch (error) {
      toast.error(`Failed to ${status} campaign`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataCleanup = async () => {
    if (!window.confirm('Are you sure you want to run Enhanced Data Cleanup? This will:\n• Merge duplicate contacts (same email/phone)\n• Standardize phone numbers to international format\n• Normalize email addresses and names\n• Remove only records with no valid email, phone, or social profiles\n• Preserve all records with any valid contact information\n\nThis action cannot be undone.')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/api/audience/cleanup');
      
      // Show detailed results
      const stats = response.data.statistics;
      let message = response.data.message;
      
      if (stats) {
        message += `\n\nDetails:`;
        if (stats.duplicates_merged > 0) message += `\n• ${stats.duplicates_merged} duplicate records merged`;
        if (stats.data_normalized > 0) message += `\n• ${stats.data_normalized} data fields normalized`;
        if (stats.social_profiles_detected > 0) message += `\n• ${stats.social_profiles_detected} social profiles detected`;
        if (stats.records_removed > 0) message += `\n• ${stats.records_removed} invalid records removed`;
        message += `\n• ${stats.records_processed} total records processed`;
      }
      
      toast.success(message);
      loadDashboardData(); // Reload all data
      loadAudienceData(); // Reload audience data to see changes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enhanced data cleanup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/audience/template/${format}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = format === 'csv' ? 'audience_template.csv' : 'audience_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} template downloaded successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download template');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadStandardizedTemplate = async (format) => {
    try {
      setIsLoading(true);
      toast.info(`Preparing ${format.toUpperCase()} template download...`);
      
      const response = await api.get(`/api/audience/standardized_template/${format}`, {
        responseType: 'blob'
      });
      
      // Verify we got a valid response
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Create filename
      const filename = format === 'csv' ? 'standardized_audience_template.csv' : 'standardized_audience_template.xlsx';
      
      // Create blob with proper MIME type
      const mimeType = format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }
      
      console.log(`Template download - Size: ${blob.size} bytes, Type: ${mimeType}`);
      
      // Multiple download attempt methods for maximum compatibility
      try {
        // Method 1: Modern Blob URL approach
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM, trigger click, then remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Cleanup blob URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
        
        toast.success(
          `✅ Standardized ${format.toUpperCase()} template (33 fields) downloaded successfully!\n` +
          `📁 Check your Downloads folder\n` +
          `📋 Filename: ${filename}`,
          { duration: 10000 }
        );
        
      } catch (blobError) {
        console.error('Blob download failed:', blobError);
        
        // Method 2: Fallback using direct navigation (less secure but more compatible)
        try {
          const token = localStorage.getItem('token');
          const fallbackUrl = `${API_BASE_URL}/api/audience/standardized_template/${format}?token=${encodeURIComponent(token)}`;
          
          // Open in new window/tab
          const newWindow = window.open(fallbackUrl, '_blank');
          if (newWindow) {
            toast.success(
              `✅ ${format.toUpperCase()} template download initiated!\n` +
              `📁 Check your Downloads folder or new browser tab\n` +
              `📋 Filename: ${filename}`,
              { duration: 8000 }
            );
          } else {
            throw new Error('Popup blocked - please disable popup blocker');
          }
          
        } catch (fallbackError) {
          console.error('Fallback download failed:', fallbackError);
          throw new Error(`All download methods failed. ${fallbackError.message}`);
        }
      }
      
    } catch (error) {
      console.error('Download template error:', error);
      
      let errorMessage = `❌ Failed to download ${format.toUpperCase()} template: `;
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage += 'Authentication failed. Please refresh the page and try again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again in a few moments.';
      } else if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        errorMessage += 'Network connection issue. Please check your internet connection.';
      } else if (error.message.includes('popup blocked')) {
        errorMessage += 'Browser blocked the download. Please disable popup blocker and try again.';
      } else if (error.message.includes('empty')) {
        errorMessage += 'Template file is empty. Please contact support.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      errorMessage += '\n\n💡 Troubleshooting:\n• Check Downloads folder\n• Disable popup/download blockers\n• Try different browser\n• Contact IT if in corporate environment';
      
      toast.error(errorMessage, { duration: 15000 });
    } finally {
      setIsLoading(false);
    }
  };

  // Social Profile Enrichment Functions
  const loadEnrichmentSettings = async () => {
    try {
      const response = await api.get('/api/settings/social-enrichment');
      setEnrichmentSettings(response.data);
    } catch (error) {
      console.error('Failed to load enrichment settings:', error);
    }
  };

  const loadEnrichmentStatus = async () => {
    try {
      const response = await api.get('/api/enrichment/status');
      setEnrichmentStatus(response.data);
    } catch (error) {
      console.error('Failed to load enrichment status:', error);
    }
  };

  const loadIntegrationSettings = async () => {
    try {
      const response = await api.get('/api/settings/integrations');
      setIntegrationSettings(response.data);
    } catch (error) {
      console.error('Failed to load integration settings:', error);
    }
  };

  const handleUpdateEnrichmentSettings = async (newSettings) => {
    setIsLoading(true);
    try {
      await api.post('/api/settings/social-enrichment', newSettings);
      toast.success('Social enrichment settings updated successfully!');
      loadEnrichmentSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestApolloConnection = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/enrichment/test');
      if (response.data.success) {
        toast.success(`Apollo.io connection successful! Rate limit: ${response.data.rate_limit_remaining} remaining`);
      } else {
        toast.error(`Apollo.io test failed: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('Failed to test Apollo.io connection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnrichSocialProfiles = async (enrichAll = false) => {
    if (!enrichmentSettings.api_key_configured) {
      toast.error('Please configure Apollo.io API key in Settings first');
      return;
    }

    if (!window.confirm(`Are you sure you want to enrich social profiles for ${enrichAll ? 'all' : 'selected'} contacts? This will consume Apollo.io credits.`)) {
      return;
    }

    setEnrichmentInProgress(true);
    try {
      const response = await api.post('/api/enrichment/social-profiles', {
        enrich_all: enrichAll,
        max_contacts: 100
      });

      toast.success(`${response.data.message}. Estimated completion: ${new Date(response.data.estimated_completion).toLocaleTimeString()}`);
      
      // Poll for status updates
      const pollInterval = setInterval(async () => {
        await loadEnrichmentStatus();
      }, 5000);

      // Stop polling after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setEnrichmentInProgress(false);
        toast.info('Social profile enrichment completed. Check the audience data for results.');
      }, 600000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start social profile enrichment');
      setEnrichmentInProgress(false);
    }
  };

  const handleConfigureIntegration = async (integrationName, configData) => {
    setIsLoading(true);
    try {
      await api.post(`/api/settings/integrations/${integrationName}`, configData);
      toast.success(`${integrationName} integration configured successfully!`);
      loadIntegrationSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to configure ${integrationName}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Integration-specific handlers
  const handleSaveIntegrationConfig = async (integrationType) => {
    setIsLoading(true);
    try {
      const formData = integrationFormData[integrationType];
      await api.post(`/api/settings/integrations/${integrationType}`, formData);
      toast.success(`${integrationType.toUpperCase()} integration configured successfully!`);
      await loadIntegrationSettings();
    } catch (error) {
      console.error('Integration configuration error:', error);
      toast.error(error.response?.data?.detail || `Failed to configure ${integrationType} integration`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestIntegrationConnection = async (integrationType) => {
    setIsLoading(true);
    try {
      const formData = integrationFormData[integrationType];
      const response = await api.post(`/api/settings/integrations/${integrationType}/test`, formData);
      
      setIntegrationTestResults(prev => ({
        ...prev,
        [integrationType]: response.data
      }));
      
      if (response.data.success) {
        toast.success(`${integrationType.toUpperCase()} connection test successful!`);
      } else {
        toast.error(`${integrationType.toUpperCase()} connection test failed: ${response.data.message}`);
      }
      
      await loadIntegrationSettings();
    } catch (error) {
      console.error('Integration test error:', error);
      toast.error(error.response?.data?.detail || `Failed to test ${integrationType} connection`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIntegrationFormChange = (integrationType, field, value, subField = null) => {
    setIntegrationFormData(prev => {
      const newData = { ...prev };
      if (subField) {
        if (!newData[integrationType][field]) {
          newData[integrationType][field] = {};
        }
        newData[integrationType][field][subField] = value;
      } else {
        newData[integrationType][field] = value;
      }
      return newData;
    });
  };

  // Load existing integration configurations
  const loadIntegrationConfigs = async () => {
    try {
      const integrationTypes = ['smtp', 'sms', 'whatsapp', 'seo_analytics', 'advertisements'];
      
      for (const integrationType of integrationTypes) {
        try {
          const response = await api.get(`/api/settings/integrations/${integrationType}`);
          if (response.data.configured && response.data.settings) {
            setIntegrationFormData(prev => ({
              ...prev,
              [integrationType]: { ...prev[integrationType], ...response.data.settings }
            }));
          }
        } catch (error) {
          // Integration not configured yet, skip
          console.log(`${integrationType} integration not configured yet`);
        }
      }
    } catch (error) {
      console.error('Failed to load integration configurations:', error);
    }
  };

  // Load integration status summary for dashboard
  const loadIntegrationStatusSummary = async () => {
    setIntegrationStatusLoading(true);
    try {
      // Add cache-busting parameter to ensure fresh data
      const response = await api.get(`/api/integrations/status-summary?t=${Date.now()}`);
      console.log('🔧 Integration Status Data Loaded:', response.data); // Debug log
      setIntegrationStatusData(response.data);
      setLastStatusUpdate(new Date().toISOString());
    } catch (error) {
      console.error('Failed to load integration status:', error);
      toast.error('Failed to load integration status');
    } finally {
      setIntegrationStatusLoading(false);
    }
  };

  // Refresh integration status
  const refreshIntegrationStatus = async () => {
    await loadIntegrationStatusSummary();
    toast.success('Integration status refreshed');
  };

  // Navigate to specific integration settings
  const navigateToIntegrationSettings = (category, serviceKey = null) => {
    // Set active tab to settings
    setActiveTab('settings');
    
    // Set settings tab to integrations
    setActiveSettingsTab('api-integrations');
    
    // Map categories to integration tab values
    const categoryToTabMap = {
      'crm': 'others',           // CRM tools are in Others tab
      'communication': 'smtp',    // Communication starts with SMTP
      'seo_tools': 'seo-analytics', // SEO tools are in SEO/Analytics tab
      'analytics': 'seo-analytics', // Analytics tools are also in SEO/Analytics tab
      'advertisements': 'advertisements', // Advertisements have their own tab
      'utilities': 'others'       // Utilities are in Others tab
    };
    
    // Set the appropriate integrations sub-tab
    const targetTab = categoryToTabMap[category] || 'smtp';
    setActiveIntegrationsTab(targetTab);
    
    // Optional: Show toast message
    const categoryDisplayNames = {
      'crm': 'CRM & Customer Management',
      'communication': 'Communication Services',
      'seo_tools': 'SEO & Research Tools',
      'analytics': 'Analytics & Tracking',
      'advertisements': 'Advertisement Platforms',
      'utilities': 'Utility Services'
    };
    
    const displayName = categoryDisplayNames[category] || 'Integration Settings';
    toast.success(`Navigating to ${displayName} settings`);
  };

  // Helper functions for persona filtering
  const addPersonaFilter = () => {
    const newFilter = {
      field: audienceFields.length > 0 ? audienceFields[0] : '',
      operator: 'equals',
      value: '',
      value2: ''
    };
    setPersonaForm({
      ...personaForm,
      filters: [...personaForm.filters, newFilter]
    });
  };

  const updatePersonaFilter = (index, key, value) => {
    const updatedFilters = [...personaForm.filters];
    updatedFilters[index] = { ...updatedFilters[index], [key]: value };
    
    // Reset value if field changes
    if (key === 'field') {
      updatedFilters[index].value = '';
      updatedFilters[index].value2 = '';
    }
    
    // Use functional update to avoid race conditions
    setPersonaForm(prevForm => ({
      ...prevForm,
      filters: updatedFilters
    }));
    
    // Force a small re-render delay to allow portal cleanup
    setTimeout(() => {
      // This ensures DOM is properly updated after state change
    }, 10);
  };

  const removePersonaFilter = (index) => {
    const updatedFilters = personaForm.filters.filter((_, i) => i !== index);
    setPersonaForm({
      ...personaForm,
      filters: updatedFilters
    });
  };

  // AI Personas Functions
  const analyzeAudienceForAI = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/personas/analyze-audience');
      if (response.data.success) {
        setAudienceAnalysis(response.data);
        toast.success('Audience analysis completed!');
      } else {
        toast.error(response.data.message || 'Analysis failed');
      }
    } catch (error) {
      console.error('Audience analysis error:', error);
      toast.error('Failed to analyze audience data');
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIPersonas = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/personas/generate', {
        min_cluster_size: aiPersonaConfig.minClusterSize,
        max_personas: aiPersonaConfig.maxPersonas,
        use_advanced_clustering: aiPersonaConfig.useAdvanced,
        clustering_method: aiPersonaConfig.clusteringMethod,
        include_insights: true
      });

      if (response.data.success) {
        setAiPersonas(response.data.personas);
        toast.success(`Generated ${response.data.personas.length} AI personas successfully!`);
        
        // Show generation info if available
        if (response.data.generation_info) {
          console.log('Generation Info:', response.data.generation_info);
        }
      } else {
        toast.error(response.data.message || 'Failed to generate personas');
      }
    } catch (error) {
      console.error('AI persona generation error:', error);
      toast.error('Failed to generate AI personas');
    } finally {
      setIsLoading(false);
    }
  };

  const viewAIPersonaDetails = async (persona) => {
    try {
      const response = await api.get(`/api/personas/${persona.id}`);
      if (response.data.success) {
        // Show detailed persona information
        toast.success(`Viewing details for "${persona.name}"`);
        console.log('Persona Details:', response.data.persona);
      }
    } catch (error) {
      console.error('Error viewing persona details:', error);
      toast.error('Failed to load persona details');
    }
  };

  const deleteAIPersona = async (personaId, personaName) => {
    if (window.confirm(`Are you sure you want to delete AI persona "${personaName}"?`)) {
      try {
        const response = await api.delete(`/api/personas/${personaId}`);
        if (response.data.success) {
          setAiPersonas(aiPersonas.filter(p => p.id !== personaId));
          toast.success('AI persona deleted successfully!');
        } else {
          toast.error('Failed to delete persona');
        }
      } catch (error) {
        console.error('Delete persona error:', error);
        toast.error('Failed to delete AI persona');
      }
    }
  };

  const loadAIPersonas = async () => {
    try {
      const response = await api.get('/api/personas');
      if (response.data.success) {
        // Filter AI personas (those with generation_method containing 'clustering')
        const aiGeneratedPersonas = response.data.personas.filter(p => 
          p.generation_method && p.generation_method.includes('clustering')
        );
        setAiPersonas(aiGeneratedPersonas);
      }
    } catch (error) {
      console.error('Error loading AI personas:', error);
    }
  };

  // Comprehensive function to load all personas (both AI and manual)
  const loadPersonas = async () => {
    try {
      const response = await api.get('/api/personas');
      if (response.data.success) {
        const allPersonas = response.data.personas;
        
        // Separate AI and manual personas
        const aiGeneratedPersonas = allPersonas.filter(p => 
          p.generation_method && (p.generation_method.includes('clustering') || p.type === 'ai')
        );
        
        const manualPersonas = allPersonas.filter(p => 
          p.type === 'manual' || p.generation_method === 'wizard'
        );
        
        // Set state for both types
        setAiPersonas(aiGeneratedPersonas);
        setPersonas(manualPersonas);
        
        console.log(`Loaded ${aiGeneratedPersonas.length} AI personas and ${manualPersonas.length} manual personas`);
      }
    } catch (error) {
      console.error('Error loading personas:', error);
      toast.error('Failed to load personas');
    }
  };

  // Enhanced view persona details function
  const handleViewPersonaDetails = async (persona) => {
    // Prevent multiple simultaneous contact loading operations
    if (isLoading) {
      console.warn('⚠️ Contact loading already in progress, skipping...');
      return;
    }
    
    try {
      setIsLoading(true);
      setSelectedPersonaDetails(persona);
      setPersonaContacts([]);
      setShowPersonaDetailsModal(true);

      console.log('=== PERSONA CONTACT LOADING DEBUG ===');
      console.log('Loading contacts for persona:', persona.name, 'Type:', persona.type);

      // Load associated contacts for this persona
      if (persona.type === 'ai' && persona.contact_ids && persona.contact_ids.length > 0) {
        console.log('AI Persona - Contact IDs:', persona.contact_ids.length);
        console.log('Contact IDs to load:', persona.contact_ids);
        
        // For AI personas, try multiple approaches to find contacts
        // Approach 1: Try to get contacts by searching in enhanced contacts
        let foundContacts = [];
        let loadingErrors = [];
        
        try {
          console.log(`Starting to load ${persona.contact_ids.length} contacts...`);
          
          // Search for contacts using the contact IDs with detailed error tracking
          for (let i = 0; i < persona.contact_ids.length; i++) {
            const contactId = persona.contact_ids[i];
            console.log(`\n--- Loading Contact ${i + 1}/${persona.contact_ids.length} ---`);
            console.log(`Contact ID: ${contactId}`);
            
            try {
              const searchResponse = await api.get(`/api/audience/enhanced/contacts?search=${contactId}&limit=1`);
              console.log(`API Response Status: ${searchResponse.status}`);
              console.log(`API Response Success: ${searchResponse.data.success}`);
              
              if (searchResponse.data.success && searchResponse.data.contacts && searchResponse.data.contacts.length > 0) {
                const contact = searchResponse.data.contacts[0];
                foundContacts.push(contact);
                console.log(`✅ SUCCESS: Found contact "${contact.full_name || contact.email_addresses?.[0] || 'Unknown'}" (ID: ${contact.id})`);
              } else {
                const error = `No contacts returned in response`;
                loadingErrors.push({ contactId, index: i + 1, error });
                console.log(`❌ FAILED: ${error}`);
                console.log(`Response data:`, searchResponse.data);
              }
            } catch (contactSearchError) {
              const error = `API Error: ${contactSearchError.message}`;
              loadingErrors.push({ contactId, index: i + 1, error });
              console.error(`❌ API ERROR for contact ${contactId}:`, contactSearchError);
            }
          }
          
          console.log('\n=== CONTACT LOADING SUMMARY ===');
          console.log(`Successfully loaded: ${foundContacts.length}/${persona.contact_ids.length} contacts`);
          console.log(`Loading errors: ${loadingErrors.length}`);
          
          if (loadingErrors.length > 0) {
            console.log('\n=== FAILED CONTACTS ===');
            loadingErrors.forEach(({ contactId, index, error }) => {
              console.log(`Contact ${index}: ${contactId} - ${error}`);
            });
          }
          
          if (foundContacts.length > 0) {
            console.log('\n=== LOADED CONTACTS ===');
            foundContacts.forEach((contact, index) => {
              console.log(`${index + 1}. ${contact.full_name || contact.email_addresses?.[0] || 'Unknown'} (ID: ${contact.id})`);
            });
            
            console.log('✅ Setting persona contacts state...');
            
            // Deduplicate contacts by ID to prevent duplicates in UI
            const uniqueContacts = [];
            const seenIds = new Set();
            
            for (const contact of foundContacts) {
              if (!seenIds.has(contact.id)) {
                seenIds.add(contact.id);
                uniqueContacts.push(contact);
              } else {
                console.warn(`⚠️ Duplicate contact detected and removed: ${contact.full_name || contact.id}`);
              }
            }
            
            console.log(`Final contacts after deduplication: ${uniqueContacts.length}/${foundContacts.length}`);
            setPersonaContacts(uniqueContacts);
            
            // Show detailed user feedback about contact loading
            if (foundContacts.length === persona.contact_ids.length) {
              toast.success(`✅ All ${foundContacts.length} contacts loaded successfully`);
            } else {
              toast.warning(`⚠️ Loaded ${foundContacts.length} out of ${persona.contact_ids.length} expected contacts. ${persona.contact_ids.length - foundContacts.length} contacts failed to load.`);
              
              // Show specific error details
              if (loadingErrors.length > 0) {
                const errorDetails = loadingErrors.map(e => `Contact ${e.index}: ${e.error}`).join('\n');
                console.warn('Contact loading errors details:', errorDetails);
              }
            }
          } else {
            console.warn('No contacts found via search, trying alternative approach...');
            
            // Approach 2: Try the original POST method
            const contactsResponse = await api.post('/api/audience/enhanced/contacts', {
              filters: [{ field: 'id', operator: 'in', value: persona.contact_ids.slice(0, 50) }],
              limit: 100
            });
            
            if (contactsResponse.data.success && contactsResponse.data.contacts) {
              console.log('Found contacts via POST filter:', contactsResponse.data.contacts.length);
              setPersonaContacts(contactsResponse.data.contacts);
            } else {
              console.warn('No contacts found via POST filter either');
              // Show helpful error message to user
              toast.error(`This persona references ${persona.contact_ids.length} contacts, but they're not available in the enhanced contacts collection. This may be a data synchronization issue.`);
            }
          }
        } catch (contactError) {
          console.error('Error loading AI persona contacts:', contactError);
          toast.error('Failed to load contacts for this AI persona');
        }
        
      } else if (persona.type === 'manual' && persona.filters) {
        console.log('Manual Persona - Filters:', persona.filters.length);
        
        // For manual personas, apply the filters to get matching contacts
        const contactsResponse = await api.post('/api/personas/preview', {
          filters: persona.filters,
          logic: persona.logic || 'AND'
        });
        
        if (contactsResponse.data.success) {
          const contacts = contactsResponse.data.preview || contactsResponse.data.sample_contacts || [];
          console.log('Manual persona contacts found:', contacts.length);
          setPersonaContacts(contacts);
        } else {
          console.warn('Failed to load manual persona contacts:', contactsResponse.data);
          toast.error('Failed to load contacts for this manual persona');
        }
      } else {
        console.warn('Persona has no contact_ids or filters:', {
          type: persona.type,
          has_contact_ids: !!(persona.contact_ids && persona.contact_ids.length > 0),
          has_filters: !!(persona.filters && persona.filters.length > 0)
        });
        
        // Show informative message about why no contacts are shown
        if (persona.type === 'ai') {
          toast.info(`This AI persona doesn't have associated contact IDs. Contact count: ${persona.contact_count || 0}`);
        } else {
          toast.info(`This manual persona doesn't have defined filters to match contacts.`);
        }
      }
    } catch (error) {
      console.error('Error loading persona details:', error);
      toast.error('Failed to load persona details');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading screen while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-semibold text-white mb-2">Loading...</h2>
          <p className="text-gray-300">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Login Form (show login if no token)
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-md border-white/20">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-6">
              <img 
                src="/logo-white.png" 
                alt="OmniMarket360" 
                className="h-24 w-auto"
              />
            </div>
            <CardDescription className="text-gray-300">
              AI-Powered Marketing Automation Platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  placeholder="Enter your password"
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            
            <div className="mt-4 p-3 bg-blue-900/30 rounded border border-blue-500/30">
              <p className="text-xs text-blue-200">Demo Admin Credentials:</p>
              <p className="text-xs text-blue-300">Email: admin@omnimarket360.com</p>
              <p className="text-xs text-blue-300">Password: admin123</p>
            </div>
          </CardContent>
        </Card>
        <Toaster />
      </div>
    );
  }

  // Helper function for debounce
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Main Application
  return (
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="/logo-black.png" 
                alt="OmniMarket360" 
                className="h-14 w-auto mr-4"
              />
              <Badge variant="secondary" className="ml-2">{user?.role?.replace('_', ' ') || 'User'}</Badge>
            </div>
            
            <div className="flex items-center space-x-4">
              {user?.role === 'marketing_manager' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setActiveTab('notifications')}
                  className="relative"
                >
                  <Bell className="h-4 w-4" />
                  {notifications.length > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 text-xs">
                      {notifications.length}
                    </Badge>
                  )}
                </Button>
              )}
              
              <span className="text-sm text-gray-700">Welcome, {user?.name}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className={`grid w-full ${
            user?.role === 'administrator' 
              ? 'grid-cols-8' // Dashboard, Audience, Personas, Campaigns, SEO/AEO, Users, Settings + potential Approvals
              : user?.role === 'marketing_manager' 
                ? 'grid-cols-6' // Dashboard, Audience, Personas, Campaigns, SEO/AEO, Approvals  
                : 'grid-cols-5'  // Dashboard, Audience, Personas, Campaigns, SEO/AEO
          } gap-1`}>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="audience" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Audience
            </TabsTrigger>
            <TabsTrigger value="personas" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Personas
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Campaigns
            </TabsTrigger>
            <TabsTrigger value="seo-aeo" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              SEO / AEO
            </TabsTrigger>
            {user?.role === 'marketing_manager' && (
              <TabsTrigger value="notifications" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Approvals
              </TabsTrigger>
            )}
            {user?.role === 'administrator' && (
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
            )}
            {user?.role === 'administrator' && (
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            {/* Performance Marketing Overview Cards */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-700 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-rose-300 to-pink-400 rounded-xl flex items-center justify-center shadow-lg">
                  <Target className="h-5 w-5 text-white" />
                </div>
                Performance Marketing Overview
              </h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card className="bg-blue-50 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Contacts</CardTitle>
                    <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800 mb-1">
                      {analytics?.audience_metrics?.total_contacts?.toLocaleString() || 0}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Total audience pool</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-emerald-50 border border-emerald-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">New Contacts (30d)</CardTitle>
                    <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <UserPlus className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800 mb-1">
                      {analytics?.audience_metrics?.new_contacts_30d?.toLocaleString() || 0}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Recent audience growth</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-rose-50 border border-rose-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">High-Value Contacts</CardTitle>
                    <div className="w-12 h-12 bg-rose-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <UserCheck className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800 mb-1">
                      {analytics?.audience_metrics?.high_value_contacts?.toLocaleString() || 0}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Rating &gt; 70</p>
                  </CardContent>
                </Card>

                <Card className="bg-amber-50 border border-amber-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Campaign-Ready Personas</CardTitle>
                    <div className="w-12 h-12 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Target className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-800 mb-1">
                      {analytics?.audience_metrics?.campaign_ready_personas?.toLocaleString() || 0}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Segmented audiences</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Performance Metrics Cards */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-700 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-300 to-teal-400 rounded-xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                Performance vs Industry Benchmarks
              </h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card className="bg-emerald-50 border border-emerald-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-sm font-semibold text-slate-700">Current ROAS</CardTitle>
                    <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-emerald-700 mb-2">
                      {analytics?.performance_metrics?.current_roas || 0}x
                    </div>
                    <p className="text-xs text-slate-600 mb-3">
                      Industry avg: {analytics?.performance_metrics?.industry_avg_roas || 4.2}x
                    </p>
                    <div className="bg-emerald-100 border border-emerald-300 rounded-full px-3 py-1.5 inline-block">
                      <p className="text-xs font-semibold text-emerald-700">
                        {(analytics?.performance_metrics?.roas_improvement || 0) > 0 ? '+' : ''}
                        {analytics?.performance_metrics?.roas_improvement || 0}% vs industry
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-50 border border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-sm font-semibold text-slate-700">Current CAC</CardTitle>
                    <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-blue-700 mb-2">
                      ${analytics?.performance_metrics?.current_cac || 0}
                    </div>
                    <p className="text-xs text-slate-600 mb-3">
                      Industry avg: ${analytics?.performance_metrics?.industry_avg_cac || 85}
                    </p>
                    <div className="bg-blue-100 border border-blue-300 rounded-full px-3 py-1.5 inline-block">
                      <p className="text-xs font-semibold text-blue-700">
                        {(analytics?.performance_metrics?.cac_reduction || 0) > 0 ? '-' : ''}
                        {analytics?.performance_metrics?.cac_reduction || 0}% reduction
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-rose-50 border border-rose-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-sm font-semibold text-slate-700">Conversion Rate</CardTitle>
                    <div className="w-12 h-12 bg-rose-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Award className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-rose-700 mb-2">
                      {analytics?.summary?.conversion_rate || 0}%
                    </div>
                    <p className="text-xs text-slate-600 mb-3">Industry avg: 2.1%</p>
                    <div className="bg-rose-100 border border-rose-300 rounded-full px-3 py-1.5 inline-block">
                      <p className="text-xs font-semibold text-rose-700">Cross-channel optimization</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-amber-50 border border-amber-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                    <CardTitle className="text-sm font-semibold text-slate-700">Monthly Savings</CardTitle>
                    <div className="w-12 h-12 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Zap className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-amber-700 mb-2">
                      ${analytics?.savings_calculator?.total_monthly_savings?.toLocaleString() || 0}
                    </div>
                    <p className="text-xs text-slate-600 mb-3">
                      ${analytics?.savings_calculator?.total_yearly_savings?.toLocaleString() || 0}/year
                    </p>
                    <div className="bg-amber-100 border border-amber-300 rounded-full px-3 py-1.5 inline-block">
                      <p className="text-xs font-semibold text-amber-700">vs traditional methods</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Traditional Campaign Metrics */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-700 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-300 to-purple-400 rounded-xl flex items-center justify-center shadow-lg">
                  <Send className="h-5 w-5 text-white" />
                </div>
                Campaign Performance
              </h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Campaigns</CardTitle>
                    <div className="w-12 h-12 bg-gradient-to-br from-slate-400 to-gray-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Send className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-700 mb-3">
                      {analytics?.summary?.total_campaigns?.toLocaleString() || 0}
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-slate-400 to-gray-500 h-2 rounded-full transition-all duration-500" style={{width: '75%'}}></div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Messages Sent</CardTitle>
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Target className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-indigo-700 mb-3">
                      {analytics?.summary?.total_sent?.toLocaleString() || 0}
                    </div>
                    <div className="w-full bg-indigo-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-indigo-400 to-blue-500 h-2 rounded-full transition-all duration-500" style={{width: '90%'}}></div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Open Rate</CardTitle>
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-teal-700 mb-3">
                      {analytics?.summary?.open_rate || 0}%
                    </div>
                    <div className="w-full bg-teal-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-teal-400 to-emerald-500 h-2 rounded-full transition-all duration-500" style={{width: `${Math.min(analytics?.summary?.open_rate || 0, 100)}%`}}></div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Converted</CardTitle>
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-rose-700 mb-3">
                      {analytics?.summary?.total_converted?.toLocaleString() || 0}
                    </div>
                    <div className="w-full bg-rose-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-rose-400 to-pink-500 h-2 rounded-full transition-all duration-500" style={{width: '65%'}}></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Detailed Campaign Performance with Filtering */}
            <div className="mb-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Campaign Performance Details</CardTitle>
                      <CardDescription>
                        Filter and analyze performance metrics for individual campaigns
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Campaign Filter Dropdown */}
                      <Select 
                        key={`campaign-filter-${selectedCampaign || 'empty'}`}
                        value={campaigns.find(c => c.id === selectedCampaign) ? selectedCampaign : 'all'} 
                        onValueChange={handleCampaignFilterChange}
                        onOpenChange={(open) => {
                          // Force cleanup when dropdown closes
                          if (!open) {
                            setTimeout(() => {
                              // Small delay to ensure portal cleanup
                            }, 100);
                          }
                        }}
                      >
                        <SelectTrigger className="w-48 relative z-10">
                          <SelectValue placeholder="Select campaign" />
                        </SelectTrigger>
                        <SelectContent 
                          className="z-[100] max-h-[200px] overflow-y-auto"
                          position="popper"
                          sideOffset={5}
                        >
                          <SelectItem value="all">
                            <div className="flex items-center gap-2">
                              <Target className="h-4 w-4" />
                              All Campaigns
                            </div>
                          </SelectItem>
                          {campaigns.map((campaign) => (
                            <SelectItem key={`campaign-filter-${campaign.id}`} value={campaign.id}>
                              <div className="flex items-center gap-2">
                                {campaign.objective === 'whatsapp' && <MessageCircle className="h-4 w-4" />}
                                {campaign.objective === 'sms' && <Smartphone className="h-4 w-4" />}
                                {campaign.objective === 'email' && <Mail className="h-4 w-4" />}
                                {campaign.objective === 'facebook_ads' && <Facebook className="h-4 w-4" />}
                                {!['whatsapp', 'sms', 'email', 'facebook_ads'].includes(campaign.objective) && <Target className="h-4 w-4" />}
                                <span className="truncate max-w-32">{campaign.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                          {campaigns.length === 0 && (
                            <SelectItem value="no_campaigns" disabled>
                              No campaigns available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>

                      {/* Date Range Filter */}
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={pendingDateRange.startDate}
                          onChange={(e) => handleDateRangeChange({
                            ...pendingDateRange,
                            startDate: e.target.value
                          })}
                          className="w-36"
                          title="Start Date"
                        />
                        <span className="text-gray-400">to</span>
                        <Input
                          type="date"
                          value={pendingDateRange.endDate}
                          onChange={(e) => handleDateRangeChange({
                            ...pendingDateRange,
                            endDate: e.target.value
                          })}
                          className="w-36"
                          title="End Date"
                        />
                        {(pendingDateRange.startDate || pendingDateRange.endDate) && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={submitDateRangeFilter}
                              title="Apply date filter"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              <Filter className="h-4 w-4 mr-1" />
                              Apply
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={clearDateRangeFilter}
                              title="Clear date filter"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Performance Summary Cards */}
                  <div className="grid gap-6 md:grid-cols-4 mb-6">
                    <Card className="bg-blue-50 border border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">Total Sent</CardTitle>
                        <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                          <Send className="h-6 w-6 text-white" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-4xl font-bold text-blue-700 mb-2">
                          {(() => {
                            const filtered = selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign);
                            return filtered.reduce((sum, campaign) => sum + (campaign.metrics?.sent || 0), 0).toLocaleString();
                          })()}
                        </div>
                        <p className="text-xs text-slate-600">
                          Messages delivered across {selectedCampaign === 'all' ? 'all campaigns' : 'selected campaign'}
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-green-50 border border-green-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">Open Rate</CardTitle>
                        <div className="w-12 h-12 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg">
                          <Eye className="h-6 w-6 text-white" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-4xl font-bold text-green-700 mb-2">
                          {(() => {
                            const filtered = selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign);
                            const totalSent = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.sent || 0), 0);
                            const totalOpened = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.opened || 0), 0);
                            return totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0.0';
                          })()}%
                        </div>
                        <p className="text-xs text-slate-600">
                          Messages opened by recipients
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-orange-50 border border-orange-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">Click Rate</CardTitle>
                        <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                          <ExternalLink className="h-6 w-6 text-white" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-4xl font-bold text-orange-700 mb-2">
                          {(() => {
                            const filtered = selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign);
                            const totalSent = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.sent || 0), 0);
                            const totalClicked = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.clicked || 0), 0);
                            return totalSent > 0 ? ((totalClicked / totalSent) * 100).toFixed(2) : '0.00';
                          })()}%
                        </div>
                        <p className="text-xs text-slate-600">
                          Links clicked by recipients
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-purple-50 border border-purple-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">Conversion Rate</CardTitle>
                        <div className="w-12 h-12 bg-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                          <Award className="h-6 w-6 text-white" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="text-4xl font-bold text-purple-700 mb-2">
                          {(() => {
                            const filtered = selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign);
                            const totalSent = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.sent || 0), 0);
                            const totalConverted = filtered.reduce((sum, campaign) => sum + (campaign.metrics?.converted || 0), 0);
                            return totalSent > 0 ? ((totalConverted / totalSent) * 100).toFixed(2) : '0.00';
                          })()}%
                        </div>
                        <p className="text-xs text-slate-600">
                          Successful conversions achieved
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Campaign Performance Table */}
                  <div className="space-y-4">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Campaign Name</TableHead>
                            <TableHead>Medium</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Total Sent</TableHead>
                            <TableHead>Opened</TableHead>
                            <TableHead>Clicked</TableHead>
                            <TableHead>Converted</TableHead>
                            <TableHead>Created</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignLoading ? (
                            // Loading skeleton rows
                            [...Array(campaignRowsPerPage)].map((_, index) => (
                              <TableRow key={`loading-${index}`}>
                                <TableCell colSpan={8} className="text-center py-8">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                    <span className="text-gray-500">Loading campaign data...</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : paginatedCampaigns.length > 0 ? (
                            paginatedCampaigns.map((campaign) => (
                              <TableRow key={campaign.id}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {campaign.objective === 'whatsapp' && <MessageCircle className="h-4 w-4 text-green-600" />}
                                    {campaign.objective === 'sms' && <Smartphone className="h-4 w-4 text-blue-600" />}
                                    {campaign.objective === 'email' && <Mail className="h-4 w-4 text-red-600" />}
                                    {campaign.objective === 'facebook_ads' && <Facebook className="h-4 w-4 text-blue-800" />}
                                    {!['whatsapp', 'sms', 'email', 'facebook_ads'].includes(campaign.objective) && <Target className="h-4 w-4 text-gray-600" />}
                                    <span className="truncate max-w-48">{campaign.name}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="capitalize">
                                    {campaign.objective ? (
                                      <>
                                        {campaign.objective === 'whatsapp' && <MessageCircle className="h-3 w-3 mr-1" />}
                                        {campaign.objective === 'sms' && <Smartphone className="h-3 w-3 mr-1" />}
                                        {campaign.objective === 'email' && <Mail className="h-3 w-3 mr-1" />}
                                        {campaign.objective === 'facebook_ads' && <Facebook className="h-3 w-3 mr-1" />}
                                        {campaign.objective}
                                      </>
                                    ) : 'Campaign'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={
                                    campaign.status === 'completed' ? 'default' :
                                    campaign.status === 'approved' ? 'secondary' :
                                    campaign.status === 'rejected' ? 'destructive' : 'outline'
                                  }>
                                    {campaign.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-blue-600">{campaign.metrics?.sent || 0}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-green-600">{campaign.metrics?.opened || 0}</span>
                                    {campaign.metrics?.opened > 0 && (
                                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-orange-600">{campaign.metrics?.clicked || 0}</span>
                                    {campaign.metrics?.clicked > 0 && (
                                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-purple-600">{campaign.metrics?.converted || 0}</span>
                                    {campaign.metrics?.converted > 0 && (
                                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs text-gray-500">
                                  {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center py-8">
                                <div className="text-gray-500">
                                  {selectedCampaign === 'all' ? 'No campaigns available' : 'No campaigns match the selected filter'}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination Controls */}
                    {paginatedCampaigns.length > 0 && totalCampaignPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-600">
                          Showing {((campaignPage - 1) * campaignRowsPerPage) + 1} to {Math.min(campaignPage * campaignRowsPerPage, (selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign)).length)} of {(selectedCampaign === 'all' ? campaigns : campaigns.filter(c => c.id === selectedCampaign)).length} campaigns
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCampaignPageChange(campaignPage - 1)}
                            disabled={campaignPage === 1 || campaignLoading}
                          >
                            Previous
                          </Button>
                          <div className="flex items-center gap-1">
                            {[...Array(Math.min(5, totalCampaignPages))].map((_, index) => {
                              const pageNum = index + 1;
                              return (
                                <Button
                                  key={pageNum}
                                  variant={campaignPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handleCampaignPageChange(pageNum)}
                                  disabled={campaignLoading}
                                  className="w-8 h-8 p-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                            {totalCampaignPages > 5 && (
                              <>
                                <span className="text-gray-500">...</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCampaignPageChange(totalCampaignPages)}
                                  disabled={campaignLoading}
                                  className="w-8 h-8 p-0"
                                >
                                  {totalCampaignPages}
                                </Button>
                              </>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCampaignPageChange(campaignPage + 1)}
                            disabled={campaignPage === totalCampaignPages || campaignLoading}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Campaign Info */}
                  {selectedCampaign !== 'all' && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Info className="h-4 w-4" />
                        <span className="font-medium">
                          Showing performance for: {campaigns.find(c => c.id === selectedCampaign)?.name || 'Selected Campaign'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Date Range Info */}
                  {(performanceDateRange.startDate || performanceDateRange.endDate) && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-800">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">
                          Date Range: {performanceDateRange.startDate || 'Start'} to {performanceDateRange.endDate || 'End'}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* API Integration Status Dashboard */}
            <div className="mb-8">
              <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 bg-white border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-3 text-slate-700">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <Zap className="h-6 w-6 text-white" />
                        </div>
                        API Integration Status
                      </CardTitle>
                      <CardDescription className="text-slate-600 mt-2">
                        Real-time status of all third-party integrations and API connections
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {integrationStatusData?.summary && (
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`${
                              integrationStatusData.summary.connected > 0 
                                ? 'text-green-700 border-green-300 bg-green-50' 
                                : 'text-slate-700 border-slate-300'
                            } font-semibold`}
                          >
                            {integrationStatusData.summary.connected}/{integrationStatusData.summary.total_services} Connected
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className="text-indigo-700 border-indigo-300 bg-indigo-50 font-semibold"
                          >
                            {integrationStatusData.summary.health_percentage}% Health
                          </Badge>
                        </div>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={refreshIntegrationStatus}
                        disabled={integrationStatusLoading}
                        className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      >
                        <RotateCcw className={`h-4 w-4 mr-2 ${integrationStatusLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {integrationStatusLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span className="text-slate-600 font-medium">Loading integration status...</span>
                      </div>
                    </div>
                  ) : integrationStatusData ? (
                    <div className="space-y-8">
                      {/* Integration Summary - Moved to Top */}
                      {integrationStatusData.summary && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-slate-600" />
                            Integration Summary
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div
                              className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg cursor-pointer rounded-lg border"
                              onClick={() => navigateToIntegrationSettings('seo_tools')}
                            >
                              <Card>
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Connected</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <CheckCircle className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-green-700 mb-3 group-hover:scale-110 transition-transform">
                                  {integrationStatusData.summary.connected}
                                </div>
                                <div className="w-full bg-green-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full transition-all duration-500" style={{width: `${(integrationStatusData.summary.connected / integrationStatusData.summary.total_services) * 100}%`}}></div>
                                </div>
                              </CardContent>
                              </Card>
                            </div>

                            <div
                              className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg cursor-pointer rounded-lg border"
                              onClick={() => navigateToIntegrationSettings('communication')}
                            >
                              <Card>
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Configured</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Settings className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-yellow-700 mb-3 group-hover:scale-110 transition-transform">
                                  {integrationStatusData.summary.configured}
                                </div>
                                <div className="w-full bg-yellow-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-500" style={{width: `${(integrationStatusData.summary.configured / integrationStatusData.summary.total_services) * 100}%`}}></div>
                                </div>
                              </CardContent>
                              </Card>
                            </div>

                            <div
                              className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg cursor-pointer rounded-lg border"
                              onClick={() => navigateToIntegrationSettings('advertisements')}
                            >
                              <Card>
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Errors</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <XCircle className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-red-700 mb-3 group-hover:scale-110 transition-transform">
                                  {integrationStatusData.summary.error}
                                </div>
                                <div className="w-full bg-red-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-red-400 to-rose-500 h-2 rounded-full transition-all duration-500" style={{width: `${integrationStatusData.summary.error > 0 ? (integrationStatusData.summary.error / integrationStatusData.summary.total_services) * 100 : 0}%`}}></div>
                                </div>
                              </CardContent>
                              </Card>
                            </div>

                            <div
                              className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg cursor-pointer rounded-lg border"
                              onClick={() => navigateToIntegrationSettings('utilities')}
                            >
                              <Card>
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Not Setup</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-gray-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Circle className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-slate-700 mb-3 group-hover:scale-110 transition-transform">
                                  {integrationStatusData.summary.not_configured}
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-slate-400 to-gray-500 h-2 rounded-full transition-all duration-500" style={{width: `${(integrationStatusData.summary.not_configured / integrationStatusData.summary.total_services) * 100}%`}}></div>
                                </div>
                              </CardContent>
                              </Card>
                            </div>
                          </div>
                          
                          <div className="mt-6 pt-4 border-t border-slate-200">
                            <div className="flex items-center justify-between text-sm text-slate-600">
                              <span className="font-medium">
                                Last updated: {lastStatusUpdate ? new Date(lastStatusUpdate).toLocaleTimeString() : 'Never'}
                              </span>
                              <span className="font-medium">
                                {integrationStatusData.summary.error > 0 && 
                                  `${integrationStatusData.summary.error} service${integrationStatusData.summary.error > 1 ? 's' : ''} need${integrationStatusData.summary.error === 1 ? 's' : ''} attention`
                                }
                                {integrationStatusData.summary.error === 0 && integrationStatusData.summary.not_configured > 0 &&
                                  `${integrationStatusData.summary.not_configured} service${integrationStatusData.summary.not_configured > 1 ? 's' : ''} available to setup`
                                }
                                {integrationStatusData.summary.error === 0 && integrationStatusData.summary.not_configured === 0 &&
                                  'All services configured'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CRM Connection Status */}
                      {integrationStatusData.crm && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <Users className="h-4 w-4 text-indigo-600" />
                            CRM & Customer Management
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Object.entries(integrationStatusData.crm).map(([key, service]) => (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('crm', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1 truncate">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Available' : service.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Communication Services */}
                      {integrationStatusData.communication && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-blue-600" />
                            Communication
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Object.entries(integrationStatusData.communication).map(([key, service]) => (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('communication', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Not Setup' : service.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Analytics Services */}
                      {integrationStatusData.analytics && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-purple-600" />
                            Analytics & Tracking
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {Object.entries(integrationStatusData.analytics).map(([key, service]) => {
                              // Debug log to check service status
                              console.log(`🔍 Analytics Service: ${service.name} (${key}) - Status: ${service.status}`);
                              return (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('analytics', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1 truncate">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Not Setup' : service.status}
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      )}

                      {/* SEO Tools */}
                      {integrationStatusData.seo_tools && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <Search className="h-4 w-4 text-green-600" />
                            SEO & Research Tools
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Object.entries(integrationStatusData.seo_tools).map(([key, service]) => (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('seo_tools', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1 truncate">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Not Setup' : service.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Advertisement Platforms */}
                      {integrationStatusData.advertisements && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-orange-600" />
                            Advertisement Platforms
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Object.entries(integrationStatusData.advertisements).map(([key, service]) => (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('advertisements', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1 truncate">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Not Setup' : service.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Utility Services */}
                      {integrationStatusData.utilities && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <Settings className="h-4 w-4 text-indigo-600" />
                            Utility Services
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Object.entries(integrationStatusData.utilities).map(([key, service]) => (
                              <div 
                                key={key}
                                className={`relative overflow-hidden group p-4 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                                  service.status === 'connected' 
                                    ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-green-100' 
                                    : service.status === 'configured'
                                    ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300 hover:shadow-yellow-100'
                                    : service.status === 'error'
                                    ? 'bg-red-50 border-red-200 hover:border-red-300 hover:shadow-red-100'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-slate-100'
                                }`}
                                onClick={() => navigateToIntegrationSettings('utilities', key)}
                                title={`Click to configure ${service.name}`}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    {service.status === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
                                    {service.status === 'configured' && <Settings className="h-5 w-5 text-yellow-600" />}
                                    {service.status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
                                    {service.status === 'not_configured' && <Circle className="h-5 w-5 text-slate-400" />}
                                  </div>
                                  <div className={`w-3 h-3 rounded-full ${
                                    service.status === 'connected' ? 'bg-green-500' :
                                    service.status === 'configured' ? 'bg-yellow-500' :
                                    service.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                  }`}></div>
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mb-1 truncate">{service.name}</div>
                                <div className={`text-xs font-medium capitalize ${
                                  service.status === 'connected' ? 'text-green-700' :
                                  service.status === 'configured' ? 'text-yellow-700' :
                                  service.status === 'error' ? 'text-red-700' : 'text-slate-500'
                                }`}>
                                  {service.status === 'not_configured' ? 'Not Setup' : service.status}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Zap className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Integration Status Unavailable</h3>
                      <p className="text-slate-500 mb-6">
                        Unable to load integration status. Please try refreshing.
                      </p>
                      <Button onClick={loadIntegrationStatusSummary} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* AI Models Status Dashboard */}
            <div className="mb-8">
              <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 bg-white border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-3 text-slate-700">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <Brain className="h-6 w-6 text-white" />
                        </div>
                        AI Models Status Dashboard
                      </CardTitle>
                      <CardDescription className="text-slate-600 mt-2">
                        Real-time status and usage metrics for AI/LLM providers and models
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {aiModelsStatusData?.summary && (
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`${
                              aiModelsStatusData.summary.active > 0 
                                ? 'text-green-700 border-green-300 bg-green-50' 
                                : 'text-slate-700 border-slate-300'
                            } font-semibold`}
                          >
                            {aiModelsStatusData.summary.active}/{aiModelsStatusData.summary.total_models} Active
                          </Badge>
                          <Badge 
                            variant="outline" 
                            className="text-purple-700 border-purple-300 bg-purple-50 font-semibold"
                          >
                            {aiModelsStatusData.summary.model_health_percentage}% Health
                          </Badge>
                        </div>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={refreshAiModelsStatus}
                        disabled={aiModelsStatusLoading}
                        className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      >
                        <RotateCcw className={`h-4 w-4 mr-2 ${aiModelsStatusLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {aiModelsStatusLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                        <span className="text-slate-600 font-medium">Loading AI models status...</span>
                      </div>
                    </div>
                  ) : aiModelsStatusData ? (
                    <div className="space-y-8">
                      {/* AI Models Summary */}
                      {aiModelsStatusData.summary && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-slate-600" />
                            AI Models Summary
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Active Models</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <CheckCircle className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-green-700 mb-3 group-hover:scale-110 transition-transform">
                                  {aiModelsStatusData.summary.active}
                                </div>
                                <div className="w-full bg-green-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full transition-all duration-500" style={{width: `${(aiModelsStatusData.summary.active / aiModelsStatusData.summary.total_models) * 100}%`}}></div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Configured</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Settings className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-yellow-700 mb-3 group-hover:scale-110 transition-transform">
                                  {aiModelsStatusData.summary.configured}
                                </div>
                                <div className="w-full bg-yellow-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-500" style={{width: `${(aiModelsStatusData.summary.configured / aiModelsStatusData.summary.total_models) * 100}%`}}></div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Available</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Zap className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-blue-700 mb-3 group-hover:scale-110 transition-transform">
                                  {aiModelsStatusData.summary.available}
                                </div>
                                <div className="w-full bg-blue-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-blue-400 to-indigo-500 h-2 rounded-full transition-all duration-500" style={{width: `${(aiModelsStatusData.summary.available / aiModelsStatusData.summary.total_models) * 100}%`}}></div>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-0 shadow-lg">
                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-semibold text-slate-700">Total Models</CardTitle>
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Brain className="h-5 w-5 text-white" />
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="text-3xl font-bold text-purple-700 mb-3 group-hover:scale-110 transition-transform">
                                  {aiModelsStatusData.summary.total_models}
                                </div>
                                <div className="w-full bg-purple-100 rounded-full h-2">
                                  <div className="bg-gradient-to-r from-purple-400 to-pink-500 h-2 rounded-full transition-all duration-500" style={{width: '100%'}}></div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                      )}

                      {/* Usage Metrics - HIDDEN: Will be implemented in Billing section with real-time tracking
                      
                      TODO: Future Enhancement - Real-time AI Usage Tracking
                      - Implement actual API call logging with timestamps, tokens, costs
                      - Create ai_usage_logs collection for historical data  
                      - Add 24h aggregation queries for real metrics
                      - Track usage per provider, model, and context
                      - Include in Billing section with accurate cost tracking
                      - Enable ROI analysis and usage optimization insights
                      */}

                      {false && aiModelsStatusData.usage_metrics && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-slate-600" />
                            Usage Metrics (24h)
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-600">Total Requests</span>
                                <MessageSquare className="h-4 w-4 text-blue-500" />
                              </div>
                              <div className="text-2xl font-bold text-slate-900">{aiModelsStatusData.usage_metrics.total_requests_24h.toLocaleString()}</div>
                            </Card>

                            <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-600">Total Tokens</span>
                                <Hash className="h-4 w-4 text-green-500" />
                              </div>
                              <div className="text-2xl font-bold text-slate-900">{aiModelsStatusData.usage_metrics.total_tokens_24h.toLocaleString()}</div>
                            </Card>

                            <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-600">Estimated Cost</span>
                                <DollarSign className="h-4 w-4 text-yellow-500" />
                              </div>
                              <div className="text-2xl font-bold text-slate-900">${aiModelsStatusData.usage_metrics.estimated_cost_24h}</div>
                            </Card>

                            <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-600">Most Used</span>
                                <Crown className="h-4 w-4 text-purple-500" />
                              </div>
                              <div className="text-2xl font-bold text-slate-900 capitalize">{aiModelsStatusData.usage_metrics.most_used_model}</div>
                            </Card>
                          </div>
                        </div>
                      )}

                      {/* Priority AI Providers */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-500" />
                          Priority AI Providers
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {/* OpenAI */}
                          {aiModelsStatusData.openai && (
                            <Card className="p-4 border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Brain className="h-5 w-5 text-blue-600" />
                                  <h5 className="font-semibold text-slate-900">{aiModelsStatusData.openai.name}</h5>
                                </div>
                                <Badge className="bg-blue-100 text-blue-800 border-blue-200">Premium</Badge>
                              </div>
                              <div className="space-y-2">
                                {Object.entries(aiModelsStatusData.openai.models).map(([key, model]) => (
                                  <div key={key} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{model.name}</span>
                                    <div className={`w-2 h-2 rounded-full ${
                                      model.status === 'connected' ? 'bg-green-500' :
                                      model.status === 'configured' ? 'bg-yellow-500' :
                                      model.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                    }`}></div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Anthropic */}
                          {aiModelsStatusData.anthropic && (
                            <Card className="p-4 border-2 border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Brain className="h-5 w-5 text-orange-600" />
                                  <h5 className="font-semibold text-slate-900">{aiModelsStatusData.anthropic.name}</h5>
                                </div>
                                <Badge className="bg-orange-100 text-orange-800 border-orange-200">Premium</Badge>
                              </div>
                              <div className="space-y-2">
                                {Object.entries(aiModelsStatusData.anthropic.models).map(([key, model]) => (
                                  <div key={key} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{model.name}</span>
                                    <div className={`w-2 h-2 rounded-full ${
                                      model.status === 'connected' ? 'bg-green-500' :
                                      model.status === 'configured' ? 'bg-yellow-500' :
                                      model.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                    }`}></div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Google */}
                          {aiModelsStatusData.google && (
                            <Card className="p-4 border-2 border-slate-200 hover:border-green-300 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Brain className="h-5 w-5 text-green-600" />
                                  <h5 className="font-semibold text-slate-900">{aiModelsStatusData.google.name}</h5>
                                </div>
                                <Badge className="bg-green-100 text-green-800 border-green-200">Premium</Badge>
                              </div>
                              <div className="space-y-2">
                                {Object.entries(aiModelsStatusData.google.models).map(([key, model]) => (
                                  <div key={key} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{model.name}</span>
                                    <div className={`w-2 h-2 rounded-full ${
                                      model.status === 'connected' ? 'bg-green-500' :
                                      model.status === 'configured' ? 'bg-yellow-500' :
                                      model.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                    }`}></div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Meta */}
                          {aiModelsStatusData.meta && (
                            <Card className="p-4 border-2 border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all duration-300">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Brain className="h-5 w-5 text-purple-600" />
                                  <h5 className="font-semibold text-slate-900">{aiModelsStatusData.meta.name}</h5>
                                </div>
                                <Badge className="bg-purple-100 text-purple-800 border-purple-200">Premium</Badge>
                              </div>
                              <div className="space-y-2">
                                {Object.entries(aiModelsStatusData.meta.models).map(([key, model]) => (
                                  <div key={key} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700">{model.name}</span>
                                    <div className={`w-2 h-2 rounded-full ${
                                      model.status === 'connected' ? 'bg-green-500' :
                                      model.status === 'configured' ? 'bg-yellow-500' :
                                      model.status === 'error' ? 'bg-red-500' : 'bg-slate-400'
                                    }`}></div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}
                        </div>
                      </div>

                      {/* Other AI Providers */}
                      {(aiModelsStatusData.mistral || aiModelsStatusData.perplexity || aiModelsStatusData.cohere) && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
                            <Layers className="h-4 w-4 text-slate-600" />
                            Other AI Providers
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {aiModelsStatusData.mistral && (
                              <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Brain className="h-4 w-4 text-indigo-600" />
                                    <h5 className="font-medium text-slate-900">{aiModelsStatusData.mistral.name}</h5>
                                  </div>
                                  <Badge variant="outline">LLM</Badge>
                                </div>
                                <div className="space-y-1">
                                  {Object.entries(aiModelsStatusData.mistral.models).map(([key, model]) => (
                                    <div key={key} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-600">{model.name}</span>
                                      <div className={`w-1.5 h-1.5 rounded-full ${
                                        model.status === 'connected' ? 'bg-green-500' : 'bg-slate-400'
                                      }`}></div>
                                    </div>
                                  ))}
                                </div>
                              </Card>
                            )}

                            {aiModelsStatusData.perplexity && (
                              <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Search className="h-4 w-4 text-teal-600" />
                                    <h5 className="font-medium text-slate-900">{aiModelsStatusData.perplexity.name}</h5>
                                  </div>
                                  <Badge variant="outline">Search</Badge>
                                </div>
                                <div className="space-y-1">
                                  {Object.entries(aiModelsStatusData.perplexity.models).map(([key, model]) => (
                                    <div key={key} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-600">{model.name}</span>
                                      <div className={`w-1.5 h-1.5 rounded-full ${
                                        model.status === 'connected' ? 'bg-green-500' : 'bg-slate-400'
                                      }`}></div>
                                    </div>
                                  ))}
                                </div>
                              </Card>
                            )}

                            {aiModelsStatusData.cohere && (
                              <Card className="p-4 border border-slate-200 hover:shadow-lg transition-all duration-300">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Brain className="h-4 w-4 text-pink-600" />
                                    <h5 className="font-medium text-slate-900">{aiModelsStatusData.cohere.name}</h5>
                                  </div>
                                  <Badge variant="outline">NLP</Badge>
                                </div>
                                <div className="space-y-1">
                                  {Object.entries(aiModelsStatusData.cohere.models).map(([key, model]) => (
                                    <div key={key} className="flex items-center justify-between text-xs">
                                      <span className="text-slate-600">{model.name}</span>
                                      <div className={`w-1.5 h-1.5 rounded-full ${
                                        model.status === 'connected' ? 'bg-green-500' : 'bg-slate-400'
                                      }`}></div>
                                    </div>
                                  ))}
                                </div>
                              </Card>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Brain className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">AI Models Status Unavailable</h3>
                      <p className="text-slate-500 mb-6">
                        Unable to load AI models status. Please try refreshing.
                      </p>
                      <Button onClick={loadAiModelsStatus} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* User Management (Admin only) */}
          {user?.role === 'administrator' && (
            <TabsContent value="users">
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5" />
                      Create New User
                    </CardTitle>
                    <CardDescription>
                      Create new users and assign roles
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div>
                        <Label htmlFor="user-name">Full Name</Label>
                        <Input
                          id="user-name"
                          value={createUserForm.name}
                          onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                          placeholder="Enter full name"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="user-email">Email</Label>
                        <Input
                          id="user-email"
                          type="email"
                          value={createUserForm.email}
                          onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                          placeholder="Enter email address"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="user-password">Password</Label>
                        <Input
                          id="user-password"
                          type="password"
                          value={createUserForm.password}
                          onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                          placeholder="Enter password"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="user-role">Role</Label>
                        <Select 
                          value={createUserForm.role} 
                          onValueChange={(value) => setCreateUserForm({...createUserForm, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="administrator">Administrator</SelectItem>
                            <SelectItem value="data_analyst">Data Analyst</SelectItem>
                            <SelectItem value="campaign_creator">Campaign Creator</SelectItem>
                            <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                            <SelectItem value="channel_manager">Channel Manager</SelectItem>
                            <SelectItem value="analytics_specialist">Analytics Specialist</SelectItem>
                            <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button type="submit" disabled={isLoading} className="w-full">
                        {isLoading ? 'Creating...' : 'Create User'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Existing Users</CardTitle>
                    <CardDescription>
                      Manage existing users and their roles
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {allUsers.map((userData) => (
                        <div key={userData.id} className="flex items-center justify-between p-4 border rounded">
                          <div className="flex-1">
                            <h3 className="font-medium">{userData.name}</h3>
                            <p className="text-sm text-gray-500">{userData.email}</p>
                            <Badge variant="outline" className="text-xs mt-1">
                              {userData.role?.replace('_', ' ') || 'User'}
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditUser(userData)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {userData.id !== user?.id && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(userData.id, userData.name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Edit User Dialog */}
              {editingUser && (
                <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Edit User</DialogTitle>
                      <DialogDescription>
                        Update user information and role
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditUser} className="space-y-4">
                      <div>
                        <Label htmlFor="edit-name">Full Name</Label>
                        <Input
                          id="edit-name"
                          value={editUserForm.name}
                          onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-email">Email</Label>
                        <Input
                          id="edit-email"
                          type="email"
                          value={editUserForm.email}
                          onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="edit-role">Role</Label>
                        <Select 
                          value={editUserForm.role} 
                          onValueChange={(value) => setEditUserForm({...editUserForm, role: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="administrator">Administrator</SelectItem>
                            <SelectItem value="data_analyst">Data Analyst</SelectItem>
                            <SelectItem value="campaign_creator">Campaign Creator</SelectItem>
                            <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                            <SelectItem value="channel_manager">Channel Manager</SelectItem>
                            <SelectItem value="analytics_specialist">Analytics Specialist</SelectItem>
                            <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? 'Updating...' : 'Update User'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </TabsContent>
          )}

          {/* Audience Management */}
          <TabsContent value="audience">
            <div className="space-y-6">
              
              {/* Enhanced Audience Overview */}
              {(enhancedContacts && enhancedContacts.length > 0) ? (
                <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
                  
                  {/* Total Contacts with Data Source Breakdown */}
                  <Card className="bg-blue-50 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700">Total Contacts</CardTitle>
                      <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-slate-800 mb-1">
                        {(enhancedStats?.total_contacts || enhancedContacts?.length || 0).toLocaleString()}
                      </div>
                      <p className="text-sm text-slate-600 font-medium mb-2">Enhanced audience pool</p>
                      
                      {/* NEW: Data Source Breakdown */}
                      {enhancedStats?.data_source_breakdown && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {enhancedStats.data_source_breakdown.upload_contacts > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded text-xs font-medium text-purple-700">
                              <span>📁</span>
                              <span>{enhancedStats.data_source_breakdown.upload_contacts}</span>
                            </div>
                          )}
                          {enhancedStats.data_source_breakdown.crm_contacts > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-xs font-medium text-green-700">
                              <span>🔗</span>
                              <span>{enhancedStats.data_source_breakdown.crm_contacts}</span>
                            </div>
                          )}
                          {enhancedStats.data_source_breakdown.manual_contacts > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">
                              <span>✋</span>
                              <span>{enhancedStats.data_source_breakdown.manual_contacts}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Hot Contacts */}
                  <Card className="bg-red-50 border border-red-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700">Hot Contacts</CardTitle>
                      <div className="w-12 h-12 bg-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Flame className="h-6 w-6 text-white" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-slate-800 mb-1">
                        {(enhancedStats?.hot_contacts || 0).toLocaleString()}
                      </div>
                      <p className="text-sm text-slate-600 font-medium">Score 80+ ready to engage</p>
                    </CardContent>
                  </Card>

                  {/* Warm Contacts */}
                  <Card className="bg-orange-50 border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700">Warm Contacts</CardTitle>
                      <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Sun className="h-6 w-6 text-white" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-slate-800 mb-1">
                        {(enhancedStats?.warm_contacts || 0).toLocaleString()}
                      </div>
                      <p className="text-sm text-slate-600 font-medium">Score 60-79 potential leads</p>
                    </CardContent>
                  </Card>

                  {/* Engagement Rate */}
                  <Card className="bg-emerald-50 border border-emerald-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-700">Engagement Rate</CardTitle>
                      <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Activity className="h-6 w-6 text-white" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-slate-800 mb-1">
                        {(enhancedStats?.engagement_rate || 0).toFixed(1)}%
                      </div>
                      <p className="text-sm text-slate-600 font-medium">Overall audience quality</p>
                    </CardContent>
                  </Card>
                </div>
              ) : null}

              {/* NEW: Tabbed Data Import & Sync Interface */}
              <Card className="border-2 border-dashed border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    Data Import & Sync Hub
                  </CardTitle>
                  <CardDescription>
                    Import contacts from files or sync from connected CRM systems
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="import-wizard" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="import-wizard" className="flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        📁 Import Wizard
                      </TabsTrigger>
                      <TabsTrigger value="crm-sync" className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        🔗 CRM Sync
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Import Wizard Tab */}
                    <TabsContent value="import-wizard" className="mt-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Upload className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Next-Gen Import Wizard</h3>
                            <p className="text-sm text-gray-600">AI-powered import with auto-mapping, duplicate detection, and header learning</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-3">
                          <Button 
                            onClick={() => {
                              console.log('🚀 Opening Import Wizard...');
                              setShowImportWizard(true);
                              setImportStep(1);
                              setFileAnalysis(null);
                              setHeaderMappings({});
                              setImportFile(null);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Start Smart Import
                          </Button>
                          
                          <Button 
                            variant="outline"
                            onClick={() => {
                              setShowHeaderManager(true);
                              loadHeaderManagementData();
                            }}
                            className="border-blue-200 text-blue-600 hover:bg-blue-50"
                          >
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Headers
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* CRM Sync Tab */}
                    <TabsContent value="crm-sync" className="mt-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <Link className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">CRM Data Sync</h3>
                            <p className="text-sm text-gray-600">Sync contact data from your connected CRM systems into your audience</p>
                          </div>
                        </div>
                        
                        {/* Enhanced Status Display with Salesforce Info */}
                        <div className="mb-6">
                          {/* Salesforce Status Section */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                                <span className="text-white text-xs font-bold">SF</span>
                              </div>
                              <h4 className="font-medium text-gray-900">Salesforce Integration</h4>
                              {salesforceStatus.demo_mode ? (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  Demo Mode
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  Live Connection
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              {salesforceStatus.demo_mode ? (
                                <>
                                  <p>• Running with sample Salesforce data</p>
                                  <p>• Connect your real Salesforce org to sync live data</p>
                                  <p>• Demo includes realistic contacts and leads</p>
                                </>
                              ) : (
                                <>
                                  <p>• Connected to: {salesforceStatus.domain}</p>
                                  <p>• API Version: {salesforceStatus.api_version}</p>
                                  <p>• Authentication: {salesforceStatus.authenticated ? 'Active' : 'Inactive'}</p>
                                </>
                              )}
                            </div>
                          </div>

                          {/* General CRM Status */}
                          {crmSyncStatus.connected ? (
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                <span className="font-medium text-green-600">Connected:</span> {crmSyncStatus.active_connections} CRM System{crmSyncStatus.active_connections !== 1 ? 's' : ''}
                              </p>
                              {crmSyncStatus.total_synced_records > 0 && (
                                <p className="text-sm text-gray-600 mb-1">
                                  <span className="font-medium">Records Synced:</span> {crmSyncStatus.total_synced_records.toLocaleString()}
                                </p>
                              )}
                              {crmSyncStatus.last_sync && (
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Last Sync:</span> {new Date(crmSyncStatus.last_sync).toLocaleString()}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium text-orange-600">Status:</span> No traditional CRM systems connected
                            </p>
                          )}
                        </div>
                        
                        {/* Enhanced Action Buttons */}
                        <div className="space-y-4">
                          {/* Salesforce Demo Sync */}
                          <div className="flex flex-col sm:flex-row gap-3">
                            <Button 
                              onClick={triggerDemoSalesforceSync}
                              disabled={isLoading}
                              className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Syncing...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Sync Salesforce {salesforceStatus.demo_mode ? 'Demo' : 'Data'}
                                </>
                              )}
                            </Button>
                            
                            {crmSyncStatus.connected && (
                              <Button 
                                onClick={handleCrmSync}
                                disabled={isLoading}
                                className="bg-green-600 hover:bg-green-700 text-white flex-1"
                              >
                                {isLoading ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Sync Other CRM Data
                                  </>
                                )}
                              </Button>
                            )}
                          </div>

                          {/* Management Buttons */}
                          <div className="flex flex-col sm:flex-row gap-3">
                            {!crmSyncStatus.connected && !salesforceStatus.has_credentials && (
                              <Button 
                                onClick={() => {
                                  setActiveTab('settings');
                                  setActiveSettingsTab('crm-integration');
                                }}
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50 flex-1"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Connect Real CRM
                              </Button>
                            )}
                            
                            <Button 
                              variant="outline"
                              onClick={() => {
                                setActiveTab('settings');
                                setActiveSettingsTab('crm-integration');
                              }}
                              className="border-gray-300 text-gray-700 hover:bg-gray-50 flex-1"
                            >
                              <Settings className="h-4 w-4 mr-2" />
                              Manage Connections
                            </Button>
                          </div>

                          {/* Help Text */}
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <p className="text-sm text-gray-600">
                              <strong>Getting Started:</strong> {' '}
                              {salesforceStatus.demo_mode ? 
                                'Try the Salesforce demo sync to see how CRM integration works with sample data. ' +
                                'To connect your real Salesforce org, add your credentials in Settings > CRM Integration.' :
                                'Your Salesforce connection is live! Sync data to keep your audience up to date.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
              
              {/* Data Quality Overview */}
              {audienceStats && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Data Quality Overview & Analytics
                    </CardTitle>
                    <CardDescription>
                      Comprehensive view of your contact database quality and enrichment status
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Main Statistics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-blue-600">{audienceStats.total_contacts}</div>
                        <p className="text-sm text-gray-600 font-medium">Total Contacts</p>
                        <div className="text-xs text-gray-500 mt-1">Complete database</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-green-600">{audienceStats.quality_contacts}</div>
                        <p className="text-sm text-gray-600 font-medium">Quality Contacts</p>
                        <div className="text-xs text-green-600 mt-1">
                          {audienceStats.total_contacts > 0 ? Math.round((audienceStats.quality_contacts / audienceStats.total_contacts) * 100) : 0}% of total
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-purple-600">{audienceStats.social_profiles || 0}</div>
                        <p className="text-sm text-gray-600 font-medium">Social Profiles</p>
                        <div className="text-xs text-purple-600 mt-1">Enriched contacts</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-indigo-600">{audienceStats.enriched_contacts || 0}</div>
                        <p className="text-sm text-gray-600 font-medium">AI Enriched</p>
                        <div className="text-xs text-indigo-600 mt-1">With AI insights</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-orange-600">{audienceStats.duplicates || 0}</div>
                        <p className="text-sm text-gray-600 font-medium">Duplicates</p>
                        <div className="text-xs text-orange-600 mt-1">Need attention</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm border text-center">
                        <div className="text-3xl font-bold text-red-600">{audienceStats.low_quality || 0}</div>
                        <p className="text-sm text-gray-600 font-medium">Low Quality</p>
                        <div className="text-xs text-red-600 mt-1">Incomplete data</div>
                      </div>
                    </div>

                    {/* Quality Rating Distribution */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                      <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <span className="text-yellow-500">⭐</span>
                        Contact Quality Rating Distribution
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-2xl font-bold text-green-600">
                            {audienceStats.rating_excellent || Math.floor(audienceStats.quality_contacts * 0.3)}
                          </div>
                          <div className="text-sm font-medium text-green-700">Excellent (80-100)</div>
                          <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{
                                width: `${audienceStats.total_contacts > 0 ? 
                                  ((audienceStats.rating_excellent || Math.floor(audienceStats.quality_contacts * 0.3)) / audienceStats.total_contacts) * 100 
                                  : 0}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="text-2xl font-bold text-yellow-600">
                            {audienceStats.rating_good || Math.floor(audienceStats.quality_contacts * 0.4)}
                          </div>
                          <div className="text-sm font-medium text-yellow-700">Good (60-79)</div>
                          <div className="w-full bg-yellow-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-yellow-600 h-2 rounded-full" 
                              style={{
                                width: `${audienceStats.total_contacts > 0 ? 
                                  ((audienceStats.rating_good || Math.floor(audienceStats.quality_contacts * 0.4)) / audienceStats.total_contacts) * 100 
                                  : 0}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="text-2xl font-bold text-orange-600">
                            {audienceStats.rating_fair || Math.floor(audienceStats.quality_contacts * 0.2)}
                          </div>
                          <div className="text-sm font-medium text-orange-700">Fair (40-59)</div>
                          <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-orange-600 h-2 rounded-full" 
                              style={{
                                width: `${audienceStats.total_contacts > 0 ? 
                                  ((audienceStats.rating_fair || Math.floor(audienceStats.quality_contacts * 0.2)) / audienceStats.total_contacts) * 100 
                                  : 0}%`
                              }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="text-2xl font-bold text-red-600">
                            {audienceStats.rating_poor || Math.floor(audienceStats.quality_contacts * 0.1)}
                          </div>
                          <div className="text-sm font-medium text-red-700">Poor (0-39)</div>
                          <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                            <div 
                              className="bg-red-600 h-2 rounded-full" 
                              style={{
                                width: `${audienceStats.total_contacts > 0 ? 
                                  ((audienceStats.rating_poor || Math.floor(audienceStats.quality_contacts * 0.1)) / audienceStats.total_contacts) * 100 
                                  : 0}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Data Enrichment Status - Hidden for MVP readiness */}
                    {false && (
                    <div className="mt-4 bg-white p-4 rounded-lg shadow-sm border">
                      <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <span className="text-blue-500">🚀</span>
                        Enrichment & Enhancement Status
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-2xl font-bold text-blue-600">
                            {audienceStats.ai_categorized || 0}
                          </div>
                          <div className="text-sm font-medium text-blue-700">AI Categorized</div>
                          <div className="text-xs text-blue-600 mt-1">Smart segments ready</div>
                        </div>
                        
                        <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="text-2xl font-bold text-purple-600">
                            {audienceStats.social_profiles || 0}
                          </div>
                          <div className="text-sm font-medium text-purple-700">Social Profiles Found</div>
                          <div className="text-xs text-purple-600 mt-1">Ready for engagement</div>
                        </div>
                        
                        <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-2xl font-bold text-green-600">
                            {audienceStats.pending_enrichment || (audienceStats.total_contacts - (audienceStats.social_profiles || 0))}
                          </div>
                          <div className="text-sm font-medium text-green-700">Pending Enrichment</div>
                          <div className="text-xs text-green-600 mt-1">Available for processing</div>
                        </div>
                      </div>
                    </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Quality Audience Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Quality Audience Data
                  </CardTitle>
                  <CardDescription>
                    {audienceStats ? `${audienceStats.quality_contacts} quality contacts (only showing contacts with name, email, or phone)` : 'Loading audience data...'}
                  </CardDescription>
                  
                  {/* Action Buttons Row */}
                  <div className="flex items-center gap-2 mt-4 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDuplicateDetection(true)}
                      className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                      title="Detect and merge duplicate contacts"
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Detect Duplicates
                    </Button>
                    {user?.role === 'administrator' && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEnrichSocialProfiles(true)}
                          disabled={enrichmentInProgress || !enrichmentSettings.api_key_configured}
                          className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          {enrichmentInProgress ? 'Enriching...' : 'Enrich Social Profiles'}
                        </Button>
                        
                        {/* AI Audience Intelligence Actions - only show if enabled */}
                        {aiModelsConfig.audience_intelligence_enabled && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={runAIAudienceCategorization}
                              disabled={isLoading}
                              className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                            >
                              🤖 Categorize Audience
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={detectAIDuplicates}
                              disabled={isLoading}
                              className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                            >
                              🔍 Detect Duplicates
                            </Button>
                          </>
                        )}
                      </>
                    )}


                    {/* Export Options */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleExportAll}
                        disabled={isLoading}
                        className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleExportSelected}
                        disabled={isLoading || selectedRecords.size === 0}
                        className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export Selected ({selectedRecords.size})
                      </Button>
                    </div>

                    {/* Tag Management Buttons (only show when records are selected) */}
                    {selectedRecords.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTagDialog(true)}
                          disabled={isLoading}
                          className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                        >
                          <Tag className="h-4 w-4 mr-1" />
                          Add Tags ({selectedRecords.size})
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRemoveTagDialog(true)}
                          disabled={isLoading}
                          className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                        >
                          <Tag className="h-4 w-4 mr-1" />
                          Remove Tags ({selectedRecords.size})
                        </Button>
                      </div>
                    )}



                    {/* Delete Selected Button (only show when records are selected) */}
                    {selectedRecords.size > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete ({selectedRecords.size})
                      </Button>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  {(enhancedContacts && enhancedContacts.length > 0) ? (
                    <div className="space-y-4">
                      {/* Quality Audience Data Section */}
                      <div className="space-y-4">
                        {/* Section Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Quality Audience Data</h3>
                            <p className="text-sm text-gray-600">
                              {enhancedContacts.length} contacts with engagement scoring and advanced filtering
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {enhancedContacts.length} Imported
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Enhanced Contacts Advanced Table */}
                        <div className="space-y-4">
                          {/* Enhanced Table Controls */}
                          <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-4">
                              {/* Search */}
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <Input
                                  type="text"
                                  placeholder="Search contacts..."
                                  value={enhancedTableSearch}
                                  onChange={(e) => setEnhancedTableSearch(e.target.value)}
                                  className="pl-10 w-64"
                                />
                              </div>
                              
                              {/* NEW: Data Source Filter */}
                              <Select 
                                value={dataSourceFilter || 'all'} 
                                onValueChange={(value) => {
                                  const newFilter = value === 'all' ? null : value;
                                  setDataSourceFilter(newFilter);
                                  // Reload data with new filter
                                  loadEnhancedAudienceData(1, enhancedTableSearch, 0, 100, newFilter);
                                }}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue placeholder="Filter by Source" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Sources</SelectItem>
                                  <SelectItem value="crm">🔗 CRM Data Only</SelectItem>
                                  <SelectItem value="upload">📁 Import Data Only</SelectItem>
                                  <SelectItem value="manual">✋ Manual Data Only</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {/* Refresh Button */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={refreshEnhancedData}
                                disabled={isLoading}
                              >
                                <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                                Refresh
                              </Button>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* Column Manager */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setShowEnhancedColumnManager(true)}
                              >
                                <Layers className="h-4 w-4 mr-1" />
                                Columns ({enhancedVisibleColumns.size})
                              </Button>
                              
                              {/* Export Selected */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={exportSelectedContacts}
                                disabled={selectedContacts.size === 0}
                              >
                                <FileDown className="h-4 w-4 mr-1" />
                                Export ({selectedContacts.size})
                              </Button>
                              
                              {/* Delete Selected */}
                              {selectedContacts.size > 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={deleteSelectedContacts}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete ({selectedContacts.size})
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Enhanced Table */}
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  {/* Select All Checkbox */}
                                  <TableHead className="w-12">
                                    <input
                                      type="checkbox"
                                      checked={(() => {
                                        const { contacts: paginatedContacts } = getPaginatedContacts();
                                        if (paginatedContacts.length === 0) return false;
                                        return paginatedContacts.every(contact => selectedContacts.has(contact.id));
                                      })()}
                                      onChange={toggleSelectAll}
                                      className="rounded border-gray-300"
                                    />
                                  </TableHead>
                                  
                                  {/* Dynamic Column Headers */}
                                  {Array.from(enhancedVisibleColumns).map(columnKey => {
                                    const column = availableColumns.find(c => c.key === columnKey);
                                    if (!column) return null;
                                    
                                    return (
                                      <TableHead 
                                        key={columnKey}
                                        className="cursor-pointer hover:bg-gray-100"
                                        onClick={() => {
                                          if (enhancedSortConfig.key === columnKey) {
                                            setEnhancedSortConfig({
                                              key: columnKey,
                                              direction: enhancedSortConfig.direction === 'asc' ? 'desc' : 'asc'
                                            });
                                          } else {
                                            setEnhancedSortConfig({ key: columnKey, direction: 'asc' });
                                          }
                                        }}
                                      >
                                        <div className="flex items-center gap-1">
                                          {column.label}
                                          {enhancedSortConfig.key === columnKey && (
                                            enhancedSortConfig.direction === 'asc' ? 
                                            <ArrowUp className="h-3 w-3" /> : 
                                            <ArrowDown className="h-3 w-3" />
                                          )}
                                        </div>
                                      </TableHead>
                                    );
                                  })}
                                  
                                  {/* Actions Column */}
                                  <TableHead className="w-20">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(() => {
                                  const { contacts: paginatedContacts } = getPaginatedContacts();
                                  return paginatedContacts.map((contact, index) => (
                                    <TableRow 
                                      key={contact.id || index}
                                      className={selectedContacts.has(contact.id) ? 'bg-blue-50' : ''}
                                    >
                                      {/* Selection Checkbox */}
                                      <TableCell>
                                        <input
                                          type="checkbox"
                                          checked={selectedContacts.has(contact.id)}
                                          onChange={() => toggleContactSelection(contact.id)}
                                          className="rounded border-gray-300"
                                        />
                                      </TableCell>
                                      
                                      {/* Dynamic Data Columns */}
                                      {Array.from(enhancedVisibleColumns).map(columnKey => {
                                        const column = availableColumns.find(c => c.key === columnKey);
                                        if (!column) return null;
                                        
                                        let cellValue = contact[columnKey];
                                        
                                        // Handle different data types
                                        if (column.type === 'source_badge') {
                                          // Handle data source badge
                                          const dataSource = cellValue || 'upload'; // Default to upload for existing data
                                          let badgeText, badgeIcon, badgeStyle;
                                          
                                          switch(dataSource) {
                                            case 'crm':
                                              badgeText = 'CRM';
                                              badgeIcon = '🔗';
                                              badgeStyle = 'bg-green-100 text-green-700 border-green-200';
                                              break;
                                            case 'upload':
                                              badgeText = 'Import';
                                              badgeIcon = '📁';
                                              badgeStyle = 'bg-purple-100 text-purple-700 border-purple-200';
                                              break;
                                            case 'manual':
                                              badgeText = 'Manual';
                                              badgeIcon = '✋';
                                              badgeStyle = 'bg-gray-100 text-gray-700 border-gray-200';
                                              break;
                                            case 'api':
                                              badgeText = 'API';
                                              badgeIcon = '🔌';
                                              badgeStyle = 'bg-blue-100 text-blue-700 border-blue-200';
                                              break;
                                            default:
                                              badgeText = 'Unknown';
                                              badgeIcon = '❓';
                                              badgeStyle = 'bg-gray-100 text-gray-500 border-gray-200';
                                          }
                                          
                                          return (
                                            <TableCell key={columnKey}>
                                              <Badge className={`text-xs font-medium ${badgeStyle}`}>
                                                <span className="mr-1">{badgeIcon}</span>
                                                {badgeText}
                                              </Badge>
                                            </TableCell>
                                          );
                                        } else if (column.type === 'linked_section') {
                                          // Handle linked section columns
                                          const sectionData = contact.linked_sections?.[column.section];
                                          if (sectionData && Array.isArray(sectionData) && sectionData.length > 0) {
                                            return (
                                              <TableCell key={columnKey} className="max-w-48">
                                                <div className="flex flex-wrap gap-1">
                                                  {sectionData.slice(0, 3).map((entry, idx) => (
                                                    <Badge 
                                                      key={idx}
                                                      variant="outline" 
                                                      className="text-xs bg-purple-50 text-purple-700 border-purple-200"
                                                    >
                                                      {Object.keys(entry).filter(k => !['imported_at', 'source', 'batch_id'].includes(k)).slice(0, 1).map(key => 
                                                        `${key}: ${String(entry[key]).substring(0, 15)}${String(entry[key]).length > 15 ? '...' : ''}`
                                                      )}
                                                    </Badge>
                                                  ))}
                                                  {sectionData.length > 3 && (
                                                    <Badge variant="outline" className="text-xs text-gray-500">
                                                      +{sectionData.length - 3} more
                                                    </Badge>
                                                  )}
                                                </div>
                                              </TableCell>
                                            );
                                          } else {
                                            return (
                                              <TableCell key={columnKey}>
                                                <span className="text-gray-400 text-sm">-</span>
                                              </TableCell>
                                            );
                                          }
                                        } else if (column.type === 'array' && Array.isArray(cellValue)) {
                                          cellValue = cellValue.join(', ') || '-';
                                        } else if (column.type === 'date' && cellValue) {
                                          cellValue = new Date(cellValue).toLocaleDateString();
                                        } else if (column.type === 'number' && columnKey === 'total_score') {
                                          return (
                                            <TableCell key={columnKey}>
                                              <Badge variant={cellValue >= 80 ? 'default' : cellValue >= 60 ? 'secondary' : 'outline'}>
                                                {cellValue || 0}
                                              </Badge>
                                            </TableCell>
                                          );
                                        } else if (columnKey === 'category') {
                                          return (
                                            <TableCell key={columnKey}>
                                              <span className={`text-sm ${
                                                contact.total_score >= 80 ? 'text-red-600' : 
                                                contact.total_score >= 60 ? 'text-orange-600' : 
                                                contact.total_score >= 40 ? 'text-blue-600' : 'text-gray-600'
                                              }`}>
                                                {contact.total_score >= 80 ? '🔥 Hot' : 
                                                 contact.total_score >= 60 ? '🌟 Warm' : 
                                                 contact.total_score >= 40 ? '🔵 Cold' : '❄️ Frozen'}
                                              </span>
                                            </TableCell>
                                          );
                                        } else if (column.type === 'number') {
                                          cellValue = cellValue || 0;
                                        } else {
                                          cellValue = cellValue || '-';
                                        }
                                        
                                        // Return regular cell for non-special columns
                                        if (column.type !== 'linked_section' && columnKey !== 'total_score' && columnKey !== 'category') {
                                          return (
                                            <TableCell 
                                              key={columnKey}
                                              className={columnKey === 'full_name' ? 'font-medium' : ''}
                                            >
                                              {cellValue}
                                            </TableCell>
                                          );
                                        }
                                      })}
                                      
                                      {/* Actions Column */}
                                      <TableCell>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openLinkedSections(contact)}
                                          >
                                            <Eye className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              // TODO: Implement edit functionality
                                              toast.info('🚧 Edit feature coming soon');
                                            }}
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ));
                                })()}
                              </TableBody>
                            </Table>
                          </div>
                          
                          {/* Pagination Controls */}
                          {(() => {
                            const { totalCount, totalPages } = getPaginatedContacts();
                            if (totalPages <= 1 && totalCount <= enhancedPageSize) return null;
                            
                            return (
                              <div className="flex items-center justify-between p-4 bg-gray-50 border-t">
                                <div className="flex items-center gap-4">
                                  {/* Rows per page selector */}
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">Rows per page:</span>
                                    <select
                                      value={enhancedPageSize}
                                      onChange={(e) => {
                                        setEnhancedPageSize(parseInt(e.target.value));
                                        setEnhancedCurrentPage(1); // Reset to first page
                                      }}
                                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value={10}>10</option>
                                      <option value={25}>25</option>
                                      <option value={50}>50</option>
                                      <option value={100}>100</option>
                                      <option value={250}>250</option>
                                    </select>
                                  </div>
                                  
                                  {/* Page info */}
                                  <div className="text-sm text-gray-600">
                                    Showing {((enhancedCurrentPage - 1) * enhancedPageSize) + 1} to {Math.min(enhancedCurrentPage * enhancedPageSize, totalCount)} of {totalCount} contacts
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEnhancedCurrentPage(1)}
                                    disabled={enhancedCurrentPage === 1}
                                  >
                                    First
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEnhancedCurrentPage(enhancedCurrentPage - 1)}
                                    disabled={enhancedCurrentPage === 1}
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  
                                  {/* Page Numbers */}
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const pageNum = Math.max(1, enhancedCurrentPage - 2) + i;
                                    if (pageNum > totalPages) return null;
                                    
                                    return (
                                      <Button
                                        key={pageNum}
                                        variant={pageNum === enhancedCurrentPage ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => {
                                          setEnhancedCurrentPage(pageNum);
                                        }}
                                      >
                                        {pageNum}
                                      </Button>
                                    );
                                  })}
                                  
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEnhancedCurrentPage(enhancedCurrentPage + 1)}
                                    disabled={enhancedCurrentPage === totalPages}
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEnhancedCurrentPage(totalPages)}
                                    disabled={enhancedCurrentPage === totalPages}
                                  >
                                    Last
                                  </Button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (audienceData && audienceData.length > 0) ? (
                    <div className="space-y-4">
                      {/* Search Bar */}
                      <div className="flex items-center gap-4">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search audience data..."
                            value={audienceSearch}
                            onChange={handleSearchInput}
                            onKeyPress={handleSearchKeyPress}
                            className="pl-10"
                          />
                        </div>
                        <Button 
                          onClick={handleSearchSubmit}
                          disabled={isLoading}
                          size="sm"
                        >
                          <Search className="h-4 w-4 mr-1" />
                          Search
                        </Button>
                      </div>

                      {/* Enhanced Data Table with Frozen Columns */}
                      <div className="rounded-md border bg-white shadow-sm">
                        <div className="relative overflow-x-auto">
                          <Table className="w-full">
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                {/* FROZEN COLUMNS - Always Visible */}
                                <TableHead className="sticky left-0 z-20 bg-gray-50 border-r-2 border-gray-200 w-12 shadow-md">
                                  <input
                                    type="checkbox"
                                    checked={selectAllChecked}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setSelectAllChecked(checked);
                                      if (checked) {
                                        const newSelected = new Set([...selectedRecords]);
                                        audienceData.forEach(record => {
                                          newSelected.add(record.id);
                                        });
                                        setSelectedRecords(newSelected);
                                      } else {
                                        const newSelected = new Set(selectedRecords);
                                        audienceData.forEach(record => {
                                          newSelected.delete(record.id);
                                        });
                                        setSelectedRecords(newSelected);
                                      }
                                    }}
                                    className="rounded border-gray-300"
                                  />
                                </TableHead>
                                
                                <TableHead className="sticky left-12 z-20 bg-gray-50 border-r-2 border-gray-200 w-20 shadow-md">
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xs font-semibold text-blue-600">Rating</span>
                                    <div className="cursor-help" title="Contact Quality Score (0-100) - Based on data completeness and validity">
                                      ℹ️
                                    </div>
                                  </div>
                                </TableHead>
                                
                                <TableHead className="sticky left-32 z-20 bg-gray-50 border-r-2 border-gray-200 w-24 shadow-md">
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xs font-semibold text-green-600">Interactions</span>
                                    <div className="cursor-help" title="Number of data interactions and updates">
                                      📊
                                    </div>
                                  </div>
                                </TableHead>
                                
                                <TableHead className="sticky left-56 z-20 bg-gray-50 border-r-4 border-blue-200 w-32 shadow-lg">
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xs font-semibold text-purple-600">Social Profiles</span>
                                    <div className="cursor-help" title="Social media profiles and links">
                                      🔗
                                    </div>
                                  </div>
                                </TableHead>
                                
                                {/* SCROLLABLE COLUMNS - Dynamic Data Fields */}
                                {selectedColumns.map((field, index) => (
                                  <TableHead key={field} className={`font-semibold text-gray-700 min-w-[120px] ${
                                    field.startsWith('ai_') ? 'bg-blue-50 text-blue-700 border-l border-blue-200' : ''
                                  }`}>
                                    <div className="flex items-center justify-between">
                                      <span className="truncate">
                                        {field.startsWith('ai_') && '🤖 '}
                                        {field.replace('ai_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </span>
                                      {field.startsWith('ai_') && (
                                        <div className="text-xs bg-blue-100 text-blue-600 px-1 py-0.5 rounded ml-1">
                                          AI
                                        </div>
                                      )}
                                    </div>
                                  </TableHead>
                                ))}
                                
                                {/* Last Updated column */}
                                <TableHead className="w-32 text-gray-600">Last Updated</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {audienceData.map((contact, rowIndex) => (
                                <TableRow 
                                  key={contact.id} 
                                  className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/50 transition-colors`}
                                >
                                  {/* FROZEN COLUMNS */}
                                  <TableCell className="sticky left-0 z-10 bg-inherit border-r-2 border-gray-200 shadow-md">
                                    <input
                                      type="checkbox"
                                      checked={selectedRecords.has(contact.id)}
                                      onChange={(e) => {
                                        const newSelected = new Set(selectedRecords);
                                        if (e.target.checked) {
                                          newSelected.add(contact.id);
                                        } else {
                                          newSelected.delete(contact.id);
                                        }
                                        setSelectedRecords(newSelected);
                                        const allCurrentPageSelected = audienceData.every(record => newSelected.has(record.id));
                                        setSelectAllChecked(allCurrentPageSelected);
                                      }}
                                      className="rounded border-gray-300"
                                    />
                                  </TableCell>
                                  
                                  <TableCell className="sticky left-12 z-10 bg-inherit border-r-2 border-gray-200 shadow-md">
                                    <div className="flex items-center justify-center">
                                      {(() => {
                                        const rating = contact.contact_rating || contact.data_quality_score || 0;
                                        const color = rating >= 80 ? 'text-green-600 bg-green-100' : 
                                                     rating >= 60 ? 'text-yellow-600 bg-yellow-100' : 
                                                     rating >= 40 ? 'text-orange-600 bg-orange-100' : 'text-red-600 bg-red-100';
                                        return (
                                          <div className={`px-2 py-1 rounded-full text-xs font-bold ${color} min-w-[40px] text-center`}>
                                            {Math.round(rating)}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </TableCell>
                                  
                                  <TableCell className="sticky left-32 z-10 bg-inherit border-r-2 border-gray-200 shadow-md text-center">
                                    <div className="flex items-center justify-center">
                                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                                        {contact.interaction_count || 1}
                                      </span>
                                    </div>
                                  </TableCell>
                                  
                                  <TableCell className="sticky left-56 z-10 bg-inherit border-r-4 border-blue-200 shadow-lg">
                                    <div className="flex items-center space-x-1 justify-center">
                                      {/* Placeholder for social profiles - will be populated by Apollo.io later */}
                                      {contact.linkedin_url ? (
                                        <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                                          <div className="w-6 h-6 bg-blue-600 text-white rounded flex items-center justify-center text-xs">
                                            in
                                          </div>
                                        </a>
                                      ) : null}
                                      {contact.twitter_url ? (
                                        <a href={contact.twitter_url} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                                          <div className="w-6 h-6 bg-sky-400 text-white rounded flex items-center justify-center text-xs">
                                            𝕏
                                          </div>
                                        </a>
                                      ) : null}
                                      {contact.facebook_url ? (
                                        <a href={contact.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                                          <div className="w-6 h-6 bg-blue-800 text-white rounded flex items-center justify-center text-xs">
                                            f
                                          </div>
                                        </a>
                                      ) : null}
                                      {!contact.linkedin_url && !contact.twitter_url && !contact.facebook_url && (
                                        <div className="text-gray-400 text-xs px-2 py-1 bg-gray-100 rounded">
                                          {contact.needs_social_enrichment ? '⏳ Pending' : 'Not Found'}
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  
                                  {/* SCROLLABLE DATA COLUMNS */}
                                  {selectedColumns.filter(field => field && typeof field === 'string').map(field => (
                                    <TableCell key={field} className={`text-sm max-w-[200px] ${
                                      field.startsWith('ai_') ? 'bg-blue-50/30' : ''
                                    }`}>
                                      <div className="truncate">
                                        {(() => {
                                          const value = contact[field];
                                          if (!value || value === 'null' || value === '') return <span className="text-gray-400">-</span>;
                                          
                                          // Special formatting for AI fields
                                          if (field.startsWith('ai_')) {
                                            return (
                                              <span className="text-blue-700 font-medium">
                                                {value.toString().length > 50 ? `${value.toString().substring(0, 50)}...` : value}
                                              </span>
                                            );
                                          }
                                          
                                          // Email formatting
                                          if (field && typeof field === 'string' && field.toLowerCase().includes('email')) {
                                            return <a href={`mailto:${value}`} className="text-blue-600 hover:underline">{value}</a>;
                                          }
                                          
                                          // Phone formatting with international + prefix
                                          if (field && typeof field === 'string' && (field.toLowerCase().includes('phone') || field.toLowerCase().includes('mobile'))) {
                                            const formattedPhone = formatPhoneNumber(value);
                                            return <a href={`tel:${formattedPhone}`} className="text-green-600 hover:underline">{formattedPhone}</a>;
                                          }
                                          
                                          // URL formatting
                                          if (field && typeof field === 'string' && (field.toLowerCase().includes('website') || field.toLowerCase().includes('url'))) {
                                            return <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{value}</a>;
                                          }
                                          
                                          return <span title={value.toString()}>{value.toString().length > 30 ? `${value.toString().substring(0, 30)}...` : value}</span>;
                                        })()}
                                      </div>
                                    </TableCell>
                                  ))}
                                  
                                  <TableCell className="text-xs text-gray-500">
                                    {contact.last_updated ? new Date(contact.last_updated).toLocaleDateString() : new Date(contact.created_at).toLocaleDateString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Enhanced Pagination with Records Per Page */}
                      {audiencePagination && (
                        <div className="flex items-center justify-between pt-4 border-t">
                          {/* Records Per Page Selector */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select 
                              value={recordsPerPage} 
                              onChange={(e) => handleRecordsPerPageChange(parseInt(e.target.value))}
                              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                            <span className="text-sm text-gray-600">records per page</span>
                          </div>

                          {/* Pagination Info */}
                          <div className="text-sm text-gray-600">
                            Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, audiencePagination.total_records)} of {audiencePagination.total_records} entries
                          </div>
                        </div>
                      )}

                      {/* Pagination */}
                      {audiencePagination && audiencePagination.total_pages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadAudienceData(currentPage - 1, audienceSearch)}
                            disabled={currentPage <= 1 || isLoading}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                          </Button>
                          
                          <div className="flex items-center gap-2">
                            {Array.from({ length: Math.min(5, audiencePagination.total_pages) }, (_, i) => {
                              const page = Math.max(1, Math.min(
                                audiencePagination.total_pages - 4,
                                Math.max(1, currentPage - 2)
                              )) + i;
                              
                              if (page > audiencePagination.total_pages) return null;
                              
                              return (
                                <Button
                                  key={page}
                                  variant={page === currentPage ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => loadAudienceData(page, audienceSearch)}
                                  disabled={isLoading}
                                  className="w-10 h-10"
                                >
                                  {page}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadAudienceData(currentPage + 1, audienceSearch)}
                            disabled={currentPage >= audiencePagination.total_pages || isLoading}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          
                          <span className="text-sm text-gray-500 ml-4">
                            Page {currentPage} of {audiencePagination.total_pages}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No audience data found</h3>
                      <p className="text-gray-500 mb-4">
                        {audienceSearch 
                          ? `No results found for "${audienceSearch}". Try adjusting your search terms.`
                          : 'Upload your first audience data file to get started with targeted campaigns.'
                        }
                      </p>
                      {audienceSearch && (
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setAudienceSearch('');
                            loadAudienceData(1, '');
                          }}
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Legacy Upload - Hidden in favor of Next-Gen Import Wizard */}
              {false && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Legacy Audience Data Upload (Deprecated)
                  </CardTitle>
                  <CardDescription>
                    This upload method has been replaced by the Next-Gen Import Wizard above
                  </CardDescription>
                  
                  {/* Enhanced Features Overview */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-3">Enhanced Intelligence Features:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                      <div className="space-y-1">
                        <div>• Smart Header Mapping: Fuzzy matching with confidence scoring</div>
                        <div>• Age/DOB Processing: Automatic calculations and verifications</div>
                        <div>• Custom Tags: Multi-tag system for contact organization</div>
                      </div>
                      <div className="space-y-1">
                        <div>• Uncertain Mapping: User confirmation for ambiguous headers</div>
                        <div>• Data Processing: Intelligent duplicate merging</div>
                        <div>• Quality Scoring: Enhanced data quality analysis</div>
                      </div>
                    </div>
                  </div>

                  {/* Schema Information */}
                  {audienceSchema && (
                    <div className="mt-4 p-3 bg-blue-50 rounded border">
                      <h4 className="text-sm font-medium mb-2 text-blue-800">📊 Standardized Schema (34 fields including Custom Tags):</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                        <div className="text-blue-700">
                          <strong>Demographics:</strong> Name, Age, Gender, Education, etc.
                        </div>
                        <div className="text-green-700">
                          <strong>Contact Info:</strong> Email, Mobile, WhatsApp, Preferences
                        </div>
                        <div className="text-purple-700">
                          <strong>Geographic:</strong> Country, State, City, Timezone
                        </div>
                        <div className="text-orange-700">
                          <strong>Professional:</strong> Company, Industry, Position
                        </div>
                        <div className="text-red-700">
                          <strong>Behavioral:</strong> Interests, Lifestyle, Personality
                        </div>
                        <div className="text-teal-700">
                          <strong>Sales/Revenue:</strong> Purchase History, CLV, Preferences
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <div className="text-yellow-800 text-xs font-medium">
                          🏷️ <strong>NEW: Custom Tags</strong> - Multi-tag system for flexible contact categorization
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        ✅ <strong>Intelligent Mapping:</strong> AI-powered header matching with confidence scoring
                        <br />
                        ✅ <strong>Age/DOB Smart Processing:</strong> Automatic calculations and consistency verification
                        <br />
                        ✅ <strong>Minimum Required:</strong> Full Name + (Email OR Phone Number)
                        <br />
                        ✅ <strong>Custom Fields + Tags:</strong> Unmapped columns become custom fields, tag support included
                      </p>
                    </div>
                  )}

                  {/* Template Download Section */}
                  <div className="mt-4 p-3 bg-gray-50 rounded border">
                    <h4 className="text-sm font-medium mb-2">📥 Download Enhanced Templates:</h4>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadStandardizedTemplate('csv')}
                        className="text-xs"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        CSV Template (34 fields)
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadStandardizedTemplate('excel')}
                        className="text-xs"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Excel Template (34 fields)
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Enhanced Intelligent Mapping Dialog */}
                  {showMappingDialog && mappingPreview ? (
                    <div className="space-y-6">
                      {/* Intelligent Mapping Results */}
                      <div className="space-y-4">
                        <h3 className="font-medium flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Intelligent Header Mapping Results
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div className="p-3 bg-green-100 rounded">
                            <div className="text-2xl font-bold text-green-700">
                              {mappingPreview.recommendations?.auto_mapped_count || 0}
                            </div>
                            <p className="text-xs text-green-600">Auto-Mapped</p>
                          </div>
                          <div className="p-3 bg-yellow-100 rounded">
                            <div className="text-2xl font-bold text-yellow-700">
                              {mappingPreview.recommendations?.uncertain_count || 0}
                            </div>
                            <p className="text-xs text-yellow-600">Need Confirmation</p>
                          </div>
                          <div className="p-3 bg-orange-100 rounded">
                            <div className="text-2xl font-bold text-orange-700">
                              {mappingPreview.recommendations?.unmapped_count || 0}
                            </div>
                            <p className="text-xs text-orange-600">Custom Fields</p>
                          </div>
                          <div className="p-3 bg-blue-100 rounded">
                            <div className="text-2xl font-bold text-blue-700">
                              {mappingPreview.total_headers || 0}
                            </div>
                            <p className="text-xs text-blue-600">Total Headers</p>
                          </div>
                        </div>
                      </div>

                      {/* Confidence-Based Mapping Display */}
                      <div className="grid gap-4">
                        {/* Automatic Mappings */}
                        {mappingPreview.mapping_result?.mapped_fields && Object.keys(mappingPreview.mapping_result.mapped_fields).length > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded p-4">
                            <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                              ✅ Automatically Mapped Fields ({Object.keys(mappingPreview.mapping_result.mapped_fields).length})
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(mappingPreview.mapping_result.mapped_fields).map(([header, standardField]) => (
                                <div key={header} className="flex items-center justify-between p-2 bg-white rounded border">
                                  <span className="text-sm font-medium">{header}</span>
                                  <span className="text-xs text-gray-500">→</span>
                                  <span className="text-sm text-green-700 font-medium">{standardField.replace('_', ' ')}</span>
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">100%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Uncertain Mappings - Need User Confirmation */}
                        {mappingPreview.mapping_result?.uncertain_mappings && mappingPreview.mapping_result.uncertain_mappings.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <h4 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
                              🤔 Please Confirm These Mappings ({mappingPreview.mapping_result.uncertain_mappings.length})
                            </h4>
                            <div className="space-y-3">
                              {mappingPreview.mapping_result.uncertain_mappings.map((header) => {
                                const suggestions = mappingPreview.mapping_result.suggested_mappings?.[header] || [];
                                const confidence = mappingPreview.confidence_levels?.[header];
                                return (
                                  <div key={header} className="p-3 bg-white rounded border">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-gray-800">"{header}"</span>
                                      {confidence && (
                                        <span className={`px-2 py-1 text-xs rounded ${
                                          confidence.level === 'high' ? 'bg-green-100 text-green-700' :
                                          confidence.level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          {Math.round(confidence.confidence_score * 100)}% confidence
                                        </span>
                                      )}
                                    </div>
                                    <div className="space-y-2">
                                      {suggestions.slice(0, 3).map(([standardField, score]) => (
                                        <label key={standardField} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`mapping-${header}`}
                                            value={standardField}
                                            onChange={(e) => {
                                              setUserConfirmations(prev => ({
                                                ...prev,
                                                [header]: e.target.value
                                              }));
                                            }}
                                            className="w-4 h-4 text-blue-600"
                                          />
                                          <span className="flex-1 text-sm">{standardField.replace('_', ' ')}</span>
                                          <span className="text-xs text-gray-500">{Math.round(score * 100)}%</span>
                                        </label>
                                      ))}
                                      <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`mapping-${header}`}
                                          value="custom_field"
                                          onChange={(e) => {
                                            setUserConfirmations(prev => ({
                                              ...prev,
                                              [header]: 'custom_field'
                                            }));
                                            setShowCustomFieldInputs(prev => ({
                                              ...prev,
                                              [header]: true
                                            }));
                                            // Initialize with original header name
                                            setCustomFieldNames(prev => ({
                                              ...prev,
                                              [header]: header
                                            }));
                                          }}
                                          className="w-4 h-4 text-purple-600"
                                        />
                                        <span className="flex-1 text-sm text-purple-700">Keep as Custom Field</span>
                                        <span className="text-xs text-purple-500">Custom</span>
                                      </label>
                                      
                                      {/* Custom Field Name Input - Show when "Keep as Custom Field" is selected */}
                                      {userConfirmations[header] === 'custom_field' && showCustomFieldInputs[header] && (
                                        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                          <div className="mb-2">
                                            <label className="block text-sm font-medium text-purple-800 mb-1">
                                              🏷️ Create Custom Header Name:
                                            </label>
                                            <Input
                                              type="text"
                                              value={customFieldNames[header] || header}
                                              onChange={(e) => {
                                                setCustomFieldNames(prev => ({
                                                  ...prev,
                                                  [header]: e.target.value
                                                }));
                                              }}
                                              placeholder={`Enter custom name for "${header}"`}
                                              className="w-full border-purple-300 focus:border-purple-500"
                                            />
                                          </div>
                                          <div className="text-xs text-purple-600 bg-purple-100 p-2 rounded">
                                            💡 <strong>Tip:</strong> Create a meaningful name that helps you track this data (e.g., "Lead Score", "Campaign Source", "Custom Priority")
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Custom Fields */}
                        {mappingPreview.mapping_result?.unmapped_headers && mappingPreview.mapping_result.unmapped_headers.length > 0 && (
                          <div className="bg-purple-50 border border-purple-200 rounded p-4">
                            <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                              🎨 Custom Fields ({mappingPreview.mapping_result.unmapped_headers.length})
                            </h4>
                            <p className="text-sm text-purple-700 mb-2">These headers will become custom fields in your database:</p>
                            <div className="flex flex-wrap gap-2">
                              {mappingPreview.mapping_result.unmapped_headers.map((header) => (
                                <span key={header} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                                  {header}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Data Preview with Enhanced Processing */}
                      {mappingPreview.data_preview && mappingPreview.data_preview.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-4">
                          <h4 className="font-medium text-blue-800 mb-3">📊 Enhanced Data Preview (First 3 Records)</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  {mappingPreview.headers && mappingPreview.headers.slice(0, 6).map((header) => (
                                    <th key={header} className="text-left p-2 font-medium">{header}</th>
                                  ))}
                                  <th className="text-left p-2 font-medium text-green-700">Age Processing</th>
                                  <th className="text-left p-2 font-medium text-purple-700">Tags</th>
                                </tr>
                              </thead>
                              <tbody>
                                {mappingPreview.data_preview.map((row, index) => (
                                  <tr key={index} className="border-b">
                                    {mappingPreview.headers && mappingPreview.headers.slice(0, 6).map((header) => (
                                      <td key={header} className="p-2">
                                        {row[header] ? String(row[header]).substring(0, 20) + (String(row[header]).length > 20 ? '...' : '') : '-'}
                                      </td>
                                    ))}
                                    <td className="p-2 text-green-700">
                                      {row.age_verification ? (
                                        <span className="px-2 py-1 bg-green-100 rounded text-xs">
                                          {row.age_verification}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="p-2 text-purple-700">
                                      {row.custom_tags && row.custom_tags.length > 0 ? (
                                        <span className="px-2 py-1 bg-purple-100 rounded text-xs">
                                          {row.custom_tags.length} tag(s)
                                        </span>
                                      ) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => {
                            const finalMappings = {
                              ...mappingPreview.mapping_result.mapped_fields,
                              ...userConfirmations
                            };
                            
                            const customMappings = {};
                            Object.entries(userConfirmations).forEach(([header, mapping]) => {
                              if (mapping === 'custom_field') {
                                // Use the custom field name if provided, otherwise use original header
                                const customName = customFieldNames[header] || header;
                                customMappings[header] = customName;
                              }
                            });
                            
                            uploadWithIntelligentMapping(finalMappings, customMappings);
                          }}
                          disabled={
                            isLoading || 
                            !mappingPreview.recommendations?.can_proceed ||
                            (mappingPreview.recommendations?.needs_user_input && 
                             mappingPreview.mapping_result?.uncertain_mappings?.some(header => !userConfirmations[header]))
                          }
                          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        >
                          {isLoading ? 'Processing...' : '🚀 Upload with Intelligent Processing'}
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowMappingDialog(false);
                            setUploadFile(null);
                            setUserConfirmations({});
                            setCustomFieldNames({});
                            setShowCustomFieldInputs({});
                          }}
                        >
                          Cancel
                        </Button>
                      </div>

                      {/* Requirements Status */}
                      <div className="text-sm">
                        {mappingPreview.recommendations?.can_proceed ? (
                          <div className="text-green-600 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            Ready to upload! All requirements met.
                          </div>
                        ) : (
                          <div className="text-red-600 flex items-center gap-2">
                            <X className="h-4 w-4" />
                            Missing required fields: {mappingPreview.mapping_result?.missing_required?.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Upload Section */}
                      <div className="space-y-4">
                        <h3 className="font-medium flex items-center gap-2">
                          <Upload className="h-4 w-4" />
                          Intelligent File Upload
                        </h3>
                        <div className="flex items-center justify-center w-full">
                          <label htmlFor="audience-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Upload className="w-8 h-8 mb-2 text-gray-400" />
                              <p className="mb-2 text-sm text-gray-600">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">CSV or Excel files (MAX 10MB)</p>
                            </div>
                            <input 
                              id="audience-file" 
                              type="file" 
                              className="hidden" 
                              accept=".csv,.xlsx,.xls"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setUploadFile(file);
                                  try {
                                    await previewIntelligentMapping(file);
                                  } catch (error) {
                                    setUploadFile(null);
                                  }
                                }
                              }}
                            />
                          </label>
                          
                          {uploadFile && (
                            <div className="p-3 bg-blue-100 rounded border">
                              <div className="flex items-center gap-2">
                                <File className="h-4 w-4 text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">{uploadFile.name}</span>
                                <span className="text-xs text-blue-600">
                                  ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Upload Statistics Display */}
                      {uploadStats && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            Upload Results - Intelligent Processing Complete!
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-700">{uploadStats.new_contacts}</div>
                              <div className="text-xs text-green-600">New Contacts</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-blue-700">{uploadStats.duplicates_merged}</div>
                              <div className="text-xs text-blue-600">Duplicates Merged</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-orange-700">{uploadStats.records_with_age_calculation}</div>
                              <div className="text-xs text-orange-600">Age Calculated</div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-purple-700">{uploadStats.records_with_tags}</div>
                              <div className="text-xs text-purple-600">Records Tagged</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Audience Operations Undo System - Moved to Bottom */}
              {recentOperations.length > 0 && (
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-blue-700">
                      <Undo2 className="h-5 w-5" />
                      Recent Operations - Undo Available
                    </CardTitle>
                    <CardDescription className="text-blue-600">
                      You can undo your last {recentOperations.length} audience operations. Operations expire after 30 days.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {recentOperations.map((operation, index) => (
                        <div key={operation.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {operation.operation_type.replace('_', ' ').toUpperCase()}
                              </Badge>
                              <span className="font-medium text-sm">{operation.operation_name}</span>
                            </div>
                            <p className="text-sm text gray-600 mt-1">{operation.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {operation.records_count} contacts affected
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(operation.created_at).toLocaleString()}
                              </span>
                              {operation.days_remaining !== null && (
                                <span className="flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {operation.days_remaining} days left to undo
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => confirmUndo(operation)}
                            variant="outline"
                            size="sm"
                            className="ml-4 text-blue-600 border-blue-300 hover:bg-blue-50"
                            disabled={!operation.can_undo}
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            Undo
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Auto-refresh button */}
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <Button
                        onClick={loadRecentOperations}
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Refresh Operations
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
            
          {/* eslint-disable-next-line */}
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns">
            <div className="space-y-6">
              {/* Campaign content will be implemented here */}
            </div>
          </TabsContent>

          {/* SEO / AEO Tab */}
          <TabsContent value="seo-aeo">
            <div className="space-y-6">
              {/* SEO/AEO Dashboard Header */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Search className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-blue-800">SEO & AEO Intelligence Hub</CardTitle>
                        <CardDescription className="text-blue-600">
                          AI-Powered Search Engine & Agentic Engine Optimization Platform
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {currentEcommerceConnection ? (
                        <Button
                          onClick={() => {
                            setActiveTab('settings');
                            setActiveSettingsTab('ecommerce-integration');
                          }}
                          variant="outline"
                          size="sm"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Manage Connection
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setActiveTab('settings');
                            setActiveSettingsTab('ecommerce-integration');
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Connect Website
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Connection Status Information - Integrated */}
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <div className="flex items-center gap-4">
                          {currentEcommerceConnection ? (
                            <>
                              <span className="text-sm font-medium text-green-700">
                                ✅ Connected: {currentEcommerceConnection.store_url}
                              </span>
                              <span className="text-xs text-gray-600">
                                Platform: {currentEcommerceConnection.platform || 'Website'}
                              </span>
                              <span className="text-xs text-gray-600">
                                Connected: {new Date(currentEcommerceConnection.created_at).toLocaleDateString()}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-red-600">
                              ❌ No website connected
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* SEO Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">SEO Score</CardTitle>
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-900">
                      {currentSEOAudit ? `${currentSEOAudit.overall_score}%` : '--'}
                    </div>
                    <p className="text-xs text-blue-600">Overall health</p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">Issues Found</CardTitle>
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-900">
                      {currentSEOAudit ? currentSEOAudit.total_issues : '--'}
                    </div>
                    <p className="text-xs text-blue-600">
                      {currentSEOAudit && currentSEOAudit.critical_issues ? 
                        `${currentSEOAudit.critical_issues.length} critical` : 
                        'Technical issues'
                      }
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">Performance</CardTitle>
                    <Zap className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-900">
                      {currentSEOAudit ? `${currentSEOAudit.web_performance_score}%` : '--'}
                    </div>
                    <p className="text-xs text-blue-600">Web vitals</p>
                  </CardContent>
                </Card>
                
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium text-blue-800">Last Audit</CardTitle>
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-900">
                      {currentSEOAudit ? 
                        new Date(currentSEOAudit.audit_date).toLocaleDateString().split('/')[1] + '/' + 
                        new Date(currentSEOAudit.audit_date).toLocaleDateString().split('/')[2] :
                        '--'
                      }
                    </div>
                    <p className="text-xs text-blue-600">
                      {currentSEOAudit ? 
                        `${Math.round((new Date() - new Date(currentSEOAudit.audit_date)) / (1000 * 60 * 60 * 24))} days ago` :
                        'Never run'
                      }
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>
                    Start your SEO optimization journey with these core features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Keyword Import & Storage */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                          <Upload className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Keyword Import & Storage</h3>
                          <p className="text-sm text-gray-600">Import and store keywords from external tools</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Import keywords from Ubersuggest, Google Keyword Planner, SEMrush, or enter manually to store in your database
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={openKeywordResearchDialog}
                      >
                        Import Keywords
                      </Button>
                    </div>

                    {/* Competitor Analysis */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                          <Users className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Competitor Intel</h3>
                          <p className="text-sm text-gray-600">Analyze competitor strategies</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Discover competitor keywords, content gaps, and backlink opportunities
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setShowCompetitorAnalysisDialog(true)}
                      >
                        Analyze Competitors
                      </Button>
                    </div>

                    {/* Content Optimization */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Content Optimization</h3>
                          <p className="text-sm text-gray-600">AI-powered content scoring</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Optimize your content for search engines and AI models
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setShowContentAnalysisDialog(true)}
                      >
                        Analyze Content
                      </Button>
                    </div>

                    {/* Technical SEO */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                          <Settings className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Technical Audit</h3>
                          <p className="text-sm text-gray-600">Site health monitoring</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Identify technical issues affecting your search performance
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setShowTechnicalAuditDialog(true)}
                      >
                        Run Audit
                      </Button>
                    </div>

                    {/* AEO Optimization */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                          <Brain className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">AEO Optimization</h3>
                          <p className="text-sm text-gray-600">AI search engine optimization</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Optimize for ChatGPT, Claude, Perplexity, and other AI engines
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setShowAeoOptimizationDialog(true)}
                      >
                        Optimize for AI
                      </Button>
                    </div>

                    {/* Website Integration */}
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                          <Globe className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Website Integration</h3>
                          <p className="text-sm text-gray-600">Connect your e-commerce site</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">
                        Analyze your existing website from e-commerce integration
                      </p>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => setActiveSettingsTab('ecommerce-integration')}
                      >
                        Connect Website
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SEO Audit System */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Search className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-blue-800">Comprehensive SEO Audit</CardTitle>
                        <CardDescription className="text-blue-600">
                          60-Step Technical SEO Analysis for Your Website
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {currentEcommerceConnection ? (
                        <Button
                          onClick={() => startSEOAudit()}
                          disabled={isAuditRunning}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {isAuditRunning ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Running Audit... ({Math.round(auditProgress)}%)
                            </>
                          ) : (
                            <>
                              <Zap className="h-4 w-4 mr-2" />
                              Run SEO Audit
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="text-center">
                          <Button
                            onClick={() => {
                              setActiveTab('settings');
                              setActiveSettingsTab('ecommerce-integration');
                            }}
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Connect Website to Start Audit
                          </Button>
                          <p className="text-xs text-gray-500 mt-1">Connect your website in Settings first</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {/* Current Audit Results */}
                {currentSEOAudit && (
                  <CardContent className="pt-0">
                    <div className="bg-white rounded-lg border border-blue-200 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                            currentSEOAudit.overall_score >= 80 ? 'bg-green-100 text-green-800' :
                            currentSEOAudit.overall_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {currentSEOAudit.overall_score}%
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">Latest Audit Results</h3>
                            <p className="text-sm text-gray-600">
                              {new Date(currentSEOAudit.audit_date).toLocaleDateString()} • 
                              {currentSEOAudit.total_issues} issues found • 
                              {currentSEOAudit.critical_issues.length} critical
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setSelectedAuditId(currentSEOAudit.id);
                              setShowAuditDetails(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                          <Button
                            onClick={() => exportAuditPDF(currentSEOAudit.id)}
                            disabled={isLoading}
                            variant="outline"
                            size="sm"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                          >
                            {isLoading ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <FileDown className="h-3 w-3 mr-1" />
                            )}
                            Export PDF
                          </Button>
                        </div>
                      </div>
                      
                      {/* Category Scores */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.crawling_indexing_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.crawling_indexing_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.crawling_indexing_score}%
                          </div>
                          <div className="text-xs text-gray-600">Crawling</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.web_performance_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.web_performance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.web_performance_score}%
                          </div>
                          <div className="text-xs text-gray-600">Performance</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.internal_linking_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.internal_linking_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.internal_linking_score}%
                          </div>
                          <div className="text-xs text-gray-600">Linking</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.javascript_seo_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.javascript_seo_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.javascript_seo_score}%
                          </div>
                          <div className="text-xs text-gray-600">JavaScript</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.structured_data_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.structured_data_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.structured_data_score}%
                          </div>
                          <div className="text-xs text-gray-600">Schema</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <div className={`text-sm font-semibold ${
                            currentSEOAudit.international_seo_score >= 80 ? 'text-green-600' :
                            currentSEOAudit.international_seo_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {currentSEOAudit.international_seo_score}%
                          </div>
                          <div className="text-xs text-gray-600">International</div>
                        </div>
                      </div>
                      
                      {/* Critical Issues */}
                      {currentSEOAudit.critical_issues.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Critical Issues to Fix
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1">
                            {currentSEOAudit.critical_issues.slice(0, 3).map((issue, index) => (
                              <li key={index} className="flex items-center gap-2">
                                <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                                {issue}
                              </li>
                            ))}
                            {currentSEOAudit.critical_issues.length > 3 && (
                              <li className="text-xs text-red-600">
                                +{currentSEOAudit.critical_issues.length - 3} more issues
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      {/* Next Audit Recommendation */}
                      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                        <span>
                          Next recommended audit: {currentSEOAudit.next_audit_date ? 
                            new Date(currentSEOAudit.next_audit_date).toLocaleDateString() : 
                            'Not scheduled'
                          }
                        </span>
                        <span>Monitoring: {currentSEOAudit.monitoring_frequency}</span>
                      </div>
                    </div>
                  </CardContent>
                )}
                
                {/* No Audit Available */}
                {!currentSEOAudit && !isAuditRunning && currentEcommerceConnection && (
                  <CardContent className="pt-0">
                    <div className="text-center py-8 bg-white rounded-lg border border-blue-200">
                      <Search className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                      <h3 className="font-semibold text-gray-900 mb-2">No SEO Audit Available</h3>
                      <p className="text-gray-600 mb-4">
                        Run your first comprehensive SEO audit to get detailed insights and recommendations
                      </p>
                      <Button
                        onClick={() => startSEOAudit()}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Zap className="h-4 w-4 mr-2" />
                        Start First Audit
                      </Button>
                    </div>
                  </CardContent>
                )}
                
                {/* No Website Connected */}
                {!currentEcommerceConnection && (
                  <CardContent className="pt-0">
                    <div className="text-center py-8 bg-white rounded-lg border border-blue-200">
                      <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="font-semibold text-gray-900 mb-2">Website Connection Required</h3>
                      <p className="text-gray-600 mb-4">
                        Connect your website in Settings to start running comprehensive SEO audits
                      </p>
                      <Button
                        onClick={() => {
                          setActiveTab('settings');
                          setActiveSettingsTab('ecommerce-integration');
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Go to Settings
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Keyword Intelligence Hub - Unified System */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Upload className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-blue-800">
                          Keyword Intelligence Hub
                        </CardTitle>
                        <CardDescription className="text-blue-600">
                          Centralized keyword storage and management system for imported keywords from external tools and manual entries
                        </CardDescription>
                      </div>
                    </div>
                    
                    {/* Migration Status & Legacy Cleanup Actions */}
                    <div className="flex gap-2">
                      {migrationStatus?.user_specific?.migration_needed && (
                        <Button
                          onClick={() => setShowMigrationDialog(true)}
                          variant="outline"
                          size="sm"
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Migrate Data ({migrationStatus.user_specific.old_seo_count + migrationStatus.user_specific.research_count} keywords)
                        </Button>
                      )}
                      
                      {/* Legacy Cleanup Button */}
                      <Button
                        onClick={cleanupLegacyKeywords}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        title="Remove unwanted keywords from old system processes"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clean Legacy Data
                      </Button>
                      
                      {currentEcommerceConnection ? (
                        <Button
                          onClick={() => runAIKeywordResearch()}
                          disabled={isLoading}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {isLoading ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Running AI Research...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              AI Research
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setActiveTab('settings');
                            setActiveSettingsTab('ecommerce-integration');
                          }}
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Connect Website
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  {/* Tabbed Interface */}
                  <Tabs value={keywordIntelligenceTab} onValueChange={setKeywordIntelligenceTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-6 bg-blue-100">
                      <TabsTrigger value="dashboard" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
                        <BarChart3 className="h-4 w-4 mr-1" />
                        Dashboard
                      </TabsTrigger>
                      <TabsTrigger value="database" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
                        <FileText className="h-4 w-4 mr-1" />
                        Database
                      </TabsTrigger>
                      <TabsTrigger value="tracking" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        Tracking
                      </TabsTrigger>
                      <TabsTrigger value="strategy" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
                        <Target className="h-4 w-4 mr-1" />
                        Strategy
                      </TabsTrigger>
                      <TabsTrigger value="analytics" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
                        <Award className="h-4 w-4 mr-1" />
                        Analytics
                      </TabsTrigger>
                    </TabsList>

                    {/* Dashboard Tab */}
                    <TabsContent value="dashboard" className="mt-6">
                      <div className="space-y-6">
                        {/* Key Metrics Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-white p-4 rounded-lg shadow-sm border text-center hover:shadow-md transition-shadow cursor-pointer"
                               onClick={() => openKeywordDetailsModal('available_keywords')}>
                            <div className="text-3xl font-bold text-blue-600">
                              {unifiedKeywords.length || 0}
                            </div>
                            <p className="text-sm text-gray-600 font-medium">Available Keywords</p>
                            <div className="text-xs text-blue-600 mt-1">Current view ({unifiedKeywordStats.total || 0} total)</div>
                            <div className="text-xs text-gray-400 mt-2">Click for details</div>
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg shadow-sm border text-center hover:shadow-md transition-shadow cursor-pointer"
                               onClick={() => openKeywordDetailsModal('high_opportunity')}>
                            <div className="text-3xl font-bold text-green-600">
                              {highOpportunityKeywords.length || 0}
                            </div>
                            <p className="text-sm text-gray-600 font-medium">High Opportunity</p>
                            <div className="text-xs text-green-600 mt-1">Top prospects</div>
                            <div className="text-xs text-gray-400 mt-2">Click for details</div>
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg shadow-sm border text-center hover:shadow-md transition-shadow cursor-pointer"
                               onClick={() => openKeywordDetailsModal('competitors_analyzed')}>
                            <div className="text-3xl font-bold text-purple-600">
                              {currentKeywordResearch?.competitors_analyzed || 0}
                            </div>
                            <p className="text-sm text-gray-600 font-medium">Competitors Analyzed</p>
                            <div className="text-xs text-purple-600 mt-1">Market intelligence</div>
                            <div className="text-xs text-gray-400 mt-2">Click for details</div>
                          </div>
                          
                          <div className="bg-white p-4 rounded-lg shadow-sm border text-center hover:shadow-md transition-shadow cursor-pointer"
                               onClick={() => openKeywordDetailsModal('content_opportunities')}>
                            <div className="text-3xl font-bold text-orange-600">
                              {currentKeywordResearch?.content_opportunities || 0}
                            </div>
                            <p className="text-sm text-gray-600 font-medium">Content Opportunities</p>
                            <div className="text-xs text-orange-600 mt-1">Ready to create</div>
                            <div className="text-xs text-gray-400 mt-2">Click for details</div>
                          </div>
                        </div>

                        {/* High Opportunity Keywords Preview */}
                        {highOpportunityKeywords.length > 0 && (
                          <div className="bg-white rounded-lg border border-blue-200 p-4">
                            <h4 className="font-medium text-blue-800 mb-4 flex items-center gap-2">
                              <Award className="h-5 w-5 text-green-600" />
                              Top Opportunities
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {highOpportunityKeywords.slice(0, 6).map((keyword, index) => (
                                <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                  <div className="font-semibold text-green-900">{keyword.keyword}</div>
                                  <div className="text-sm text-green-700">Score: {keyword.opportunity_score}/100</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    {/* Database Tab */}
                    <TabsContent value="database" className="mt-6">
                      <div className="space-y-6">
                        {unifiedKeywords.length === 0 ? (
                          <div className="bg-white rounded-lg border border-blue-200 p-6 text-center">
                            <FileText className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Keywords in Database</h3>
                            <p className="text-gray-600 mb-4">Start your keyword research to populate your intelligence database</p>
                            <Button
                              onClick={openKeywordResearchDialog}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Search className="h-4 w-4 mr-2" />
                              Start Research
                            </Button>
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg border border-blue-200 p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="font-medium text-blue-800">
                                  Keyword Database 
                                  {unifiedKeywordStats.total ? (
                                    <span className="text-sm text-gray-600 ml-2">
                                      (Showing {getFilteredKeywords.length} of {unifiedKeywordStats.total} keywords)
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-600 ml-2">({getFilteredKeywords.length} keywords)</span>
                                  )}
                                </h4>
                                <p className="text-sm text-blue-600">
                                  Manage your unified keyword intelligence database
                                  {unifiedKeywordStats.totalPages > 1 && (
                                    <span className="ml-2 text-orange-600 font-medium">
                                      • Page {unifiedKeywordStats.page || 1} of {unifiedKeywordStats.totalPages}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                {selectedKeywords.length > 0 && (
                                  <Button
                                    onClick={() => setShowBulkDeleteDialog(true)}
                                    size="sm"
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete {selectedKeywords.length}
                                  </Button>
                                )}
                                <Button
                                  onClick={() => exportUnifiedKeywords('csv')}
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                  disabled={isLoading}
                                >
                                  {isLoading ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4 mr-2" />
                                  )}
                                  Export CSV
                                </Button>
                                <Button
                                  onClick={() => setShowManualKeywordDialog(true)}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add More
                                </Button>
                              </div>
                            </div>

                            {/* Enhanced Filtering Section */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Filter className="h-4 w-4 text-gray-600" />
                                <h5 className="font-medium text-gray-700">Filter Keywords</h5>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                                {/* Search Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Search Keywords</label>
                                  <input
                                    type="text"
                                    placeholder="Search..."
                                    value={databaseFilters.search}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, search: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>

                                {/* Priority Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Priority Level</label>
                                  <select
                                    value={databaseFilters.priority}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, priority: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="all">All Priorities</option>
                                    <option value="high">High Priority (80+)</option>
                                    <option value="medium">Medium Priority (50-79)</option>
                                    <option value="low">Low Priority (&lt;50)</option>
                                  </select>
                                </div>

                                {/* Data Source Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Data Source</label>
                                  <select
                                    value={databaseFilters.dataSource}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, dataSource: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="all">All Sources</option>
                                    <option value="research">Research</option>
                                    <option value="manual">Manual Entry</option>
                                  </select>
                                </div>

                                {/* Date Range Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Added</label>
                                  <select
                                    value={databaseFilters.dateRange}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="all">All Time</option>
                                    <option value="today">Today</option>
                                    <option value="week">This Week</option>
                                    <option value="month">This Month</option>
                                  </select>
                                </div>

                                {/* Search Volume Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Min Search Volume</label>
                                  <input
                                    type="number"
                                    placeholder="e.g. 100"
                                    value={databaseFilters.minSearchVolume}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, minSearchVolume: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>

                                {/* Difficulty Filter */}
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">Max Difficulty</label>
                                  <input
                                    type="number"
                                    placeholder="e.g. 50"
                                    max="100"
                                    value={databaseFilters.maxDifficulty}
                                    onChange={(e) => setDatabaseFilters(prev => ({ ...prev, maxDifficulty: e.target.value }))}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              </div>

                              {/* Clear Filters Button */}
                              <div className="flex justify-end mt-3">
                                <Button
                                  onClick={() => setDatabaseFilters({
                                    search: '',
                                    priority: 'all',
                                    dataSource: 'all',
                                    dateRange: 'all',
                                    minSearchVolume: '',
                                    maxDifficulty: ''
                                  })}
                                  size="sm"
                                  variant="outline"
                                  className="text-gray-600 hover:text-gray-800"
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  Clear Filters
                                </Button>
                              </div>
                            </div>

                            {/* Professional Data Table */}
                            <KeywordDataTable 
                              data={getFilteredKeywords} 
                              columns={keywordColumns}
                            />

                            {/* Pagination Controls */}
                            {unifiedKeywordStats.totalPages > 1 && (
                              <div className="flex items-center justify-between py-4 border-t bg-gray-50 rounded-b-lg">
                                <div className="text-sm text-gray-600">
                                  Showing {Math.min(((unifiedKeywordStats.page || 1) - 1) * 100 + 1, unifiedKeywordStats.total || 0)}-{Math.min((unifiedKeywordStats.page || 1) * 100, unifiedKeywordStats.total || 0)} of {unifiedKeywordStats.total || 0} keywords
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => {
                                      if (unifiedKeywordStats.hasPrev) {
                                        loadUnifiedKeywords({ 
                                          ...databaseFilters, 
                                          page: (unifiedKeywordStats.page || 1) - 1,
                                          limit: 100
                                        });
                                      }
                                    }}
                                    disabled={!unifiedKeywordStats.hasPrev || isLoading}
                                    size="sm"
                                    variant="outline"
                                    className="border-gray-300"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                  </Button>
                                  
                                  <span className="text-sm text-gray-600 px-3">
                                    Page {unifiedKeywordStats.page || 1} of {unifiedKeywordStats.totalPages || 1}
                                  </span>
                                  
                                  <Button
                                    onClick={() => {
                                      if (unifiedKeywordStats.hasNext) {
                                        loadUnifiedKeywords({ 
                                          ...databaseFilters, 
                                          page: (unifiedKeywordStats.page || 1) + 1,
                                          limit: 100
                                        });
                                      }
                                    }}
                                    disabled={!unifiedKeywordStats.hasNext || isLoading}
                                    size="sm"
                                    variant="outline"
                                    className="border-gray-300"
                                  >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                  
                                  <Button
                                    onClick={() => {
                                      loadUnifiedKeywords({ 
                                        ...databaseFilters, 
                                        page: 1,
                                        limit: Math.min(unifiedKeywordStats.total || 100, 500) // Cap at 500 for performance
                                      });
                                      toast.info(`Loading ${Math.min(unifiedKeywordStats.total || 100, 500)} keywords...`);
                                    }}
                                    disabled={isLoading}
                                    size="sm"
                                    variant="outline" 
                                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                  >
                                    {isLoading ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Eye className="h-4 w-4 mr-1" />
                                    )}
                                    Show All ({unifiedKeywordStats.total})
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    {/* Tracking Tab */}
                    <TabsContent value="tracking" className="mt-6">
                      <div className="bg-white rounded-lg border border-blue-200 p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <TrendingUp className="h-5 w-5 text-blue-600" />
                          <h4 className="font-medium text-blue-800">Ranking Tracker</h4>
                        </div>
                        <p className="text-blue-600 mb-6">
                          Monitor keyword rankings and track performance improvements
                        </p>
                        <div className="text-center py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50">
                          <TrendingUp className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                          <h3 className="font-semibold text-gray-900 mb-2">Ranking Intelligence</h3>
                          <p className="text-gray-600">Advanced ranking tracking and competitor position monitoring coming soon</p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Strategy Tab */}
                    <TabsContent value="strategy" className="mt-6">
                      <div className="bg-white rounded-lg border border-blue-200 p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Target className="h-5 w-5 text-blue-600" />
                          <h4 className="font-medium text-blue-800">Content Strategy</h4>
                        </div>
                        <p className="text-blue-600 mb-6">
                          Strategic content planning and keyword opportunity mapping
                        </p>
                        <div className="text-center py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50">
                          <Target className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                          <h3 className="font-semibold text-gray-900 mb-2">Strategic Intelligence</h3>
                          <p className="text-gray-600">AI-powered content strategy and opportunity mapping coming soon</p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Analytics Tab */}
                    <TabsContent value="analytics" className="mt-6">
                      {keywordAnalytics ? (
                        <div className="space-y-6">
                          {/* Performance Overview - Consistent Header Style */}
                          <div className="bg-white rounded-lg border border-blue-200 p-6">
                            <div className="flex items-center gap-2 mb-4">
                              <Award className="h-5 w-5 text-blue-600" />
                              <h4 className="font-medium text-blue-800">Performance Analytics</h4>
                            </div>
                            <p className="text-blue-600 mb-6">
                              Deep insights into keyword performance and competitive intelligence
                            </p>
                            
                            {/* High-level metrics in consistent blue cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div 
                                className="bg-blue-50 p-3 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => handleAnalyticsMetricClick(
                                  'total_keywords',
                                  'Total Keywords',
                                  keywordAnalytics.total_keywords || 0,
                                  'Total number of keywords in your database'
                                )}
                                title="Click for detailed breakdown"
                              >
                                <div className="text-2xl font-bold text-blue-900">
                                  {keywordAnalytics.total_keywords || 0}
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <p className="text-xs text-blue-600">Total Keywords</p>
                                  <Info className="h-3 w-3 text-blue-500" title="Total keywords across all sources - click for details" />
                                </div>
                              </div>
                              
                              <div 
                                className="bg-blue-50 p-3 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => handleAnalyticsMetricClick(
                                  'high_priority',
                                  'High Priority Keywords',
                                  keywordAnalytics.high_priority_keywords || 0,
                                  'Keywords with strategic priority score ≥ 80'
                                )}
                                title="Click for detailed breakdown"
                              >
                                <div className="text-2xl font-bold text-blue-900">
                                  {keywordAnalytics.high_priority_keywords || 0}
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <p className="text-xs text-blue-600">High Priority</p>
                                  <Info className="h-3 w-3 text-blue-500" title="High-value keywords with score ≥ 80 - click for details" />
                                </div>
                              </div>
                              
                              <div 
                                className="bg-blue-50 p-3 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => handleAnalyticsMetricClick(
                                  'content_opportunities',
                                  'Content Opportunities',
                                  keywordAnalytics.content_opportunities || 0,
                                  'Content gaps from competitor analysis'
                                )}
                                title="Click for detailed breakdown"
                              >
                                <div className="text-2xl font-bold text-blue-900">
                                  {keywordAnalytics.content_opportunities || 0}
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <p className="text-xs text-blue-600">Content Opportunities</p>
                                  <Info className="h-3 w-3 text-blue-500" title="Content gaps identified from competitor analysis - click for details" />
                                </div>
                              </div>
                              
                              <div 
                                className="bg-blue-50 p-3 rounded-lg text-center cursor-pointer hover:bg-blue-100 transition-colors"
                                onClick={() => handleAnalyticsMetricClick(
                                  'growth_rate',
                                  'Growth Rate',
                                  keywordAnalytics.keyword_trends ? 
                                    `${keywordAnalytics.keyword_trends.weekly_growth_rate >= 0 ? '+' : ''}${keywordAnalytics.keyword_trends.weekly_growth_rate || 0}%` 
                                    : '0%',
                                  'Weekly keyword portfolio growth percentage'
                                )}
                                title="Click for detailed breakdown"
                              >
                                <div className="text-2xl font-bold text-blue-900">
                                  {keywordAnalytics.keyword_trends ? 
                                    `${keywordAnalytics.keyword_trends.weekly_growth_rate >= 0 ? '+' : ''}${keywordAnalytics.keyword_trends.weekly_growth_rate || 0}%` 
                                    : '0%'
                                  }
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <p className="text-xs text-blue-600">Growth Rate</p>
                                  <Info className="h-3 w-3 text-blue-500" title="Weekly portfolio growth rate - click for details" />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Performance Insights Section - Consistent Style */}
                          {keywordAnalytics.performance_insights && (
                            <div className="bg-white rounded-lg border border-blue-200 p-6">
                              <div className="flex items-center gap-2 mb-4">
                                <Target className="h-5 w-5 text-blue-600" />
                                <h4 className="font-medium text-blue-800">Performance Insights</h4>
                              </div>
                              <p className="text-blue-600 mb-6">Portfolio composition and strategic analysis</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div 
                                  className="bg-blue-50 p-4 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                                  onClick={() => handleAnalyticsMetricClick(
                                    'portfolio_balance',
                                    'Portfolio Balance',
                                    keywordAnalytics.performance_insights?.portfolio_health?.balance_score || 0,
                                    'Balance between informational and transactional keywords'
                                  )}
                                  title="Click for detailed portfolio analysis"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-semibold text-blue-900">Portfolio Health</h5>
                                    <Info className="h-4 w-4 text-blue-500" title="Click for detailed analysis" />
                                  </div>
                                  <div className="space-y-2">
                                    {keywordAnalytics.performance_insights.portfolio_health && (
                                      <>
                                        <div className="text-sm text-blue-700">
                                          Balance Score: <span className="font-bold">{keywordAnalytics.performance_insights.portfolio_health.balance_score?.toFixed(1)}%</span>
                                        </div>
                                        <div className="text-sm text-blue-700">
                                          Diversity: <span className="font-bold">{keywordAnalytics.performance_insights.portfolio_health.diversity_index?.toFixed(1)}%</span>
                                        </div>
                                        <div className="text-sm text-blue-700">
                                          Strategy: <span className="font-bold capitalize">{keywordAnalytics.performance_insights.portfolio_health.strategic_focus}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="bg-blue-50 p-4 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-semibold text-blue-900">Analysis Summary</h5>
                                    <Info className="h-4 w-4 text-blue-500" title="Summary of portfolio analysis across all keywords" />
                                  </div>
                                  <div className="space-y-2">
                                    <div className="text-sm text-blue-700">
                                      Keywords: <span className="font-bold">{keywordAnalytics.performance_insights.total_keywords_analyzed || 0}</span>
                                    </div>
                                    <div className="text-sm text-blue-700">
                                      Intent Types: <span className="font-bold">{keywordAnalytics.performance_insights.intent_distribution?.length || 0}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="bg-blue-50 p-4 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-semibold text-blue-900">Quick Insights</h5>
                                    <Info className="h-4 w-4 text-blue-500" title="AI-generated recommendations based on your keyword portfolio" />
                                  </div>
                                  <div className="space-y-1">
                                    {keywordAnalytics.performance_insights.recommendations?.slice(0, 2).map((rec, index) => (
                                      <div key={index} className="text-xs text-blue-700">{rec}</div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Keyword Trends Section - Consistent Style */}
                          {keywordAnalytics.keyword_trends && (
                            <div className="bg-white rounded-lg border border-blue-200 p-6">
                              <div className="flex items-center gap-2 mb-4">
                                <TrendingUp className="h-5 w-5 text-blue-600" />
                                <h4 className="font-medium text-blue-800">Growth Trends</h4>
                              </div>
                              <p className="text-blue-600 mb-6">Keyword discovery and growth patterns</p>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-blue-900">{keywordAnalytics.keyword_trends.keywords_last_7_days || 0}</div>
                                  <div className="text-xs text-blue-600">Last 7 Days</div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-blue-900">{keywordAnalytics.keyword_trends.keywords_last_30_days || 0}</div>
                                  <div className="text-xs text-blue-600">Last 30 Days</div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-blue-900">{keywordAnalytics.keyword_trends.keywords_last_90_days || 0}</div>
                                  <div className="text-xs text-blue-600">Last 90 Days</div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <div className="text-sm font-bold capitalize text-blue-900">
                                    {keywordAnalytics.keyword_trends.activity_level || 'Unknown'}
                                  </div>
                                  <div className="text-xs text-blue-600">Activity Level</div>
                                </div>
                              </div>
                              
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-blue-900">Trend Direction:</span>
                                  <span className="text-sm font-bold text-blue-900 capitalize">
                                    {keywordAnalytics.keyword_trends.trend_direction || 'Unknown'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Competitive Analysis Section - Consistent Style */}
                          {keywordAnalytics.competitive_analysis && (
                            <div className="bg-white rounded-lg border border-blue-200 p-6">
                              <div className="flex items-center gap-2 mb-4">
                                <Users className="h-5 w-5 text-blue-600" />
                                <h4 className="font-medium text-blue-800">Competitive Position</h4>
                              </div>
                              <p className="text-blue-600 mb-6">Difficulty distribution and competitive analysis</p>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h5 className="font-semibold text-blue-900 mb-3">Difficulty Distribution</h5>
                                  <div className="space-y-2">
                                    {keywordAnalytics.competitive_analysis.difficulty_distribution && (
                                      <>
                                        <div className="bg-blue-50 p-2 rounded flex justify-between items-center">
                                          <span className="text-sm font-medium text-blue-700">Low Competition</span>
                                          <span className="text-sm font-bold text-blue-900">{keywordAnalytics.competitive_analysis.difficulty_distribution.low_competition || 0}</span>
                                        </div>
                                        <div className="bg-blue-50 p-2 rounded flex justify-between items-center">
                                          <span className="text-sm font-medium text-blue-700">Medium Competition</span>
                                          <span className="text-sm font-bold text-blue-900">{keywordAnalytics.competitive_analysis.difficulty_distribution.medium_competition || 0}</span>
                                        </div>
                                        <div className="bg-blue-50 p-2 rounded flex justify-between items-center">
                                          <span className="text-sm font-medium text-blue-700">High Competition</span>
                                          <span className="text-sm font-bold text-blue-900">{keywordAnalytics.competitive_analysis.difficulty_distribution.high_competition || 0}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                                
                                <div>
                                  <h5 className="font-semibold text-blue-900 mb-3">Strategic Position</h5>
                                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                                    <span className="text-lg font-bold text-blue-900 capitalize">
                                      {keywordAnalytics.competitive_analysis.competitive_position || 'Unknown'}
                                    </span>
                                    <p className="text-sm text-blue-600 mt-1">Competitive Position</p>
                                    <p className="text-xs text-blue-700 mt-2">
                                      {keywordAnalytics.competitive_analysis.strategy_recommendation || 'Analyzing competitive landscape...'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Content Strategy & Growth Trajectory Combined - Consistent Style */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Content Strategy */}
                            {keywordAnalytics.content_strategy_metrics && (
                              <div className="bg-white rounded-lg border border-blue-200 p-6">
                                <div className="flex items-center gap-2 mb-4">
                                  <FileText className="h-5 w-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Content Strategy</h4>
                                </div>
                                <p className="text-blue-600 mb-4">Content optimization metrics</p>
                                
                                <div className="space-y-3">
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">Primary Focus</div>
                                    <div className="font-semibold text-blue-900 capitalize">
                                      {keywordAnalytics.content_strategy_metrics.efficiency_metrics?.primary_content_focus || 'Unknown'}
                                    </div>
                                  </div>
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">Strategy Type</div>
                                    <div className="font-semibold text-blue-900 capitalize">
                                      {keywordAnalytics.content_strategy_metrics.efficiency_metrics?.content_distribution_balance || 'Unknown'}
                                    </div>
                                  </div>
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">Search Potential</div>
                                    <div className="font-semibold text-blue-900">
                                      {keywordAnalytics.content_strategy_metrics.efficiency_metrics?.total_search_potential?.toLocaleString() || '0'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Growth Trajectory */}
                            {keywordAnalytics.growth_trajectory && (
                              <div className="bg-white rounded-lg border border-blue-200 p-6">
                                <div className="flex items-center gap-2 mb-4">
                                  <BarChart3 className="h-5 w-5 text-blue-600" />
                                  <h4 className="font-medium text-blue-800">Growth Trajectory</h4>
                                </div>
                                <p className="text-blue-600 mb-4">Performance projections</p>
                                
                                <div className="space-y-3">
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">Weekly Rate</div>
                                    <div className="font-semibold text-blue-900">
                                      {keywordAnalytics.growth_trajectory.current_weekly_rate || 0} keywords
                                    </div>
                                  </div>
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">30-Day Projection</div>
                                    <div className="font-semibold text-blue-900">
                                      +{keywordAnalytics.growth_trajectory.projected_30_day_growth || 0} keywords
                                    </div>
                                  </div>
                                  <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-xs text-blue-600">Momentum Score</div>
                                    <div className="font-semibold text-blue-900">
                                      {keywordAnalytics.growth_trajectory.momentum_score?.toFixed(0) || 0}%
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white rounded-lg border border-blue-200 p-6 text-center">
                          <Award className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Analytics...</h3>
                          <p className="text-gray-600 mb-6">
                            Gathering performance insights and analytics data
                          </p>
                          <Button 
                            onClick={loadKeywordAnalytics}
                            variant="outline"
                            disabled={isLoading}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          >
                            {isLoading ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Refresh Analytics
                          </Button>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Connected Website Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Connected Websites
                  </CardTitle>
                  <CardDescription>
                    Websites connected through e-commerce integration for SEO analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {currentEcommerceConnection ? (
                    <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                            {currentEcommerceConnection.platform_type === 'shopify' ? (
                              <ShoppingBag className="h-5 w-5 text-white" />
                            ) : (
                              <Store className="h-5 w-5 text-white" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-green-900">{currentEcommerceConnection.connection_name}</h3>
                            <p className="text-sm text-green-700">
                              {currentEcommerceConnection.platform_type.charAt(0).toUpperCase() + currentEcommerceConnection.platform_type.slice(1)} Store
                            </p>
                            <p className="text-xs text-green-600">{currentEcommerceConnection.store_url}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-200">
                            Available for SEO
                          </span>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              if (currentEcommerceConnection?.store_url) {
                                setTechnicalAuditForm({ domain: currentEcommerceConnection.store_url });
                                setShowTechnicalAuditDialog(true);
                              }
                            }}
                          >
                            <Search className="h-4 w-4 mr-1" />
                            Analyze Site
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Website Connected</h3>
                      <p className="text-gray-600 mb-4">Connect your e-commerce store to enable website SEO analysis</p>
                      <Button
                        onClick={() => setActiveSettingsTab('ecommerce-integration')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Connect E-commerce Store
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SEO/AEO Modal Dialogs */}
              
              {/* Keyword Import & Storage Dialog */}
              <Dialog open={showKeywordResearchDialog} onOpenChange={setShowKeywordResearchDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Upload className="h-5 w-5 text-blue-600" />
                      Keyword Import & Storage
                    </DialogTitle>
                    <DialogDescription>
                      Import keywords from external tools (Ubersuggest, Google Keyword Planner, SEMrush) or enter manually to store in your OmniMarket360 database for further usage
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    
                    {/* Research Type Selection */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Keyword Import Method
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        {/* Manual Entry Option */}
                        <div 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            keywordResearchForm.importMethod === 'manual' 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setKeywordResearchForm(prev => ({ ...prev, importMethod: 'manual' }))}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <PenTool className="h-5 w-5 text-green-600" />
                            <span className="font-semibold text-green-900">Manual Entry</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Enter keywords manually (comma-separated) to store in your database
                          </p>
                          {keywordResearchForm.importMethod === 'manual' && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-green-700">
                              <Target className="h-3 w-3" />
                              <span>Quick manual import</span>
                            </div>
                          )}
                        </div>

                        {/* File Upload Option */}
                        <div 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            keywordResearchForm.importMethod === 'upload' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setKeywordResearchForm(prev => ({ ...prev, importMethod: 'upload' }))}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Upload className="h-5 w-5 text-blue-600" />
                            <span className="font-semibold text-blue-900">File Upload</span>
                          </div>
                          <p className="text-xs text-gray-600">
                            Upload CSV, TXT, or Excel files from Ubersuggest, Google Keyword Planner, etc.
                          </p>
                          {keywordResearchForm.importMethod === 'upload' && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-blue-700">
                              <FileText className="h-3 w-3" />
                              <span>Bulk import from tools</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Manual Keywords Input */}
                    {keywordResearchForm.importMethod === 'manual' && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Keywords to Import & Store
                        </label>
                        <textarea
                          rows={4}
                          placeholder="Enter keywords separated by commas (e.g., natural shampoo, organic hair care, chemical free products)"
                          value={keywordResearchForm.keywords}
                          onChange={(e) => setKeywordResearchForm(prev => ({ ...prev, keywords: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter keywords you want to store in your OmniMarket360 database for campaigns, content planning, and SEO optimization
                        </p>
                      </div>
                    )}

                    {/* File Upload */}
                    {keywordResearchForm.importMethod === 'upload' && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Upload Keyword File
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-600 mb-1">
                            Upload CSV, TXT, or Excel file
                          </p>
                          <p className="text-xs text-gray-500 mb-3">
                            From Ubersuggest, Google Keyword Planner, SEMrush, Ahrefs, or any keyword tool
                          </p>
                          <input
                            type="file"
                            accept=".csv,.txt,.xlsx,.xls"
                            className="hidden"
                            id="keyword-file-upload"
                            onChange={(e) => handleKeywordFileUpload(e.target.files[0])}
                          />
                          <Button
                            onClick={() => document.getElementById('keyword-file-upload').click()}
                            variant="outline"
                            size="sm"
                            className="mb-2"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Choose File
                          </Button>
                          <div className="text-xs text-gray-500">
                            Supported formats: CSV, TXT, Excel (.xlsx, .xls)
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Target Location */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Target Location
                      </label>
                      <select
                        value={keywordResearchForm.location}
                        onChange={(e) => setKeywordResearchForm(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="IN">India</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="ES">Spain</option>
                        <option value="IT">Italy</option>
                        <option value="NL">Netherlands</option>
                        <option value="BE">Belgium</option>
                        <option value="SE">Sweden</option>
                        <option value="DK">Denmark</option>
                        <option value="NO">Norway</option>
                        <option value="FI">Finland</option>
                        <option value="IE">Ireland</option>
                        <option value="PT">Portugal</option>
                        <option value="AT">Austria</option>
                        <option value="CH">Switzerland</option>
                        <option value="JP">Japan</option>
                        <option value="KR">South Korea</option>
                        <option value="SG">Singapore</option>
                        <option value="HK">Hong Kong</option>
                        <option value="BR">Brazil</option>
                        <option value="MX">Mexico</option>
                        <option value="ZA">South Africa</option>
                        <option value="IL">Israel</option>
                        <option value="AE">United Arab Emirates</option>
                        <option value="SA">Saudi Arabia</option>
                        <option value="TR">Turkey</option>
                        <option value="RU">Russia</option>
                        <option value="PL">Poland</option>
                        <option value="CZ">Czech Republic</option>
                        <option value="HU">Hungary</option>
                        <option value="RO">Romania</option>
                        <option value="GR">Greece</option>
                        <option value="NZ">New Zealand</option>
                        <option value="MY">Malaysia</option>
                        <option value="TH">Thailand</option>
                        <option value="PH">Philippines</option>
                        <option value="ID">Indonesia</option>
                        <option value="VN">Vietnam</option>
                        <option value="TW">Taiwan</option>
                        <option value="CN">China</option>
                        <option value="EG">Egypt</option>
                        <option value="MA">Morocco</option>
                        <option value="NG">Nigeria</option>
                      </select>
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        onClick={() => setShowKeywordResearchDialog(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          // Validation for import methods
                          if (keywordResearchForm.importMethod === 'manual' && !keywordResearchForm.keywords.trim()) {
                            toast.error('Please enter keywords to import');
                            return;
                          }
                          
                          if (keywordResearchForm.importMethod === 'upload' && !keywordResearchForm.uploadedFile) {
                            toast.error('Please upload a keyword file');
                            return;
                          }

                          setSeoLoading(true);
                          try {
                            if (keywordResearchForm.importMethod === 'manual') {
                              const keywords = keywordResearchForm.keywords ? 
                                keywordResearchForm.keywords.split(',').map(k => k.trim()).filter(k => k) : [];
                              
                              await importKeywords(
                                'manual',
                                keywords,
                                keywordResearchForm.location
                              );
                            } else {
                              await importKeywords(
                                'file',
                                keywordResearchForm.uploadedFile,
                                keywordResearchForm.location
                              );
                            }
                            
                            setShowKeywordResearchDialog(false);
                            setKeywordResearchForm({ 
                              keywords: '', 
                              location: 'US', 
                              researchType: 'manual',
                              importMethod: 'manual',
                              domain: '',
                              uploadedFile: null
                            });
                          } catch (error) {
                            console.error('Keyword import failed:', error);
                          } finally {
                            setSeoLoading(false);
                          }
                        }}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white"
                        disabled={seoLoading}
                      >
                        {seoLoading ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            {keywordResearchForm.importMethod === 'manual' ? (
                              <PenTool className="h-4 w-4 mr-2" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Import & Store Keywords
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Competitor Analysis Dialog */}
              <Dialog open={showCompetitorAnalysisDialog} onOpenChange={setShowCompetitorAnalysisDialog}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Competitor Intelligence Analysis</DialogTitle>
                    <DialogDescription>
                      Compare your website against competitors to identify opportunities and threats
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Your Domain Section */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Your Domain (Auto-filled from connected website)
                      </label>
                      <input
                        type="text"
                        value={competitorAnalysisForm.domain || (currentEcommerceConnection?.store_url?.replace('https://', '').replace('http://', '') || '')}
                        onChange={(e) => setCompetitorAnalysisForm(prev => ({ ...prev, domain: e.target.value }))}
                        placeholder={currentEcommerceConnection ? "Domain auto-filled from connection" : "Enter your domain (e.g., yourdomain.com)"}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                        disabled={!!currentEcommerceConnection}
                      />
                      {!currentEcommerceConnection && (
                        <p className="text-xs text-orange-600 mt-1">
                          Connect your e-commerce store to auto-fill your domain, or enter manually
                        </p>
                      )}
                    </div>

                    {/* Saved Competitors Section */}
                    {savedCompetitors.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Saved Competitors ({savedCompetitors.length}/5)
                        </label>
                        <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          {savedCompetitors.map((competitor, index) => (
                            <div key={index} className="flex items-center gap-1 bg-white px-2 py-1 rounded border">
                              <span className="text-sm text-gray-700">{competitor}</span>
                              <button
                                onClick={() => removeCompetitor(competitor)}
                                className="text-red-500 hover:text-red-700 ml-1"
                                type="button"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Competitor Domains Section */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Competitor Domains * (Required: 1-5 competitors)
                      </label>
                      <textarea
                        value={competitorAnalysisForm.competitors}
                        onChange={(e) => setCompetitorAnalysisForm(prev => ({ ...prev, competitors: e.target.value }))}
                        placeholder="Enter competitor domains separated by commas&#10;e.g., competitor1.com, competitor2.com, competitor3.com&#10;&#10;Maximum 5 competitors allowed"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={4}
                      />
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-500">
                          Separate multiple domains with commas. Min: 1, Max: 5 competitors
                        </p>
                        <p className="text-xs text-gray-500">
                          {competitorAnalysisForm.competitors ? 
                            competitorAnalysisForm.competitors.split(',').map(c => c.trim()).filter(c => c).length : 0}/5
                        </p>
                      </div>
                    </div>

                    {/* Analysis Options */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Analysis Scope</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Keyword Gap Analysis</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Content Opportunities</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Ranking Comparison</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Strategic Recommendations</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => setShowCompetitorAnalysisDialog(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          // Enhanced validation
                          const userDomain = competitorAnalysisForm.domain.trim() || 
                            (currentEcommerceConnection?.store_url?.replace('https://', '').replace('http://', '') || '');
                          
                          if (!userDomain) {
                            toast.error('Please enter your domain or connect your website');
                            return;
                          }

                          const competitorList = competitorAnalysisForm.competitors
                            ? competitorAnalysisForm.competitors.split(',').map(c => c.trim()).filter(c => c)
                            : [];

                          if (competitorList.length === 0) {
                            toast.error('Please enter at least 1 competitor domain');
                            return;
                          }

                          if (competitorList.length > 5) {
                            toast.error('Maximum 5 competitors allowed');
                            return;
                          }

                          // Validate domain formats
                          const invalidDomains = competitorList.filter(domain => {
                            const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
                            const cleanDomain = domain.replace('https://', '').replace('http://', '').split('/')[0];
                            return !domainRegex.test(cleanDomain);
                          });

                          if (invalidDomains.length > 0) {
                            toast.error(`Invalid domain format: ${invalidDomains.join(', ')}`);
                            return;
                          }

                          setSeoLoading(true);
                          try {
                            // Save competitors for future use
                            saveCompetitors(competitorList);
                            
                            // Run analysis
                            await analyzeCompetitors(userDomain, competitorList);
                            
                            setShowCompetitorAnalysisDialog(false);
                            toast.success(`Analyzing ${userDomain} against ${competitorList.length} competitors`);
                            
                            // CRITICAL FIX: Refresh accumulated metrics to show updated competitor data in Dashboard
                            console.log('🔄 Refreshing accumulated metrics after competitor analysis...');
                            setTimeout(() => {
                              loadAccumulatedMetrics();
                            }, 1000); // Small delay to ensure backend has processed the data
                            
                          } catch (error) {
                            console.error('Competitor analysis failed:', error);
                            toast.error('Analysis failed. Please try again.');
                          } finally {
                            setSeoLoading(false);
                          }
                        }}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        disabled={seoLoading}
                      >
                        {seoLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Users className="h-4 w-4 mr-2" />
                            Analyze Competitors
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Content Analysis Dialog */}
              <Dialog open={showContentAnalysisDialog} onOpenChange={setShowContentAnalysisDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>AI Content Optimization</DialogTitle>
                    <DialogDescription>
                      Analyze your content for SEO score, readability, and optimization suggestions
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Page URL *
                      </label>
                      <input
                        type="url"
                        value={contentAnalysisForm.url}
                        onChange={(e) => setContentAnalysisForm(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://example.com/page-to-analyze"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Target Keywords (optional, comma-separated)
                      </label>
                      <input
                        type="text"
                        value={contentAnalysisForm.target_keywords}
                        onChange={(e) => setContentAnalysisForm(prev => ({ ...prev, target_keywords: e.target.value }))}
                        placeholder="e.g., SEO optimization, content marketing"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => setShowContentAnalysisDialog(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          if (!contentAnalysisForm.url.trim()) {
                            toast.error('Please enter a URL to analyze');
                            return;
                          }
                          setSeoLoading(true);
                          try {
                            const targetKeywords = contentAnalysisForm.target_keywords
                              ? contentAnalysisForm.target_keywords.split(',').map(k => k.trim()).filter(k => k)
                              : [];
                            await analyzeContent(contentAnalysisForm.url, targetKeywords);
                            setShowContentAnalysisDialog(false);
                            setContentAnalysisForm({ url: '', target_keywords: '' });
                          } catch (error) {
                            console.error('Content analysis failed:', error);
                          } finally {
                            setSeoLoading(false);
                          }
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={seoLoading}
                      >
                        {seoLoading ? 'Analyzing...' : 'Analyze Content'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Technical Audit Dialog */}
              <Dialog open={showTechnicalAuditDialog} onOpenChange={setShowTechnicalAuditDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Technical SEO Audit</DialogTitle>
                    <DialogDescription>
                      Comprehensive site health check for: {currentEcommerceConnection?.store_url || 'Connect your website first'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Website URL (Auto-filled from connected website)
                      </label>
                      <input
                        type="text"
                        value={technicalAuditForm.domain || currentEcommerceConnection?.store_url || ''}
                        onChange={(e) => setTechnicalAuditForm(prev => ({ ...prev, domain: e.target.value }))}
                        placeholder="Connect e-commerce store to auto-fill"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                        disabled={!!currentEcommerceConnection}
                      />
                      {!currentEcommerceConnection && (
                        <p className="text-xs text-orange-600 mt-1">
                          Connect your e-commerce store to auto-fill your website URL
                        </p>
                      )}
                    </div>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Info className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800">Audit Includes</span>
                      </div>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Page Speed (Mobile & Desktop)</li>
                        <li>• Core Web Vitals</li>
                        <li>• SSL Certificate Check</li>
                        <li>• Mobile-Friendly Test</li>
                        <li>• Sitemap & Robots.txt</li>
                      </ul>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => setShowTechnicalAuditDialog(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          if (!technicalAuditForm.domain.trim()) {
                            toast.error('Please enter a domain to audit');
                            return;
                          }
                          setSeoLoading(true);
                          try {
                            await performTechnicalAudit(technicalAuditForm.domain);
                            setShowTechnicalAuditDialog(false);
                            setTechnicalAuditForm({ domain: '' });
                          } catch (error) {
                            console.error('Technical audit failed:', error);
                          } finally {
                            setSeoLoading(false);
                          }
                        }}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                        disabled={seoLoading}
                      >
                        {seoLoading ? 'Auditing...' : 'Run Audit'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* AEO Optimization Dialog */}
              <Dialog open={showAeoOptimizationDialog} onOpenChange={setShowAeoOptimizationDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>AEO (Agentic Engine Optimization)</DialogTitle>
                    <DialogDescription>
                      Optimize your content for AI search engines like ChatGPT, Claude, Perplexity, and Gemini
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                      <h3 className="font-semibold text-indigo-900 mb-2">🤖 AI Search Optimization</h3>
                      <p className="text-sm text-indigo-800">
                        AEO focuses on optimizing content to appear in AI-generated responses and chatbot answers.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 border rounded-lg text-center">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Brain className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium">ChatGPT</span>
                      </div>
                      <div className="p-3 border rounded-lg text-center">
                        <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Brain className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium">Claude</span>
                      </div>
                      <div className="p-3 border rounded-lg text-center">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Search className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium">Perplexity</span>
                      </div>
                      <div className="p-3 border rounded-lg text-center">
                        <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Brain className="h-4 w-4 text-white" />
                        </div>
                        <span className="text-sm font-medium">Gemini</span>
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Coming Soon in Phase 2!</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• AI Answer Engine Optimization</li>
                        <li>• Featured Snippet Targeting</li>
                        <li>• Voice Search Optimization</li>
                        <li>• Entity Recognition & Knowledge Graphs</li>
                        <li>• Conversational Query Optimization</li>
                      </ul>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => setShowAeoOptimizationDialog(false)}
                        variant="outline"
                        className="flex-1"
                      >
                        Close
                      </Button>
                      <Button
                        onClick={() => {
                          toast.success('AEO features will be available in Phase 2!');
                          setShowAeoOptimizationDialog(false);
                        }}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Notify Me
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Data Source Information Dialog */}
              <Dialog open={showDataSourceDialog} onOpenChange={setShowDataSourceDialog}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-blue-600">
                      <Info className="h-5 w-5" />
                      SEO Data Sources & Transparency
                    </DialogTitle>
                    <DialogDescription>
                      Understanding where your keyword data comes from and its accuracy levels
                    </DialogDescription>
                  </DialogHeader>
                  
                  {seoDataSources ? (
                    <div className="py-4 space-y-6">
                      {/* Data Sources Overview */}
                      <div className="grid gap-4 md:grid-cols-3">
                        {Object.entries(seoDataSources.data_sources).map(([key, source]) => (
                          <div key={key} className="p-4 border rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-3 h-3 rounded-full ${
                                source.available ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              <h3 className="font-semibold">{source.name}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                source.type === 'free' ? 'bg-green-100 text-green-800' :
                                source.type === 'premium' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {source.type}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{source.description}</p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Status:</span>
                                <span className={`text-sm ${
                                  source.available ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {source.status.replace(/_/g, ' ')}
                                </span>
                              </div>
                              {source.data_types && (
                                <div>
                                  <span className="text-sm font-medium">Data Types:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {source.data_types.map((type, idx) => (
                                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                        {type.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Data Transparency */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="flex items-center gap-2 font-semibold text-blue-800 mb-3">
                          <Info className="h-4 w-4" />
                          Data Transparency
                        </h3>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <h4 className="font-medium text-blue-700 mb-2">Search Volume Sources</h4>
                            <p className="text-sm text-blue-600">
                              Primary: {seoDataSources.data_transparency.search_volume_sources.primary}
                            </p>
                            <p className="text-sm text-blue-600">
                              Fallback: {seoDataSources.data_transparency.search_volume_sources.fallback}
                            </p>
                            <p className="text-sm text-blue-600">
                              Accuracy: {seoDataSources.data_transparency.search_volume_sources.accuracy_level}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-medium text-blue-700 mb-2">Data Freshness</h4>
                            {Object.entries(seoDataSources.data_transparency.data_freshness).map(([source, freshness]) => (
                              <p key={source} className="text-sm text-blue-600">
                                {source.replace(/_/g, ' ')}: {freshness.replace(/_/g, ' ')}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Recommendations */}
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h3 className="flex items-center gap-2 font-semibold text-yellow-800 mb-3">
                          <AlertCircle className="h-4 w-4" />
                          Important Notes & Recommendations
                        </h3>
                        <ul className="space-y-2">
                          {seoDataSources.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-yellow-700">
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-2 flex-shrink-0"></div>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Legend for Table Icons */}
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h3 className="font-semibold text-gray-800 mb-3">Data Source Legend</h3>
                        <div className="grid gap-2 md:grid-cols-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              Google Trends
                            </span>
                            <span className="text-sm text-gray-600">Real relative search data</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                              Estimated
                            </span>
                            <span className="text-sm text-gray-600">AI-powered estimation</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                              ↗️ Rising
                            </span>
                            <span className="text-sm text-gray-600">Increasing search interest</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                              ↘️ Declining
                            </span>
                            <span className="text-sm text-gray-600">Decreasing search interest</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading data source information...</p>
                    </div>
                  )}
                  
                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      onClick={() => setShowDataSourceDialog(false)}
                      variant="outline"
                    >
                      Close
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Delete Keyword Confirmation Dialog */}
              <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                      <Trash2 className="h-5 w-5" />
                      Delete Keyword
                    </DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete this keyword? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  {keywordToDelete && (
                    <div className="py-4">
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="font-medium">{keywordToDelete.keyword}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Search Volume: {keywordToDelete.search_volume?.toLocaleString() || 'N/A'} | 
                          Competition: {keywordToDelete.competition || 'N/A'} | 
                          Research Type: {keywordToDelete.research_type || 'N/A'}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={() => setShowDeleteConfirmDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => keywordToDelete && deleteKeyword(keywordToDelete.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Keyword
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Bulk Delete Confirmation Dialog */}
              <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                      <Trash2 className="h-5 w-5" />
                      Delete Multiple Keywords
                    </DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete {selectedKeywords.length} selected keywords? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-800">
                        <Trash2 className="h-4 w-4" />
                        <span className="font-medium">
                          {selectedKeywords.length} keywords will be permanently deleted
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={() => setShowBulkDeleteDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={deleteBulkKeywords}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete {selectedKeywords.length} Keywords
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

            </div>
          </TabsContent>

          {/* Clean Unified Persona Management */}
          <TabsContent value="personas">
            <div className="space-y-6">
              {/* Clean Personas Header - Matching App Style */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Persona Management
                      <Badge variant="secondary" className="ml-2">
                        {personas.length + aiPersonas.length} Total
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Create and manage customer personas with AI-powered insights or manual filters
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Persona Creation Options - New Clean Design */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Build Your Personas Button - WORKING VERSION */}
                <div className="border-2 border-green-200 bg-green-50 rounded-lg p-6 text-center hover:shadow-lg transition-all duration-300">
                  <div className="space-y-4">
                    <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                      <UserPlus className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-green-900">Build Your Personas</h3>
                      <p className="text-sm text-green-700 mt-2">
                        Create detailed buyer personas through our guided 7-step wizard. 
                        Define demographics, behaviors, and preferences manually.
                      </p>
                    </div>
                    <button 
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold flex items-center justify-center mx-auto"
                      onClick={() => {
                        console.log('Start Building button clicked!');
                        console.log('Before setState - showPersonaBuilder:', showPersonaBuilder);
                        setShowPersonaBuilder(true);
                        console.log('After setState - should trigger re-render');
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Start Building
                    </button>
                  </div>
                </div>
                
                {/* AI Discovery Button - Keep Same Style */}
                <Card className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 border-purple-200 bg-purple-50">
                  <CardContent 
                    className="p-6 text-center"
                    onClick={() => setActivePersonaTab('ai')}
                  >
                    <div className="space-y-4">
                      <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                        <Bot className="h-8 w-8 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-purple-900">AI Discovery</h3>
                        <p className="text-sm text-purple-700 mt-2">
                          Let AI analyze your audience data to automatically discover and create personas 
                          based on behavioral patterns and demographics.
                        </p>
                      </div>
                      <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Discover Personas
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Manual Creation Content */}
              {activePersonaTab === 'manual' && (
                <div className="space-y-6">
                  {/* Manual Creation Info Banner - Clean Style */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Filter className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-blue-900 mb-2">Manual Persona Creation</h3>
                        <p className="text-sm text-blue-700">
                          Create personas using custom filters and rules. This gives you precise control over audience segmentation.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Persona Creation Form - Clean Style */}
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {editingPersona ? (
                          <>
                            <Edit2 className="h-5 w-5" />
                            Edit Persona: {editingPersona.name}
                          </>
                        ) : (
                          <>
                            <Plus className="h-5 w-5" />
                            Create New Persona
                          </>
                        )}
                      </CardTitle>
                      <CardDescription>
                        {editingPersona 
                          ? `Editing "${editingPersona.name}" - modify filters and settings below`
                          : 'Build a persona with custom filtering logic for precise audience targeting'
                        }
                      </CardDescription>
                      {editingPersona && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-blue-900">Editing Mode Active</p>
                              <p className="text-sm text-blue-700 mt-1">
                                You are currently editing an existing persona. Changes will update the original persona.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <Label htmlFor="persona-name" className="text-sm font-medium">
                                Persona Name *
                              </Label>
                              <Input
                                id="persona-name"
                                value={personaForm.name}
                                onChange={(e) => setPersonaForm({...personaForm, name: e.target.value})}
                                placeholder="e.g., Tech Professionals, Young Millennials"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="persona-description" className="text-sm font-medium">
                                Description
                              </Label>
                              <Textarea
                                id="persona-description"
                                value={personaForm.description}
                                onChange={(e) => setPersonaForm({...personaForm, description: e.target.value})}
                                placeholder="Brief description of this persona"
                                className="mt-1"
                                rows={3}
                              />
                            </div>
                          </div>

                          {/* Filters Section */}
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <Label className="text-sm font-medium">Audience Filters</Label>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={addPersonaFilter}
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Filter
                                </Button>
                                {personaForm.filters.length > 1 && (
                                  <Select
                                    value={personaForm.logic || 'AND'}
                                    onValueChange={(value) => setPersonaForm({...personaForm, logic: value})}
                                  >
                                    <SelectTrigger className="w-20">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="AND">AND</SelectItem>
                                      <SelectItem value="OR">OR</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            </div>

                            {personaForm.filters.length === 0 && (
                              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm font-medium text-yellow-800">No filters defined</p>
                                    <p className="text-sm text-yellow-700 mt-1">
                                      Add filters to define your persona criteria. Each filter will help narrow down your target audience.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {personaForm.filters.length > 0 && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <strong>Logic:</strong> Contacts must match{' '}
                                  <span className="font-mono bg-blue-100 px-1 rounded">
                                    {personaForm.logic || 'AND' === 'AND' ? 'ALL' : 'ANY'}
                                  </span>{' '}
                                  of the following {personaForm.filters.length} filter(s):
                                </p>
                              </div>
                            )}

                            <div className="space-y-3">
                              {personaForm.filters.map((filter, index) => (
                                <div key={index} className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-blue-500 rounded-full">
                                        {index + 1}
                                      </span>
                                      <span className="text-sm font-medium text-gray-700">Filter {index + 1}</span>
                                      {index > 0 && (
                                        <Badge variant="outline" className="text-xs">
                                          {personaForm.logic || 'AND'}
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removePersonaFilter(index)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                      <Label className="text-xs font-medium text-gray-600">Field</Label>
                                      <Select
                                        value={filter.field || ''}
                                        onValueChange={(value) => updatePersonaFilter(index, 'field', value)}
                                      >
                                        <SelectTrigger className="mt-1">
                                          <SelectValue placeholder="Select field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="email">Email</SelectItem>
                                          <SelectItem value="job_title">Job Title</SelectItem>
                                          <SelectItem value="company">Company</SelectItem>
                                          <SelectItem value="phone_number">Phone Number</SelectItem>
                                          <SelectItem value="city">City</SelectItem>
                                          <SelectItem value="state">State</SelectItem>
                                          <SelectItem value="country">Country</SelectItem>
                                          <SelectItem value="data_source">Data Source</SelectItem>
                                          <SelectItem value="engagement_score">Engagement Score</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    
                                    <div>
                                      <Label className="text-xs font-medium text-gray-600">Operator</Label>
                                      <Select
                                        value={filter.operator || ''}
                                        onValueChange={(value) => updatePersonaFilter(index, 'operator', value)}
                                      >
                                        <SelectTrigger className="mt-1">
                                          <SelectValue placeholder="Select operator" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="equals">Equals</SelectItem>
                                          <SelectItem value="contains">Contains</SelectItem>
                                          <SelectItem value="not_equals">Not Equals</SelectItem>
                                          <SelectItem value="not_contains">Does Not Contain</SelectItem>
                                          <SelectItem value="greater_than">Greater Than</SelectItem>
                                          <SelectItem value="less_than">Less Than</SelectItem>
                                          <SelectItem value="is_empty">Is Empty</SelectItem>
                                          <SelectItem value="is_not_empty">Is Not Empty</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    
                                    <div>
                                      <Label className="text-xs font-medium text-gray-600">Value</Label>
                                      <Input
                                        className="mt-1"
                                        value={filter.value || ''}
                                        onChange={(e) => updatePersonaFilter(index, 'value', e.target.value)}
                                        placeholder={filter.operator && ['is_empty', 'is_not_empty'].includes(filter.operator) ? 'No value needed' : 'Enter filter value'}
                                        disabled={filter.operator && ['is_empty', 'is_not_empty'].includes(filter.operator)}
                                      />
                                    </div>
                                  </div>

                                  {/* Filter validation */}
                                  {filter.field && filter.operator && (
                                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                      <strong>Preview:</strong> {filter.field} {filter.operator.replace('_', ' ')} {filter.value || (filter.operator.includes('empty') ? '(empty)' : '...')}
                                    </div>
                                  )}

                                  {(!filter.field || !filter.operator || (!filter.value && !['is_empty', 'is_not_empty'].includes(filter.operator))) && (
                                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                      <strong>Incomplete:</strong> Please select field, operator{!['is_empty', 'is_not_empty'].includes(filter.operator) && ', and value'}.
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="flex gap-2 pt-4">
                            <Button 
                              onClick={handleCreatePersona} 
                              disabled={!personaForm.name.trim() || isLoading}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  {editingPersona ? 'Updating...' : 'Creating...'}
                                </>
                              ) : (
                                editingPersona ? 'Update Persona' : 'Create Persona'
                              )}
                            </Button>
                            
                            {editingPersona && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingPersona(null);
                                  setPersonaForm({ name: '', description: '', filters: [] });
                                }}
                              >
                                Cancel Edit
                              </Button>
                            )}
                            
                            <Button
                              variant="outline"
                              onClick={() => setPersonaForm({ name: '', description: '', filters: [] })}
                            >
                              Clear Form
                            </Button>
                            
                            <Button
                              variant="outline"
                              onClick={() => handlePersonaPreview()}
                              disabled={personaForm.filters.length === 0 || isLoading}
                              className="ml-auto"
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Previewing...
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Preview Audience ({personaPreview?.match_count || '?'})
                                </>
                              )}
                            </Button>
                          </div>

                          {/* Preview Results */}
                          {personaPreview && (
                            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <h4 className="font-medium text-green-900">Persona Preview Results</h4>
                                  <p className="text-sm text-green-700 mt-1">
                                    <strong>{personaPreview.match_count || 0} contacts</strong> match your current filter criteria.
                                  </p>
                                  {personaPreview.preview && personaPreview.preview.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-green-600 mb-2">Sample matching contacts:</p>
                                      <div className="space-y-1">
                                        {personaPreview.preview.slice(0, 3).map((contact, idx) => (
                                          <div key={idx} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                                            {contact.email} {contact.job_title && `- ${contact.job_title}`} {contact.company && `at ${contact.company}`}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* AI Discovery Content */}
                  {activePersonaTab === 'ai' && (
                    <div className="space-y-6">
                      {/* AI Analysis Section - Clean Style */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <h3 className="font-medium text-blue-900 mb-2">AI Persona Generation</h3>
                            <p className="text-sm text-blue-700 mb-4">
                              Let AI analyze your 1070+ contacts to discover natural persona patterns automatically.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3 mb-4">
                          <Button
                            onClick={() => analyzeAudienceForAI()}
                            disabled={isLoading}
                            variant="outline"
                          >
                            {isLoading ? 'Analyzing...' : 'Analyze Audience Data'}
                          </Button>
                        </div>
                        {audienceAnalysis && (
                          <div className="p-3 bg-white border border-gray-200 rounded text-sm">
                            <div className="grid grid-cols-3 gap-4 text-gray-700">
                              <div>
                                <strong>Data Completeness:</strong> {audienceAnalysis.audit_data?.data_completeness?.overall_completeness?.toFixed(1)}%
                              </div>
                              <div>
                                <strong>Total Contacts:</strong> {audienceAnalysis.audit_data?.total_contacts || 0}
                              </div>
                              <div>
                                <strong>Clustering Ready:</strong> {audienceAnalysis.audit_data?.clustering_readiness?.overall_readiness_score?.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Advanced AI Configuration */}
                      <Card className="shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            Advanced AI Configuration
                          </CardTitle>
                          <CardDescription>
                            Configure machine learning parameters for intelligent persona generation
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {/* AI Logic Guide - Clean Style */}
                          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                              <div>
                                <h4 className="font-semibold text-blue-900 mb-2">AI Persona Generation Guide</h4>
                                <p className="text-sm text-blue-800 mb-3">
                                  Our AI analyzes <strong>Enhanced Audience Data + Linked Sections</strong> to create intelligent personas:
                                </p>
                                
                                <div className="grid md:grid-cols-2 gap-4 text-xs">
                                  {/* Data Sources Used */}
                                  <div className="bg-white p-3 rounded border border-blue-100">
                                    <h5 className="font-medium text-blue-900 mb-2">📊 Data Sources Analyzed</h5>
                                    <ul className="space-y-1 text-blue-700">
                                      <li>• <strong>Basic Contact Data</strong>: Name, email, company, job title</li>
                                      <li>• <strong>📦 Purchase History</strong>: Buying patterns, product preferences</li>
                                      <li>• <strong>🏢 Employment Data</strong>: Career level, industry, experience</li>
                                      <li>• <strong>🏦 Financial Info</strong>: Budget range, purchase capacity</li>
                                      <li>• <strong>🛡️ Insurance Policies</strong>: Risk profile, coverage needs</li>
                                      <li>• <strong>🎯 Preferences</strong>: Communication style, channel preferences</li>
                                      <li>• <strong>📍 Location</strong>: Geographic patterns, market regions</li>
                                    </ul>
                                  </div>

                                  {/* AI Processing Logic */}
                                  <div className="bg-white p-3 rounded border border-blue-100">
                                    <h5 className="font-medium text-blue-900 mb-2">🧠 AI Processing Logic</h5>
                                    <div className="space-y-2 text-blue-700 text-xs">
                                      <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 bg-green-100 text-green-600 rounded text-center text-[10px] font-bold">1</span>
                                        <span><strong>Data Enrichment</strong>: Combines basic + linked section data</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 bg-blue-100 text-blue-600 rounded text-center text-[10px] font-bold">2</span>
                                        <span><strong>Feature Engineering</strong>: Creates 40+ behavioral dimensions</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 bg-purple-100 text-purple-600 rounded text-center text-[10px] font-bold">3</span>
                                        <span><strong>ML Clustering</strong>: Groups similar contacts using your settings</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 bg-orange-100 text-orange-600 rounded text-center text-[10px] font-bold">4</span>
                                        <span><strong>Persona Creation</strong>: Generates profiles with insights</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Key Benefits */}
                                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                                  <p className="text-xs text-green-800">
                                    <strong>✅ Enhanced Intelligence:</strong> Rich linked section data enables AI to create personas that understand <em>buying behavior, career stage, financial capacity,</em> and <em>communication preferences</em> - not just basic demographics!
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Basic Settings */}
                            <div className="space-y-4">
                              <h4 className="font-medium text-sm text-gray-900">Basic Settings</h4>
                              
                              <div>
                                <Label className="text-sm font-medium">Minimum Cluster Size</Label>
                                <Input
                                  type="number"
                                  value={aiPersonaConfig.minClusterSize}
                                  onChange={(e) => setAiPersonaConfig({...aiPersonaConfig, minClusterSize: parseInt(e.target.value)})}
                                  min="2"
                                  max="50"
                                  className="mt-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Minimum contacts required per persona (recommended: 5-10)
                                </p>
                              </div>

                              <div>
                                <Label className="text-sm font-medium">Maximum Personas</Label>
                                <Input
                                  type="number"
                                  value={aiPersonaConfig.maxPersonas}
                                  onChange={(e) => setAiPersonaConfig({...aiPersonaConfig, maxPersonas: parseInt(e.target.value)})}
                                  min="2"
                                  max="15"
                                  className="mt-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Maximum personas to generate (recommended: 5-8)
                                </p>
                              </div>

                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id="use-advanced"
                                  checked={aiPersonaConfig.useAdvanced}
                                  onChange={(e) => setAiPersonaConfig({...aiPersonaConfig, useAdvanced: e.target.checked})}
                                  className="rounded"
                                />
                                <div>
                                  <Label htmlFor="use-advanced" className="text-sm font-medium">
                                    Use Advanced ML Algorithms
                                  </Label>
                                  <p className="text-xs text-gray-500">
                                    Enable sophisticated clustering with feature engineering
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Advanced Settings */}
                            <div className="space-y-4">
                              <h4 className="font-medium text-sm text-gray-900">Algorithm Settings</h4>

                              <div>
                                <Label className="text-sm font-medium">Clustering Method</Label>
                                <Select 
                                  value={aiPersonaConfig.clusteringMethod} 
                                  onValueChange={(value) => setAiPersonaConfig({...aiPersonaConfig, clusteringMethod: value})}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="auto">Auto (Best Algorithm)</SelectItem>
                                    <SelectItem value="kmeans">K-Means Clustering</SelectItem>
                                    <SelectItem value="dbscan">DBSCAN Clustering</SelectItem>
                                    <SelectItem value="hierarchical">Hierarchical Clustering</SelectItem>
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 mt-1">
                                  AI will select optimal algorithm or use your preference
                                </p>
                              </div>

                              <div>
                                <Label className="text-sm font-medium">Feature Weight Focus</Label>
                                <Select 
                                  value={aiPersonaConfig.featureFocus || 'balanced'} 
                                  onValueChange={(value) => setAiPersonaConfig({...aiPersonaConfig, featureFocus: value})}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="balanced">Balanced (All Features)</SelectItem>
                                    <SelectItem value="demographic">Demographic Focus</SelectItem>
                                    <SelectItem value="behavioral">Behavioral Focus</SelectItem>
                                    <SelectItem value="engagement">Engagement Focus</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                                  {aiPersonaConfig.featureFocus === 'demographic' && (
                                    <span>📊 <strong>Demographic Focus:</strong> Emphasizes job title, company, location data. Uses employment & financial linked sections heavily.</span>
                                  )}
                                  {aiPersonaConfig.featureFocus === 'behavioral' && (
                                    <span>🛒 <strong>Behavioral Focus:</strong> Prioritizes purchase history, product preferences, and usage patterns from linked sections.</span>
                                  )}
                                  {aiPersonaConfig.featureFocus === 'engagement' && (
                                    <span>📱 <strong>Engagement Focus:</strong> Weighs communication preferences, channel activity, and interaction data more heavily.</span>
                                  )}
                                  {(!aiPersonaConfig.featureFocus || aiPersonaConfig.featureFocus === 'balanced') && (
                                    <span>⚖️ <strong>Balanced:</strong> Equal weight to all data sources - demographics, purchase history, employment, financial, and preferences.</span>
                                  )}
                                </div>
                              </div>

                              <div>
                                <Label className="text-sm font-medium">Similarity Threshold</Label>
                                <div className="flex items-center space-x-2 mt-1">
                                  <input
                                    type="range"
                                    min="0.5"
                                    max="0.9"
                                    step="0.1"
                                    value={aiPersonaConfig.similarityThreshold || 0.7}
                                    onChange={(e) => setAiPersonaConfig({...aiPersonaConfig, similarityThreshold: parseFloat(e.target.value)})}
                                    className="flex-1"
                                  />
                                  <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded min-w-[3rem]">
                                    {((aiPersonaConfig.similarityThreshold || 0.7) * 100).toFixed(0)}%
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Lower = more personas, Higher = fewer but more distinct
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Linked Section Data Usage - Clean Style */}
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                              <Database className="h-4 w-4" />
                              Linked Section Data Intelligence
                            </h4>
                            
                            <div className="grid md:grid-cols-2 gap-4">
                              {/* Financial Intelligence */}
                              <div className="bg-white p-3 rounded border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-800 mb-2">🏦 Financial Intelligence</h5>
                                <ul className="text-[11px] text-gray-700 space-y-1">
                                  <li>• Income level patterns for budget segmentation</li>
                                  <li>• Insurance coverage for risk profiling</li>
                                  <li>• Investment behavior analysis</li>
                                  <li>• Payment method preferences</li>
                                </ul>
                              </div>

                              {/* Behavioral Insights */}
                              <div className="bg-white p-3 rounded border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-800 mb-2">🛒 Behavioral Insights</h5>
                                <ul className="text-[11px] text-gray-700 space-y-1">
                                  <li>• Purchase frequency and seasonality</li>
                                  <li>• Product category preferences</li>
                                  <li>• Brand loyalty indicators</li>
                                  <li>• Price sensitivity analysis</li>
                                </ul>
                              </div>

                              {/* Professional Context */}
                              <div className="bg-white p-3 rounded border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-800 mb-2">🏢 Professional Context</h5>
                                <ul className="text-[11px] text-gray-700 space-y-1">
                                  <li>• Career stage and advancement trajectory</li>
                                  <li>• Industry expertise and specialization</li>
                                  <li>• Company size and role influence</li>
                                  <li>• Professional network patterns</li>
                                </ul>
                              </div>

                              {/* Engagement Patterns */}
                              <div className="bg-white p-3 rounded border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-800 mb-2">📱 Engagement Patterns</h5>
                                <ul className="text-[11px] text-gray-700 space-y-1">
                                  <li>• Communication channel preferences</li>
                                  <li>• Response time and frequency</li>
                                  <li>• Content consumption habits</li>
                                  <li>• Interaction quality scoring</li>
                                </ul>
                              </div>
                            </div>

                            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                              <p className="text-xs text-blue-800">
                                <strong>💡 Pro Tip:</strong> The more linked section data you have, the more accurate and actionable your AI personas become. Ensure your contacts have rich purchase history, employment, and preference data for optimal results.
                              </p>
                            </div>
                          </div>

                          {/* Generation Controls */}
                          <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-sm text-gray-900">Generate Personas</h4>
                                <p className="text-xs text-gray-500">
                                  Create AI personas using the configuration above
                                </p>
                              </div>
                              <Button
                                onClick={() => generateAIPersonas()}
                                disabled={isLoading}
                                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                              >
                                {isLoading ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Generating AI Personas...
                                  </>
                                ) : (
                                  <>
                                    <Brain className="h-4 w-4 mr-2" />
                                    Generate AI Personas
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

              {/* Enhanced Personas List with Pagination & Bulk Actions - Clean Style */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      All Personas
                      <Badge variant="secondary" className="ml-2">
                        {personas.length + aiPersonas.length} Total
                      </Badge>
                      {selectedPersonaIds.size > 0 && (
                        <Badge variant="default" className="ml-2 bg-blue-600">
                          {selectedPersonaIds.size} Selected
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedPersonaIds.size > 0 && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={clearPersonaSelection}
                          >
                            Clear Selection
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setShowBulkDeleteModal(true)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Selected ({selectedPersonaIds.size})
                          </Button>
                        </div>
                      )}
                      <div className="text-sm text-gray-500">
                        Page {personasPage} of {Math.ceil((personas.length + aiPersonas.length) / personasPerPage)}
                      </div>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    All your personas (manual and AI-generated) sorted by creation date
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(personas.length + aiPersonas.length) > 0 ? (
                    <div className="space-y-4">
                      {/* Bulk Actions Header - Clean Style */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={(() => {
                              const allPersonas = [...personas.map(p => ({...p, type: 'manual'})), ...aiPersonas.map(p => ({...p, type: 'ai'}))];
                              const sortedPersonas = allPersonas.sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
                              const startIndex = (personasPage - 1) * personasPerPage;
                              const paginatedPersonas = sortedPersonas.slice(startIndex, startIndex + personasPerPage);
                              const currentPageIds = paginatedPersonas.map(p => p.id);
                              return currentPageIds.length > 0 && currentPageIds.every(id => selectedPersonaIds.has(id));
                            })()}
                            onChange={toggleSelectAllPersonas}
                            className="rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Select All on This Page
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {selectedPersonaIds.size > 0 ? `${selectedPersonaIds.size} personas selected` : 'No personas selected'}
                        </div>
                      </div>

                      {/* Personas List */}
                      <div className="space-y-3">
                        {(() => {
                          const allPersonas = [...personas.map(p => ({...p, type: 'manual'})), ...aiPersonas.map(p => ({...p, type: 'ai'}))];
                          const sortedPersonas = allPersonas.sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
                          const startIndex = (personasPage - 1) * personasPerPage;
                          const paginatedPersonas = sortedPersonas.slice(startIndex, startIndex + personasPerPage);
                          
                          return paginatedPersonas.map((persona) => (
                            <div key={persona.id} className={`p-4 border-2 rounded-lg hover:shadow-md transition-all ${
                              selectedPersonaIds.has(persona.id) 
                                ? 'border-blue-300 bg-blue-50' 
                                : 'border-gray-100 hover:border-gray-200'
                            }`}>
                              <div className="flex items-start gap-4">
                                {/* Selection Checkbox */}
                                <div className="flex items-center pt-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedPersonaIds.has(persona.id)}
                                    onChange={() => togglePersonaSelection(persona.id)}
                                    className="rounded"
                                  />
                                </div>

                                {/* Persona Content */}
                                <div className="flex items-start justify-between flex-1">
                                  <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                      persona.type === 'ai' 
                                        ? 'bg-gradient-to-br from-purple-500 to-blue-500' 
                                        : 'bg-gradient-to-br from-blue-500 to-green-500'
                                    }`}>
                                      {persona.type === 'ai' ? (
                                        <Brain className="h-6 w-6 text-white" />
                                      ) : (
                                        <Filter className="h-6 w-6 text-white" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-gray-900">{persona.name}</h3>
                                        <Badge variant="outline" className={`text-xs ${
                                          persona.type === 'ai' 
                                            ? 'bg-purple-50 border-purple-200 text-purple-700' 
                                            : 'bg-blue-50 border-blue-200 text-blue-700'
                                        }`}>
                                          {persona.type === 'ai' ? 'AI Generated' : 'Manual'}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-2">{persona.description || 'No description available'}</p>
                                      <div className="flex items-center gap-4 text-xs text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Users className="h-3 w-3" />
                                          {persona.contact_count || persona.contactCount || (persona.contact_ids ? persona.contact_ids.length : 0)} contacts
                                        </span>
                                        {persona.type === 'ai' && persona.confidence_score && (
                                          <span className="flex items-center gap-1">
                                            <Target className="h-3 w-3" />
                                            {(persona.confidence_score * 100).toFixed(0)}% confidence
                                          </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                          <Calendar className="h-3 w-3" />
                                          {new Date(persona.created_at || persona.createdAt || 0).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleViewPersonaDetails(persona)}
                                      title="View persona details and contacts"
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    {persona.type === 'manual' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditPersona(persona)}
                                        title="Edit manual persona"
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => persona.type === 'ai' ? deleteAIPersona(persona.id, persona.name) : handleDeletePersona(persona.id, persona.name)}
                                      title="Delete persona"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Pagination - Clean Style */}
                      {(personas.length + aiPersonas.length) > personasPerPage && (
                        <div className="flex items-center justify-between pt-4 border-t">
                          <div className="text-sm text-gray-600">
                            Showing {Math.min((personasPage - 1) * personasPerPage + 1, personas.length + aiPersonas.length)}-{Math.min(personasPage * personasPerPage, personas.length + aiPersonas.length)} of {personas.length + aiPersonas.length} personas
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPersonasPage(Math.max(1, personasPage - 1))}
                              disabled={personasPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </Button>
                            <div className="flex gap-1">
                              {Array.from({ length: Math.ceil((personas.length + aiPersonas.length) / personasPerPage) }).map((_, i) => (
                                <Button
                                  key={i}
                                  size="sm"
                                  variant={personasPage === i + 1 ? "default" : "outline"}
                                  onClick={() => setPersonasPage(i + 1)}
                                  className="w-8 h-8 p-0"
                                >
                                  {i + 1}
                                </Button>
                              ))}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPersonasPage(Math.min(Math.ceil((personas.length + aiPersonas.length) / personasPerPage), personasPage + 1))}
                              disabled={personasPage === Math.ceil((personas.length + aiPersonas.length) / personasPerPage)}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 mb-2 font-medium">No personas created yet</p>
                      <p className="text-sm text-gray-400">Create your first persona using manual filters or AI discovery above</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Enhanced Persona Details Modal */}
              {showPersonaDetailsModal && selectedPersonaDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
                    {/* Enhanced Modal Header */}
                    <div className="flex items-center justify-between p-6 border-b bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          selectedPersonaDetails.type === 'ai' 
                            ? 'bg-gradient-to-br from-purple-500 to-blue-500' 
                            : 'bg-gradient-to-br from-blue-500 to-green-500'
                        }`}>
                          {selectedPersonaDetails.type === 'ai' ? (
                            <Brain className="h-5 w-5 text-white" />
                          ) : (
                            <Filter className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold">{selectedPersonaDetails.name}</h2>
                          <p className="text-sm text-gray-600">
                            {selectedPersonaDetails.type === 'ai' ? 'AI Generated Persona' : 'Manual Persona'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            toast.success(`📝 Modify Persona: Opening edit mode for "${selectedPersonaDetails.name}". This would allow you to adjust filters, update description, or modify AI configuration parameters.`);
                          }}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Modify Persona
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPersonaDetailsModal(false)}
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Enhanced Modal Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        {/* Left Column - Persona Development Details */}
                        <div className="space-y-6">
                          {/* Persona Development Card */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-lg">
                                <Settings className="h-5 w-5" />
                                Persona Development Details
                              </CardTitle>
                              <CardDescription>
                                Information used to create and develop this persona
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {/* Basic Information */}
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <h4 className="font-medium text-sm mb-2">Basic Information</h4>
                                <div className="space-y-1 text-sm">
                                  <p><strong>Description:</strong> {selectedPersonaDetails.description || 'No description available'}</p>
                                  <p><strong>Contact Count:</strong> {selectedPersonaDetails.contact_count || selectedPersonaDetails.contactCount || (selectedPersonaDetails.contact_ids ? selectedPersonaDetails.contact_ids.length : 0)}</p>
                                  <p><strong>Created:</strong> {new Date(selectedPersonaDetails.created_at || selectedPersonaDetails.createdAt || 0).toLocaleDateString()}</p>
                                  {selectedPersonaDetails.type === 'ai' && selectedPersonaDetails.confidence_score && (
                                    <p><strong>Confidence Score:</strong> {(selectedPersonaDetails.confidence_score * 100).toFixed(1)}%</p>
                                  )}
                                  {selectedPersonaDetails.generation_method && (
                                    <p><strong>Generation Method:</strong> {selectedPersonaDetails.generation_method.replace(/_/g, ' ')}</p>
                                  )}
                                </div>
                              </div>

                              {/* Filters Used (for Manual Personas) */}
                              {selectedPersonaDetails.type === 'manual' && selectedPersonaDetails.filters && (
                                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <h4 className="font-medium text-sm mb-2 text-blue-900">Filters Used</h4>
                                  <div className="space-y-2">
                                    {selectedPersonaDetails.filters.map((filter, index) => (
                                      <div key={index} className="text-xs bg-white p-2 rounded border border-blue-100">
                                        <span className="font-medium">{filter.field}</span> {filter.operator} <span className="font-medium">"{filter.value}"</span>
                                      </div>
                                    ))}
                                    <p className="text-xs text-blue-700 mt-2">
                                      <strong>Logic:</strong> Contacts must match {selectedPersonaDetails.logic === 'AND' ? 'ALL' : 'ANY'} of the above filters
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* AI Configuration Used (for AI Personas) */}
                              {selectedPersonaDetails.type === 'ai' && (
                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                                  <h4 className="font-medium text-sm mb-2 text-purple-900">AI Configuration Used</h4>
                                  <div className="space-y-1 text-xs text-purple-800">
                                    <p><strong>Algorithm:</strong> {selectedPersonaDetails.algorithm || 'Advanced Clustering'}</p>
                                    <p><strong>Data Sources:</strong> Enhanced Contacts + Linked Sections</p>
                                    <p><strong>Feature Focus:</strong> {selectedPersonaDetails.feature_focus || 'Balanced'}</p>
                                    {selectedPersonaDetails.cluster_size && (
                                      <p><strong>Cluster Size:</strong> {selectedPersonaDetails.cluster_size} contacts</p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Campaign Alignment Options */}
                              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                <h4 className="font-medium text-sm mb-2 text-green-900">Campaign Alignment</h4>
                                <p className="text-xs text-green-700 mb-3">
                                  Modify this persona to align with specific campaign requirements
                                </p>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    onClick={() => handleOptimizePersona(selectedPersonaDetails, 'engagement')}
                                    disabled={isLoading}
                                  >
                                    <Target className="h-3 w-3 mr-1" />
                                    Optimize for Engagement
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-xs"
                                    onClick={() => handleOptimizePersona(selectedPersonaDetails, 'conversion')}
                                    disabled={isLoading}
                                  >
                                    <DollarSign className="h-3 w-3 mr-1" />
                                    Optimize for Conversion
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>

                        {/* Right Column - Enhanced Contact Management */}
                        <div className="space-y-6">
                          {/* Associated Contacts with Table */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Users className="h-5 w-5" />
                                  Associated Contacts ({personaContacts.length})
                                </div>
                                <div className="flex items-center gap-2">
                                  {personaContacts.length === 0 && selectedPersonaDetails && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleFixPersonaContacts(selectedPersonaDetails)}
                                      disabled={isLoading}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      {isLoading ? (
                                        <>
                                          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                          Fixing...
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="h-3 w-3 mr-2" />
                                          Fix Contact Links
                                        </>
                                      )}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddContactsToPersona(selectedPersonaDetails)}
                                    disabled={isLoading}
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Contacts
                                  </Button>
                                </div>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Contact Management Actions */}
                              {personaContacts.length > 0 && (
                                <div className="mb-4 flex items-center gap-2">
                                  <Button size="sm" variant="outline" className="text-xs">
                                    <Search className="h-3 w-3 mr-1" />
                                    Search Contacts
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-xs">
                                    <Filter className="h-3 w-3 mr-1" />
                                    Filter
                                  </Button>
                                </div>
                              )}

                              {/* Enhanced Contact Table */}
                              {personaContacts.length > 0 ? (
                                <div className="border rounded-lg">
                                  <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b">
                                      <tr>
                                        <th className="text-left p-3 font-medium">Contact</th>
                                        <th className="text-left p-3 font-medium">Company</th>
                                        <th className="text-left p-3 font-medium">Location</th>
                                        <th className="text-right p-3 font-medium">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {personaContacts.slice(0, 20).map((contact, index) => (
                                        <tr key={contact.id || index} className="border-b hover:bg-gray-50">
                                          <td className="p-3">
                                            <div>
                                              <p className="font-medium">
                                                {contact.full_name || contact.name || 
                                                 (contact.email_addresses && contact.email_addresses[0]) || 
                                                 contact.email || 'Unnamed Contact'}
                                              </p>
                                              {(contact.email_addresses && contact.email_addresses[0]) && (
                                                <p className="text-xs text-blue-600">{contact.email_addresses[0]}</p>
                                              )}
                                              {(contact.phone_numbers && contact.phone_numbers[0]) && (
                                                <p className="text-xs text-green-600">{contact.phone_numbers[0]}</p>
                                              )}
                                              {contact.job_title && (
                                                <p className="text-xs text-gray-600">{contact.job_title}</p>
                                              )}
                                            </div>
                                          </td>
                                          <td className="p-3">
                                            <p className="text-gray-700">{contact.company_name || contact.company || 'N/A'}</p>
                                          </td>
                                          <td className="p-3">
                                            <p className="text-gray-700">{contact.city || 'N/A'}</p>
                                            {contact.state_province && (
                                              <p className="text-xs text-gray-500">{contact.state_province}</p>
                                            )}
                                            {contact.country && (
                                              <p className="text-xs text-gray-500">{contact.country}</p>
                                            )}
                                          </td>
                                          <td className="p-3 text-right">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => {
                                                toast.info('Remove contact feature coming soon!');
                                              }}
                                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {personaContacts.length > 20 && (
                                    <div className="p-3 text-center border-t bg-gray-50">
                                      <p className="text-sm text-gray-600">
                                        Showing first 20 contacts. Total: {personaContacts.length} contacts
                                      </p>
                                      <Button size="sm" variant="outline" className="mt-2">
                                        Load More Contacts
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                  <p className="text-gray-600 font-medium mb-2">No contacts currently loaded</p>
                                  {selectedPersonaDetails && selectedPersonaDetails.contact_count > 0 ? (
                                    <div className="space-y-3">
                                      <p className="text-sm text-gray-500">
                                        This persona shows {selectedPersonaDetails.contact_count} contacts, but they're not loading properly.
                                      </p>
                                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <p className="text-xs text-blue-700 mb-2">
                                          <strong>💡 Contact Loading Issue:</strong> The enhanced contacts collection appears to be empty. To see contacts, first import contact data using the Audience → Import Wizard.
                                        </p>
                                        <Button
                                          size="sm"
                                          onClick={() => handleFixPersonaContacts(selectedPersonaDetails)}
                                          disabled={isLoading}
                                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                        >
                                          {isLoading ? (
                                            <>
                                              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                              Fixing Contact Links...
                                            </>
                                          ) : (
                                            <>
                                              <RefreshCw className="h-3 w-3 mr-2" />
                                              Fix Contact Links
                                            </>
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">
                                      This persona doesn't have any associated contacts yet.
                                    </p>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Delete Confirmation Modal */}
              {showBulkDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <Trash2 className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Delete Selected Personas</h3>
                          <p className="text-sm text-gray-600">This action cannot be undone</p>
                        </div>
                      </div>
                      
                      <div className="mb-6">
                        <p className="text-gray-700 mb-3">
                          Are you sure you want to delete <strong>{selectedPersonaIds.size} persona(s)</strong>? This will permanently remove:
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          <li>All persona definitions and configurations</li>
                          <li>Associated filter rules and criteria</li>
                          <li>Persona analytics and historical data</li>
                        </ul>
                        <p className="text-sm text-red-600 mt-3 font-medium">
                          ⚠️ Contact data will remain intact, but persona associations will be lost.
                        </p>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => setShowBulkDeleteModal(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleBulkDeletePersonas}
                          disabled={isLoading}
                          className="flex-1"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete {selectedPersonaIds.size} Personas
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Build Your Personas Modal */}
            <PersonaBuilderModal 
              showPersonaBuilder={showPersonaBuilder}
              setShowPersonaBuilder={setShowPersonaBuilder}
              wizardOptions={memoizedWizardOptions}
              wizardOptionsLoaded={wizardOptionsLoaded}
              setWizardOptionsLoaded={setWizardOptionsLoaded}
              onPersonaCreated={(persona) => {
                // Refresh personas list
                loadPersonas();
              }}
            />
            
            {/* Debug: Show modal state */}
            {process.env.NODE_ENV === 'development' && (
              <div className="fixed bottom-4 right-4 bg-black text-white p-2 rounded text-xs z-50">
                Modal State: {showPersonaBuilder ? 'OPEN' : 'CLOSED'}
              </div>
            )}
            
          </TabsContent>



          {/* Enhanced Multi-Channel Campaigns */}
          <TabsContent value="campaigns">
            <div className="space-y-6">
              {/* Campaign Creation Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Multi-Channel Campaign Creation
                  </CardTitle>
                  <CardDescription>
                    Create targeted campaigns across WhatsApp, SMS, Email, and Facebook Ads
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeCampaignTab} onValueChange={setActiveCampaignTab}>
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="whatsapp" className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </TabsTrigger>
                      <TabsTrigger value="sms" className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        SMS
                      </TabsTrigger>
                      <TabsTrigger value="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email
                      </TabsTrigger>
                      <TabsTrigger value="facebook" className="flex items-center gap-2">
                        <Facebook className="h-4 w-4" />
                        Facebook Ads
                      </TabsTrigger>
                    </TabsList>

                    {/* WhatsApp Campaign */}
                    <TabsContent value="whatsapp">
                      <div className="grid lg:grid-cols-2 gap-6 mt-6">
                        {/* Left Side - Campaign Form */}
                        <div className="space-y-6">
                          <div className="flex items-center gap-2 mb-4">
                            <MessageCircle className="h-5 w-5 text-green-600" />
                            <h3 className="text-lg font-semibold text-gray-900">WhatsApp Campaign Setup</h3>
                          </div>
                          
                          <div className="grid gap-4">
                            <div>
                              <Label>Campaign Name *</Label>
                              <Input
                                value={whatsappForm.name}
                                onChange={(e) => setWhatsappForm({...whatsappForm, name: e.target.value})}
                                placeholder="Enter WhatsApp campaign name"
                              />
                            </div>

                            <div>
                              <Label>Target Persona *</Label>
                              <Select 
                                key={`whatsapp-persona-${whatsappForm.persona_id || 'empty'}`}
                                value={personas.find(p => p.id === whatsappForm.persona_id) ? whatsappForm.persona_id : ''} 
                                onValueChange={(value) => setWhatsappForm({...whatsappForm, persona_id: value})}
                                onOpenChange={(open) => {
                                  // Force cleanup when dropdown closes
                                  if (!open) {
                                    setTimeout(() => {
                                      // Small delay to ensure portal cleanup
                                    }, 100);
                                  }
                                }}
                              >
                                <SelectTrigger className="relative z-10">
                                  <SelectValue placeholder="Select target persona" />
                                </SelectTrigger>
                                <SelectContent 
                                  className="z-[100] max-h-[200px] overflow-y-auto"
                                  position="popper"
                                  sideOffset={5}
                                >
                                  {personas.map(persona => (
                                    <SelectItem key={`whatsapp-persona-${persona.id}`} value={persona.id}>
                                      {persona.name} ({persona.match_count} matches)
                                    </SelectItem>
                                  ))}
                                  {personas.length === 0 && (
                                    <SelectItem value="no_personas" disabled>
                                      No personas available
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label>Message * (Max 1000 characters)</Label>
                              <div className="relative">
                                <Textarea
                                  value={whatsappForm.message}
                                  onChange={handleWhatsAppMessageChange}
                                  placeholder="Enter your WhatsApp message..."
                                  rows={4}
                                  maxLength={1000}
                                  disabled={urlShorteningInProgress}
                                />
                                {urlShorteningInProgress && (
                                  <div className="absolute right-2 top-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between text-sm mt-1">
                                <span className="text-gray-500">
                                  {whatsappForm.message.length}/1000 characters
                                </span>
                                {urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && (
                                  <span className="text-blue-600 text-xs flex items-center gap-1">
                                    <Link className="h-3 w-3" />
                                    URLs auto-shortened
                                  </span>
                                )}
                              </div>
                            </div>

                            <div>
                              <Label>Website URL (Optional)</Label>
                              <div className="relative">
                                <Input
                                  value={whatsappForm.url}
                                  onChange={handleWhatsAppUrlChange}
                                  placeholder="https://your-website.com"
                                  type="url"
                                  disabled={urlShorteningInProgress}
                                />
                                {urlShorteningInProgress && (
                                  <div className="absolute right-2 top-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                  </div>
                                )}
                              </div>
                              {urlShortenerSettings.configured && urlShortenerSettings.auto_shorten && (
                                <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                                  <Link className="h-3 w-3" />
                                  URL will be automatically shortened
                                </p>
                              )}
                            </div>

                            {/* Media Upload Section */}
                            <div>
                              <Label>Attach Media (Optional)</Label>
                              <div className="mt-2">
                                <div className="flex items-center justify-center w-full">
                                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      {uploadedMedia ? (
                                        <div className="text-center">
                                          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                                          <p className="text-sm text-gray-500">
                                            {uploadedMedia.filename}
                                          </p>
                                          <p className="text-xs text-gray-400">
                                            {(uploadedMedia.size / 1024).toFixed(1)} KB
                                          </p>
                                        </div>
                                      ) : (
                                        <div className="text-center">
                                          {uploadProgress > 0 ? (
                                            <div>
                                              <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                                              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                                <div 
                                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                                  style={{ width: `${uploadProgress}%` }}
                                                ></div>
                                              </div>
                                              <p className="text-sm text-gray-500">{uploadProgress}%</p>
                                            </div>
                                          ) : (
                                            <div>
                                              <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                              <p className="mb-2 text-sm text-gray-500">
                                                <span className="font-semibold">Click to upload</span> media
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                Images, Videos, GIFs (MAX 10MB)
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <input 
                                      type="file" 
                                      className="hidden" 
                                      accept="image/*,video/*,.gif"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handleMediaUpload(file);
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                                {uploadedMedia && (
                                  <div className="mt-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => {
                                        setUploadedMedia(null);
                                        setWhatsappForm(prev => ({...prev, media_id: null}));
                                      }}
                                    >
                                      Remove Media
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Campaign Scheduling Section */}
                            <div className="border-t pt-4">
                              <div className="space-y-4">
                                <div className="flex items-center space-x-4">
                                  <div>
                                    <Label className="text-base font-medium">Campaign Timing</Label>
                                    <p className="text-sm text-gray-500">Choose when to send this campaign</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div 
                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                      whatsappForm.send_immediately 
                                        ? 'border-green-500 bg-green-50 ring-1 ring-green-500' 
                                        : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                    onClick={() => setWhatsappForm({...whatsappForm, send_immediately: true, schedule_date: ''})}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${
                                        whatsappForm.send_immediately 
                                          ? 'border-green-500 bg-green-500' 
                                          : 'border-gray-300'
                                      }`}>
                                        {whatsappForm.send_immediately && (
                                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                        )}
                                      </div>
                                      <Play className="h-4 w-4 text-green-600" />
                                      <span className="font-medium">Send Now</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 ml-6">
                                      Send for approval immediately
                                    </p>
                                  </div>

                                  <div 
                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                      !whatsappForm.send_immediately 
                                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' 
                                        : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                    onClick={() => setWhatsappForm({...whatsappForm, send_immediately: false})}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${
                                        !whatsappForm.send_immediately 
                                          ? 'border-blue-500 bg-blue-500' 
                                          : 'border-gray-300'
                                      }`}>
                                        {!whatsappForm.send_immediately && (
                                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                        )}
                                      </div>
                                      <Clock className="h-4 w-4 text-blue-600" />
                                      <span className="font-medium">Schedule Later</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 ml-6">
                                      Schedule for a future date
                                    </p>
                                  </div>
                                </div>

                                {!whatsappForm.send_immediately && (
                                  <div className="mt-4">
                                    <Label>Schedule Date & Time *</Label>
                                    <Input
                                      type="datetime-local"
                                      value={whatsappForm.schedule_date}
                                      onChange={(e) => setWhatsappForm({...whatsappForm, schedule_date: e.target.value})}
                                      min={new Date().toISOString().slice(0, 16)}
                                      className="mt-1"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">
                                      Campaign will be automatically sent at the scheduled time
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <Button 
                              onClick={handleCreateWhatsappCampaign} 
                              disabled={isLoading || !whatsappForm.name || !whatsappForm.message || !whatsappForm.persona_id || (!whatsappForm.send_immediately && !whatsappForm.schedule_date)}
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />
                              {isLoading ? 'Processing...' : 
                               whatsappForm.send_immediately ? 'Create & Submit for Approval' : 'Schedule WhatsApp Campaign'}
                            </Button>
                          </div>
                        </div>

                        {/* Right Side - WhatsApp Preview */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Eye className="h-5 w-5 text-gray-600" />
                            <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                          </div>
                          
                          <WhatsAppPreview form={whatsappForm} uploadedMedia={uploadedMedia} />
                        </div>
                      </div>
                    </TabsContent>

                    {/* SMS Campaign */}
                    <TabsContent value="sms">
                      <div className="grid lg:grid-cols-2 gap-6 mt-6">
                        {/* Left Side - Campaign Form */}
                        <div className="space-y-6">
                          <div className="flex items-center gap-2 mb-4">
                            <Smartphone className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">SMS Campaign Setup</h3>
                          </div>
                          
                          <div className="grid gap-4">
                            <div>
                              <Label>Campaign Name *</Label>
                              <Input
                                value={smsForm.name}
                                onChange={(e) => setSmsForm({...smsForm, name: e.target.value})}
                                placeholder="Enter SMS campaign name"
                              />
                            </div>

                            <div>
                              <Label>Target Persona *</Label>
                              <Select 
                                key={`sms-persona-${smsForm.persona_id || 'empty'}`}
                                value={personas.find(p => p.id === smsForm.persona_id) ? smsForm.persona_id : ''} 
                                onValueChange={(value) => setSmsForm({...smsForm, persona_id: value})}
                                onOpenChange={(open) => {
                                  // Force cleanup when dropdown closes
                                  if (!open) {
                                    setTimeout(() => {
                                      // Small delay to ensure portal cleanup
                                    }, 100);
                                  }
                                }}
                              >
                                <SelectTrigger className="relative z-10">
                                  <SelectValue placeholder="Select target persona" />
                                </SelectTrigger>
                                <SelectContent 
                                  className="z-[100] max-h-[200px] overflow-y-auto"
                                  position="popper"
                                  sideOffset={5}
                                >
                                  {personas.map(persona => (
                                    <SelectItem key={`sms-persona-${persona.id}`} value={persona.id}>
                                      {persona.name} ({persona.match_count} matches)
                                    </SelectItem>
                                  ))}
                                  {personas.length === 0 && (
                                    <SelectItem value="no_personas" disabled>
                                      No personas available
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label>Message * (Max 160 characters)</Label>
                              <Textarea
                                value={smsForm.message}
                                onChange={handleSMSMessageChange}
                                placeholder="Enter your SMS message..."
                                rows={3}
                                maxLength={160}
                                disabled={urlShorteningInProgress}
                              />
                              {urlShorteningInProgress && (
                                <div className="flex items-center gap-2 text-sm text-blue-600 mt-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                  Shortening URLs...
                                </div>
                              )}
                              <div className={`text-sm mt-1 ${smsForm.message.length > 160 ? 'text-red-500' : 'text-gray-500'}`}>
                                {smsForm.message.length}/160 characters
                                {smsForm.message.length > 160 && (
                                  <span className="ml-2 text-red-500 font-medium">
                                    Message too long!
                                  </span>
                                )}
                              </div>
                            </div>

                            <div>
                              <Label>Website URL (Optional)</Label>
                              <Input
                                value={smsForm.url}
                                onChange={handleSMSUrlChange}
                                placeholder="https://your-website.com"
                                type="url"
                                disabled={urlShorteningInProgress}
                              />
                              {urlShorteningInProgress && (
                                <div className="flex items-center gap-2 text-sm text-blue-600 mt-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                  Shortening URL...
                                </div>
                              )}
                            </div>

                            <div>
                              <Label>Language</Label>
                              <Select 
                                value={smsForm.language} 
                                onValueChange={(value) => setSmsForm({...smsForm, language: value})}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="en">English</SelectItem>
                                  <SelectItem value="es">Spanish</SelectItem>
                                  <SelectItem value="fr">French</SelectItem>
                                  <SelectItem value="de">German</SelectItem>
                                  <SelectItem value="it">Italian</SelectItem>
                                  <SelectItem value="pt">Portuguese</SelectItem>
                                  <SelectItem value="ar">Arabic</SelectItem>
                                  <SelectItem value="hi">Hindi</SelectItem>
                                  <SelectItem value="zh">Chinese</SelectItem>
                                  <SelectItem value="ja">Japanese</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Campaign Scheduling Section */}
                            <div className="border-t pt-4">
                              <div className="space-y-4">
                                <div className="flex items-center space-x-4">
                                  <div>
                                    <Label className="text-base font-medium">Campaign Timing</Label>
                                    <p className="text-sm text-gray-500">Choose when to send this SMS campaign</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div 
                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                      smsForm.send_immediately 
                                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' 
                                        : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                    onClick={() => setSmsForm({...smsForm, send_immediately: true, schedule_date: ''})}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${
                                        smsForm.send_immediately 
                                          ? 'border-blue-500 bg-blue-500' 
                                          : 'border-gray-300'
                                      }`}>
                                        {smsForm.send_immediately && (
                                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                        )}
                                      </div>
                                      <Play className="h-4 w-4 text-blue-600" />
                                      <span className="font-medium">Send Now</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 ml-6">
                                      Send for approval immediately
                                    </p>
                                  </div>

                                  <div 
                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                      !smsForm.send_immediately 
                                        ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' 
                                        : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                    onClick={() => setSmsForm({...smsForm, send_immediately: false})}
                                  >
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-4 h-4 rounded-full border-2 ${
                                        !smsForm.send_immediately 
                                          ? 'border-indigo-500 bg-indigo-500' 
                                          : 'border-gray-300'
                                      }`}>
                                        {!smsForm.send_immediately && (
                                          <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                        )}
                                      </div>
                                      <Clock className="h-4 w-4 text-indigo-600" />
                                      <span className="font-medium">Schedule Later</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 ml-6">
                                      Schedule for a future date
                                    </p>
                                  </div>
                                </div>

                                {!smsForm.send_immediately && (
                                  <div className="mt-4">
                                    <Label>Schedule Date & Time *</Label>
                                    <Input
                                      type="datetime-local"
                                      value={smsForm.schedule_date}
                                      onChange={(e) => setSmsForm({...smsForm, schedule_date: e.target.value})}
                                      min={new Date().toISOString().slice(0, 16)}
                                      className="mt-1"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">
                                      SMS campaign will be automatically sent at the scheduled time
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <Button 
                              onClick={handleCreateSMSCampaign} 
                              disabled={isLoading || !smsForm.name || !smsForm.message || !smsForm.persona_id || smsForm.message.length > 160 || (!smsForm.send_immediately && !smsForm.schedule_date)}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                              <Smartphone className="h-4 w-4 mr-2" />
                              {isLoading ? 'Processing...' : 
                               smsForm.send_immediately ? 'Create & Submit for Approval' : 'Schedule SMS Campaign'}
                            </Button>
                          </div>
                        </div>

                        {/* Right Side - SMS Preview */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Eye className="h-5 w-5 text-gray-600" />
                            <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                          </div>
                          
                          <SMSPreview form={smsForm} />
                        </div>
                      </div>
                    </TabsContent>

                    {/* Email Campaign - Placeholder */}
                    <TabsContent value="email">
                      <div className="space-y-6 mt-6">
                        <div className="text-center py-12">
                          <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Email Campaigns Coming Soon
                          </h3>
                          <p className="text-gray-500 max-w-md mx-auto">
                            Email campaign functionality will be available in the next update. 
                            Focus on WhatsApp and SMS campaigns for now.
                          </p>
                          <div className="mt-4">
                            <Badge variant="outline">
                              In Development
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Facebook Ads - Placeholder */}
                    <TabsContent value="facebook">
                      <div className="space-y-6 mt-6">
                        <div className="text-center py-12">
                          <Facebook className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Facebook Ads Coming Soon
                          </h3>
                          <p className="text-gray-500 max-w-md mx-auto">
                            Facebook Ads integration will be available in the next update. 
                            Focus on WhatsApp and SMS campaigns for now.
                          </p>
                          <div className="mt-4">
                            <Badge variant="outline">
                              In Development
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Campaign History and Scheduled Campaigns */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Campaign History */}
                <Card>
                  <CardHeader>
                    <CardTitle>Campaign History</CardTitle>
                    <CardDescription>
                      View your created and completed campaigns
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {campaigns.length > 0 ? (
                        campaigns.map(campaign => (
                          <div key={campaign.id} className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{campaign.name}</h3>
                                <Badge variant="outline" className="capitalize">
                                  {campaign.type === 'whatsapp' && <MessageCircle className="h-3 w-3 mr-1" />}
                                  {campaign.type === 'sms' && <Smartphone className="h-3 w-3 mr-1" />}
                                  {campaign.type === 'email' && <Mail className="h-3 w-3 mr-1" />}
                                  {campaign.type}
                                </Badge>
                              </div>
                              <Badge variant={
                                campaign.status === 'completed' ? 'default' :
                                campaign.status === 'approved' ? 'secondary' :
                                campaign.status === 'rejected' ? 'destructive' :
                                campaign.status === 'scheduled' ? 'default' : 'outline'
                              }>
                                {campaign.status === 'scheduled' && <Clock className="h-3 w-3 mr-1" />}
                                {campaign.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{campaign.message}</p>
                            {campaign.persona_name && (
                              <p className="text-xs text-blue-600 mb-1">
                                Persona: {campaign.persona_name} • {campaign.audience_count} contacts
                              </p>
                            )}
                            {campaign.url && (
                              <p className="text-xs text-green-600 mb-1">
                                <Globe className="h-3 w-3 inline mr-1" />
                                {campaign.url}
                              </p>
                            )}
                            {campaign.scheduled_for && (
                              <p className="text-xs text-orange-600 mb-1">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                Scheduled: {new Date(campaign.scheduled_for).toLocaleString()}
                              </p>
                            )}
                            <div className="text-xs text-gray-400">
                              Created: {new Date(campaign.created_at).toLocaleDateString()}
                              {campaign.campaign_data?.estimated_cost && (
                                <span className="ml-4">
                                  Est. Cost: ${campaign.campaign_data.estimated_cost.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">
                            No campaigns created yet. Create your first multi-channel campaign!
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Scheduled Campaigns */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Scheduled Campaigns
                    </CardTitle>
                    <CardDescription>
                      Manage your upcoming scheduled campaigns
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {scheduledCampaigns.length > 0 ? (
                        scheduledCampaigns.map(campaign => (
                          <div key={campaign.id} className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{campaign.name}</h3>
                                <Badge variant="outline" className="capitalize bg-white">
                                  {campaign.type === 'whatsapp' && <MessageCircle className="h-3 w-3 mr-1" />}
                                  {campaign.type === 'sms' && <Smartphone className="h-3 w-3 mr-1" />}
                                  {campaign.type}
                                </Badge>
                              </div>
                              <Badge variant="default" className="bg-orange-500">
                                <Clock className="h-3 w-3 mr-1" />
                                Scheduled
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{campaign.message}</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-blue-600 mb-1">
                                  Persona: {campaign.persona_name} • {campaign.audience_count} contacts
                                </p>
                                <p className="text-sm text-orange-700 font-medium">
                                  <Calendar className="h-4 w-4 inline mr-1" />
                                  {new Date(campaign.scheduled_for).toLocaleString()}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Implement reschedule functionality
                                    toast.info('Reschedule functionality will be available soon');
                                  }}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    // Implement cancel functionality
                                    toast.info('Cancel functionality will be available soon');
                                  }}
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">
                            No scheduled campaigns. Schedule your first campaign for future delivery!
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Approvals (Marketing Manager only) */}
          {user?.role === 'marketing_manager' && (
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Campaign Approvals</CardTitle>
                  <CardDescription>
                    Review and approve pending campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {campaigns.filter(c => c.status === 'pending_approval').map(campaign => (
                      <div key={campaign.id} className="p-4 border rounded">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium">{campaign.name}</h3>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleCampaignApproval(campaign.id, 'approved')}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleCampaignApproval(campaign.id, 'rejected')}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">{campaign.objective}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Persona: {campaign.persona_name} • Created: {new Date(campaign.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                    {campaigns.filter(c => c.status === 'pending_approval').length === 0 && (
                      <p className="text-gray-500 text-center py-8">No pending approvals</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Settings (Admin only) */}
          {user?.role === 'administrator' && (
            <TabsContent value="settings">
              <div className="space-y-6">
                {/* Settings Sub-Navigation */}
                <Card>
                  <CardHeader>
                    <CardTitle>System Settings</CardTitle>
                    <CardDescription>
                      Configure integrations and system settings for OmniMarket360
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab}>
                      <TabsList className="grid w-full grid-cols-6">
                        <TabsTrigger value="general" className="text-sm">
                          <Settings className="h-4 w-4 mr-1" />
                          General
                        </TabsTrigger>
                        <TabsTrigger value="ai-models" className="text-sm">
                          🤖 AI Models
                        </TabsTrigger>
                        <TabsTrigger value="crm-integration" className="text-sm">
                          <Link className="h-4 w-4 mr-1" />
                          CRM Integration
                        </TabsTrigger>
                        <TabsTrigger value="ecommerce-integration" className="text-sm">
                          <Globe className="h-4 w-4 mr-1" />
                          Website Integration
                        </TabsTrigger>
                        <TabsTrigger value="api-integrations" className="text-sm">
                          Integrations
                        </TabsTrigger>
                      </TabsList>

                      {/* General Settings Tab */}
                      <TabsContent value="general">
                        <div className="space-y-6">
                          {/* General Settings Header */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Settings className="h-5 w-5 text-blue-600" />
                                General Settings
                              </CardTitle>
                              <CardDescription>
                                Configure system-wide settings including localization, currency, accessibility, compliance, notifications, and backup preferences.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              {/* Sub-tabs for General Settings */}
                              <Tabs value={activeGeneralSettingsTab} onValueChange={setActiveGeneralSettingsTab}>
                                <TabsList className="grid w-full grid-cols-6 mb-6">
                                  <TabsTrigger value="localization" className="text-xs">
                                    🌍 Localization
                                  </TabsTrigger>
                                  <TabsTrigger value="currency" className="text-xs">
                                    💰 Currency
                                  </TabsTrigger>
                                  <TabsTrigger value="accessibility" className="text-xs">
                                    🎨 Accessibility
                                  </TabsTrigger>
                                  <TabsTrigger value="compliance" className="text-xs">
                                    📋 Compliance
                                  </TabsTrigger>
                                  <TabsTrigger value="notifications" className="text-xs">
                                    🔔 Notifications
                                  </TabsTrigger>
                                  <TabsTrigger value="backup" className="text-xs">
                                    💾 Backup
                                  </TabsTrigger>
                                </TabsList>

                                {/* Localization Settings */}
                                <TabsContent value="localization">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">🌍 Localization Settings</CardTitle>
                                      <CardDescription>
                                        Configure date, time, number formatting and text direction for your locale
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Locale
                                          </label>
                                          <select 
                                            value={generalSettings.localization.locale}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              localization: { ...prev.localization, locale: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="en-US">English (US)</option>
                                            <option value="en-GB">English (UK)</option>
                                            <option value="es-ES">Spanish (Spain)</option>
                                            <option value="fr-FR">French (France)</option>
                                            <option value="de-DE">German (Germany)</option>
                                            <option value="ar-SA">Arabic (Saudi Arabia)</option>
                                            <option value="zh-CN">Chinese (China)</option>
                                            <option value="ja-JP">Japanese (Japan)</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Time Zone
                                          </label>
                                          <select 
                                            value={generalSettings.localization.timeZone}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              localization: { ...prev.localization, timeZone: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="UTC">UTC</option>
                                            <option value="America/New_York">Eastern Time</option>
                                            <option value="America/Chicago">Central Time</option>
                                            <option value="America/Denver">Mountain Time</option>
                                            <option value="America/Los_Angeles">Pacific Time</option>
                                            <option value="Europe/London">London</option>
                                            <option value="Europe/Paris">Paris</option>
                                            <option value="Asia/Tokyo">Tokyo</option>
                                            <option value="Asia/Kolkata">India</option>
                                          </select>
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Date Format
                                          </label>
                                          <select 
                                            value={generalSettings.localization.dateFormat}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              localization: { ...prev.localization, dateFormat: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="MM/dd/yyyy">MM/dd/yyyy (US)</option>
                                            <option value="dd/MM/yyyy">dd/MM/yyyy (UK)</option>
                                            <option value="yyyy-MM-dd">yyyy-MM-dd (ISO)</option>
                                            <option value="dd.MM.yyyy">dd.MM.yyyy (DE)</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Text Direction
                                          </label>
                                          <select 
                                            value={generalSettings.localization.textDirection}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              localization: { ...prev.localization, textDirection: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="LTR">Left to Right (LTR)</option>
                                            <option value="RTL">Right to Left (RTL)</option>
                                          </select>
                                        </div>
                                      </div>

                                      <div className="flex items-center space-x-4">
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={generalSettings.localization.timeFormat12h}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              localization: { ...prev.localization, timeFormat12h: e.target.checked }
                                            }))}
                                            className="mr-2"
                                          />
                                          Use 12-hour time format
                                        </label>
                                      </div>

                                      {/* Live Preview */}
                                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-medium text-gray-900 mb-2">Live Preview</h4>
                                        <div className="space-y-2 text-sm">
                                          <p><strong>Date:</strong> {new Date().toLocaleDateString(generalSettings.localization.locale)}</p>
                                          <p><strong>Time:</strong> {new Date().toLocaleTimeString(generalSettings.localization.locale, { hour12: generalSettings.localization.timeFormat12h })}</p>
                                          <p><strong>Number:</strong> {(12345.67).toLocaleString(generalSettings.localization.locale)}</p>
                                          <p><strong>Direction:</strong> {generalSettings.localization.textDirection}</p>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>

                                {/* Currency Settings */}
                                <TabsContent value="currency">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">💰 Currency Settings</CardTitle>
                                      <CardDescription>
                                        Configure multi-currency support with automatic conversion
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Primary Currency
                                          </label>
                                          <select 
                                            value={generalSettings.currency.primaryCurrency}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              currency: { ...prev.currency, primaryCurrency: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="USD">USD - US Dollar</option>
                                            <option value="EUR">EUR - Euro</option>
                                            <option value="GBP">GBP - British Pound</option>
                                            <option value="JPY">JPY - Japanese Yen</option>
                                            <option value="CNY">CNY - Chinese Yuan</option>
                                            <option value="INR">INR - Indian Rupee</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Exchange Rate Provider
                                          </label>
                                          <select 
                                            value={generalSettings.currency.fxProvider}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              currency: { ...prev.currency, fxProvider: e.target.value }
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          >
                                            <option value="ECB">European Central Bank</option>
                                            <option value="OpenX">Open Exchange Rates</option>
                                            <option value="Custom">Custom Provider</option>
                                          </select>
                                        </div>
                                      </div>

                                      <div className="space-y-4">
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={generalSettings.currency.showLocalCurrency}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              currency: { ...prev.currency, showLocalCurrency: e.target.checked }
                                            }))}
                                            className="mr-2"
                                          />
                                          Show local currency alongside primary currency
                                        </label>
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={generalSettings.currency.autoConversion}
                                            onChange={(e) => setGeneralSettings(prev => ({
                                              ...prev,
                                              currency: { ...prev.currency, autoConversion: e.target.checked }
                                            }))}
                                            className="mr-2"
                                          />
                                          Enable automatic currency conversion
                                        </label>
                                      </div>

                                      {/* Currency Preview */}
                                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-medium text-gray-900 mb-2">Currency Preview</h4>
                                        <div className="space-y-2 text-sm">
                                          <p><strong>Primary:</strong> {generalSettings.currency.primaryCurrency} $1,234.56</p>
                                          {generalSettings.currency.showLocalCurrency && (
                                            <p><strong>With Local:</strong> $1,234.56 (€1,123.45)</p>
                                          )}
                                          <p><strong>Provider:</strong> {generalSettings.currency.fxProvider}</p>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>

                                {/* Accessibility Settings */}
                                <TabsContent value="accessibility">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">🎨 Accessibility Settings</CardTitle>
                                      <CardDescription>
                                        Configure display preferences and accessibility options
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                          Theme Preference
                                        </label>
                                        <div className="grid grid-cols-3 gap-4">
                                          {[
                                            { value: 'light', label: '☀️ Light', desc: 'Light theme' },
                                            { value: 'dark', label: '🌙 Dark', desc: 'Dark theme' },
                                            { value: 'system', label: '🖥️ System', desc: 'Follow system preference' }
                                          ].map((theme) => (
                                            <label key={theme.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                              <input
                                                type="radio"
                                                value={theme.value}
                                                checked={generalSettings.accessibility.theme === theme.value}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  accessibility: { ...prev.accessibility, theme: e.target.value }
                                                }))}
                                                className="mr-2"
                                              />
                                              <div>
                                                <div className="font-medium">{theme.label}</div>
                                                <div className="text-xs text-gray-500">{theme.desc}</div>
                                              </div>
                                            </label>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Theme Preview */}
                                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="font-medium text-gray-900 mb-2">Theme Preview</h4>
                                        <p className="text-sm text-gray-600">
                                          Current selection: <strong>{generalSettings.accessibility.theme}</strong>
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Theme changes will be applied immediately across the application
                                        </p>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>

                                {/* Compliance Settings */}
                                <TabsContent value="compliance">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">📋 Compliance & Legal Settings</CardTitle>
                                      <CardDescription>
                                        Configure privacy frameworks and consent management for GDPR, CCPA compliance
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                      {/* Privacy Frameworks */}
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                          Privacy Compliance Frameworks
                                        </label>
                                        <div className="space-y-3">
                                          {[
                                            { value: 'GDPR', label: 'GDPR - General Data Protection Regulation (EU)', desc: 'European Union data protection compliance' },
                                            { value: 'CCPA', label: 'CCPA - California Consumer Privacy Act (US)', desc: 'California privacy compliance' },
                                            { value: 'PIPEDA', label: 'PIPEDA - Personal Information Protection (CA)', desc: 'Canadian privacy compliance' },
                                            { value: 'None', label: 'No specific framework', desc: 'Basic privacy practices only' }
                                          ].map((framework) => (
                                            <label key={framework.value} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                              <input
                                                type="checkbox"
                                                checked={generalSettings.compliance.privacyFrameworks.includes(framework.value)}
                                                onChange={(e) => {
                                                  const frameworks = generalSettings.compliance.privacyFrameworks;
                                                  let newFrameworks;
                                                  if (e.target.checked) {
                                                    newFrameworks = [...frameworks.filter(f => f !== 'None'), framework.value];
                                                  } else {
                                                    newFrameworks = frameworks.filter(f => f !== framework.value);
                                                    if (newFrameworks.length === 0) {
                                                      newFrameworks = ['None'];
                                                    }
                                                  }
                                                  setGeneralSettings(prev => ({
                                                    ...prev,
                                                    compliance: {
                                                      ...prev.compliance,
                                                      privacyFrameworks: newFrameworks
                                                    }
                                                  }));
                                                }}
                                                className="mt-1 mr-3"
                                              />
                                              <div>
                                                <div className="font-medium text-sm">{framework.label}</div>
                                                <div className="text-xs text-gray-500">{framework.desc}</div>
                                              </div>
                                            </label>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Cookie Consent Configuration */}
                                      <div className="border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                          <div>
                                            <h4 className="font-medium text-gray-900">Cookie Consent Banner</h4>
                                            <p className="text-sm text-gray-600">Configure the cookie consent banner and tracking permissions</p>
                                          </div>
                                          <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={generalSettings.compliance.cookieConsent.enabled}
                                              onChange={(e) => setGeneralSettings(prev => ({
                                                ...prev,
                                                compliance: {
                                                  ...prev.compliance,
                                                  cookieConsent: {
                                                    ...prev.compliance.cookieConsent,
                                                    enabled: e.target.checked
                                                  }
                                                }
                                              }))}
                                              className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                          </label>
                                        </div>

                                        {generalSettings.compliance.cookieConsent.enabled && (
                                          <div className="space-y-4 pl-4 border-l-2 border-blue-200">
                                            <h5 className="font-medium text-gray-900">Cookie Categories</h5>
                                            
                                            <div className="space-y-3">
                                              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                  <Shield className="h-4 w-4 text-green-600" />
                                                  <div>
                                                    <div className="font-medium text-sm">Necessary Cookies</div>
                                                    <div className="text-xs text-gray-600">Required for basic site functionality</div>
                                                  </div>
                                                </div>
                                                <div className="text-sm font-medium text-green-600">Always Required</div>
                                              </div>

                                              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                  <TrendingUp className="h-4 w-4 text-blue-600" />
                                                  <div>
                                                    <div className="font-medium text-sm">Analytics Cookies</div>
                                                    <div className="text-xs text-gray-600">Help understand visitor behavior and site usage</div>
                                                  </div>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={generalSettings.compliance.cookieConsent.categories.analytics}
                                                    onChange={(e) => setGeneralSettings(prev => ({
                                                      ...prev,
                                                      compliance: {
                                                        ...prev.compliance,
                                                        cookieConsent: {
                                                          ...prev.compliance.cookieConsent,
                                                          categories: {
                                                            ...prev.compliance.cookieConsent.categories,
                                                            analytics: e.target.checked
                                                          }
                                                        }
                                                      }
                                                    }))}
                                                    className="sr-only peer"
                                                  />
                                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                </label>
                                              </div>

                                              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                  <Eye className="h-4 w-4 text-purple-600" />
                                                  <div>
                                                    <div className="font-medium text-sm">Marketing Cookies</div>
                                                    <div className="text-xs text-gray-600">Used for targeted advertising and marketing campaigns</div>
                                                  </div>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={generalSettings.compliance.cookieConsent.categories.marketing}
                                                    onChange={(e) => setGeneralSettings(prev => ({
                                                      ...prev,
                                                      compliance: {
                                                        ...prev.compliance,
                                                        cookieConsent: {
                                                          ...prev.compliance.cookieConsent,
                                                          categories: {
                                                            ...prev.compliance.cookieConsent.categories,
                                                            marketing: e.target.checked
                                                          }
                                                        }
                                                      }
                                                    }))}
                                                    className="sr-only peer"
                                                  />
                                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Data Rights Information */}
                                      {generalSettings.compliance.privacyFrameworks.some(f => ['GDPR', 'CCPA', 'PIPEDA'].includes(f)) && (
                                        <div className="border-t pt-6">
                                          <h4 className="font-medium text-gray-900 mb-3">Data Subject Rights</h4>
                                          <div className="grid grid-cols-2 gap-4">
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                              <div className="font-medium text-sm text-blue-800">Right to Access</div>
                                              <div className="text-xs text-blue-600 mt-1">Users can request their personal data</div>
                                            </div>
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                              <div className="font-medium text-sm text-blue-800">Right to Deletion</div>
                                              <div className="text-xs text-blue-600 mt-1">Users can request data deletion</div>
                                            </div>
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                              <div className="font-medium text-sm text-blue-800">Right to Portability</div>
                                              <div className="text-xs text-blue-600 mt-1">Users can export their data</div>
                                            </div>
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                              <div className="font-medium text-sm text-blue-800">Right to Rectification</div>
                                              <div className="text-xs text-blue-600 mt-1">Users can correct their data</div>
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {/* Save Button */}
                                      <div className="border-t pt-6">
                                        <Button 
                                          onClick={async () => {
                                            try {
                                              // Here you would implement the API call to save compliance settings
                                              console.log('Saving compliance settings:', generalSettings.compliance);
                                              // Show success message
                                            } catch (error) {
                                              console.error('Error saving compliance settings:', error);
                                            }
                                          }}
                                          className="bg-blue-600 hover:bg-blue-700"
                                        >
                                          <Shield className="h-4 w-4 mr-2" />
                                          Save Compliance Settings
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>

                                {/* Notifications Settings */}
                                <TabsContent value="notifications">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">🔔 Notification Settings</CardTitle>
                                      <CardDescription>
                                        Configure email, SMS, push notification preferences and quiet hours
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                      {/* Notification Channels */}
                                      <div>
                                        <h4 className="font-medium text-gray-900 mb-4">Notification Channels</h4>
                                        <div className="space-y-4">
                                          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                            <div className="flex items-center gap-3">
                                              <Mail className="h-5 w-5 text-blue-600" />
                                              <div>
                                                <div className="font-medium text-sm">Email Notifications</div>
                                                <div className="text-xs text-gray-600">Receive notifications via email</div>
                                              </div>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={generalSettings.notifications.channels.email}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    channels: {
                                                      ...prev.notifications.channels,
                                                      email: e.target.checked
                                                    }
                                                  }
                                                }))}
                                                className="sr-only peer"
                                              />
                                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                          </div>

                                          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                            <div className="flex items-center gap-3">
                                              <MessageSquare className="h-5 w-5 text-green-600" />
                                              <div>
                                                <div className="font-medium text-sm">SMS Notifications</div>
                                                <div className="text-xs text-gray-600">Receive notifications via text message</div>
                                              </div>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={generalSettings.notifications.channels.sms}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    channels: {
                                                      ...prev.notifications.channels,
                                                      sms: e.target.checked
                                                    }
                                                  }
                                                }))}
                                                className="sr-only peer"
                                              />
                                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                            </label>
                                          </div>

                                          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                            <div className="flex items-center gap-3">
                                              <Bell className="h-5 w-5 text-purple-600" />
                                              <div>
                                                <div className="font-medium text-sm">Push Notifications</div>
                                                <div className="text-xs text-gray-600">Receive browser push notifications</div>
                                              </div>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={generalSettings.notifications.channels.push}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    channels: {
                                                      ...prev.notifications.channels,
                                                      push: e.target.checked
                                                    }
                                                  }
                                                }))}
                                                className="sr-only peer"
                                              />
                                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                            </label>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Quiet Hours */}
                                      <div className="border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                          <div>
                                            <h4 className="font-medium text-gray-900">Quiet Hours</h4>
                                            <p className="text-sm text-gray-600">Set times when notifications should be suppressed</p>
                                          </div>
                                          <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={!!generalSettings.notifications.quietHours}
                                              onChange={(e) => setGeneralSettings(prev => ({
                                                ...prev,
                                                notifications: {
                                                  ...prev.notifications,
                                                  quietHours: e.target.checked ? {
                                                    start: '22:00',
                                                    end: '07:00',
                                                    timeZone: 'UTC'
                                                  } : null
                                                }
                                              }))}
                                              className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                          </label>
                                        </div>

                                        {generalSettings.notifications.quietHours && (
                                          <div className="grid grid-cols-3 gap-4 pl-4 border-l-2 border-blue-200">
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Start Time
                                              </label>
                                              <input
                                                type="time"
                                                value={generalSettings.notifications.quietHours.start}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    quietHours: {
                                                      ...prev.notifications.quietHours,
                                                      start: e.target.value
                                                    }
                                                  }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                End Time
                                              </label>
                                              <input
                                                type="time"
                                                value={generalSettings.notifications.quietHours.end}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    quietHours: {
                                                      ...prev.notifications.quietHours,
                                                      end: e.target.value
                                                    }
                                                  }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Time Zone
                                              </label>
                                              <select
                                                value={generalSettings.notifications.quietHours.timeZone}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  notifications: {
                                                    ...prev.notifications,
                                                    quietHours: {
                                                      ...prev.notifications.quietHours,
                                                      timeZone: e.target.value
                                                    }
                                                  }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              >
                                                <option value="UTC">UTC</option>
                                                <option value="America/New_York">Eastern Time</option>
                                                <option value="America/Chicago">Central Time</option>
                                                <option value="America/Denver">Mountain Time</option>
                                                <option value="America/Los_Angeles">Pacific Time</option>
                                                <option value="Europe/London">London</option>
                                                <option value="Europe/Paris">Paris</option>
                                                <option value="Asia/Tokyo">Tokyo</option>
                                                <option value="Asia/Kolkata">India</option>
                                              </select>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Notification Types */}
                                      <div className="border-t pt-6">
                                        <h4 className="font-medium text-gray-900 mb-4">Notification Types</h4>
                                        <div className="space-y-3">
                                          {[
                                            { key: 'campaigns', label: 'Campaign Updates', desc: 'Notifications about campaign performance and status changes' },
                                            { key: 'keywords', label: 'SEO/AEO Alerts', desc: 'Keyword ranking changes and SEO opportunities' },
                                            { key: 'audience', label: 'Audience Insights', desc: 'New audience segments and behavior insights' },
                                            { key: 'integrations', label: 'Integration Status', desc: 'Connection issues and sync updates' },
                                            { key: 'security', label: 'Security Alerts', desc: 'Account security and login notifications' }
                                          ].map((type) => (
                                            <div key={type.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                              <div>
                                                <div className="font-medium text-sm">{type.label}</div>
                                                <div className="text-xs text-gray-600">{type.desc}</div>
                                              </div>
                                              <input
                                                type="checkbox"
                                                defaultChecked={true}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Save Button */}
                                      <div className="border-t pt-6">
                                        <Button 
                                          onClick={async () => {
                                            try {
                                              console.log('Saving notification settings:', generalSettings.notifications);
                                              // Implementation would save via API
                                            } catch (error) {
                                              console.error('Error saving notification settings:', error);
                                            }
                                          }}
                                          className="bg-blue-600 hover:bg-blue-700"
                                        >
                                          <Bell className="h-4 w-4 mr-2" />
                                          Save Notification Settings
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>

                                {/* Backup Settings */}
                                <TabsContent value="backup">
                                  <Card>
                                    <CardHeader>
                                      <CardTitle className="text-lg">💾 Backup & Recovery Settings</CardTitle>
                                      <CardDescription>
                                        Configure data export, restore points, and cloud backup options
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                      {/* Data Export Options */}
                                      <div>
                                        <h4 className="font-medium text-gray-900 mb-4">Data Export Formats</h4>
                                        <div className="space-y-3">
                                          {[
                                            { value: 'JSON', label: 'JSON Format', desc: 'Complete data structure with full fidelity', icon: '📄' },
                                            { value: 'CSV', label: 'CSV Format', desc: 'Spreadsheet-compatible tables (ZIP archive)', icon: '📊' },
                                            { value: 'PARQUET', label: 'Parquet Format', desc: 'Optimized columnar format for analytics', icon: '🗂️' }
                                          ].map((format) => (
                                            <label key={format.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                              <input
                                                type="checkbox"
                                                checked={generalSettings.backup.exportFormats.includes(format.value)}
                                                onChange={(e) => {
                                                  const formats = generalSettings.backup.exportFormats;
                                                  let newFormats;
                                                  if (e.target.checked) {
                                                    newFormats = [...formats, format.value];
                                                  } else {
                                                    newFormats = formats.filter(f => f !== format.value);
                                                    if (newFormats.length === 0) {
                                                      newFormats = ['JSON']; // Ensure at least one format
                                                    }
                                                  }
                                                  setGeneralSettings(prev => ({
                                                    ...prev,
                                                    backup: {
                                                      ...prev.backup,
                                                      exportFormats: newFormats
                                                    }
                                                  }));
                                                }}
                                                className="mr-3"
                                              />
                                              <div className="flex items-center gap-3">
                                                <span className="text-2xl">{format.icon}</span>
                                                <div>
                                                  <div className="font-medium text-sm">{format.label}</div>
                                                  <div className="text-xs text-gray-600">{format.desc}</div>
                                                </div>
                                              </div>
                                            </label>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Restore Points */}
                                      <div className="border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                          <div>
                                            <h4 className="font-medium text-gray-900">Automatic Restore Points</h4>
                                            <p className="text-sm text-gray-600">Create automatic snapshots before major changes</p>
                                          </div>
                                          <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={generalSettings.backup.restorePointsEnabled}
                                              onChange={(e) => setGeneralSettings(prev => ({
                                                ...prev,
                                                backup: {
                                                  ...prev.backup,
                                                  restorePointsEnabled: e.target.checked
                                                }
                                              }))}
                                              className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                          </label>
                                        </div>

                                        {generalSettings.backup.restorePointsEnabled && (
                                          <div className="pl-4 border-l-2 border-blue-200">
                                            <div className="grid grid-cols-2 gap-4">
                                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                <div className="font-medium text-sm text-blue-800">Before Campaign Changes</div>
                                                <div className="text-xs text-blue-600 mt-1">Automatic backup before modifying campaigns</div>
                                              </div>
                                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                <div className="font-medium text-sm text-blue-800">Before Integration Updates</div>
                                                <div className="text-xs text-blue-600 mt-1">Automatic backup before connection changes</div>
                                              </div>
                                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                <div className="font-medium text-sm text-blue-800">Daily Snapshots</div>
                                                <div className="text-xs text-blue-600 mt-1">Daily automatic backup at 2 AM UTC</div>
                                              </div>
                                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                <div className="font-medium text-sm text-blue-800">Retention: 30 Days</div>
                                                <div className="text-xs text-blue-600 mt-1">Restore points kept for 30 days</div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Google Drive Integration */}
                                      <div className="border-t pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                          <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                              <svg className="h-6 w-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M6.28 3l5.208 9.01L15 3h7.5L15 12H9l-3.48-6.01L12 12l-5.72 9L2 15l4.28-3z"/>
                                              </svg>
                                            </div>
                                            <div>
                                              <h4 className="font-medium text-gray-900">Google Drive Backup</h4>
                                              <p className="text-sm text-gray-600">Automatically backup to your Google Drive</p>
                                            </div>
                                          </div>
                                          <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={generalSettings.backup.googleDrive.enabled}
                                              onChange={(e) => setGeneralSettings(prev => ({
                                                ...prev,
                                                backup: {
                                                  ...prev.backup,
                                                  googleDrive: {
                                                    ...prev.backup.googleDrive,
                                                    enabled: e.target.checked
                                                  }
                                                }
                                              }))}
                                              className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                          </label>
                                        </div>

                                        {generalSettings.backup.googleDrive.enabled && (
                                          <div className="pl-4 border-l-2 border-blue-200 space-y-4">
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                OAuth Client ID
                                              </label>
                                              <input
                                                type="text"
                                                value={generalSettings.backup.googleDrive.oauthClientId}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  backup: {
                                                    ...prev.backup,
                                                    googleDrive: {
                                                      ...prev.backup.googleDrive,
                                                      oauthClientId: e.target.value
                                                    }
                                                  }
                                                }))}
                                                placeholder="Enter Google OAuth Client ID"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Target Folder ID (Optional)
                                              </label>
                                              <input
                                                type="text"
                                                value={generalSettings.backup.googleDrive.targetFolderId}
                                                onChange={(e) => setGeneralSettings(prev => ({
                                                  ...prev,
                                                  backup: {
                                                    ...prev.backup,
                                                    googleDrive: {
                                                      ...prev.backup.googleDrive,
                                                      targetFolderId: e.target.value
                                                    }
                                                  }
                                                }))}
                                                placeholder="Leave empty for root folder"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            </div>
                                            
                                            <div className="flex gap-2">
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  // Implementation would handle Google OAuth flow
                                                  console.log('Starting Google Drive authentication...');
                                                }}
                                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                              >
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                Connect Google Drive
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                  console.log('Testing Google Drive connection...');
                                                }}
                                              >
                                                Test Connection
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Quick Actions */}
                                      <div className="border-t pt-6">
                                        <h4 className="font-medium text-gray-900 mb-4">Quick Actions</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                          <Button
                                            onClick={() => {
                                              console.log('Creating manual backup...');
                                              // Implementation would trigger immediate backup
                                            }}
                                            className="bg-green-600 hover:bg-green-700"
                                          >
                                            <Download className="h-4 w-4 mr-2" />
                                            Create Backup Now
                                          </Button>
                                          <Button
                                            onClick={() => {
                                              console.log('Creating restore point...');
                                              // Implementation would create restore point
                                            }}
                                            variant="outline"
                                            className="border-green-600 text-green-600 hover:bg-green-50"
                                          >
                                            <Clock className="h-4 w-4 mr-2" />
                                            Create Restore Point
                                          </Button>
                                        </div>
                                      </div>

                                      {/* Save Button */}
                                      <div className="border-t pt-6">
                                        <Button 
                                          onClick={async () => {
                                            try {
                                              console.log('Saving backup settings:', generalSettings.backup);
                                              // Implementation would save via API
                                            } catch (error) {
                                              console.error('Error saving backup settings:', error);
                                            }
                                          }}
                                          className="bg-blue-600 hover:bg-blue-700"
                                        >
                                          <Download className="h-4 w-4 mr-2" />
                                          Save Backup Settings
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </TabsContent>
                              </Tabs>
                            </CardContent>
                          </Card>
                        </div>
                      </TabsContent>

                      {/* AI Models Configuration Tab */}
                      <TabsContent value="ai-models">
                        <div className="space-y-6">
                          {/* Enhanced Header with Overview */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                🤖 AI Models Configuration & Management
                              </CardTitle>
                              <CardDescription>
                                Configure and manage top AI models for various marketing tasks. Monitor usage, costs, and performance.
                                Select the best models for your specific needs and budget.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-blue-600" />
                                    <span className="text-blue-700 font-medium">Active Models</span>
                                  </div>
                                  <p className="text-2xl font-bold text-blue-700 mt-1">
                                    {Object.values(aiModelsConfig.providers || {}).filter(p => p.is_active).length}
                                  </p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-green-600" />
                                    <span className="text-green-700 font-medium">Total Usage</span>
                                  </div>
                                  <p className="text-2xl font-bold text-green-700 mt-1">
                                    ${(aiUsageStats.total_cost || 0).toFixed(2)}
                                  </p>
                                </div>
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-orange-600" />
                                    <span className="text-orange-700 font-medium">API Calls</span>
                                  </div>
                                  <p className="text-2xl font-bold text-orange-700 mt-1">
                                    {aiUsageStats.total_calls || 0}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Clean AI Intelligence System Toggle */}
                              <Card className="bg-white border border-gray-200 shadow-sm">
                                <CardContent className="p-6">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                          <Brain className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <div>
                                          <h3 className="text-xl font-bold text-gray-900">AI Intelligence System</h3>
                                          <p className="text-sm text-blue-600 font-medium">Master Control for AI Features</p>
                                        </div>
                                      </div>
                                      
                                      <p className="text-gray-600 mb-6 leading-relaxed">
                                        Enable cutting-edge AI-powered features across the entire OmniMarket360 platform. 
                                        This master switch controls audience intelligence, smart persona creation, content generation, 
                                        and advanced behavioral analysis.
                                      </p>
                                      
                                      {/* Clean AI Features Grid */}
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <Users className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Audience Intelligence
                                          </span>
                                        </div>
                                        
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <PenTool className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Content Generation
                                          </span>
                                        </div>
                                        
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <TrendingUp className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Lead Scoring
                                          </span>
                                        </div>
                                        
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <Target className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Campaign Optimization
                                          </span>
                                        </div>
                                        
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <BarChart3 className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Behavioral Analysis
                                          </span>
                                        </div>
                                        
                                        <div className={`p-4 rounded-lg border transition-all ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                          <div className="flex items-center gap-2 mb-1">
                                            <Sparkles className={`h-4 w-4 ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                                            {aiModelsConfig.audience_intelligence_enabled && <CheckCircle className="h-3 w-3 text-green-500" />}
                                          </div>
                                          <span className={`text-sm font-medium ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-gray-500'}`}>
                                            Data Enrichment
                                          </span>
                                        </div>
                                      </div>
                                      
                                      {/* Clean Status Indicator */}
                                      <div className={`flex items-center gap-3 p-4 rounded-lg border ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                                        <div className={`h-3 w-3 rounded-full ${aiModelsConfig.audience_intelligence_enabled ? 'bg-green-500 animate-pulse' : 'bg-blue-500'}`}></div>
                                        <span className={`text-sm font-semibold ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-700' : 'text-blue-700'}`}>
                                          AI System Status: {aiModelsConfig.audience_intelligence_enabled ? 'Active & Running' : 'Ready to Enable'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Clean Toggle Button */}
                                    <div className="ml-8 flex flex-col items-center">
                                      <Button
                                        size="lg"
                                        variant={aiModelsConfig.audience_intelligence_enabled ? "default" : "outline"}
                                        onClick={async () => {
                                          // Toggle global AI system
                                          const newState = !aiModelsConfig.audience_intelligence_enabled;
                                          
                                          try {
                                            // Call backend API to enable/disable AI Intelligence System
                                            const response = await fetch(`${API_BASE_URL}/api/ai-models/intelligence-system?enabled=${newState}`, {
                                              method: 'POST',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${token}`
                                              }
                                            });
                                            
                                            if (response.ok) {
                                              const data = await response.json();
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                audience_intelligence_enabled: newState
                                              }));
                                              toast.success(`🤖 AI Intelligence System ${newState ? 'Activated! All AI features are now available.' : 'Deactivated! AI processing paused for privacy/cost optimization.'}`);
                                            } else {
                                              const error = await response.json();
                                              toast.error(`Failed to ${newState ? 'enable' : 'disable'} AI Intelligence System: ${error.detail}`);
                                            }
                                          } catch (error) {
                                            toast.error(`Failed to ${newState ? 'enable' : 'disable'} AI Intelligence System. Please try again.`);
                                            console.error('AI Intelligence System toggle error:', error);
                                          }
                                        }}
                                        className={`min-w-[140px] h-12 text-base font-semibold transition-all duration-300 ${
                                          aiModelsConfig.audience_intelligence_enabled 
                                            ? "bg-green-600 hover:bg-green-700 text-white shadow-lg" 
                                            : "border-2 border-blue-300 text-blue-600 hover:border-blue-500 hover:bg-blue-50"
                                        }`}
                                      >
                                        {aiModelsConfig.audience_intelligence_enabled ? (
                                          <>
                                            <CheckCircle className="h-5 w-5 mr-2" />
                                            Enabled
                                          </>
                                        ) : (
                                          <>
                                            <Power className="h-5 w-5 mr-2" />
                                            Enable AI
                                          </>
                                        )}
                                      </Button>
                                      
                                      {/* Clean Impact Stats */}
                                      <div className="mt-4 text-center p-3 bg-gray-50 rounded-lg border">
                                        <div className="text-xs text-gray-500 mb-1">Active Models</div>
                                        <div className={`text-2xl font-bold ${aiModelsConfig.audience_intelligence_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                                          {Object.values(aiModelsConfig.providers || {}).filter(p => p.is_active).length}
                                        </div>
                                        <div className="text-xs text-gray-400">of 10 total</div>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </CardContent>
                          </Card>

                          {/* Top 10 AI Models in Accordion Format */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                🏆 Top 10 AI Models
                              </CardTitle>
                              <CardDescription>
                                Popular and successful models organized by performance and use case. Click to configure each model.
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              
                              {/* Model 1: OpenAI (Dynamic Header) */}
                              <div className="border rounded-lg">
                                <div 
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                  onClick={() => setAiModelTesting(prev => ({...prev, 'openai_expanded': !prev.openai_expanded}))}
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge variant="secondary" className="bg-green-100 text-green-700">🥇 Most Popular</Badge>
                                    <div>
                                      <h4 className="font-semibold">
                                        🚀 OpenAI
                                        {aiModelsConfig.providers?.openai?.selected_model && (
                                          <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.openai.available_models?.find(m => m.id === aiModelsConfig.providers.openai.selected_model)?.name || aiModelsConfig.providers.openai.selected_model}</span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {aiModelsConfig.providers?.openai?.selected_model 
                                          ? `Configured with ${aiModelsConfig.providers.openai.selected_model}`
                                          : "Latest & fastest models with vision capabilities"
                                        }
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={
                                      aiModelsConfig.providers?.openai?.is_active ? "default" : 
                                      aiModelsConfig.providers?.openai?.selected_model ? "secondary" : "outline"
                                    }>
                                      {aiModelsConfig.providers?.openai?.is_active ? "✅ Active & Running" : 
                                       aiModelsConfig.providers?.openai?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                    </Badge>
                                    {aiModelsConfig.providers?.openai?.selected_model && (
                                      <span className="text-sm text-gray-500">
                                        ${aiModelsConfig.providers.openai.available_models?.find(m => m.id === aiModelsConfig.providers.openai.selected_model)?.pricing_input || '0.01'}/1K tokens
                                      </span>
                                    )}
                                    {aiModelTesting.openai_expanded ? "−" : "+"}
                                  </div>
                                </div>
                                {aiModelTesting.openai_expanded && (
                                  <div className="border-t p-4 bg-gray-50">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Configuration */}
                                      <div>
                                        <Label className="text-sm font-medium">API Key</Label>
                                        <div className="flex gap-2">
                                          <Input 
                                            type="password" 
                                            placeholder="sk-..." 
                                            className="flex-1"
                                            value={aiModelsConfig.providers?.openai?.api_key || ''}
                                            onChange={(e) => {
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  openai: {
                                                    ...prev.providers?.openai,
                                                    api_key: e.target.value,
                                                    name: 'OpenAI',
                                                    available_models: [] // Clear models when key changes
                                                  }
                                                }
                                              }));
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={async () => {
                                              const apiKey = aiModelsConfig.providers?.openai?.api_key;
                                              if (!apiKey) {
                                                toast.error('Please enter an API key first');
                                                return;
                                              }
                                              
                                              setAiModelTesting(prev => ({...prev, openai_discovering: true}));
                                              
                                              try {
                                                const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    provider: 'openai',
                                                    api_key: apiKey
                                                  })
                                                });
                                                
                                                if (response.ok) {
                                                  const data = await response.json();
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      openai: {
                                                        ...prev.providers?.openai,
                                                        available_models: data.available_models,
                                                        provider_info: data.provider_info,
                                                        api_key_status: 'valid'
                                                      }
                                                    }
                                                  }));
                                                  toast.success(`Discovered ${data.available_models.length} OpenAI models!`);
                                                } else {
                                                  const error = await response.json();
                                                  toast.error(`Discovery failed: ${error.detail}`);
                                                }
                                              } catch (error) {
                                                toast.error('Failed to discover models. Please check your API key.');
                                              } finally {
                                                setAiModelTesting(prev => ({...prev, openai_discovering: false}));
                                              }
                                            }}
                                            disabled={aiModelTesting.openai_discovering || !aiModelsConfig.providers?.openai?.api_key}
                                          >
                                            {aiModelTesting.openai_discovering ? (
                                              <>
                                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                Discovering...
                                              </>
                                            ) : (
                                              <>
                                                🔍 Discover Models
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                        
                                        {/* Dynamic Model Selection */}
                                        {aiModelsConfig.providers?.openai?.available_models?.length > 0 && (
                                          <div className="mt-3">
                                            <Label className="text-sm font-medium">Choose Your Model</Label>
                                            <Select
                                              value={aiModelsConfig.providers?.openai?.selected_model || ''}
                                              onValueChange={(value) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    openai: {
                                                      ...prev.providers?.openai,
                                                      selected_model: value
                                                    }
                                                  }
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select a model from your account" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {aiModelsConfig.providers.openai.available_models.map((model) => (
                                                  <SelectItem key={model.id} value={model.id}>
                                                    <div className="flex flex-col">
                                                      <span className="font-medium">{model.name}</span>
                                                      <span className="text-xs text-gray-500">
                                                        {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                      </span>
                                                    </div>
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Provider Info */}
                                        {aiModelsConfig.providers?.openai?.provider_info && (
                                          <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                            ✅ <strong>{aiModelsConfig.providers.openai.provider_info.name}</strong> - 
                                            {aiModelsConfig.providers.openai.provider_info.total_models} models available
                                          </div>
                                        )}
                                        
                                        <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                          {[
                                            'Audience Intelligence', 'Persona Creation', 'Content Generation', 
                                            'Lead Scoring', 'Campaign Optimization', 'Behavioral Analysis'
                                          ].map(context => (
                                            <label key={context} className="flex items-center gap-2 text-sm">
                                              <input 
                                                type="checkbox" 
                                                className="rounded"
                                                checked={aiModelsConfig.providers?.openai?.contexts?.includes(context) || false}
                                                onChange={(e) => {
                                                  const contexts = aiModelsConfig.providers?.openai?.contexts || [];
                                                  const newContexts = e.target.checked 
                                                    ? [...contexts, context]
                                                    : contexts.filter(c => c !== context);
                                                  
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      openai: {
                                                        ...prev.providers?.openai,
                                                        contexts: newContexts
                                                      }
                                                    }
                                                  }));
                                                }}
                                              />
                                              {context}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Usage & Controls */}
                                      <div>
                                        <div className="bg-white rounded p-3 border mb-3">
                                          <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                          <div className="space-y-1 text-xs">
                                            <div className="flex justify-between">
                                              <span>API Calls:</span>
                                              <span className="font-mono">{aiUsageStats.openai?.calls || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Tokens Used:</span>
                                              <span className="font-mono">{aiUsageStats.openai?.tokens || 0}</span>
                                            </div>
                                            <div className="flex justify-between font-medium">
                                              <span>Cost:</span>
                                              <span className="font-mono">${(aiUsageStats.openai?.cost || 0).toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => {
                                              toast.success('Testing OpenAI connection...');
                                            }}
                                            disabled={!aiModelsConfig.providers?.openai?.selected_model}
                                          >
                                            🧪 Test
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant={aiModelsConfig.providers?.openai?.is_active ? "destructive" : "default"}
                                            onClick={() => {
                                              const newState = !aiModelsConfig.providers?.openai?.is_active;
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  openai: {
                                                    ...prev.providers?.openai,
                                                    is_active: newState
                                                  }
                                                }
                                              }));
                                              toast.success(`OpenAI ${newState ? 'Activated' : 'Deactivated'}`);
                                            }}
                                            disabled={!aiModelsConfig.providers?.openai?.selected_model}
                                          >
                                            {aiModelsConfig.providers?.openai?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Model 2: Anthropic Claude (Complete Configuration) */}
                              <div className="border rounded-lg">
                                <div 
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                  onClick={() => setAiModelTesting(prev => ({...prev, 'anthropic_expanded': !prev.anthropic_expanded}))}
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">🥈 Best Reasoning</Badge>
                                    <div>
                                      <h4 className="font-semibold">
                                        🧠 Anthropic
                                        {aiModelsConfig.providers?.anthropic?.selected_model && (
                                          <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.anthropic.available_models?.find(m => m.id === aiModelsConfig.providers.anthropic.selected_model)?.name || aiModelsConfig.providers.anthropic.selected_model}</span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {aiModelsConfig.providers?.anthropic?.selected_model 
                                          ? `Configured with ${aiModelsConfig.providers.anthropic.selected_model}`
                                          : "Superior reasoning and analysis capabilities"
                                        }
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={
                                      aiModelsConfig.providers?.anthropic?.is_active ? "default" : 
                                      aiModelsConfig.providers?.anthropic?.selected_model ? "secondary" : "outline"
                                    }>
                                      {aiModelsConfig.providers?.anthropic?.is_active ? "✅ Active & Running" : 
                                       aiModelsConfig.providers?.anthropic?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                    </Badge>
                                    {aiModelsConfig.providers?.anthropic?.selected_model && (
                                      <span className="text-sm text-gray-500">
                                        ${aiModelsConfig.providers.anthropic.available_models?.find(m => m.id === aiModelsConfig.providers.anthropic.selected_model)?.pricing_input || '0.003'}/1K tokens
                                      </span>
                                    )}
                                    {aiModelTesting.anthropic_expanded ? "−" : "+"}
                                  </div>
                                </div>
                                {aiModelTesting.anthropic_expanded && (
                                  <div className="border-t p-4 bg-gray-50">
                                    <div className="text-sm text-gray-600 mb-4">
                                      🚀 <strong>Best for:</strong> Complex audience analysis, detailed persona creation, nuanced behavioral insights, advanced reasoning tasks
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Configuration */}
                                      <div>
                                        <Label className="text-sm font-medium">API Key</Label>
                                        <div className="flex gap-2">
                                          <Input 
                                            type="password" 
                                            placeholder="sk-ant-..."
                                            className="flex-1"
                                            value={aiModelsConfig.providers?.anthropic?.api_key || ''}
                                            onChange={(e) => {
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  anthropic: {
                                                    ...prev.providers?.anthropic,
                                                    api_key: e.target.value,
                                                    name: 'Anthropic',
                                                    available_models: []
                                                  }
                                                }
                                              }));
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={async () => {
                                              const apiKey = aiModelsConfig.providers?.anthropic?.api_key;
                                              if (!apiKey) {
                                                toast.error('Please enter an API key first');
                                                return;
                                              }
                                              
                                              setAiModelTesting(prev => ({...prev, anthropic_discovering: true}));
                                              
                                              try {
                                                const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    provider: 'anthropic',
                                                    api_key: apiKey
                                                  })
                                                });
                                                
                                                if (response.ok) {
                                                  const data = await response.json();
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      anthropic: {
                                                        ...prev.providers?.anthropic,
                                                        available_models: data.available_models,
                                                        provider_info: data.provider_info,
                                                        api_key_status: 'valid'
                                                      }
                                                    }
                                                  }));
                                                  toast.success(`Discovered ${data.available_models.length} Anthropic models!`);
                                                } else {
                                                  const error = await response.json();
                                                  toast.error(`Discovery failed: ${error.detail}`);
                                                }
                                              } catch (error) {
                                                toast.error('Failed to discover models. Please check your API key.');
                                              } finally {
                                                setAiModelTesting(prev => ({...prev, anthropic_discovering: false}));
                                              }
                                            }}
                                            disabled={aiModelTesting.anthropic_discovering || !aiModelsConfig.providers?.anthropic?.api_key}
                                          >
                                            {aiModelTesting.anthropic_discovering ? (
                                              <>
                                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                Discovering...
                                              </>
                                            ) : (
                                              <>
                                                🔍 Discover Models
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                        
                                        {/* Dynamic Model Selection */}
                                        {aiModelsConfig.providers?.anthropic?.available_models?.length > 0 && (
                                          <div className="mt-3">
                                            <Label className="text-sm font-medium">Choose Your Model</Label>
                                            <Select
                                              value={aiModelsConfig.providers?.anthropic?.selected_model || ''}
                                              onValueChange={(value) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    anthropic: {
                                                      ...prev.providers?.anthropic,
                                                      selected_model: value
                                                    }
                                                  }
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select a model from your account" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {aiModelsConfig.providers.anthropic.available_models.map((model) => (
                                                  <SelectItem key={model.id} value={model.id}>
                                                    <div className="flex flex-col">
                                                      <span className="font-medium">{model.name}</span>
                                                      <span className="text-xs text-gray-500">
                                                        {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                      </span>
                                                    </div>
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Provider Info */}
                                        {aiModelsConfig.providers?.anthropic?.provider_info && (
                                          <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                            ✅ <strong>{aiModelsConfig.providers.anthropic.provider_info.name}</strong> - 
                                            {aiModelsConfig.providers.anthropic.provider_info.total_models} models available
                                          </div>
                                        )}
                                        
                                        <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                          {[
                                            'Behavioral Analysis', 'Persona Creation', 'Data Quality Assessment',
                                            'Customer Journey Analysis', 'Predictive Analytics', 'Content Strategy'
                                          ].map(context => (
                                            <label key={context} className="flex items-center gap-2 text-sm">
                                              <input 
                                                type="checkbox" 
                                                className="rounded"
                                                checked={aiModelsConfig.providers?.anthropic?.contexts?.includes(context) || false}
                                                onChange={(e) => {
                                                  const contexts = aiModelsConfig.providers?.anthropic?.contexts || [];
                                                  const newContexts = e.target.checked 
                                                    ? [...contexts, context]
                                                    : contexts.filter(c => c !== context);
                                                  
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      anthropic: {
                                                        ...prev.providers?.anthropic,
                                                        contexts: newContexts
                                                      }
                                                    }
                                                  }));
                                                }}
                                              />
                                              {context}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      <div>
                                        <div className="bg-white rounded p-3 border mb-3">
                                          <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                          <div className="space-y-1 text-xs">
                                            <div className="flex justify-between"><span>API Calls:</span><span>0</span></div>
                                            <div className="flex justify-between"><span>Tokens:</span><span>0</span></div>
                                            <div className="flex justify-between font-medium"><span>Cost:</span><span>$0.00</span></div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => {
                                              toast.success('Testing Anthropic connection...');
                                            }}
                                            disabled={!aiModelsConfig.providers?.anthropic?.selected_model}
                                          >
                                            🧪 Test
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant={aiModelsConfig.providers?.anthropic?.is_active ? "destructive" : "default"}
                                            onClick={async () => {
                                              const newState = !aiModelsConfig.providers?.anthropic?.is_active;
                                              
                                              try {
                                                // Call backend API to activate/deactivate the provider
                                                const response = await fetch(`${API_BASE_URL}/api/ai-models/provider/anthropic`, {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    is_active: newState,
                                                    api_key: aiModelsConfig.providers?.anthropic?.api_key,
                                                    selected_model: aiModelsConfig.providers?.anthropic?.selected_model,
                                                    available_models: aiModelsConfig.providers?.anthropic?.available_models,
                                                    contexts: aiModelsConfig.providers?.anthropic?.contexts,
                                                    name: 'Anthropic Claude'
                                                  })
                                                });
                                                
                                                if (response.ok) {
                                                  // Update frontend state
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      anthropic: {
                                                        ...prev.providers?.anthropic,
                                                        is_active: newState
                                                      }
                                                    }
                                                  }));
                                                  toast.success(`🎉 Anthropic Claude ${newState ? 'Activated! Ready for AI processing.' : 'Deactivated'}`);
                                                } else {
                                                  const error = await response.json();
                                                  toast.error(`Failed to ${newState ? 'activate' : 'deactivate'} Anthropic: ${error.message}`);
                                                }
                                              } catch (error) {
                                                toast.error(`Failed to ${newState ? 'activate' : 'deactivate'} Anthropic. Please try again.`);
                                                console.error('Anthropic activation error:', error);
                                              }
                                            }}
                                            disabled={!aiModelsConfig.providers?.anthropic?.selected_model}
                                          >
                                            {aiModelsConfig.providers?.anthropic?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Model 3: Meta Llama 3.1-405B (Dynamic Header) */}
                              <div className="border rounded-lg">
                                <div 
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                  onClick={() => setAiModelTesting(prev => ({...prev, 'llama_expanded': !prev.llama_expanded}))}
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">🥉 Best Open Source</Badge>
                                    <div>
                                      <h4 className="font-semibold">
                                        🦙 Meta Llama
                                        {aiModelsConfig.providers?.meta?.selected_model && (
                                          <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.meta.available_models?.find(m => m.id === aiModelsConfig.providers.meta.selected_model)?.name || aiModelsConfig.providers.meta.selected_model}</span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {aiModelsConfig.providers?.meta?.selected_model 
                                          ? `Configured with ${aiModelsConfig.providers.meta.selected_model}`
                                          : "Most powerful open-source model for cost-effective AI"
                                        }
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={
                                      aiModelsConfig.providers?.meta?.is_active ? "default" : 
                                      aiModelsConfig.providers?.meta?.selected_model ? "secondary" : "outline"
                                    }>
                                      {aiModelsConfig.providers?.meta?.is_active ? "✅ Active & Running" : 
                                       aiModelsConfig.providers?.meta?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                    </Badge>
                                    {aiModelsConfig.providers?.meta?.selected_model && (
                                      <span className="text-sm text-gray-500">
                                        ${aiModelsConfig.providers.meta.available_models?.find(m => m.id === aiModelsConfig.providers.meta.selected_model)?.pricing_input || '0.002'}/1K tokens
                                      </span>
                                    )}
                                    {aiModelTesting.llama_expanded ? "−" : "+"}
                                  </div>
                                </div>
                                {aiModelTesting.llama_expanded && (
                                  <div className="border-t p-4 bg-gray-50">
                                    <div className="text-sm text-gray-600 mb-4">
                                      🚀 <strong>Best for:</strong> Cost-effective audience analysis, batch processing, custom training, open-source deployment
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Configuration */}
                                      <div>
                                        <Label className="text-sm font-medium">API Key (Replicate/Together/Fireworks)</Label>
                                        <div className="flex gap-2">
                                          <Input 
                                            type="password" 
                                            placeholder="Enter API key for Llama access"
                                            className="flex-1"
                                            value={aiModelsConfig.providers?.meta?.api_key || ''}
                                            onChange={(e) => {
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  meta: {
                                                    ...prev.providers?.meta,
                                                    api_key: e.target.value,
                                                    name: 'Meta Llama',
                                                    available_models: []
                                                  }
                                                }
                                              }));
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={async () => {
                                              const apiKey = aiModelsConfig.providers?.meta?.api_key;
                                              if (!apiKey) {
                                                toast.error('Please enter an API key first');
                                                return;
                                              }
                                              
                                              setAiModelTesting(prev => ({...prev, meta_discovering: true}));
                                              
                                              try {
                                                const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    provider: 'meta',
                                                    api_key: apiKey
                                                  })
                                                });
                                                
                                                if (response.ok) {
                                                  const data = await response.json();
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      meta: {
                                                        ...prev.providers?.meta,
                                                        available_models: data.available_models,
                                                        provider_info: data.provider_info,
                                                        api_key_status: 'valid'
                                                      }
                                                    }
                                                  }));
                                                  toast.success(`Discovered ${data.available_models.length} Meta Llama models!`);
                                                } else {
                                                  const error = await response.json();
                                                  toast.error(`Discovery failed: ${error.detail}`);
                                                }
                                              } catch (error) {
                                                toast.error('Failed to discover models. Please check your API key.');
                                              } finally {
                                                setAiModelTesting(prev => ({...prev, meta_discovering: false}));
                                              }
                                            }}
                                            disabled={aiModelTesting.meta_discovering || !aiModelsConfig.providers?.meta?.api_key}
                                          >
                                            {aiModelTesting.meta_discovering ? (
                                              <>
                                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                Discovering...
                                              </>
                                            ) : (
                                              <>
                                                🔍 Discover Models
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                        
                                        {/* Dynamic Model Selection */}
                                        {aiModelsConfig.providers?.meta?.available_models?.length > 0 && (
                                          <div className="mt-3">
                                            <Label className="text-sm font-medium">Choose Your Model</Label>
                                            <Select
                                              value={aiModelsConfig.providers?.meta?.selected_model || ''}
                                              onValueChange={(value) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    meta: {
                                                      ...prev.providers?.meta,
                                                      selected_model: value
                                                    }
                                                  }
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select a model from your account" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {aiModelsConfig.providers.meta.available_models.map((model) => (
                                                  <SelectItem key={model.id} value={model.id}>
                                                    <div className="flex flex-col">
                                                      <span className="font-medium">{model.name}</span>
                                                      <span className="text-xs text-gray-500">
                                                        {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                      </span>
                                                    </div>
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Provider Info */}
                                        {aiModelsConfig.providers?.meta?.provider_info && (
                                          <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                            ✅ <strong>{aiModelsConfig.providers.meta.provider_info.name}</strong> - 
                                            {aiModelsConfig.providers.meta.provider_info.total_models} models available
                                          </div>
                                        )}
                                        
                                        <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                          {[
                                            'Cost-Effective Analysis', 'Batch Processing', 'Custom Training', 
                                            'Open Source Deployment', 'Large Scale Processing', 'Research Tasks'
                                          ].map(context => (
                                            <label key={context} className="flex items-center gap-2 text-sm">
                                              <input 
                                                type="checkbox" 
                                                className="rounded"
                                                checked={aiModelsConfig.providers?.meta?.contexts?.includes(context) || false}
                                                onChange={(e) => {
                                                  const contexts = aiModelsConfig.providers?.meta?.contexts || [];
                                                  const newContexts = e.target.checked 
                                                    ? [...contexts, context]
                                                    : contexts.filter(c => c !== context);
                                                  
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      meta: {
                                                        ...prev.providers?.meta,
                                                        contexts: newContexts
                                                      }
                                                    }
                                                  }));
                                                }}
                                              />
                                              {context}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Usage & Controls */}
                                      <div>
                                        <div className="bg-white rounded p-3 border mb-3">
                                          <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                          <div className="space-y-1 text-xs">
                                            <div className="flex justify-between">
                                              <span>API Calls:</span>
                                              <span className="font-mono">{aiUsageStats.meta?.calls || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Tokens Used:</span>
                                              <span className="font-mono">{aiUsageStats.meta?.tokens || 0}</span>
                                            </div>
                                            <div className="flex justify-between font-medium">
                                              <span>Cost:</span>
                                              <span className="font-mono">${(aiUsageStats.meta?.cost || 0).toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => {
                                              toast.success('Testing Meta Llama connection...');
                                            }}
                                            disabled={!aiModelsConfig.providers?.meta?.selected_model}
                                          >
                                            🧪 Test
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant={aiModelsConfig.providers?.meta?.is_active ? "destructive" : "default"}
                                            onClick={() => {
                                              const newState = !aiModelsConfig.providers?.meta?.is_active;
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  meta: {
                                                    ...prev.providers?.meta,
                                                    is_active: newState
                                                  }
                                                }
                                              }));
                                              toast.success(`Meta Llama ${newState ? 'Activated' : 'Deactivated'}`);
                                            }}
                                            disabled={!aiModelsConfig.providers?.meta?.selected_model}
                                          >
                                            {aiModelsConfig.providers?.meta?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Model 4: Google Gemini 1.5 Pro (Dynamic Header) */}
                              <div className="border rounded-lg">
                                <div 
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                  onClick={() => setAiModelTesting(prev => ({...prev, 'gemini_expanded': !prev.gemini_expanded}))}
                                >
                                  <div className="flex items-center gap-3">
                                    <Badge variant="secondary" className="bg-orange-100 text-orange-700">🎯 Best Multimodal</Badge>
                                    <div>
                                      <h4 className="font-semibold">
                                        ✨ Google Gemini
                                        {aiModelsConfig.providers?.google?.selected_model && (
                                          <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.google.available_models?.find(m => m.id === aiModelsConfig.providers.google.selected_model)?.name || aiModelsConfig.providers.google.selected_model}</span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {aiModelsConfig.providers?.google?.selected_model 
                                          ? `Configured with ${aiModelsConfig.providers.google.selected_model}`
                                          : "Advanced multimodal capabilities with vision and reasoning"
                                        }
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={
                                      aiModelsConfig.providers?.google?.is_active ? "default" : 
                                      aiModelsConfig.providers?.google?.selected_model ? "secondary" : "outline"
                                    }>
                                      {aiModelsConfig.providers?.google?.is_active ? "✅ Active & Running" : 
                                       aiModelsConfig.providers?.google?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                    </Badge>
                                    {aiModelsConfig.providers?.google?.selected_model && (
                                      <span className="text-sm text-gray-500">
                                        ${aiModelsConfig.providers.google.available_models?.find(m => m.id === aiModelsConfig.providers.google.selected_model)?.pricing_input || '0.004'}/1K tokens
                                      </span>
                                    )}
                                    {aiModelTesting.gemini_expanded ? "−" : "+"}
                                  </div>
                                </div>
                                {aiModelTesting.gemini_expanded && (
                                  <div className="border-t p-4 bg-gray-50">
                                    <div className="text-sm text-gray-600 mb-4">
                                      🚀 <strong>Best for:</strong> Multimodal analysis, image understanding, video processing, complex reasoning with visual context
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Configuration */}
                                      <div>
                                        <Label className="text-sm font-medium">API Key</Label>
                                        <div className="flex gap-2">
                                          <Input 
                                            type="password" 
                                            placeholder="AI..."
                                            className="flex-1"
                                            value={aiModelsConfig.providers?.google?.api_key || ''}
                                            onChange={(e) => {
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  google: {
                                                    ...prev.providers?.google,
                                                    api_key: e.target.value,
                                                    name: 'Google Gemini',
                                                    available_models: []
                                                  }
                                                }
                                              }));
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={async () => {
                                              const apiKey = aiModelsConfig.providers?.google?.api_key;
                                              if (!apiKey) {
                                                toast.error('Please enter an API key first');
                                                return;
                                              }
                                              
                                              setAiModelTesting(prev => ({...prev, google_discovering: true}));
                                              
                                              try {
                                                const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                  method: 'POST',
                                                  headers: {
                                                    'Content-Type': 'application/json',
                                                    'Authorization': `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    provider: 'google',
                                                    api_key: apiKey
                                                  })
                                                });
                                                
                                                if (response.ok) {
                                                  const data = await response.json();
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      google: {
                                                        ...prev.providers?.google,
                                                        available_models: data.available_models,
                                                        provider_info: data.provider_info,
                                                        api_key_status: 'valid'
                                                      }
                                                    }
                                                  }));
                                                  toast.success(`Discovered ${data.available_models.length} Google models!`);
                                                } else {
                                                  const error = await response.json();
                                                  toast.error(`Discovery failed: ${error.detail}`);
                                                }
                                              } catch (error) {
                                                toast.error('Failed to discover models. Please check your API key.');
                                              } finally {
                                                setAiModelTesting(prev => ({...prev, google_discovering: false}));
                                              }
                                            }}
                                            disabled={aiModelTesting.google_discovering || !aiModelsConfig.providers?.google?.api_key}
                                          >
                                            {aiModelTesting.google_discovering ? (
                                              <>
                                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                Discovering...
                                              </>
                                            ) : (
                                              <>
                                                🔍 Discover Models
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                        
                                        {/* Dynamic Model Selection */}
                                        {aiModelsConfig.providers?.google?.available_models?.length > 0 && (
                                          <div className="mt-3">
                                            <Label className="text-sm font-medium">Choose Your Model</Label>
                                            <Select
                                              value={aiModelsConfig.providers?.google?.selected_model || ''}
                                              onValueChange={(value) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    google: {
                                                      ...prev.providers?.google,
                                                      selected_model: value
                                                    }
                                                  }
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select a model from your account" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {aiModelsConfig.providers.google.available_models.map((model) => (
                                                  <SelectItem key={model.id} value={model.id}>
                                                    <div className="flex flex-col">
                                                      <span className="font-medium">{model.name}</span>
                                                      <span className="text-xs text-gray-500">
                                                        {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                      </span>
                                                    </div>
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Provider Info */}
                                        {aiModelsConfig.providers?.google?.provider_info && (
                                          <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                            ✅ <strong>{aiModelsConfig.providers.google.provider_info.name}</strong> - 
                                            {aiModelsConfig.providers.google.provider_info.total_models} models available
                                          </div>
                                        )}
                                        
                                        <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                          {[
                                            'Multimodal Analysis', 'Image Processing', 'Video Understanding', 
                                            'Visual Content', 'Document Analysis', 'Complex Reasoning'
                                          ].map(context => (
                                            <label key={context} className="flex items-center gap-2 text-sm">
                                              <input 
                                                type="checkbox" 
                                                className="rounded"
                                                checked={aiModelsConfig.providers?.google?.contexts?.includes(context) || false}
                                                onChange={(e) => {
                                                  const contexts = aiModelsConfig.providers?.google?.contexts || [];
                                                  const newContexts = e.target.checked 
                                                    ? [...contexts, context]
                                                    : contexts.filter(c => c !== context);
                                                  
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      google: {
                                                        ...prev.providers?.google,
                                                        contexts: newContexts
                                                      }
                                                    }
                                                  }));
                                                }}
                                              />
                                              {context}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                      
                                      {/* Usage & Controls */}
                                      <div>
                                        <div className="bg-white rounded p-3 border mb-3">
                                          <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                          <div className="space-y-1 text-xs">
                                            <div className="flex justify-between">
                                              <span>API Calls:</span>
                                              <span className="font-mono">{aiUsageStats.google?.calls || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Tokens Used:</span>
                                              <span className="font-mono">{aiUsageStats.google?.tokens || 0}</span>
                                            </div>
                                            <div className="flex justify-between font-medium">
                                              <span>Cost:</span>
                                              <span className="font-mono">${(aiUsageStats.google?.cost || 0).toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => {
                                              toast.success('Testing Google Gemini connection...');
                                            }}
                                            disabled={!aiModelsConfig.providers?.google?.selected_model}
                                          >
                                            🧪 Test
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant={aiModelsConfig.providers?.google?.is_active ? "destructive" : "default"}
                                            onClick={() => {
                                              const newState = !aiModelsConfig.providers?.google?.is_active;
                                              setAiModelsConfig(prev => ({
                                                ...prev,
                                                providers: {
                                                  ...prev.providers,
                                                  google: {
                                                    ...prev.providers?.google,
                                                    is_active: newState
                                                  }
                                                }
                                              }));
                                              toast.success(`Google Gemini ${newState ? 'Activated' : 'Deactivated'}`);
                                            }}
                                            disabled={!aiModelsConfig.providers?.google?.selected_model}
                                          >
                                            {aiModelsConfig.providers?.google?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Additional 6 Models in Accordion Format (Consistent Design) */}
                              <div className="space-y-3">
                                <h4 className="font-semibold mb-3 text-gray-700">🔧 More AI Models</h4>
                                
                                {/* Model 5: Mistral Large */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'mistral_large_expanded': !prev.mistral_large_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-purple-100 text-purple-700">🇪🇺 European Leader</Badge>
                                      <div>
                                        <h4 className="font-semibold">
                                          🇫🇷 Mistral Large
                                          {aiModelsConfig.providers?.mistral_large?.selected_model && (
                                            <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.mistral_large.available_models?.find(m => m.id === aiModelsConfig.providers.mistral_large.selected_model)?.name || aiModelsConfig.providers.mistral_large.selected_model}</span>
                                          )}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                          {aiModelsConfig.providers?.mistral_large?.selected_model 
                                            ? `Configured with ${aiModelsConfig.providers.mistral_large.selected_model}`
                                            : "Advanced European AI model with excellent multilingual capabilities"
                                          }
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant={
                                        aiModelsConfig.providers?.mistral_large?.is_active ? "default" : 
                                        aiModelsConfig.providers?.mistral_large?.selected_model ? "secondary" : "outline"
                                      }>
                                        {aiModelsConfig.providers?.mistral_large?.is_active ? "✅ Active & Running" : 
                                         aiModelsConfig.providers?.mistral_large?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                      </Badge>
                                      {aiModelsConfig.providers?.mistral_large?.selected_model && (
                                        <span className="text-sm text-gray-500">
                                          ${aiModelsConfig.providers.mistral_large.available_models?.find(m => m.id === aiModelsConfig.providers.mistral_large.selected_model)?.pricing_input || '0.008'}/1K tokens
                                        </span>
                                      )}
                                      {aiModelTesting.mistral_large_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.mistral_large_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-sm text-gray-600 mb-4">
                                        🚀 <strong>Best for:</strong> Multilingual content, European data compliance, advanced reasoning tasks
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Configuration for Mistral */}
                                        <div>
                                          <Label className="text-sm font-medium">API Key</Label>
                                          <div className="flex gap-2">
                                            <Input 
                                              type="password" 
                                              placeholder="Enter Mistral AI API key"
                                              className="flex-1"
                                              value={aiModelsConfig.providers?.mistral_large?.api_key || ''}
                                              onChange={(e) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    mistral_large: {
                                                      ...prev.providers?.mistral_large,
                                                      api_key: e.target.value,
                                                      name: 'Mistral Large'
                                                    }
                                                  }
                                                }));
                                              }}
                                            />
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={async () => {
                                                const apiKey = aiModelsConfig.providers?.mistral_large?.api_key;
                                                if (!apiKey) {
                                                  toast.error('Please enter an API key first');
                                                  return;
                                                }
                                                
                                                setAiModelTesting(prev => ({...prev, mistral_large_discovering: true}));
                                                
                                                try {
                                                  const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                    method: 'POST',
                                                    headers: {
                                                      'Content-Type': 'application/json',
                                                      'Authorization': `Bearer ${token}`
                                                    },
                                                    body: JSON.stringify({
                                                      provider: 'mistral_large',
                                                      api_key: apiKey
                                                    })
                                                  });
                                                  
                                                  if (response.ok) {
                                                    const data = await response.json();
                                                    setAiModelsConfig(prev => ({
                                                      ...prev,
                                                      providers: {
                                                        ...prev.providers,
                                                        mistral_large: {
                                                          ...prev.providers?.mistral_large,
                                                          available_models: data.available_models,
                                                          provider_info: data.provider_info
                                                        }
                                                      }
                                                    }));
                                                    toast.success(`Discovered ${data.available_models.length} Mistral models!`);
                                                  } else {
                                                    const error = await response.json();
                                                    toast.error(`Discovery failed: ${error.detail}`);
                                                  }
                                                } catch (error) {
                                                  toast.error('Failed to discover models');
                                                } finally {
                                                  setAiModelTesting(prev => ({...prev, mistral_large_discovering: false}));
                                                }
                                              }}
                                              disabled={aiModelTesting.mistral_large_discovering}
                                              className="text-xs"
                                            >
                                              {aiModelTesting.mistral_large_discovering ? (
                                                <>
                                                  <Clock className="h-3 w-3 mr-1 animate-spin" />
                                                  Discovering...
                                                </>
                                              ) : (
                                                '🔍 Discover'
                                              )}
                                            </Button>
                                          </div>
                                          
                                          {/* Dynamic Model Selection */}
                                          {aiModelsConfig.providers?.mistral_large?.available_models?.length > 0 && (
                                            <div className="mt-3">
                                              <Label className="text-sm font-medium">Choose Your Model</Label>
                                              <Select
                                                value={aiModelsConfig.providers?.mistral_large?.selected_model || ''}
                                                onValueChange={(value) => {
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      mistral_large: {
                                                        ...prev.providers?.mistral_large,
                                                        selected_model: value
                                                      }
                                                    }
                                                  }));
                                                }}
                                              >
                                                <SelectTrigger>
                                                  <SelectValue placeholder="Select a model from your account" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {aiModelsConfig.providers.mistral_large.available_models.map((model) => (
                                                    <SelectItem key={model.id} value={model.id}>
                                                      <div className="flex flex-col">
                                                        <span className="font-medium">{model.name}</span>
                                                        <span className="text-xs text-gray-500">
                                                          {model.description} • ${model.pricing_input}/1K tokens
                                                        </span>
                                                      </div>
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          )}
                                          
                                          <Label className="text-sm font-medium mt-3">Usage Contexts</Label>
                                          <div className="grid grid-cols-2 gap-2 mt-2">
                                            {[
                                              'Content Generation', 'Multilingual Analysis', 'European Compliance',
                                              'Advanced Reasoning', 'Technical Writing', 'Data Privacy'
                                            ].map(context => (
                                              <label key={context} className="flex items-center gap-2 text-sm">
                                                <input type="checkbox" className="rounded" />
                                                {context}
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                        
                                        <div>
                                          <div className="bg-white rounded p-3 border mb-3">
                                            <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                            <div className="space-y-1 text-xs">
                                              <div className="flex justify-between"><span>API Calls:</span><span>0</span></div>
                                              <div className="flex justify-between"><span>Tokens:</span><span>0</span></div>
                                              <div className="flex justify-between font-medium"><span>Cost:</span><span>$0.00</span></div>
                                            </div>
                                          </div>
                                          
                                          <div className="flex gap-2">
                                            <Button size="sm" variant="outline">🧪 Test</Button>
                                            <Button 
                                              size="sm" 
                                              variant={aiModelsConfig.providers?.mistral_large?.is_active ? "destructive" : "default"}
                                              onClick={() => {
                                                const newState = !aiModelsConfig.providers?.mistral_large?.is_active;
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    mistral_large: {
                                                      ...prev.providers?.mistral_large,
                                                      is_active: newState
                                                    }
                                                  }
                                                }));
                                                toast.success(`Mistral Large ${newState ? 'Activated' : 'Deactivated'}`);
                                              }}
                                            >
                                              {aiModelsConfig.providers?.mistral_large?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Model 6: Perplexity 70B (Dynamic Header) */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'perplexity_70b_expanded': !prev.perplexity_70b_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-teal-100 text-teal-700">🔍 Best Research</Badge>
                                      <div>
                                        <h4 className="font-semibold">
                                          🌟 Perplexity
                                          {aiModelsConfig.providers?.perplexity?.selected_model && (
                                            <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.perplexity.available_models?.find(m => m.id === aiModelsConfig.providers.perplexity.selected_model)?.name || aiModelsConfig.providers.perplexity.selected_model}</span>
                                          )}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                          {aiModelsConfig.providers?.perplexity?.selected_model 
                                            ? `Configured with ${aiModelsConfig.providers.perplexity.selected_model}`
                                            : "Outstanding for research and fact-checking tasks"
                                          }
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant={
                                        aiModelsConfig.providers?.perplexity?.is_active ? "default" : 
                                        aiModelsConfig.providers?.perplexity?.selected_model ? "secondary" : "outline"
                                      }>
                                        {aiModelsConfig.providers?.perplexity?.is_active ? "✅ Active & Running" : 
                                         aiModelsConfig.providers?.perplexity?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                      </Badge>
                                      {aiModelsConfig.providers?.perplexity?.selected_model && (
                                        <span className="text-sm text-gray-500">
                                          ${aiModelsConfig.providers.perplexity.available_models?.find(m => m.id === aiModelsConfig.providers.perplexity.selected_model)?.pricing_input || '0.001'}/1K tokens
                                        </span>
                                      )}
                                      {aiModelTesting.perplexity_70b_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.perplexity_70b_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-sm text-gray-600 mb-4">
                                        🚀 <strong>Best for:</strong> Research, fact-checking, data verification, competitive analysis, real-time web search
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Configuration */}
                                        <div>
                                          <Label className="text-sm font-medium">API Key</Label>
                                          <div className="flex gap-2">
                                            <Input 
                                              type="password" 
                                              placeholder="pplx-..."
                                              className="flex-1"
                                              value={aiModelsConfig.providers?.perplexity?.api_key || ''}
                                              onChange={(e) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    perplexity: {
                                                      ...prev.providers?.perplexity,
                                                      api_key: e.target.value,
                                                      name: 'Perplexity',
                                                      available_models: []
                                                    }
                                                  }
                                                }));
                                              }}
                                            />
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={async () => {
                                                const apiKey = aiModelsConfig.providers?.perplexity?.api_key;
                                                if (!apiKey) {
                                                  toast.error('Please enter an API key first');
                                                  return;
                                                }
                                                
                                                setAiModelTesting(prev => ({...prev, perplexity_discovering: true}));
                                                
                                                try {
                                                  const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                    method: 'POST',
                                                    headers: {
                                                      'Content-Type': 'application/json',
                                                      'Authorization': `Bearer ${token}`
                                                    },
                                                    body: JSON.stringify({
                                                      provider: 'perplexity',
                                                      api_key: apiKey
                                                    })
                                                  });
                                                  
                                                  if (response.ok) {
                                                    const data = await response.json();
                                                    setAiModelsConfig(prev => ({
                                                      ...prev,
                                                      providers: {
                                                        ...prev.providers,
                                                        perplexity: {
                                                          ...prev.providers?.perplexity,
                                                          available_models: data.available_models,
                                                          provider_info: data.provider_info,
                                                          api_key_status: 'valid'
                                                        }
                                                      }
                                                    }));
                                                    toast.success(`Discovered ${data.available_models.length} Perplexity models!`);
                                                  } else {
                                                    const error = await response.json();
                                                    toast.error(`Discovery failed: ${error.detail}`);
                                                  }
                                                } catch (error) {
                                                  toast.error('Failed to discover models. Please check your API key.');
                                                } finally {
                                                  setAiModelTesting(prev => ({...prev, perplexity_discovering: false}));
                                                }
                                              }}
                                              disabled={aiModelTesting.perplexity_discovering || !aiModelsConfig.providers?.perplexity?.api_key}
                                            >
                                              {aiModelTesting.perplexity_discovering ? (
                                                <>
                                                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                  Discovering...
                                                </>
                                              ) : (
                                                <>
                                                  🔍 Discover Models
                                                </>
                                              )}
                                            </Button>
                                          </div>
                                          
                                          {/* Dynamic Model Selection */}
                                          {aiModelsConfig.providers?.perplexity?.available_models?.length > 0 && (
                                            <div className="mt-3">
                                              <Label className="text-sm font-medium">Choose Your Model</Label>
                                              <Select
                                                value={aiModelsConfig.providers?.perplexity?.selected_model || ''}
                                                onValueChange={(value) => {
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      perplexity: {
                                                        ...prev.providers?.perplexity,
                                                        selected_model: value
                                                      }
                                                    }
                                                  }));
                                                }}
                                              >
                                                <SelectTrigger>
                                                  <SelectValue placeholder="Select a model from your account" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {aiModelsConfig.providers.perplexity.available_models.map((model) => (
                                                    <SelectItem key={model.id} value={model.id}>
                                                      <div className="flex flex-col">
                                                        <span className="font-medium">{model.name}</span>
                                                        <span className="text-xs text-gray-500">
                                                          {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                        </span>
                                                      </div>
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          )}
                                          
                                          {/* Provider Info */}
                                          {aiModelsConfig.providers?.perplexity?.provider_info && (
                                            <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                              ✅ <strong>{aiModelsConfig.providers.perplexity.provider_info.name}</strong> - 
                                              {aiModelsConfig.providers.perplexity.provider_info.total_models} models available
                                            </div>
                                          )}
                                          
                                          <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                          <div className="grid grid-cols-2 gap-2 mt-2">
                                            {[
                                              'Research & Fact-Checking', 'Competitive Analysis', 'Market Research', 
                                              'Data Verification', 'Real-time Information', 'Web Search Enhancement'
                                            ].map(context => (
                                              <label key={context} className="flex items-center gap-2 text-sm">
                                                <input 
                                                  type="checkbox" 
                                                  className="rounded"
                                                  checked={aiModelsConfig.providers?.perplexity?.contexts?.includes(context) || false}
                                                  onChange={(e) => {
                                                    const contexts = aiModelsConfig.providers?.perplexity?.contexts || [];
                                                    const newContexts = e.target.checked 
                                                      ? [...contexts, context]
                                                      : contexts.filter(c => c !== context);
                                                    
                                                    setAiModelsConfig(prev => ({
                                                      ...prev,
                                                      providers: {
                                                        ...prev.providers,
                                                        perplexity: {
                                                          ...prev.providers?.perplexity,
                                                          contexts: newContexts
                                                        }
                                                      }
                                                    }));
                                                  }}
                                                />
                                                {context}
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                        
                                        {/* Usage & Controls */}
                                        <div>
                                          <div className="bg-white rounded p-3 border mb-3">
                                            <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                            <div className="space-y-1 text-xs">
                                              <div className="flex justify-between">
                                                <span>API Calls:</span>
                                                <span className="font-mono">{aiUsageStats.perplexity?.calls || 0}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span>Tokens Used:</span>
                                                <span className="font-mono">{aiUsageStats.perplexity?.tokens || 0}</span>
                                              </div>
                                              <div className="flex justify-between font-medium">
                                                <span>Cost:</span>
                                                <span className="font-mono">${(aiUsageStats.perplexity?.cost || 0).toFixed(2)}</span>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="flex gap-2">
                                            <Button 
                                              size="sm" 
                                              variant="outline"
                                              onClick={() => {
                                                toast.success('Testing Perplexity connection...');
                                              }}
                                              disabled={!aiModelsConfig.providers?.perplexity?.selected_model}
                                            >
                                              🧪 Test
                                            </Button>
                                            <Button 
                                              size="sm" 
                                              variant={aiModelsConfig.providers?.perplexity?.is_active ? "destructive" : "default"}
                                              onClick={() => {
                                                const newState = !aiModelsConfig.providers?.perplexity?.is_active;
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    perplexity: {
                                                      ...prev.providers?.perplexity,
                                                      is_active: newState
                                                    }
                                                  }
                                                }));
                                                toast.success(`Perplexity ${newState ? 'Activated' : 'Deactivated'}`);
                                              }}
                                              disabled={!aiModelsConfig.providers?.perplexity?.selected_model}
                                            >
                                              {aiModelsConfig.providers?.perplexity?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Model 7: Cohere Command-R+ (Dynamic Header) */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'cohere_command_expanded': !prev.cohere_command_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">🏢 Enterprise</Badge>
                                      <div>
                                        <h4 className="font-semibold">
                                          🚀 Cohere Command-R+
                                          {aiModelsConfig.providers?.cohere?.selected_model && (
                                            <span className="text-sm font-normal text-gray-600"> - {aiModelsConfig.providers.cohere.available_models?.find(m => m.id === aiModelsConfig.providers.cohere.selected_model)?.name || aiModelsConfig.providers.cohere.selected_model}</span>
                                          )}
                                        </h4>
                                        <p className="text-sm text-gray-600">
                                          {aiModelsConfig.providers?.cohere?.selected_model 
                                            ? `Configured with ${aiModelsConfig.providers.cohere.selected_model}`
                                            : "Enterprise-grade model with excellent RAG capabilities"
                                          }
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant={
                                        aiModelsConfig.providers?.cohere?.is_active ? "default" : 
                                        aiModelsConfig.providers?.cohere?.selected_model ? "secondary" : "outline"
                                      }>
                                        {aiModelsConfig.providers?.cohere?.is_active ? "✅ Active & Running" : 
                                         aiModelsConfig.providers?.cohere?.selected_model ? "⚙️ Configured" : "❌ Not Configured"}
                                      </Badge>
                                      {aiModelsConfig.providers?.cohere?.selected_model && (
                                        <span className="text-sm text-gray-500">
                                          ${aiModelsConfig.providers.cohere.available_models?.find(m => m.id === aiModelsConfig.providers.cohere.selected_model)?.pricing_input || '0.003'}/1K tokens
                                        </span>
                                      )}
                                      {aiModelTesting.cohere_command_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.cohere_command_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-sm text-gray-600 mb-4">
                                        🚀 <strong>Best for:</strong> RAG applications, document understanding, enterprise search, knowledge management
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Configuration */}
                                        <div>
                                          <Label className="text-sm font-medium">API Key</Label>
                                          <div className="flex gap-2">
                                            <Input 
                                              type="password" 
                                              placeholder="Enter Cohere API key"
                                              className="flex-1"
                                              value={aiModelsConfig.providers?.cohere?.api_key || ''}
                                              onChange={(e) => {
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    cohere: {
                                                      ...prev.providers?.cohere,
                                                      api_key: e.target.value,
                                                      name: 'Cohere Command-R+',
                                                      available_models: []
                                                    }
                                                  }
                                                }));
                                              }}
                                            />
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={async () => {
                                                const apiKey = aiModelsConfig.providers?.cohere?.api_key;
                                                if (!apiKey) {
                                                  toast.error('Please enter an API key first');
                                                  return;
                                                }
                                                
                                                setAiModelTesting(prev => ({...prev, cohere_discovering: true}));
                                                
                                                try {
                                                  const response = await fetch(`${API_BASE_URL}/api/ai-models/discover`, {
                                                    method: 'POST',
                                                    headers: {
                                                      'Content-Type': 'application/json',
                                                      'Authorization': `Bearer ${token}`
                                                    },
                                                    body: JSON.stringify({
                                                      provider: 'cohere',
                                                      api_key: apiKey
                                                    })
                                                  });
                                                  
                                                  if (response.ok) {
                                                    const data = await response.json();
                                                    setAiModelsConfig(prev => ({
                                                      ...prev,
                                                      providers: {
                                                        ...prev.providers,
                                                        cohere: {
                                                          ...prev.providers?.cohere,
                                                          available_models: data.available_models,
                                                          provider_info: data.provider_info,
                                                          api_key_status: 'valid'
                                                        }
                                                      }
                                                    }));
                                                    toast.success(`Discovered ${data.available_models.length} Cohere models!`);
                                                  } else {
                                                    const error = await response.json();
                                                    toast.error(`Discovery failed: ${error.detail}`);
                                                  }
                                                } catch (error) {
                                                  toast.error('Failed to discover models. Please check your API key.');
                                                } finally {
                                                  setAiModelTesting(prev => ({...prev, cohere_discovering: false}));
                                                }
                                              }}
                                              disabled={aiModelTesting.cohere_discovering || !aiModelsConfig.providers?.cohere?.api_key}
                                            >
                                              {aiModelTesting.cohere_discovering ? (
                                                <>
                                                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                                                  Discovering...
                                                </>
                                              ) : (
                                                <>
                                                  🔍 Discover Models
                                                </>
                                              )}
                                            </Button>
                                          </div>
                                          
                                          {/* Dynamic Model Selection */}
                                          {aiModelsConfig.providers?.cohere?.available_models?.length > 0 && (
                                            <div className="mt-3">
                                              <Label className="text-sm font-medium">Choose Your Model</Label>
                                              <Select
                                                value={aiModelsConfig.providers?.cohere?.selected_model || ''}
                                                onValueChange={(value) => {
                                                  setAiModelsConfig(prev => ({
                                                    ...prev,
                                                    providers: {
                                                      ...prev.providers,
                                                      cohere: {
                                                        ...prev.providers?.cohere,
                                                        selected_model: value
                                                      }
                                                    }
                                                  }));
                                                }}
                                              >
                                                <SelectTrigger>
                                                  <SelectValue placeholder="Select a model from your account" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {aiModelsConfig.providers.cohere.available_models.map((model) => (
                                                    <SelectItem key={model.id} value={model.id}>
                                                      <div className="flex flex-col">
                                                        <span className="font-medium">{model.name}</span>
                                                        <span className="text-xs text-gray-500">
                                                          {model.description} • ${model.pricing_input}/1K tokens • {model.context_length} context
                                                        </span>
                                                      </div>
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </div>
                                          )}
                                          
                                          {/* Provider Info */}
                                          {aiModelsConfig.providers?.cohere?.provider_info && (
                                            <div className="mt-2 p-2 bg-green-50 rounded text-xs">
                                              ✅ <strong>{aiModelsConfig.providers.cohere.provider_info.name}</strong> - 
                                              {aiModelsConfig.providers.cohere.provider_info.total_models} models available
                                            </div>
                                          )}
                                          
                                          <Label className="text-sm font-medium mt-3">Usage Contexts (Multi-select)</Label>
                                          <div className="grid grid-cols-2 gap-2 mt-2">
                                            {[
                                              'RAG Applications', 'Document Analysis', 'Enterprise Search', 
                                              'Knowledge Management', 'Q&A Systems', 'Text Classification'
                                            ].map(context => (
                                              <label key={context} className="flex items-center gap-2 text-sm">
                                                <input 
                                                  type="checkbox" 
                                                  className="rounded"
                                                  checked={aiModelsConfig.providers?.cohere?.contexts?.includes(context) || false}
                                                  onChange={(e) => {
                                                    const contexts = aiModelsConfig.providers?.cohere?.contexts || [];
                                                    const newContexts = e.target.checked 
                                                      ? [...contexts, context]
                                                      : contexts.filter(c => c !== context);
                                                    
                                                    setAiModelsConfig(prev => ({
                                                      ...prev,
                                                      providers: {
                                                        ...prev.providers,
                                                        cohere: {
                                                          ...prev.providers?.cohere,
                                                          contexts: newContexts
                                                        }
                                                      }
                                                    }));
                                                  }}
                                                />
                                                {context}
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                        
                                        {/* Usage & Controls */}
                                        <div>
                                          <div className="bg-white rounded p-3 border mb-3">
                                            <h5 className="font-medium text-sm mb-2">Usage This Month</h5>
                                            <div className="space-y-1 text-xs">
                                              <div className="flex justify-between">
                                                <span>API Calls:</span>
                                                <span className="font-mono">{aiUsageStats.cohere?.calls || 0}</span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span>Tokens Used:</span>
                                                <span className="font-mono">{aiUsageStats.cohere?.tokens || 0}</span>
                                              </div>
                                              <div className="flex justify-between font-medium">
                                                <span>Cost:</span>
                                                <span className="font-mono">${(aiUsageStats.cohere?.cost || 0).toFixed(2)}</span>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="flex gap-2">
                                            <Button 
                                              size="sm" 
                                              variant="outline"
                                              onClick={() => {
                                                toast.success('Testing Cohere connection...');
                                              }}
                                              disabled={!aiModelsConfig.providers?.cohere?.selected_model}
                                            >
                                              🧪 Test
                                            </Button>
                                            <Button 
                                              size="sm" 
                                              variant={aiModelsConfig.providers?.cohere?.is_active ? "destructive" : "default"}
                                              onClick={() => {
                                                const newState = !aiModelsConfig.providers?.cohere?.is_active;
                                                setAiModelsConfig(prev => ({
                                                  ...prev,
                                                  providers: {
                                                    ...prev.providers,
                                                    cohere: {
                                                      ...prev.providers?.cohere,
                                                      is_active: newState
                                                    }
                                                  }
                                                }));
                                                toast.success(`Cohere ${newState ? 'Activated' : 'Deactivated'}`);
                                              }}
                                              disabled={!aiModelsConfig.providers?.cohere?.selected_model}
                                            >
                                              {aiModelsConfig.providers?.cohere?.is_active ? "⏹️ Deactivate" : "▶️ Activate"}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Model 8: GPT-3.5 Turbo */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'gpt35_turbo_expanded': !prev.gpt35_turbo_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-green-100 text-green-700">💰 Cost Effective</Badge>
                                      <div>
                                        <h4 className="font-semibold">⚡ GPT-3.5 Turbo</h4>
                                        <p className="text-sm text-gray-600">Highly cost-effective for high-volume tasks</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary">⚙️ Inactive</Badge>
                                      <span className="text-sm text-gray-500">$0.0005/1K tokens</span>
                                      {aiModelTesting.gpt35_turbo_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.gpt35_turbo_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-center py-8 text-gray-500">
                                        <p className="mb-2">⚡ GPT-3.5 Turbo configuration interface</p>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Model 9: Claude 3 Haiku */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'claude_haiku_expanded': !prev.claude_haiku_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">⚡ Fast & Cheap</Badge>
                                      <div>
                                        <h4 className="font-semibold">🌸 Claude 3 Haiku</h4>
                                        <p className="text-sm text-gray-600">Lightning-fast responses at minimal cost</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary">⚙️ Inactive</Badge>
                                      <span className="text-sm text-gray-500">$0.00025/1K tokens</span>
                                      {aiModelTesting.claude_haiku_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.claude_haiku_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-center py-8 text-gray-500">
                                        <p className="mb-2">🌸 Claude 3 Haiku configuration interface</p>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Model 10: Llama 3.1-70B */}
                                <div className="border rounded-lg">
                                  <div 
                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50" 
                                    onClick={() => setAiModelTesting(prev => ({...prev, 'llama_70b_expanded': !prev.llama_70b_expanded}))}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant="secondary" className="bg-orange-100 text-orange-700">⚖️ Balanced</Badge>
                                      <div>
                                        <h4 className="font-semibold">🦙 Llama 3.1-70B</h4>
                                        <p className="text-sm text-gray-600">Perfect balance of performance and cost</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary">⚙️ Inactive</Badge>
                                      <span className="text-sm text-gray-500">$0.0009/1K tokens</span>
                                      {aiModelTesting.llama_70b_expanded ? "−" : "+"}
                                    </div>
                                  </div>
                                  {aiModelTesting.llama_70b_expanded && (
                                    <div className="border-t p-4 bg-gray-50">
                                      <div className="text-center py-8 text-gray-500">
                                        <p className="mb-2">🦙 Llama 3.1-70B configuration interface</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                              </div>

                              {/* Must-Have Usage Contexts */}
                              <Card className="border-yellow-200 bg-yellow-50">
                                <CardHeader>
                                  <CardTitle className="text-lg text-yellow-800">⭐ Must-Have Usage Contexts</CardTitle>
                                  <CardDescription className="text-yellow-700">
                                    Essential AI use cases for marketing success. Configure at least one model for each context.
                                  </CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <Target className="h-5 w-5 text-blue-500" />
                                        <div>
                                          <h4 className="font-medium">Audience Intelligence</h4>
                                          <p className="text-sm text-gray-600">Analyze and categorize audience data</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-green-100 text-green-700">Required</Badge>
                                      </div>
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <Users className="h-5 w-5 text-purple-500" />
                                        <div>
                                          <h4 className="font-medium">Persona Creation</h4>
                                          <p className="text-sm text-gray-600">Build detailed customer personas</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-green-100 text-green-700">Required</Badge>
                                      </div>
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <Edit className="h-5 w-5 text-orange-500" />
                                        <div>
                                          <h4 className="font-medium">Content Generation</h4>
                                          <p className="text-sm text-gray-600">Create personalized campaign content</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">Recommended</Badge>
                                      </div>
                                    </div>
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <TrendingUp className="h-5 w-5 text-green-500" />
                                        <div>
                                          <h4 className="font-medium">Lead Scoring</h4>
                                          <p className="text-sm text-gray-600">Qualify and score potential customers</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-green-100 text-green-700">Required</Badge>
                                      </div>
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <BarChart3 className="h-5 w-5 text-red-500" />
                                        <div>
                                          <h4 className="font-medium">Campaign Optimization</h4>
                                          <p className="text-sm text-gray-600">Improve campaign performance</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-blue-100 text-blue-700">Recommended</Badge>
                                      </div>
                                      <div className="flex items-center gap-3 p-3 bg-white rounded border">
                                        <CheckCircle className="h-5 w-5 text-teal-500" />
                                        <div>
                                          <h4 className="font-medium">Data Quality Assessment</h4>
                                          <p className="text-sm text-gray-600">Validate and clean audience data</p>
                                        </div>
                                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Optional</Badge>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                              
                            </CardContent>
                          </Card>

                          {/* AI Provider Intelligence System */}
                          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-purple-800">
                                <Brain className="h-6 w-6 text-purple-600" />
                                🚀 AI Provider Intelligence System
                                <Badge className="bg-purple-100 text-purple-700 ml-2">NEW</Badge>
                              </CardTitle>
                              <CardDescription className="text-purple-700">
                                Intelligent AI provider selection that automatically routes tasks to the optimal AI model based on context, 
                                performance, and cost efficiency. Experience next-generation AI automation.
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                              
                              {/* Intelligence Features */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white/70 border border-purple-200 rounded-lg p-4">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                      <Zap className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800">Smart Context Routing</h4>
                                      <p className="text-sm text-gray-600">Automatically selects best AI for each task</p>
                                    </div>
                                  </div>
                                  <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Content Generation → OpenAI GPT-4</li>
                                    <li>• Behavioral Analysis → Anthropic Claude</li>
                                    <li>• Market Research → Perplexity AI</li>
                                    <li>• Image Processing → Google Gemini</li>
                                  </ul>
                                </div>

                                <div className="bg-white/70 border border-purple-200 rounded-lg p-4">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                      <DollarSign className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800">Cost Optimization</h4>
                                      <p className="text-sm text-gray-600">Uses cheaper models for simpler tasks</p>
                                    </div>
                                  </div>
                                  <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Simple tasks → Light models (60% cost savings)</li>
                                    <li>• Complex tasks → Premium models</li>
                                    <li>• Batch processing → Optimized routing</li>
                                    <li>• Real-time cost tracking</li>
                                  </ul>
                                </div>

                                <div className="bg-white/70 border border-purple-200 rounded-lg p-4">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                                      <RefreshCw className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800">Intelligent Fallbacks</h4>
                                      <p className="text-sm text-gray-600">Automatic backup when providers fail</p>
                                    </div>
                                  </div>
                                  <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Primary provider down → Auto switch</li>
                                    <li>• Rate limits → Alternative routing</li>
                                    <li>• 99.9% reliability guarantee</li>
                                    <li>• Zero manual intervention</li>
                                  </ul>
                                </div>

                                <div className="bg-white/70 border border-purple-200 rounded-lg p-4">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                      <BarChart3 className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800">Performance Analytics</h4>
                                      <p className="text-sm text-gray-600">Detailed insights and optimization</p>
                                    </div>
                                  </div>
                                  <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Provider performance scoring</li>
                                    <li>• Context-based optimization</li>
                                    <li>• Usage pattern analysis</li>
                                    <li>• ROI optimization recommendations</li>
                                  </ul>
                                </div>
                              </div>

                              {/* Test AI Provider Intelligence */}
                              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-4">
                                  <div>
                                    <h3 className="text-lg font-semibold mb-2">🧪 Test AI Provider Intelligence</h3>
                                    <p className="text-purple-100">
                                      Experience intelligent AI routing with sample data and see which provider gets selected for different contexts.
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold">🎯</div>
                                    <div className="text-sm text-purple-200">Live Demo</div>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                  <div>
                                    <Label className="text-purple-100 text-sm">Test Context</Label>
                                    <select 
                                      className="w-full mt-1 p-2 rounded bg-white/20 text-white border border-white/30"
                                      value={aiIntelligenceTest.context}
                                      onChange={(e) => setAiIntelligenceTest(prev => ({...prev, context: e.target.value}))}
                                    >
                                      <option value="Audience Intelligence">👥 Audience Intelligence</option>
                                      <option value="Content Generation">✍️ Content Generation</option>
                                      <option value="Behavioral Analysis">🧠 Behavioral Analysis</option>
                                      <option value="Market Research">📊 Market Research</option>
                                      <option value="Predictive Analytics">🔮 Predictive Analytics</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label className="text-purple-100 text-sm">Task Complexity</Label>
                                    <select 
                                      className="w-full mt-1 p-2 rounded bg-white/20 text-white border border-white/30"
                                      value={aiIntelligenceTest.complexity}
                                      onChange={(e) => setAiIntelligenceTest(prev => ({...prev, complexity: e.target.value}))}
                                    >
                                      <option value="simple">🟢 Simple</option>
                                      <option value="moderate">🟡 Moderate</option>
                                      <option value="complex">🟠 Complex</option>
                                      <option value="critical">🔴 Critical</option>
                                    </select>
                                  </div>
                                  <div className="flex items-end">
                                    <Button
                                      onClick={async () => {
                                        setAiIntelligenceTest(prev => ({...prev, testing: true, result: null}));
                                        try {
                                          const response = await api.post('/api/ai-provider-intelligence/test', {
                                            context: aiIntelligenceTest.context,
                                            complexity: aiIntelligenceTest.complexity,
                                            test_data: {
                                              name: "Test User",
                                              email: "test@example.com",
                                              location: "San Francisco, CA",
                                              engagement: "high"
                                            }
                                          });
                                          setAiIntelligenceTest(prev => ({...prev, result: response.data, testing: false}));
                                        } catch (error) {
                                          setAiIntelligenceTest(prev => ({...prev, result: {test_status: 'error', error: error.message}, testing: false}));
                                        }
                                      }}
                                      disabled={aiIntelligenceTest.testing}
                                      className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
                                    >
                                      {aiIntelligenceTest.testing ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Testing...
                                        </>
                                      ) : (
                                        <>
                                          <Play className="h-4 w-4 mr-2" />
                                          Run Test
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>

                                {/* Test Results */}
                                {aiIntelligenceTest.result && (
                                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                                    <h4 className="font-medium mb-3 flex items-center gap-2">
                                      <Target className="h-5 w-5" />
                                      Intelligence Test Results
                                    </h4>
                                    
                                    {aiIntelligenceTest.result.test_status === 'success' ? (
                                      <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <div>
                                            <div className="text-sm text-purple-200 mb-1">Selected AI Provider</div>
                                            <div className="text-lg font-semibold">
                                              🤖 {aiIntelligenceTest.result.provider_selection?.primary_provider?.provider_id || 'Unknown'}
                                            </div>
                                            <div className="text-sm text-purple-200">
                                              Model: {aiIntelligenceTest.result.provider_selection?.primary_provider?.model || 'N/A'}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-sm text-purple-200 mb-1">Optimization Score</div>
                                            <div className="text-lg font-semibold">
                                              ⭐ {(aiIntelligenceTest.result.provider_selection?.primary_provider?.optimization_score * 100 || 0).toFixed(1)}%
                                            </div>
                                            <div className="text-sm text-purple-200">
                                              Context: {aiIntelligenceTest.result.test_context}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {aiIntelligenceTest.result.provider_selection?.selection_reasoning && (
                                          <div>
                                            <div className="text-sm text-purple-200 mb-2">Selection Reasoning</div>
                                            <ul className="text-sm space-y-1">
                                              {aiIntelligenceTest.result.provider_selection.selection_reasoning.map((reason, idx) => (
                                                <li key={idx}>• {reason}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2 pt-2 border-t border-white/20">
                                          <CheckCircle className="h-5 w-5 text-green-300" />
                                          <span className="text-sm">Intelligent routing is working perfectly!</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-red-200">
                                        <div className="flex items-center gap-2 mb-2">
                                          <XCircle className="h-5 w-5" />
                                          <span>Test Failed</span>
                                        </div>
                                        <p className="text-sm">{aiIntelligenceTest.result.error}</p>
                                        {aiIntelligenceTest.result.recommendation && (
                                          <p className="text-sm mt-2 text-yellow-200">
                                            💡 {aiIntelligenceTest.result.recommendation}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Status and Recommendations */}
                              <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="font-semibold text-gray-800">🎯 System Status & Recommendations</h4>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const response = await api.get('/api/ai-provider-intelligence/recommendations');
                                        setAiProviderRecommendations(response.data);
                                      } catch (error) {
                                        console.error('Failed to get recommendations:', error);
                                      }
                                    }}
                                  >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Refresh Status
                                  </Button>
                                </div>
                                
                                {aiProviderRecommendations ? (
                                  <div className="space-y-3">
                                    {aiProviderRecommendations.recommendations?.map((rec, idx) => (
                                      <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                                        rec.priority === 'critical' ? 'bg-red-50 border-red-400' :
                                        rec.priority === 'high' ? 'bg-orange-50 border-orange-400' :
                                        'bg-blue-50 border-blue-400'
                                      }`}>
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge variant={rec.priority === 'critical' ? 'destructive' : rec.priority === 'high' ? 'default' : 'secondary'}>
                                            {rec.priority}
                                          </Badge>
                                          <span className="font-medium">{rec.title}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                                        <p className="text-xs text-gray-500">Action: {rec.action}</p>
                                      </div>
                                    ))}
                                    
                                    {aiProviderRecommendations.optimization_score && (
                                      <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                            <TrendingUp className="h-4 w-4 text-green-600" />
                                          </div>
                                          <div>
                                            <div className="font-semibold text-green-800">
                                              Optimization Score: {aiProviderRecommendations.optimization_score.toFixed(1)}%
                                            </div>
                                            <div className="text-sm text-green-600">
                                              Your AI setup is {aiProviderRecommendations.optimization_score > 80 ? 'excellent' : 
                                                               aiProviderRecommendations.optimization_score > 60 ? 'good' : 'needs improvement'}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-gray-500">
                                    <Brain className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                    <p>Click "Refresh Status" to get personalized recommendations</p>
                                  </div>
                                )}
                              </div>

                            </CardContent>
                          </Card>

                        </div>
                      </TabsContent>

                      {/* CRM Integration Configuration Tab */}
                      <TabsContent value="crm-integration">
                        <div className="space-y-6">
                          {/* Enhanced Header with Overview */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Link className="h-5 w-5 text-blue-600" />
                                CRM Integration Hub
                              </CardTitle>
                              <CardDescription>
                                Connect and synchronize your audience data with popular CRM systems for unified customer management.
                                Manage HubSpot, Zoho, Salesforce and other integrations with bidirectional data sync.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              {/* CRM Stats Overview */}
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <Link className="h-5 w-5 text-blue-600" />
                                    <span className="text-blue-700 font-medium">Connected CRMs</span>
                                  </div>
                                  <p className="text-2xl font-bold text-blue-700 mt-1">
                                    {crmConnections.length}
                                  </p>
                                </div>
                                
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-5 w-5 text-green-600" />
                                    <span className="text-green-700 font-medium">Synced Contacts</span>
                                  </div>
                                  <p className="text-2xl font-bold text-green-700 mt-1">
                                    {crmStats.total_contacts_synced || 0}
                                  </p>
                                </div>
                                
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5 text-purple-600" />
                                    <span className="text-purple-700 font-medium">Success Rate</span>
                                  </div>
                                  <p className="text-2xl font-bold text-purple-700 mt-1">
                                    {crmStats.sync_success_rate || 0}%
                                  </p>
                                </div>
                                
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-orange-600" />
                                    <span className="text-orange-700 font-medium">Last Sync</span>
                                  </div>
                                  <p className="text-2xl font-bold text-orange-700 mt-1">
                                    {crmStats.last_sync ? new Date(crmStats.last_sync).toLocaleDateString() : 'Never'}
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* CRM Connections Management */}
                          <Card>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="flex items-center gap-2">
                                    🔗 CRM Connections
                                  </CardTitle>
                                  <CardDescription>
                                    Manage your CRM system connections and synchronization settings.
                                  </CardDescription>
                                </div>
                                <Button 
                                  onClick={() => setShowConnectionWizard(true)}
                                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add CRM
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent>
                              {crmConnections.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                  <Link className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                  <h3 className="text-lg font-medium text-gray-900 mb-2">No CRM Connections</h3>
                                  <p className="text-gray-600 mb-4">Connect your first CRM to start synchronizing customer data</p>
                                  <Button 
                                    onClick={() => setShowConnectionWizard(true)}
                                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                                  >
                                    Connect CRM
                                  </Button>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {crmConnections.map((connection) => (
                                    <Card key={connection.id} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                      <CardContent className="p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            {connection.crm_type === 'hubspot' && (
                                              <div className="p-2 bg-orange-100 rounded-lg">
                                                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs">H</div>
                                              </div>
                                            )}
                                            {connection.crm_type === 'zoho' && (
                                              <div className="p-2 bg-blue-100 rounded-lg">
                                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xs">Z</div>
                                              </div>
                                            )}
                                            <div>
                                              <h4 className="font-semibold text-gray-900">{connection.connection_name}</h4>
                                              <p className="text-sm text-gray-600 capitalize">{connection.crm_type} CRM</p>
                                            </div>
                                          </div>
                                          <div className={`px-2 py-1 rounded-full text-xs font-medium border ${
                                            connection.connection_status === 'active' 
                                              ? 'bg-green-50 text-green-700 border-green-200' 
                                              : 'bg-red-50 text-red-700 border-red-200'
                                          }`}>
                                            {connection.connection_status}
                                          </div>
                                        </div>
                                        
                                        <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg border">
                                          <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Last Sync:</span>
                                            <span className="font-medium text-gray-900">
                                              {connection.last_sync_at 
                                                ? new Date(connection.last_sync_at).toLocaleDateString()
                                                : 'Never'
                                              }
                                            </span>
                                          </div>
                                          <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Synced Records:</span>
                                            <span className="font-medium text-gray-900">{connection.sync_stats?.total_synced || 0}</span>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button 
                                            size="sm" 
                                            onClick={() => triggerCrmSync(connection.id)}
                                            disabled={isLoading}
                                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                          >
                                            <Zap className="h-3 w-3 mr-1" />
                                            Sync Now
                                          </Button>
                                          <Button 
                                            size="sm" 
                                            variant="outline"
                                            onClick={() => deleteCrmConnection(connection.id)}
                                            className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          {/* Recent Sync Jobs */}
                          {syncJobs.length > 0 && (
                            <Card>
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <CardTitle className="flex items-center gap-2">
                                      📊 Recent Synchronizations
                                    </CardTitle>
                                    <CardDescription>
                                      Monitor CRM synchronization activity and status.
                                    </CardDescription>
                                  </div>
                                  <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowSyncHistory(true)}
                                  >
                                    View All History
                                  </Button>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3">
                                  {syncJobs.slice(0, 5).map((job) => (
                                    <div key={job.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${
                                          job.status === 'completed' ? 'bg-green-500' :
                                          job.status === 'failed' ? 'bg-red-500' :
                                          job.status === 'running' ? 'bg-blue-500 animate-pulse' :
                                          'bg-gray-400'
                                        }`}></div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-900 capitalize">{job.sync_type} Sync</p>
                                          <p className="text-xs text-gray-600">
                                            {job.records_synced} records synced • {job.duration_seconds}s • {job.direction}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-xs text-gray-600">
                                          {job.started_at ? new Date(job.started_at).toLocaleString() : 'Unknown'}
                                        </p>
                                        <p className={`text-xs font-medium capitalize ${
                                          job.status === 'completed' ? 'text-green-600' :
                                          job.status === 'failed' ? 'text-red-600' :
                                          'text-blue-600'
                                        }`}>
                                          {job.status}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {/* Supported CRM Systems */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                🌐 Supported CRM Systems
                              </CardTitle>
                              <CardDescription>
                                Connect with popular CRM platforms using secure OAuth authentication.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Card className="bg-orange-50 border border-orange-200 shadow-sm">
                                  <CardContent className="p-4">
                                    <div className="flex items-center gap-3 mb-3">
                                      <div className="p-2 bg-orange-100 rounded-lg">
                                        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">H</div>
                                      </div>
                                      <div>
                                        <h4 className="font-semibold text-gray-900">HubSpot</h4>
                                        <p className="text-xs text-orange-600 font-medium">Marketing & Sales CRM</p>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-700 mb-3">Popular marketing and sales CRM platform with comprehensive contact management and automation tools.</p>
                                    <div className="flex flex-wrap gap-1">
                                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full border border-orange-200">Contacts</span>
                                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full border border-orange-200">Companies</span>
                                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full border border-orange-200">Deals</span>
                                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full border border-orange-200">Bidirectional Sync</span>
                                    </div>
                                  </CardContent>
                                </Card>

                                <Card className="bg-blue-50 border border-blue-200 shadow-sm">
                                  <CardContent className="p-4">
                                    <div className="flex items-center gap-3 mb-3">
                                      <div className="p-2 bg-blue-100 rounded-lg">
                                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">Z</div>
                                      </div>
                                      <div>
                                        <h4 className="font-semibold text-gray-900">Zoho CRM</h4>
                                        <p className="text-xs text-blue-600 font-medium">Business Management Suite</p>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-700 mb-3">Comprehensive business management suite with powerful CRM capabilities, global reach, and affordable pricing.</p>
                                    <div className="flex flex-wrap gap-1">
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">Contacts</span>
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">Accounts</span>
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">Deals</span>
                                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-200">Bidirectional Sync</span>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>

                              <Card className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 shadow-sm">
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="p-1 bg-blue-100 rounded">
                                      <Zap className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <h4 className="font-semibold text-blue-800">Coming Soon</h4>
                                  </div>
                                  <p className="text-sm text-blue-700 mb-3">Additional CRM integrations planned for future releases:</p>
                                  <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-white text-blue-700 text-sm rounded-full border border-blue-200 shadow-sm">Salesforce</span>
                                    <span className="px-3 py-1 bg-white text-blue-700 text-sm rounded-full border border-blue-200 shadow-sm">Pipedrive</span>
                                    <span className="px-3 py-1 bg-white text-blue-700 text-sm rounded-full border border-blue-200 shadow-sm">Monday.com</span>
                                    <span className="px-3 py-1 bg-white text-blue-700 text-sm rounded-full border border-blue-200 shadow-sm">Custom APIs</span>
                                  </div>
                                </CardContent>
                              </Card>
                            </CardContent>
                          </Card>
                        </div>
                      </TabsContent>

                      {/* Website Integration Configuration Tab with Sub-Tabs */}
                      <TabsContent value="ecommerce-integration">
                        <div className="space-y-6">
                          {/* Enhanced Header */}
                          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                            <CardHeader>
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                                  <Globe className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                  <CardTitle className="text-xl">Website Integration</CardTitle>
                                  <CardDescription className="text-gray-600">
                                    Connect your websites and e-commerce platforms to import data for targeted marketing campaigns
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                          </Card>

                          {/* Sub-Tabs: E-Commerce and Websites */}
                          <Tabs value={activeWebsiteTab} onValueChange={setActiveWebsiteTab}>
                            <TabsList className="grid w-full grid-cols-2 mb-6">
                              <TabsTrigger value="ecommerce" className="flex items-center gap-2">
                                <ShoppingCart className="h-4 w-4" />
                                E-Commerce
                              </TabsTrigger>
                              <TabsTrigger value="websites" className="flex items-center gap-2">
                                <Globe className="h-4 w-4" />
                                Websites
                              </TabsTrigger>
                            </TabsList>

                            {/* E-Commerce Sub-Tab (Existing Functionality) */}
                            <TabsContent value="ecommerce">
                              <div className="space-y-6">
                                {/* E-Commerce Integration Overview Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <Users className="h-8 w-8 text-blue-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Total Customers</p>
                                          <div className="text-2xl font-bold">{ecommerceAnalytics?.overview?.total_customers || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <ShoppingBag className="h-8 w-8 text-green-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Total Orders</p>
                                          <div className="text-2xl font-bold">{ecommerceAnalytics?.overview?.total_orders || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <DollarSign className="h-8 w-8 text-purple-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                                          <div className="text-2xl font-bold">${ecommerceAnalytics?.overview?.total_revenue || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <Package className="h-8 w-8 text-orange-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Total Products</p>
                                          <div className="text-2xl font-bold">{ecommerceAnalytics?.overview?.total_products || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>

                                {/* E-Commerce Connection Status */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                      <ShoppingCart className="h-5 w-5" />
                                      E-commerce Store Connection
                                    </CardTitle>
                                    <CardDescription>
                                      Manage your e-commerce platform connection and sync settings
                                    </CardDescription>
                                  </CardHeader>
                                  <CardContent>
                                    {!currentEcommerceConnection ? (
                                      <div className="text-center py-8">
                                        <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                        {connectionsStatus.has_website_connection ? (
                                          <>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">Website Connection Already Active</h3>
                                            <p className="text-gray-600 mb-4">
                                              You have a {connectionsStatus.active_connection?.connection?.platform_type?.replace('_', ' ')} website connected.
                                              Only one website connection is allowed at a time.
                                            </p>
                                            <p className="text-sm text-blue-600 mb-4">
                                              To connect an e-commerce store, please disconnect your current website from the "Websites" tab first.
                                            </p>
                                            <Button
                                              onClick={() => setActiveWebsiteTab('websites')}
                                              variant="outline"
                                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                            >
                                              <ArrowRight className="h-4 w-4 mr-2" />
                                              Go to Websites Tab
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No E-commerce Connection</h3>
                                            <p className="text-gray-600 mb-4">Connect your e-commerce store to start importing customer data</p>
                                            <Button
                                              onClick={() => setShowEcommerceConnectionDialog(true)}
                                              className="bg-blue-600 hover:bg-blue-700"
                                            >
                                              <Plus className="h-4 w-4 mr-2" />
                                              Connect Your Store
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                                          <div className="flex items-center">
                                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                              <CheckCircle className="h-6 w-6 text-green-600" />
                                            </div>
                                            <div>
                                              <h4 className="font-medium text-green-900">{currentEcommerceConnection.connection_name}</h4>
                                              <p className="text-sm text-green-700">
                                                {currentEcommerceConnection.platform_type.charAt(0).toUpperCase() + currentEcommerceConnection.platform_type.slice(1)} Store
                                              </p>
                                              <p className="text-xs text-green-600">Last sync: {currentEcommerceConnection.last_sync_at ? new Date(currentEcommerceConnection.last_sync_at).toLocaleString() : 'Never'}</p>
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              onClick={() => setShowSwitchPlatformDialog(true)}
                                              variant="outline"
                                              size="sm"
                                              className="border-green-300 text-green-700 hover:bg-green-50"
                                            >
                                              <RefreshCw className="h-4 w-4 mr-2" />
                                              Switch
                                            </Button>
                                            <Button
                                              onClick={() => deleteEcommerceConnection(currentEcommerceConnection.id)}
                                              variant="destructive"
                                              size="sm"
                                            >
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Disconnect Store
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Sync Controls for E-commerce */}
                                        <div className="flex gap-2">
                                          <Button
                                            onClick={() => syncEcommerceData(currentEcommerceConnection.id)}
                                            className="bg-blue-600 hover:bg-blue-700"
                                          >
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                            Sync Now
                                          </Button>
                                          <Button
                                            onClick={() => setShowEcommerceSyncDialog(true)}
                                            variant="outline"
                                          >
                                            <Settings className="h-4 w-4 mr-2" />
                                            Sync Settings
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>

                                {/* Supported E-Commerce Platforms Information */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                      <Info className="h-5 w-5" />
                                      Supported E-commerce Platforms
                                    </CardTitle>
                                    <CardDescription>
                                      Learn about supported e-commerce platforms and their requirements
                                    </CardDescription>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="grid gap-4">
                                      {/* WordPress WooCommerce */}
                                      <div className="p-4 border rounded-lg">
                                        <h4 className="font-medium flex items-center gap-2 mb-2">
                                          <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                            <span className="text-blue-600 font-bold text-sm">W</span>
                                          </div>
                                          WordPress (WooCommerce)
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                          Connect your WooCommerce e-commerce store to import customer data, orders, and products.
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                          <li>• <strong>Required:</strong> WordPress Site URL</li>
                                          <li>• <strong>Required:</strong> WooCommerce Consumer Key & Secret</li>
                                          <li>• <strong>Data:</strong> Customers, Orders, Products, Analytics</li>
                                          <li>• <strong>Features:</strong> Real-time sync, Order tracking, Customer segments</li>
                                        </ul>
                                      </div>

                                      {/* Shopify */}
                                      <div className="p-4 border rounded-lg">
                                        <h4 className="font-medium flex items-center gap-2 mb-2">
                                          <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center">
                                            <span className="text-green-600 font-bold text-sm">S</span>
                                          </div>
                                          Shopify
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                          Connect your Shopify store to import customers, orders, and products automatically.
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                          <li>• <strong>Required:</strong> Store URL (.myshopify.com domain)</li>
                                          <li>• <strong>Required:</strong> Private App Access Token</li>
                                          <li>• <strong>Data:</strong> Customers, Orders, Products, Inventory</li>
                                          <li>• <strong>Features:</strong> Real-time sync, Order tracking, Customer segments</li>
                                        </ul>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            </TabsContent>

                            {/* Websites Sub-Tab (New Phase 4) */}
                            <TabsContent value="websites">
                              <div className="space-y-6">
                                {/* Website Integration Overview Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <Users className="h-8 w-8 text-blue-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Monthly Visitors</p>
                                          <div className="text-2xl font-bold">{websiteAnalytics?.overview?.monthly_visitors || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <BarChart3 className="h-8 w-8 text-green-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Page Views</p>
                                          <div className="text-2xl font-bold">{websiteAnalytics?.overview?.page_views || 0}</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <TrendingUp className="h-8 w-8 text-purple-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">SEO Score</p>
                                          <div className="text-2xl font-bold">{websiteAnalytics?.overview?.seo_score || 0}/100</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>

                                  <Card>
                                    <CardContent className="p-4">
                                      <div className="flex items-center">
                                        <Clock className="h-8 w-8 text-orange-600 mr-3" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-600">Avg. Load Time</p>
                                          <div className="text-2xl font-bold">{websiteAnalytics?.overview?.avg_load_time || 0}s</div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>

                                {/* Website Connection Status */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                      <Globe className="h-5 w-5" />
                                      Website Connection
                                    </CardTitle>
                                    <CardDescription>
                                      Manage your website platform connection for analytics and SEO data
                                    </CardDescription>
                                  </CardHeader>
                                  <CardContent>
                                    {!currentWebsiteConnection ? (
                                      <div className="text-center py-8">
                                        <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                        {connectionsStatus.has_ecommerce_connection ? (
                                          <>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">E-commerce Store Already Connected</h3>
                                            <p className="text-gray-600 mb-4">
                                              You have a {connectionsStatus.active_connection?.connection?.platform_type} store connected.
                                              Only one website connection is allowed at a time.
                                            </p>
                                            <p className="text-sm text-blue-600 mb-4">
                                              To connect a website, please disconnect your current e-commerce store from the "E-Commerce" tab first.
                                            </p>
                                            <Button
                                              onClick={() => setActiveWebsiteTab('ecommerce')}
                                              variant="outline"
                                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                            >
                                              <ArrowRight className="h-4 w-4 mr-2" />
                                              Go to E-Commerce Tab
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Website Connection</h3>
                                            <p className="text-gray-600 mb-4">Connect your website to start importing analytics and SEO data</p>
                                            <Button
                                              onClick={() => setShowWebsiteConnectionDialog(true)}
                                              className="bg-blue-600 hover:bg-blue-700"
                                            >
                                              <Plus className="h-4 w-4 mr-2" />
                                              Connect Your Website
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                                          <div className="flex items-center">
                                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                              <CheckCircle className="h-6 w-6 text-green-600" />
                                            </div>
                                            <div>
                                              <h4 className="font-medium text-green-900">{currentWebsiteConnection.connection_name}</h4>
                                              <p className="text-sm text-green-700">
                                                {currentWebsiteConnection.platform_type.charAt(0).toUpperCase() + currentWebsiteConnection.platform_type.slice(1)} Website
                                              </p>
                                              <p className="text-xs text-green-600">Last sync: {currentWebsiteConnection.last_sync_at ? new Date(currentWebsiteConnection.last_sync_at).toLocaleString() : 'Never'}</p>
                                            </div>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              onClick={() => setShowSwitchWebsitePlatformDialog(true)}
                                              variant="outline"
                                              size="sm"
                                              className="border-green-300 text-green-700 hover:bg-green-50"
                                            >
                                              <RefreshCw className="h-4 w-4 mr-2" />
                                              Switch
                                            </Button>
                                            <Button
                                              onClick={() => deleteWebsiteConnection(currentWebsiteConnection.id)}
                                              variant="destructive"
                                              size="sm"
                                            >
                                              <Trash2 className="h-4 w-4 mr-2" />
                                              Disconnect Website
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Sync Controls for Website */}
                                        <div className="flex gap-2">
                                          <Button
                                            onClick={() => syncWebsiteData(currentWebsiteConnection.id)}
                                            className="bg-blue-600 hover:bg-blue-700"
                                          >
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                            Sync Analytics
                                          </Button>
                                          <Button
                                            onClick={() => setShowWebsiteSyncDialog(true)}
                                            variant="outline"
                                          >
                                            <Settings className="h-4 w-4 mr-2" />
                                            Sync Settings
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>

                                {/* Supported Website Platforms Information */}
                                <Card>
                                  <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                      <Info className="h-5 w-5" />
                                      Supported Website Types
                                    </CardTitle>
                                    <CardDescription>
                                      Connect different types of websites for analytics and SEO monitoring
                                    </CardDescription>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="grid gap-4">
                                      {/* HTML Website */}
                                      <div className="p-4 border rounded-lg">
                                        <h4 className="font-medium flex items-center gap-2 mb-2">
                                          <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center">
                                            <span className="text-orange-600 font-bold text-sm">H</span>
                                          </div>
                                          HTML-Based Website
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                          Connect static HTML websites for SEO monitoring and basic analytics.
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                          <li>• <strong>Required:</strong> Website URL</li>
                                          <li>• <strong>Optional:</strong> Google Analytics API Key</li>
                                          <li>• <strong>Data:</strong> SEO metrics, Page speed, Site health</li>
                                          <li>• <strong>Features:</strong> SEO monitoring, Performance tracking</li>
                                        </ul>
                                      </div>

                                      {/* React Website */}
                                      <div className="p-4 border rounded-lg">
                                        <h4 className="font-medium flex items-center gap-2 mb-2">
                                          <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                                            <span className="text-blue-600 font-bold text-sm">R</span>
                                          </div>
                                          React Website
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                          Connect React-based websites and SPAs for enhanced analytics and performance monitoring.
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                          <li>• <strong>Required:</strong> Website URL</li>
                                          <li>• <strong>Optional:</strong> Google Analytics API Key</li>
                                          <li>• <strong>Data:</strong> User analytics, Performance metrics, Bundle analysis</li>
                                          <li>• <strong>Features:</strong> SPA monitoring, Performance optimization</li>
                                        </ul>
                                      </div>

                                      {/* WordPress Content */}
                                      <div className="p-4 border rounded-lg">
                                        <h4 className="font-medium flex items-center gap-2 mb-2">
                                          <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center">
                                            <span className="text-purple-600 font-bold text-sm">W</span>
                                          </div>
                                          WordPress Content Site
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-2">
                                          Connect WordPress content/blog websites (non-e-commerce) for content analytics.
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                          <li>• <strong>Required:</strong> WordPress Site URL</li>
                                          <li>• <strong>Required:</strong> WordPress Application Password</li>
                                          <li>• <strong>Data:</strong> Posts, Pages, Comments, User engagement</li>
                                          <li>• <strong>Features:</strong> Content analytics, SEO tracking, User engagement</li>
                                        </ul>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              </div>
                            </TabsContent>
                          </Tabs>

                          {/* E-Commerce Connection Dialog */}
                          <Dialog open={showEcommerceConnectionDialog} onOpenChange={setShowEcommerceConnectionDialog}>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>Add E-commerce Store</DialogTitle>
                                <DialogDescription>
                                  Connect your e-commerce platform to start importing customer data
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Platform</Label>
                                  <Select value={ecommerceConnectionForm.platform_type} onValueChange={(value) => setEcommerceConnectionForm(prev => ({ ...prev, platform_type: value }))}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select e-commerce platform" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {ecommercePlatforms.map(platform => (
                                        <SelectItem key={platform.type} value={platform.type}>
                                          {platform.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div>
                                  <Label>Connection Name</Label>
                                  <Input
                                    value={ecommerceConnectionForm.connection_name}
                                    onChange={(e) => setEcommerceConnectionForm(prev => ({ ...prev, connection_name: e.target.value }))}
                                    placeholder="My Store"
                                  />
                                </div>

                                <div>
                                  <Label>Store URL</Label>
                                  <Input
                                    value={ecommerceConnectionForm.store_url}
                                    onChange={(e) => setEcommerceConnectionForm(prev => ({ ...prev, store_url: e.target.value }))}
                                    placeholder="https://your-store.com"
                                  />
                                </div>

                                {/* Dynamic fields based on platform */}
                                {ecommerceConnectionForm.platform_type === 'shopify' && (
                                  <div>
                                    <Label>Access Token</Label>
                                    <Input
                                      type="password"
                                      value={ecommerceConnectionForm.credentials.access_token || ''}
                                      onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                        ...prev, 
                                        credentials: { ...prev.credentials, access_token: e.target.value }
                                      }))}
                                      placeholder="shppa_..."
                                    />
                                  </div>
                                )}

                                {ecommerceConnectionForm.platform_type === 'woocommerce' && (
                                  <>
                                    <div>
                                      <Label>Consumer Key</Label>
                                      <Input
                                        value={ecommerceConnectionForm.credentials.consumer_key || ''}
                                        onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                          ...prev, 
                                          credentials: { ...prev.credentials, consumer_key: e.target.value }
                                        }))}
                                        placeholder="ck_..."
                                      />
                                    </div>
                                    <div>
                                      <Label>Consumer Secret</Label>
                                      <Input
                                        type="password"
                                        value={ecommerceConnectionForm.credentials.consumer_secret || ''}
                                        onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                          ...prev, 
                                          credentials: { ...prev.credentials, consumer_secret: e.target.value }
                                        }))}
                                        placeholder="cs_..."
                                      />
                                    </div>
                                  </>
                                )}

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label>Industry</Label>
                                    <Select value={ecommerceConnectionForm.industry} onValueChange={(value) => setEcommerceConnectionForm(prev => ({ ...prev, industry: value }))}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select industry" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {industries.map(industry => (
                                          <SelectItem key={industry} value={industry}>
                                            {industry}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>Country</Label>
                                    <Select value={ecommerceConnectionForm.target_country} onValueChange={(value) => setEcommerceConnectionForm(prev => ({ ...prev, target_country: value }))}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select country" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {countries.map(country => (
                                          <SelectItem key={country} value={country}>
                                            {country}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>

                                <div className="flex gap-2 pt-4">
                                  <Button
                                    onClick={() => setShowEcommerceConnectionDialog(false)}
                                    variant="outline"
                                    className="flex-1"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={createEcommerceConnection}
                                    className="flex-1"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Connect
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          {/* Switch E-Commerce Platform Dialog */}
                          <Dialog open={showSwitchPlatformDialog} onOpenChange={setShowSwitchPlatformDialog}>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>Switch E-commerce Platform</DialogTitle>
                                <DialogDescription>
                                  Connect to a different e-commerce platform. This will replace your current connection.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>New Platform</Label>
                                  <Select value={ecommerceConnectionForm.platform_type} onValueChange={(value) => setEcommerceConnectionForm(prev => ({ ...prev, platform_type: value }))}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select platform" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {ecommercePlatforms.filter(p => p.type !== currentEcommerceConnection?.platform_type).map(platform => (
                                        <SelectItem key={platform.type} value={platform.type}>
                                          {platform.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div>
                                  <Label>Connection Name</Label>
                                  <Input
                                    value={ecommerceConnectionForm.connection_name}
                                    onChange={(e) => setEcommerceConnectionForm(prev => ({ ...prev, connection_name: e.target.value }))}
                                    placeholder="My New Store"
                                  />
                                </div>

                                <div>
                                  <Label>Store URL</Label>
                                  <Input
                                    value={ecommerceConnectionForm.store_url}
                                    onChange={(e) => setEcommerceConnectionForm(prev => ({ ...prev, store_url: e.target.value }))}
                                    placeholder="https://your-new-store.com"
                                  />
                                </div>

                                {ecommerceConnectionForm.platform_type === 'shopify' && (
                                  <div>
                                    <Label>Access Token</Label>
                                    <input
                                      type="password"
                                      value={ecommerceConnectionForm.credentials.access_token || ''}
                                      onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                        ...prev, 
                                        credentials: { ...prev.credentials, access_token: e.target.value }
                                      }))}
                                      placeholder="shppa_..."
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                )}

                                {ecommerceConnectionForm.platform_type === 'woocommerce' && (
                                  <>
                                    <div>
                                      <Label>Consumer Key</Label>
                                      <input
                                        value={ecommerceConnectionForm.credentials.consumer_key || ''}
                                        onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                          ...prev, 
                                          credentials: { ...prev.credentials, consumer_key: e.target.value }
                                        }))}
                                        placeholder="ck_..."
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                    </div>
                                    <div>
                                      <Label>Consumer Secret</Label>
                                      <input
                                        type="password"
                                        value={ecommerceConnectionForm.credentials.consumer_secret || ''}
                                        onChange={(e) => setEcommerceConnectionForm(prev => ({ 
                                          ...prev, 
                                          credentials: { ...prev.credentials, consumer_secret: e.target.value }
                                        }))}
                                        placeholder="cs_..."
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                    </div>
                                  </>
                                )}

                                <div className="flex gap-2 pt-4">
                                  <Button
                                    onClick={() => setShowSwitchPlatformDialog(false)}
                                    variant="outline"
                                    className="flex-1"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={switchEcommercePlatform}
                                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                                  >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Switch Platform
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>

                        {/* Website Connection Dialog (Phase 4) */}
                        <Dialog open={showWebsiteConnectionDialog} onOpenChange={setShowWebsiteConnectionDialog}>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Add Website Connection</DialogTitle>
                              <DialogDescription>
                                Connect your website for analytics and SEO monitoring
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label>Platform Type</Label>
                                <Select value={websiteConnectionForm.platform_type} onValueChange={(value) => setWebsiteConnectionForm(prev => ({ ...prev, platform_type: value }))}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select website type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="html">HTML-Based Website</SelectItem>
                                    <SelectItem value="react">React Website</SelectItem>
                                    <SelectItem value="wordpress">WordPress Content Site</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <Label>Connection Name</Label>
                                <Input
                                  value={websiteConnectionForm.connection_name}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ ...prev, connection_name: e.target.value }))}
                                  placeholder="My Website"
                                />
                              </div>

                              <div>
                                <Label>Website URL</Label>
                                <Input
                                  value={websiteConnectionForm.website_url}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ ...prev, website_url: e.target.value }))}
                                  placeholder="https://your-website.com"
                                />
                              </div>

                              {websiteConnectionForm.platform_type === 'wordpress' && (
                                <div>
                                  <Label>Application Password</Label>
                                  <Input
                                    type="password"
                                    value={websiteConnectionForm.credentials.app_password || ''}
                                    onChange={(e) => setWebsiteConnectionForm(prev => ({ 
                                      ...prev, 
                                      credentials: { ...prev.credentials, app_password: e.target.value }
                                    }))}
                                    placeholder="WordPress Application Password"
                                  />
                                </div>
                              )}

                              <div>
                                <Label>Google Analytics API Key (Optional)</Label>
                                <Input
                                  type="password"
                                  value={websiteConnectionForm.credentials.ga_api_key || ''}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ 
                                    ...prev, 
                                    credentials: { ...prev.credentials, ga_api_key: e.target.value }
                                  }))}
                                  placeholder="Google Analytics API Key"
                                />
                              </div>

                              <div className="flex gap-2 pt-4">
                                <Button
                                  onClick={() => setShowWebsiteConnectionDialog(false)}
                                  variant="outline"
                                  className="flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={createWebsiteConnection}
                                  className="flex-1"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Connect
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>

                        {/* Switch Website Platform Dialog (Phase 4) */}
                        <Dialog open={showSwitchWebsitePlatformDialog} onOpenChange={setShowSwitchWebsitePlatformDialog}>
                          <DialogContent className="max-w-md">
                            <DialogHeader>
                              <DialogTitle>Switch Website Platform</DialogTitle>
                              <DialogDescription>
                                Connect to a different website type. This will replace your current connection.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label>New Platform Type</Label>
                                <Select value={websiteConnectionForm.platform_type} onValueChange={(value) => setWebsiteConnectionForm(prev => ({ ...prev, platform_type: value }))}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select website type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="html">HTML-Based Website</SelectItem>
                                    <SelectItem value="react">React Website</SelectItem>
                                    <SelectItem value="wordpress">WordPress Content Site</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <Label>Connection Name</Label>
                                <Input
                                  value={websiteConnectionForm.connection_name}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ ...prev, connection_name: e.target.value }))}
                                  placeholder="My New Website"
                                />
                              </div>

                              <div>
                                <Label>Website URL</Label>
                                <Input
                                  value={websiteConnectionForm.website_url}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ ...prev, website_url: e.target.value }))}
                                  placeholder="https://your-new-website.com"
                                />
                              </div>

                              {websiteConnectionForm.platform_type === 'wordpress' && (
                                <div>
                                  <Label>Application Password</Label>
                                  <Input
                                    type="password"
                                    value={websiteConnectionForm.credentials.app_password || ''}
                                    onChange={(e) => setWebsiteConnectionForm(prev => ({ 
                                      ...prev, 
                                      credentials: { ...prev.credentials, app_password: e.target.value }
                                    }))}
                                    placeholder="WordPress Application Password"
                                  />
                                </div>
                              )}

                              <div>
                                <Label>Google Analytics API Key (Optional)</Label>
                                <Input
                                  type="password"
                                  value={websiteConnectionForm.credentials.ga_api_key || ''}
                                  onChange={(e) => setWebsiteConnectionForm(prev => ({ 
                                    ...prev, 
                                    credentials: { ...prev.credentials, ga_api_key: e.target.value }
                                  }))}
                                  placeholder="Google Analytics API Key"
                                />
                              </div>

                              <div className="flex gap-2 pt-4">
                                <Button
                                  onClick={() => setShowSwitchWebsitePlatformDialog(false)}
                                  variant="outline"
                                  className="flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => {
                                    // Delete current and create new
                                    if (currentWebsiteConnection) {
                                      deleteWebsiteConnection(currentWebsiteConnection.id).then(() => {
                                        createWebsiteConnection();
                                      });
                                    } else {
                                      createWebsiteConnection();
                                    }
                                    setShowSwitchWebsitePlatformDialog(false);
                                  }}
                                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Switch Platform
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </TabsContent>

                      {/* API Integrations Tab */}
                      <TabsContent value="api-integrations">
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Zap className="h-5 w-5 text-blue-600" />
                              Integrations
                            </CardTitle>
                            <CardDescription>
                              Configure third-party service integrations for enhanced functionality
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <Tabs value={activeIntegrationsTab} onValueChange={setActiveIntegrationsTab}>
                              <TabsList className="grid w-full grid-cols-6 mb-6">
                                <TabsTrigger value="smtp" className="flex items-center gap-2">
                                  <Mail className="h-4 w-4" />
                                  SMTP
                                </TabsTrigger>
                                <TabsTrigger value="sms" className="flex items-center gap-2">
                                  <MessageCircle className="h-4 w-4" />
                                  SMS
                                </TabsTrigger>
                                <TabsTrigger value="whatsapp" className="flex items-center gap-2">
                                  <MessageCircle className="h-4 w-4" />
                                  WhatsApp
                                </TabsTrigger>
                                <TabsTrigger value="seo-analytics" className="flex items-center gap-2">
                                  <BarChart3 className="h-4 w-4" />
                                  SEO / Analytics
                                </TabsTrigger>
                                <TabsTrigger value="advertisements" className="flex items-center gap-2">
                                  <TrendingUp className="h-4 w-4" />
                                  Advertisements
                                </TabsTrigger>
                                <TabsTrigger value="others" className="flex items-center gap-2">
                                  <Settings className="h-4 w-4" />
                                  Others
                                </TabsTrigger>
                              </TabsList>

                              {/* SMTP Tab */}
                              <TabsContent value="smtp" className="space-y-6">
                                <div className="border rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900">SMTP Configuration</h3>
                                      <p className="text-sm text-gray-600">Configure email delivery settings for notifications and campaigns</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${
                                        integrationSettings?.integrations?.smtp?.configured 
                                          ? 'bg-green-500' 
                                          : 'bg-gray-400'
                                      }`}></div>
                                      <span className="text-sm text-gray-500">
                                        {integrationSettings?.integrations?.smtp?.configured ? 'Configured' : 'Not Configured'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <Label htmlFor="smtp-host">SMTP Host</Label>
                                      <Input
                                        id="smtp-host"
                                        placeholder="smtp.gmail.com"
                                        className="mt-1"
                                        value={integrationFormData.smtp.host}
                                        onChange={(e) => handleIntegrationFormChange('smtp', 'host', e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <Label htmlFor="smtp-port">Port</Label>
                                      <Input
                                        id="smtp-port"
                                        type="number"
                                        placeholder="587"
                                        className="mt-1"
                                        value={integrationFormData.smtp.port}
                                        onChange={(e) => handleIntegrationFormChange('smtp', 'port', parseInt(e.target.value) || 587)}
                                      />
                                    </div>
                                    <div>
                                      <Label htmlFor="smtp-username">Username</Label>
                                      <Input
                                        id="smtp-username"
                                        placeholder="your-email@gmail.com"
                                        className="mt-1"
                                        value={integrationFormData.smtp.username}
                                        onChange={(e) => handleIntegrationFormChange('smtp', 'username', e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <Label htmlFor="smtp-password">Password</Label>
                                      <Input
                                        id="smtp-password"
                                        type="password"
                                        placeholder="App Password"
                                        className="mt-1"
                                        value={integrationFormData.smtp.password}
                                        onChange={(e) => handleIntegrationFormChange('smtp', 'password', e.target.value)}
                                      />
                                    </div>
                                    <div className="md:col-span-2">
                                      <Label htmlFor="from-email">From Email</Label>
                                      <Input
                                        id="from-email"
                                        placeholder="noreply@yourdomain.com"
                                        className="mt-1"
                                        value={integrationFormData.smtp.from_email}
                                        onChange={(e) => handleIntegrationFormChange('smtp', 'from_email', e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  
                                  {integrationTestResults.smtp && (
                                    <div className={`mt-4 p-3 rounded-lg ${
                                      integrationTestResults.smtp.success 
                                        ? 'bg-green-50 border border-green-200' 
                                        : 'bg-red-50 border border-red-200'
                                    }`}>
                                      <div className={`text-sm font-medium ${
                                        integrationTestResults.smtp.success ? 'text-green-800' : 'text-red-800'
                                      }`}>
                                        {integrationTestResults.smtp.message}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="flex gap-2 mt-4">
                                    <Button 
                                      size="sm" 
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => handleSaveIntegrationConfig('smtp')}
                                      disabled={isLoading || !integrationFormData.smtp.host || !integrationFormData.smtp.username}
                                    >
                                      {isLoading ? 'Saving...' : 'Save Configuration'}
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleTestIntegrationConnection('smtp')}
                                      disabled={isLoading || !integrationFormData.smtp.host || !integrationFormData.smtp.username}
                                    >
                                      {isLoading ? 'Testing...' : 'Test Connection'}
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="border rounded-lg p-4 bg-blue-50">
                                  <h4 className="font-medium text-blue-900 mb-2">Popular SMTP Providers</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div className="bg-white p-3 rounded border">
                                      <div className="font-medium">Gmail</div>
                                      <div className="text-sm text-gray-600">smtp.gmail.com:587</div>
                                    </div>
                                    <div className="bg-white p-3 rounded border">
                                      <div className="font-medium">SendGrid</div>
                                      <div className="text-sm text-gray-600">smtp.sendgrid.net:587</div>
                                    </div>
                                    <div className="bg-white p-3 rounded border">
                                      <div className="font-medium">Mailgun</div>
                                      <div className="text-sm text-gray-600">smtp.mailgun.org:587</div>
                                    </div>
                                  </div>
                                </div>
                              </TabsContent>

                              {/* SMS Tab */}
                              <TabsContent value="sms" className="space-y-6">
                                <div className="border rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900">SMS Configuration</h3>
                                      <p className="text-sm text-gray-600">Configure SMS delivery for notifications and marketing campaigns</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                      <span className="text-sm text-gray-500">Not Configured</span>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-4">
                                    <div>
                                      <Label htmlFor="sms-provider">SMS Provider</Label>
                                      <select className="w-full p-2 border border-gray-300 rounded-lg mt-1">
                                        <option value="">Select SMS Provider</option>
                                        <option value="twilio">Twilio</option>
                                        <option value="aws-sns">AWS SNS</option>
                                        <option value="nexmo">Vonage (Nexmo)</option>
                                        <option value="textlocal">TextLocal</option>
                                      </select>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <Label htmlFor="sms-api-key">API Key</Label>
                                        <Input
                                          id="sms-api-key"
                                          type="password"
                                          placeholder="Your SMS API Key"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="sms-api-secret">API Secret</Label>
                                        <Input
                                          id="sms-api-secret"
                                          type="password"
                                          placeholder="Your SMS API Secret"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="sms-sender-id">Sender ID</Label>
                                        <Input
                                          id="sms-sender-id"
                                          placeholder="OMNIMARKET"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="sms-webhook">Webhook URL</Label>
                                        <Input
                                          id="sms-webhook"
                                          placeholder="https://yourdomain.com/sms/webhook"
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2 mt-4">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                      Save Configuration
                                    </Button>
                                    <Button size="sm" variant="outline">
                                      Send Test SMS
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="border rounded-lg p-4 bg-green-50">
                                  <h4 className="font-medium text-green-900 mb-2">SMS Features</h4>
                                  <ul className="text-sm text-green-700 space-y-1">
                                    <li>• Bulk SMS campaigns</li>
                                    <li>• Automated notifications</li>
                                    <li>• Delivery tracking</li>
                                    <li>• Two-way SMS support</li>
                                    <li>• Global number support</li>
                                  </ul>
                                </div>
                              </TabsContent>

                              {/* WhatsApp Tab */}
                              <TabsContent value="whatsapp" className="space-y-6">
                                <div className="border rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900">WhatsApp Business API</h3>
                                      <p className="text-sm text-gray-600">Connect with customers through WhatsApp Business messaging</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                      <span className="text-sm text-gray-500">Not Configured</span>
                                    </div>
                                  </div>
                                  
                                  <div className="space-y-4">
                                    <div>
                                      <Label htmlFor="whatsapp-provider">WhatsApp Provider</Label>
                                      <select className="w-full p-2 border border-gray-300 rounded-lg mt-1">
                                        <option value="">Select WhatsApp Provider</option>
                                        <option value="meta">Meta (Official)</option>
                                        <option value="twilio">Twilio WhatsApp</option>
                                        <option value="360dialog">360Dialog</option>
                                        <option value="chat-api">Chat API</option>
                                      </select>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <Label htmlFor="whatsapp-phone">WhatsApp Number</Label>
                                        <Input
                                          id="whatsapp-phone"
                                          placeholder="+1234567890"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="whatsapp-token">Access Token</Label>
                                        <Input
                                          id="whatsapp-token"
                                          type="password"
                                          placeholder="Your WhatsApp API Token"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="whatsapp-app-id">App ID</Label>
                                        <Input
                                          id="whatsapp-app-id"
                                          placeholder="Your WhatsApp App ID"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor="whatsapp-webhook">Webhook URL</Label>
                                        <Input
                                          id="whatsapp-webhook"
                                          placeholder="https://yourdomain.com/whatsapp/webhook"
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex gap-2 mt-4">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                      Save Configuration
                                    </Button>
                                    <Button size="sm" variant="outline">
                                      Verify Number
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="border rounded-lg p-4 bg-green-50">
                                  <h4 className="font-medium text-green-900 mb-2">WhatsApp Capabilities</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                                    <div>
                                      <div className="font-medium mb-2">Messaging</div>
                                      <ul className="space-y-1">
                                        <li>• Template messages</li>
                                        <li>• Media messages</li>
                                        <li>• Interactive buttons</li>
                                        <li>• List messages</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-2">Business Tools</div>
                                      <ul className="space-y-1">
                                        <li>• Customer support</li>
                                        <li>• Order notifications</li>
                                        <li>• Catalog integration</li>
                                        <li>• Payment support</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </TabsContent>

                              {/* SEO / Analytics Tab */}
                              <TabsContent value="seo-analytics" className="space-y-6">
                                <div className="border rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900">SEO & Analytics Integration</h3>
                                      <p className="text-sm text-gray-600">Connect with SEO tools and analytics platforms for enhanced insights</p>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Google Analytics */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <BarChart3 className="h-5 w-5 text-orange-600" />
                                          <span className="font-medium">Google Analytics</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Website traffic and user behavior insights</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="ga-property-id" className="text-sm">Property ID</Label>
                                          <Input
                                            id="ga-property-id"
                                            placeholder="GA4-XXXXXXXXX-X"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Google Analytics
                                      </Button>
                                    </div>

                                    {/* Google Search Console */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Search className="h-5 w-5 text-blue-600" />
                                          <span className="font-medium">Search Console</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Search performance and indexing data</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="gsc-site-url" className="text-sm">Site URL</Label>
                                          <Input
                                            id="gsc-site-url"
                                            placeholder="https://yourdomain.com"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Search Console
                                      </Button>
                                    </div>

                                    {/* SEMrush */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <TrendingUp className="h-5 w-5 text-purple-600" />
                                          <span className="font-medium">SEMrush</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Competitive research and keyword data</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="semrush-api-key" className="text-sm">API Key</Label>
                                          <Input
                                            id="semrush-api-key"
                                            type="password"
                                            placeholder="Your SEMrush API Key"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect SEMrush
                                      </Button>
                                    </div>

                                    {/* Ahrefs */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Globe className="h-5 w-5 text-green-600" />
                                          <span className="font-medium">Ahrefs</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Backlink analysis and SEO metrics</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="ahrefs-api-key" className="text-sm">API Token</Label>
                                          <Input
                                            id="ahrefs-api-key"
                                            type="password"
                                            placeholder="Your Ahrefs API Token"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Ahrefs
                                      </Button>
                                    </div>

                                    {/* Microsoft Clarity */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <BarChart3 className="h-5 w-5 text-blue-500" />
                                          <span className="font-medium">Microsoft Clarity</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Free heatmaps and session recordings</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="clarity-project-id" className="text-sm">Project ID</Label>
                                          <Input
                                            id="clarity-project-id"
                                            placeholder="Your Clarity Project ID"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.microsoft_clarity?.project_id || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'microsoft_clarity', e.target.value, 'project_id')}
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="clarity-website-url" className="text-sm">Website URL</Label>
                                          <Input
                                            id="clarity-website-url"
                                            placeholder="https://yourdomain.com"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.microsoft_clarity?.website_url || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'microsoft_clarity', e.target.value, 'website_url')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Clarity
                                      </Button>
                                    </div>

                                    {/* Google Tag Manager */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Globe className="h-5 w-5 text-yellow-600" />
                                          <span className="font-medium">Google Tag Manager</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Manage tracking codes and analytics tags</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="gtm-container-id" className="text-sm">Container ID</Label>
                                          <Input
                                            id="gtm-container-id"
                                            placeholder="GTM-XXXXXXX"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.google_tag_manager?.container_id || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'google_tag_manager', e.target.value, 'container_id')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect GTM
                                      </Button>
                                    </div>

                                    {/* Hotjar */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <TrendingUp className="h-5 w-5 text-red-600" />
                                          <span className="font-medium">Hotjar</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Heatmaps, recordings, and user feedback</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="hotjar-site-id" className="text-sm">Site ID</Label>
                                          <Input
                                            id="hotjar-site-id"
                                            placeholder="1234567"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.hotjar?.site_id || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'hotjar', e.target.value, 'site_id')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Hotjar
                                      </Button>
                                    </div>

                                    {/* Mixpanel */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <BarChart3 className="h-5 w-5 text-purple-500" />
                                          <span className="font-medium">Mixpanel</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Event tracking and user analytics</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="mixpanel-project-token" className="text-sm">Project Token</Label>
                                          <Input
                                            id="mixpanel-project-token"
                                            type="password"
                                            placeholder="Your Mixpanel Project Token"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.mixpanel?.project_token || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'mixpanel', e.target.value, 'project_token')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Mixpanel
                                      </Button>
                                    </div>

                                    {/* Facebook Pixel */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Users className="h-5 w-5 text-blue-700" />
                                          <span className="font-medium">Facebook Pixel</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Facebook/Meta conversion tracking</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="facebook-pixel-id" className="text-sm">Pixel ID</Label>
                                          <Input
                                            id="facebook-pixel-id"
                                            placeholder="1234567890123456"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.facebook_pixel?.pixel_id || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'facebook_pixel', e.target.value, 'pixel_id')}
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="facebook-pixel-token" className="text-sm">Access Token (Optional)</Label>
                                          <Input
                                            id="facebook-pixel-token"
                                            type="password"
                                            placeholder="For API access"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.facebook_pixel?.access_token || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'facebook_pixel', e.target.value, 'access_token')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Facebook Pixel
                                      </Button>
                                    </div>

                                    {/* Moz */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <TrendingUp className="h-5 w-5 text-orange-500" />
                                          <span className="font-medium">Moz</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">SEO metrics and domain authority</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="moz-access-id" className="text-sm">Access ID</Label>
                                          <Input
                                            id="moz-access-id"
                                            placeholder="Your Moz Access ID"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.moz?.access_id || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'moz', e.target.value, 'access_id')}
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="moz-secret-key" className="text-sm">Secret Key</Label>
                                          <Input
                                            id="moz-secret-key"
                                            type="password"
                                            placeholder="Your Moz Secret Key"
                                            size="sm"
                                            className="mt-1"
                                            value={integrationFormData.seo_analytics.moz?.secret_key || ''}
                                            onChange={(e) => handleIntegrationFormChange('seo_analytics', 'moz', e.target.value, 'secret_key')}
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Moz
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Second Row - Advanced Analytics */}
                                  <div className="mt-6">
                                    <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">Advanced Analytics & Behavior Tracking</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      
                                      {/* Matomo */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <BarChart3 className="h-5 w-5 text-orange-600" />
                                            <span className="font-medium">Matomo</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Privacy-focused analytics alternative</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="matomo-site-id" className="text-sm">Site ID</Label>
                                            <Input
                                              id="matomo-site-id"
                                              placeholder="1"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.matomo?.site_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'matomo', e.target.value, 'site_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="matomo-base-url" className="text-sm">Base URL</Label>
                                            <Input
                                              id="matomo-base-url"
                                              placeholder="https://analytics.yourdomain.com"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.matomo?.base_url || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'matomo', e.target.value, 'base_url')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="matomo-auth-token" className="text-sm">Auth Token (Optional)</Label>
                                            <Input
                                              id="matomo-auth-token"
                                              type="password"
                                              placeholder="API Auth Token"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.matomo?.auth_token || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'matomo', e.target.value, 'auth_token')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Matomo
                                        </Button>
                                      </div>

                                      {/* Adobe Analytics */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-red-600" />
                                            <span className="font-medium">Adobe Analytics</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Enterprise analytics platform</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="adobe-report-suite" className="text-sm">Report Suite ID</Label>
                                            <Input
                                              id="adobe-report-suite"
                                              placeholder="your-report-suite-id"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.adobe_analytics?.report_suite_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'adobe_analytics', e.target.value, 'report_suite_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="adobe-client-id" className="text-sm">Client ID</Label>
                                            <Input
                                              id="adobe-client-id"
                                              placeholder="Adobe Client ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.adobe_analytics?.client_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'adobe_analytics', e.target.value, 'client_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="adobe-client-secret" className="text-sm">Client Secret</Label>
                                            <Input
                                              id="adobe-client-secret"
                                              type="password"
                                              placeholder="Adobe Client Secret"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.adobe_analytics?.client_secret || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'adobe_analytics', e.target.value, 'client_secret')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="adobe-org-id" className="text-sm">Organization ID</Label>
                                            <Input
                                              id="adobe-org-id"
                                              placeholder="Adobe Org ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.adobe_analytics?.org_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'adobe_analytics', e.target.value, 'org_id')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Adobe Analytics
                                        </Button>
                                      </div>

                                      {/* Heap */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <BarChart3 className="h-5 w-5 text-indigo-600" />
                                            <span className="font-medium">Heap</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Automatic event tracking</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="heap-app-id" className="text-sm">App ID</Label>
                                            <Input
                                              id="heap-app-id"
                                              placeholder="12345678"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.heap?.app_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'heap', e.target.value, 'app_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="heap-api-key" className="text-sm">API Key (Optional)</Label>
                                            <Input
                                              id="heap-api-key"
                                              type="password"
                                              placeholder="For data access"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.heap?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'heap', e.target.value, 'api_key')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Heap
                                        </Button>
                                      </div>

                                      {/* Crazy Egg */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-yellow-600" />
                                            <span className="font-medium">Crazy Egg</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Heatmaps and A/B testing</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="crazy-egg-account-id" className="text-sm">Account ID</Label>
                                            <Input
                                              id="crazy-egg-account-id"
                                              placeholder="1234567"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.crazy_egg?.account_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'crazy_egg', e.target.value, 'account_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="crazy-egg-api-key" className="text-sm">API Key (Optional)</Label>
                                            <Input
                                              id="crazy-egg-api-key"
                                              type="password"
                                              placeholder="For API access"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.crazy_egg?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'crazy_egg', e.target.value, 'api_key')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Crazy Egg
                                        </Button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Third Row - SEO Tools */}
                                  <div className="mt-6">
                                    <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">SEO & Technical Analysis Tools</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      
                                      {/* Screaming Frog */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Search className="h-5 w-5 text-green-700" />
                                            <span className="font-medium">Screaming Frog</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Technical SEO auditing</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="screaming-frog-api-key" className="text-sm">API Key</Label>
                                            <Input
                                              id="screaming-frog-api-key"
                                              type="password"
                                              placeholder="Your Screaming Frog API Key"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.screaming_frog?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'screaming_frog', e.target.value, 'api_key')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Screaming Frog
                                        </Button>
                                      </div>

                                      {/* Majestic SEO */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Globe className="h-5 w-5 text-purple-700" />
                                            <span className="font-medium">Majestic SEO</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Advanced backlink analysis</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="majestic-access-id" className="text-sm">Access ID</Label>
                                            <Input
                                              id="majestic-access-id"
                                              placeholder="Your Majestic Access ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.majestic_seo?.access_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'majestic_seo', e.target.value, 'access_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="majestic-secret-key" className="text-sm">Secret Key</Label>
                                            <Input
                                              id="majestic-secret-key"
                                              type="password"
                                              placeholder="Your Majestic Secret Key"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.majestic_seo?.secret_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'majestic_seo', e.target.value, 'secret_key')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="majestic-environment" className="text-sm">Environment</Label>
                                            <select 
                                              id="majestic-environment"
                                              className="w-full p-2 border border-gray-300 rounded-lg mt-1 text-sm"
                                              value={integrationFormData.seo_analytics.majestic_seo?.environment || 'sandbox'}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'majestic_seo', e.target.value, 'environment')}
                                            >
                                              <option value="sandbox">Sandbox (Testing)</option>
                                              <option value="production">Production</option>
                                            </select>
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Majestic SEO
                                        </Button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Fourth Row - Social Media Analytics */}
                                  <div className="mt-6">
                                    <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">Social Media Analytics</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                      
                                      {/* Meta Analytics */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Users className="h-5 w-5 text-blue-600" />
                                            <span className="font-medium">Meta Analytics</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Facebook & Instagram insights</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="meta-app-id" className="text-sm">App ID</Label>
                                            <Input
                                              id="meta-app-id"
                                              placeholder="Your Meta App ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.meta_analytics?.app_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'meta_analytics', e.target.value, 'app_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="meta-app-secret" className="text-sm">App Secret</Label>
                                            <Input
                                              id="meta-app-secret"
                                              type="password"
                                              placeholder="Your Meta App Secret"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.meta_analytics?.app_secret || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'meta_analytics', e.target.value, 'app_secret')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="meta-access-token" className="text-sm">Access Token</Label>
                                            <Input
                                              id="meta-access-token"
                                              type="password"
                                              placeholder="Your Meta Access Token"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.meta_analytics?.access_token || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'meta_analytics', e.target.value, 'access_token')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="meta-instagram-account" className="text-sm">Instagram Business ID (Optional)</Label>
                                            <Input
                                              id="meta-instagram-account"
                                              placeholder="Instagram Business Account ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.meta_analytics?.instagram_business_account_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'meta_analytics', e.target.value, 'instagram_business_account_id')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Meta Analytics
                                        </Button>
                                      </div>

                                      {/* Twitter/X Analytics */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <MessageCircle className="h-5 w-5 text-black" />
                                            <span className="font-medium">Twitter/X Analytics</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Twitter/X social insights</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="twitter-api-key" className="text-sm">API Key</Label>
                                            <Input
                                              id="twitter-api-key"
                                              type="password"
                                              placeholder="Your Twitter API Key"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.twitter_analytics?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'twitter_analytics', e.target.value, 'api_key')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="twitter-api-secret" className="text-sm">API Secret</Label>
                                            <Input
                                              id="twitter-api-secret"
                                              type="password"
                                              placeholder="Your Twitter API Secret"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.twitter_analytics?.api_secret || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'twitter_analytics', e.target.value, 'api_secret')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="twitter-access-token" className="text-sm">Access Token</Label>
                                            <Input
                                              id="twitter-access-token"
                                              type="password"
                                              placeholder="Your Twitter Access Token"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.twitter_analytics?.access_token || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'twitter_analytics', e.target.value, 'access_token')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="twitter-access-token-secret" className="text-sm">Access Token Secret</Label>
                                            <Input
                                              id="twitter-access-token-secret"
                                              type="password"
                                              placeholder="Your Access Token Secret"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.twitter_analytics?.access_token_secret || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'twitter_analytics', e.target.value, 'access_token_secret')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect Twitter/X
                                        </Button>
                                      </div>

                                      {/* LinkedIn Analytics */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Users className="h-5 w-5 text-blue-800" />
                                            <span className="font-medium">LinkedIn Analytics</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">LinkedIn B2B insights</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="linkedin-client-id" className="text-sm">Client ID</Label>
                                            <Input
                                              id="linkedin-client-id"
                                              placeholder="Your LinkedIn Client ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.linkedin_analytics?.client_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'linkedin_analytics', e.target.value, 'client_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="linkedin-client-secret" className="text-sm">Client Secret</Label>
                                            <Input
                                              id="linkedin-client-secret"
                                              type="password"
                                              placeholder="Your LinkedIn Client Secret"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.linkedin_analytics?.client_secret || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'linkedin_analytics', e.target.value, 'client_secret')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="linkedin-access-token" className="text-sm">Access Token (Optional)</Label>
                                            <Input
                                              id="linkedin-access-token"
                                              type="password"
                                              placeholder="LinkedIn Access Token"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.linkedin_analytics?.access_token || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'linkedin_analytics', e.target.value, 'access_token')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect LinkedIn
                                        </Button>
                                      </div>

                                      {/* TikTok Pixel */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-pink-600" />
                                            <span className="font-medium">TikTok Pixel</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">TikTok conversion tracking</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="tiktok-pixel-id" className="text-sm">Pixel ID</Label>
                                            <Input
                                              id="tiktok-pixel-id"
                                              placeholder="Your TikTok Pixel ID"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.tiktok_pixel?.pixel_id || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'tiktok_pixel', e.target.value, 'pixel_id')}
                                            />
                                          </div>
                                          <div>
                                            <Label htmlFor="tiktok-access-token" className="text-sm">Access Token (Optional)</Label>
                                            <Input
                                              id="tiktok-access-token"
                                              type="password"
                                              placeholder="For Business API access"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.tiktok_pixel?.access_token || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'tiktok_pixel', e.target.value, 'access_token')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect TikTok Pixel
                                        </Button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Fifth Row - Google APIs */}
                                  <div className="mt-6">
                                    <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b">Google APIs & Tools</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      
                                      {/* PageSpeed Insights */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Zap className="h-5 w-5 text-yellow-600" />
                                            <span className="font-medium">PageSpeed Insights</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Site speed and Core Web Vitals analysis</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="pagespeed-api-key" className="text-sm">API Key</Label>
                                            <Input
                                              id="pagespeed-api-key"
                                              type="password"
                                              placeholder="Your PageSpeed Insights API Key"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.pagespeed_insights?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'pagespeed_insights', e.target.value, 'api_key')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect PageSpeed Insights
                                        </Button>
                                      </div>

                                      {/* YouTube Analytics */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <Video className="h-5 w-5 text-red-600" />
                                            <span className="font-medium">YouTube Analytics</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <span className="text-xs text-gray-500">Not Connected</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Video content performance analytics</p>
                                        <div className="space-y-2">
                                          <div>
                                            <Label htmlFor="youtube-api-key" className="text-sm">API Key</Label>
                                            <Input
                                              id="youtube-api-key"
                                              type="password"
                                              placeholder="Your YouTube Data API Key"
                                              size="sm"
                                              className="mt-1"
                                              value={integrationFormData.seo_analytics.youtube_analytics?.api_key || ''}
                                              onChange={(e) => handleIntegrationFormChange('seo_analytics', 'youtube_analytics', e.target.value, 'api_key')}
                                            />
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline">
                                          Connect YouTube Analytics
                                        </Button>
                                      </div>

                                      {/* Google Trends */}
                                      <div className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-green-600" />
                                            <span className="font-medium">Google Trends</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-xs text-green-600">Ready</span>
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-3">Keyword trend analysis (No API key required)</p>
                                        <div className="space-y-2">
                                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center gap-2">
                                              <CheckCircle className="h-4 w-4 text-green-600" />
                                              <span className="text-sm text-green-800 font-medium">Ready to Use</span>
                                            </div>
                                            <p className="text-xs text-green-700 mt-1">Google Trends is available through pytrends library without requiring API keys.</p>
                                          </div>
                                        </div>
                                        <Button size="sm" className="w-full mt-3" variant="outline" className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300">
                                          <CheckCircle className="h-4 w-4 mr-2" />
                                          Google Trends Ready
                                        </Button>
                                      </div>

                                    </div>
                                  </div>

                                  {/* Save and Test buttons for SEO Analytics */}
                                  <div className="flex gap-2 mt-6 pt-6 border-t">
                                    <Button 
                                      size="sm" 
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => handleSaveIntegrationConfig('seo_analytics')}
                                      disabled={isLoading}
                                    >
                                      {isLoading ? 'Saving...' : 'Save All SEO/Analytics Configurations'}
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleTestIntegrationConnection('seo_analytics')}
                                      disabled={isLoading}
                                    >
                                      {isLoading ? 'Testing...' : 'Test All Connections'}
                                    </Button>
                                  </div>
                                </div>
                              </TabsContent>

                              {/* Advertisements Tab */}
                              <TabsContent value="advertisements" className="space-y-6">
                                <div className="border rounded-lg p-6">
                                  <div className="flex items-center justify-between mb-4">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900">Advertisement Platforms</h3>
                                      <p className="text-sm text-gray-600">Connect with advertising platforms for campaign management and analytics</p>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Google Ads */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Search className="h-5 w-5 text-blue-600" />
                                          <span className="font-medium">Google Ads</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Search and display advertising campaigns</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="google-ads-customer-id" className="text-sm">Customer ID</Label>
                                          <Input
                                            id="google-ads-customer-id"
                                            placeholder="123-456-7890"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="google-ads-developer-token" className="text-sm">Developer Token</Label>
                                          <Input
                                            id="google-ads-developer-token"
                                            type="password"
                                            placeholder="Your Developer Token"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Google Ads
                                      </Button>
                                    </div>

                                    {/* Facebook Ads */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Users className="h-5 w-5 text-blue-700" />
                                          <span className="font-medium">Facebook Ads</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Social media advertising and audience targeting</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="facebook-app-id" className="text-sm">App ID</Label>
                                          <Input
                                            id="facebook-app-id"
                                            placeholder="Your Facebook App ID"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="facebook-access-token" className="text-sm">Access Token</Label>
                                          <Input
                                            id="facebook-access-token"
                                            type="password"
                                            placeholder="Your Access Token"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Facebook Ads
                                      </Button>
                                    </div>

                                    {/* LinkedIn Ads */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Users className="h-5 w-5 text-blue-800" />
                                          <span className="font-medium">LinkedIn Ads</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Professional network advertising</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="linkedin-client-id" className="text-sm">Client ID</Label>
                                          <Input
                                            id="linkedin-client-id"
                                            placeholder="Your LinkedIn Client ID"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="linkedin-client-secret" className="text-sm">Client Secret</Label>
                                          <Input
                                            id="linkedin-client-secret"
                                            type="password"
                                            placeholder="Your Client Secret"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect LinkedIn Ads
                                      </Button>
                                    </div>

                                    {/* Microsoft Ads (Bing) */}
                                    <div className="border rounded-lg p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <Search className="h-5 w-5 text-orange-600" />
                                          <span className="font-medium">Microsoft Ads</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                          <span className="text-xs text-gray-500">Not Connected</span>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-600 mb-3">Bing search advertising campaigns</p>
                                      <div className="space-y-2">
                                        <div>
                                          <Label htmlFor="bing-customer-id" className="text-sm">Customer ID</Label>
                                          <Input
                                            id="bing-customer-id"
                                            placeholder="Your Customer ID"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="bing-developer-token" className="text-sm">Developer Token</Label>
                                          <Input
                                            id="bing-developer-token"
                                            type="password"
                                            placeholder="Your Developer Token"
                                            size="sm"
                                            className="mt-1"
                                          />
                                        </div>
                                      </div>
                                      <Button size="sm" className="w-full mt-3" variant="outline">
                                        Connect Microsoft Ads
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                                <div className="border rounded-lg p-4 bg-orange-50">
                                  <h4 className="font-medium text-orange-900 mb-2">Advertisement Features</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-orange-700">
                                    <div>
                                      <div className="font-medium mb-1">Campaign Management</div>
                                      <ul className="space-y-1">
                                        <li>• Create & optimize campaigns</li>
                                        <li>• Keyword bidding strategies</li>
                                        <li>• Ad copy optimization</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-1">Analytics & Reporting</div>
                                      <ul className="space-y-1">
                                        <li>• Performance metrics</li>
                                        <li>• ROI tracking</li>
                                        <li>• Conversion analysis</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-1">Automation</div>
                                      <ul className="space-y-1">
                                        <li>• Auto-bidding rules</li>
                                        <li>• Budget optimization</li>
                                        <li>• Performance alerts</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </TabsContent>

                              {/* Others Tab - Social Enrichment & URL Shortener */}
                              <TabsContent value="others" className="space-y-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                  
                                  {/* Social Profile Enrichment */}
                                  <div className="border rounded-lg p-6">
                                    <div className="flex items-center justify-between mb-4">
                                      <div>
                                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                          <Users className="h-5 w-5 text-blue-600" />
                                          Social Profile Enrichment
                                        </h3>
                                        <p className="text-sm text-gray-600">Apollo.io integration for automatic social profile discovery</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${
                                          enrichmentSettings.api_key_configured 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-400'
                                        }`}></div>
                                        <span className="text-sm text-gray-500">
                                          {enrichmentSettings.api_key_configured ? 'Configured' : 'Not Configured'}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="space-y-4">
                                      <div>
                                        <Label htmlFor="apollo-api-key-others">Apollo.io API Key</Label>
                                        <Input
                                          id="apollo-api-key-others"
                                          type="password"
                                          value={enrichmentSettings.apollo_api_key}
                                          onChange={(e) => setEnrichmentSettings({
                                            ...enrichmentSettings,
                                            apollo_api_key: e.target.value
                                          })}
                                          placeholder="Enter your Apollo.io API key"
                                          className="mt-1"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                          {enrichmentSettings.api_key_configured ? 
                                            '✅ API key configured' : 
                                            '⚠️ API key not configured'}
                                        </p>
                                      </div>

                                      <div className="space-y-2">
                                        <div className="flex items-center space-x-2">
                                          <input
                                            type="checkbox"
                                            id="social-enrichment-enabled-others"
                                            checked={enrichmentSettings.social_enrichment_enabled}
                                            onChange={(e) => setEnrichmentSettings({
                                              ...enrichmentSettings,
                                              social_enrichment_enabled: e.target.checked
                                            })}
                                            className="rounded"
                                          />
                                          <Label htmlFor="social-enrichment-enabled-others" className="text-sm">
                                            Enable Social Profile Enrichment
                                          </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <input
                                            type="checkbox"
                                            id="auto-enrich-others"
                                            checked={enrichmentSettings.auto_enrich_new_contacts}
                                            onChange={(e) => setEnrichmentSettings({
                                              ...enrichmentSettings,
                                              auto_enrich_new_contacts: e.target.checked
                                            })}
                                            className="rounded"
                                          />
                                          <Label htmlFor="auto-enrich-others" className="text-sm">
                                            Auto-enrich new contacts
                                          </Label>
                                        </div>
                                      </div>

                                      <div className="flex gap-2">
                                        <Button 
                                          size="sm"
                                          onClick={() => handleUpdateEnrichmentSettings(enrichmentSettings)}
                                          disabled={isLoading}
                                          className="bg-blue-600 hover:bg-blue-700"
                                        >
                                          {isLoading ? 'Saving...' : 'Save Settings'}
                                        </Button>
                                        <Button 
                                          size="sm"
                                          variant="outline"
                                          onClick={handleTestApolloConnection}
                                          disabled={isLoading || !enrichmentSettings.apollo_api_key}
                                        >
                                          Test Connection
                                        </Button>
                                      </div>

                                      {/* Quick Stats */}
                                      <div className="pt-4 border-t">
                                        <h4 className="text-sm font-medium mb-2">Enrichment Stats</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div className="text-center p-2 bg-gray-50 rounded text-xs">
                                            <div className="font-bold text-blue-600">{enrichmentStatus.total_contacts}</div>
                                            <div className="text-gray-500">Total</div>
                                          </div>
                                          <div className="text-center p-2 bg-gray-50 rounded text-xs">
                                            <div className="font-bold text-green-600">{enrichmentStatus.enrichment_status?.completed || 0}</div>
                                            <div className="text-gray-500">Enriched</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* URL Shortener Service */}
                                  <div className="border rounded-lg p-6">
                                    <div className="flex items-center justify-between mb-4">
                                      <div>
                                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                          <Link className="h-5 w-5 text-green-600" />
                                          URL Shortener Service
                                        </h3>
                                        <p className="text-sm text-gray-600">Configure URL shortening for cleaner campaign links</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${
                                          urlShortenerSettings.configured 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-400'
                                        }`}></div>
                                        <span className="text-sm text-gray-500">
                                          {urlShortenerSettings.configured ? 'Configured' : 'Not Configured'}
                                        </span>
                                      </div>
                                    </div>

                                    {urlShortenerSettings.configured ? (
                                      <div className="space-y-4">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                          <div className="grid grid-cols-2 gap-3 text-xs">
                                            <div>
                                              <span className="font-medium text-green-700">Service:</span>
                                              <div className="capitalize text-green-800">{urlShortenerSettings.service}</div>
                                            </div>
                                            <div>
                                              <span className="font-medium text-green-700">Status:</span>
                                              <div className="text-green-800">Active</div>
                                            </div>
                                            <div>
                                              <span className="font-medium text-green-700">Domain:</span>
                                              <div className="text-green-800">{urlShortenerSettings.domain || 'Default'}</div>
                                            </div>
                                            <div>
                                              <span className="font-medium text-green-700">Auto-Shorten:</span>
                                              <div className="text-green-800">{urlShortenerSettings.auto_shorten ? 'Enabled' : 'Disabled'}</div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setShowUrlShortenerEdit(!showUrlShortenerEdit)}
                                            className="border-green-300 text-green-700 hover:bg-green-100"
                                          >
                                            <Edit className="h-4 w-4 mr-1" />
                                            Edit
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={testUrlShortenerConnection}
                                            disabled={urlShortenerTesting}
                                            className="border-green-300 text-green-700 hover:bg-green-100"
                                          >
                                            <ExternalLink className="h-4 w-4 mr-1" />
                                            {urlShortenerTesting ? 'Testing...' : 'Test'}
                                          </Button>
                                        </div>

                                        {/* Edit Form */}
                                        {showUrlShortenerEdit && (
                                          <div className="border-t pt-4 space-y-3">
                                            <div>
                                              <Label className="text-sm">Service</Label>
                                              <Select
                                                value={urlShortenerForm.service}
                                                onValueChange={(value) => setUrlShortenerForm({...urlShortenerForm, service: value})}
                                              >
                                                <SelectTrigger className="mt-1">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="bitly">Bitly</SelectItem>
                                                  <SelectItem value="tinyurl">TinyURL</SelectItem>
                                                  <SelectItem value="custom">Custom</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            
                                            {urlShortenerForm.service !== 'custom' && (
                                              <div>
                                                <Label className="text-sm">API Key</Label>
                                                <Input
                                                  type="password"
                                                  value={urlShortenerForm.api_key}
                                                  onChange={(e) => setUrlShortenerForm({...urlShortenerForm, api_key: e.target.value})}
                                                  placeholder={`${urlShortenerForm.service} API key`}
                                                  className="mt-1"
                                                />
                                              </div>
                                            )}
                                            
                                            <Button
                                              size="sm"
                                              onClick={handleUrlShortenerConfig}
                                              disabled={isLoading}
                                              className="bg-blue-600 hover:bg-blue-700"
                                            >
                                              {isLoading ? 'Saving...' : 'Save Configuration'}
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="space-y-4">
                                        <div className="text-center py-4">
                                          <Link className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                                          <p className="text-sm text-gray-500">Configure URL shortening service</p>
                                        </div>

                                        <div>
                                          <Label className="text-sm">Service</Label>
                                          <Select
                                            value={urlShortenerForm.service}
                                            onValueChange={(value) => setUrlShortenerForm({...urlShortenerForm, service: value})}
                                          >
                                            <SelectTrigger className="mt-1">
                                              <SelectValue placeholder="Select service" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="bitly">Bitly (Recommended)</SelectItem>
                                              <SelectItem value="tinyurl">TinyURL</SelectItem>
                                              <SelectItem value="custom">Custom Service</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>

                                        {urlShortenerForm.service !== 'custom' && (
                                          <div>
                                            <Label className="text-sm">API Key</Label>
                                            <Input
                                              type="password"
                                              value={urlShortenerForm.api_key}
                                              onChange={(e) => setUrlShortenerForm({...urlShortenerForm, api_key: e.target.value})}
                                              placeholder={`Enter your ${urlShortenerForm.service} API key`}
                                              className="mt-1"
                                            />
                                          </div>
                                        )}

                                        <Button
                                          size="sm"
                                          onClick={handleUrlShortenerConfig}
                                          disabled={isLoading || !urlShortenerForm.service || (urlShortenerForm.service !== 'custom' && !urlShortenerForm.api_key)}
                                          className="w-full bg-blue-600 hover:bg-blue-700"
                                        >
                                          {isLoading ? 'Setting up...' : 'Set up URL Shortener'}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Service Information */}
                                <div className="border rounded-lg p-6">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Information</h3>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    
                                    {/* Apollo.io Info */}
                                    <div className="space-y-3">
                                      <h4 className="font-medium text-blue-700">Apollo.io Social Enrichment</h4>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        <p>• <strong>Free:</strong> 100 credits/month</p>
                                        <p>• <strong>Basic:</strong> $49/year - 5,000 credits</p>
                                        <p>• <strong>Professional:</strong> $79/year - 10,000 credits</p>
                                        <p className="text-xs">
                                          Get API key: <a href="https://apollo.io" target="_blank" rel="noopener noreferrer" className="text-blue-600">apollo.io</a>
                                        </p>
                                      </div>
                                    </div>

                                    {/* URL Shortener Info */}
                                    <div className="space-y-3">
                                      <h4 className="font-medium text-green-700">URL Shortening Services</h4>
                                      <div className="text-sm text-gray-600 space-y-1">
                                        <p>• <strong>Bitly:</strong> 1,000 free links/month</p>
                                        <p>• <strong>TinyURL:</strong> Unlimited free links</p>
                                        <p>• <strong>Custom:</strong> Use your own service</p>
                                        <p className="text-xs">
                                          Get keys: <a href="https://app.bitly.com/settings/api/" target="_blank" rel="noopener noreferrer" className="text-blue-600">Bitly API</a> | <a href="https://tinyurl.com/app" target="_blank" rel="noopener noreferrer" className="text-blue-600">TinyURL</a>
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </TabsContent>
                            </Tabs>
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Tag Management Dialogs */}
      {/* Add Tags Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Tags to Selected Contacts</DialogTitle>
            <DialogDescription>
              Add tags to {selectedRecords.size} selected contacts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* New Tag Input */}
            <div>
              <label className="text-sm font-medium">Create New Tag:</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  placeholder="Enter new tag name"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newTagInput.trim()) {
                      handleAddNewTag();
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleAddNewTag}
                  disabled={!newTagInput.trim()}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Available Tags */}
            <div>
              <label className="text-sm font-medium">Or select existing tags:</label>
              <div className="mt-2 max-h-32 overflow-y-auto border rounded-md p-2">
                {availableTags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {availableTags.map((tagObj, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleAddExistingTag(tagObj.tag)}
                      >
                        {tagObj.tag} ({tagObj.usage_count})
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No existing tags found. Create your first tag above.
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Tags Dialog */}
      <Dialog open={showRemoveTagDialog} onOpenChange={setShowRemoveTagDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Tags from Selected Contacts</DialogTitle>
            <DialogDescription>
              Remove tags from {selectedRecords.size} selected contacts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select tags to remove:</label>
              <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {availableTags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {availableTags.map((tagObj, index) => (
                      <Button
                        key={index}
                        variant="destructive"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleRemoveTag(tagObj.tag)}
                      >
                        Remove "{tagObj.tag}" ({tagObj.usage_count} contacts)
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No tags found to remove.
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Smart Phone Consolidation Dialog */}
      <Dialog open={showPhoneStandardization} onOpenChange={setShowPhoneStandardization}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-green-600" />
              Smart Phone Number Consolidation
            </DialogTitle>
            <DialogDescription>
              Intelligently merge multiple phone columns into organized primary/secondary structure with E.164 standardization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Multi-Column Phone Selection */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                Select Phone Columns (Multi-select)
              </label>
              <p className="text-xs text-gray-500 mb-3">Choose all columns that contain phone numbers. Smart consolidation will merge them into primary/secondary phones.</p>
              
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {audienceFields.filter(field => field && typeof field === 'string').map((field) => {
                  const isPhoneField = field.includes('phone') || field.includes('mobile') || field.includes('tel') || field.includes('number');
                  const isSelected = phoneStandardizationForm.phoneColumns.includes(field);
                  
                  return (
                    <label key={field} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-green-50 border border-green-200' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const newColumns = e.target.checked
                            ? [...phoneStandardizationForm.phoneColumns, field]
                            : phoneStandardizationForm.phoneColumns.filter(col => col !== field);
                          
                          setPhoneStandardizationForm(prev => ({
                            ...prev,
                            phoneColumns: newColumns
                          }));
                        }}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className={`text-sm ${isPhoneField ? 'font-medium text-green-700' : 'text-gray-600'}`}>
                        {field} {isPhoneField ? '📱' : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
              
              {phoneStandardizationForm.phoneColumns.length > 0 && (
                <div className="mt-2 text-sm text-green-700">
                  ✅ Selected {phoneStandardizationForm.phoneColumns.length} column(s): {phoneStandardizationForm.phoneColumns.join(', ')}
                </div>
              )}
            </div>

            {/* Default Country Selection */}
            <div>
              <label className="text-sm font-semibold text-gray-700">Default Country</label>
              <p className="text-xs text-gray-500 mb-2">Used for numbers without country codes</p>
              <select
                value={phoneStandardizationForm.defaultCountry}
                onChange={(e) => setPhoneStandardizationForm(prev => ({
                  ...prev,
                  defaultCountry: e.target.value
                }))}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="US">🇺🇸 United States (US)</option>
                <option value="IN">🇮🇳 India (IN)</option>
                <option value="GB">🇬🇧 United Kingdom (GB)</option>
                <option value="CA">🇨🇦 Canada (CA)</option>
                <option value="AU">🇦🇺 Australia (AU)</option>
                <option value="DE">🇩🇪 Germany (DE)</option>
                <option value="FR">🇫🇷 France (FR)</option>
                <option value="BR">🇧🇷 Brazil (BR)</option>
                <option value="JP">🇯🇵 Japan (JP)</option>
                <option value="CN">🇨🇳 China (CN)</option>
                <option value="MX">🇲🇽 Mexico (MX)</option>
                <option value="IT">🇮🇹 Italy (IT)</option>
                <option value="ES">🇪🇸 Spain (ES)</option>
                <option value="KR">🇰🇷 South Korea (KR)</option>
                <option value="NL">🇳🇱 Netherlands (NL)</option>
              </select>
            </div>

            {/* Advanced Options Toggle */}
            {phoneStandardizationForm.phoneColumns.length > 1 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvancedPhoneOptions(!showAdvancedPhoneOptions)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  ⚙️ Advanced Consolidation Options
                  <span className="text-xs">{showAdvancedPhoneOptions ? '▲' : '▼'}</span>
                </button>
                
                {showAdvancedPhoneOptions && (
                  <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Consolidation Strategy</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="consolidationStrategy"
                          value="smart"
                          checked={phoneStandardizationForm.consolidationStrategy === 'smart'}
                          onChange={(e) => setPhoneStandardizationForm(prev => ({
                            ...prev,
                            consolidationStrategy: e.target.value
                          }))}
                          className="text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm">
                          <strong>🧠 Smart (Recommended)</strong> - AI-powered selection prioritizing mobile numbers and quality
                        </span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="consolidationStrategy"
                          value="priority"
                          checked={phoneStandardizationForm.consolidationStrategy === 'priority'}
                          onChange={(e) => setPhoneStandardizationForm(prev => ({
                            ...prev,
                            consolidationStrategy: e.target.value
                          }))}
                          className="text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm">
                          <strong>📋 Priority</strong> - Use column order and quality scores
                        </span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="consolidationStrategy"
                          value="merge_all"
                          checked={phoneStandardizationForm.consolidationStrategy === 'merge_all'}
                          onChange={(e) => setPhoneStandardizationForm(prev => ({
                            ...prev,
                            consolidationStrategy: e.target.value
                          }))}
                          className="text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm">
                          <strong>📂 Preserve All</strong> - Keep all phone numbers in separate fields
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Information Box */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">
                📞 {phoneStandardizationForm.phoneColumns.length > 1 ? 'Smart Consolidation Process:' : 'Standardization Process:'}
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                {phoneStandardizationForm.phoneColumns.length > 1 ? (
                  <>
                    <li>• Analyzes all selected phone columns and rates quality (1-10 score)</li>
                    <li>• Creates <strong>primary_phone</strong> (best for SMS/marketing) and <strong>secondary_phone</strong> (backup)</li>
                    <li>• Detects phone types (Mobile/Landline/Work) and prioritizes mobile numbers</li>
                    <li>• Converts all numbers to E.164 international format (+country + number)</li>
                    <li>• Preserves original data in backup fields for reference</li>
                  </>
                ) : (
                  <>
                    <li>• Numbers already in E.164 format (+country + number) will remain unchanged</li>
                    <li>• Local numbers will be converted using the selected default country</li>
                    <li>• Numbers starting with 0 will have the leading zero removed</li>
                    <li>• Invalid numbers will be flagged for review</li>
                    <li>• Original numbers will be preserved in a backup field</li>
                  </>
                )}
              </ul>
            </div>

            {/* Results Display */}
            {phoneStandardizationResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3">✅ Consolidation Results:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-700">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{phoneStandardizationResults.summary.primary_phones_created || phoneStandardizationResults.summary.successfully_standardized}</div>
                    <div className="text-xs">Primary Phones</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{phoneStandardizationResults.summary.secondary_phones_created || phoneStandardizationResults.summary.already_standardized}</div>
                    <div className="text-xs">Secondary Phones</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{phoneStandardizationResults.summary.consolidation_rate || phoneStandardizationResults.summary.success_rate}%</div>
                    <div className="text-xs">Success Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{phoneStandardizationResults.summary.columns_processed?.length || 1}</div>
                    <div className="text-xs">Columns Processed</div>
                  </div>
                </div>
                
                {phoneStandardizationResults.examples && (
                  <div className="mt-3 text-xs">
                    <strong>Sample Results:</strong>
                    <div className="mt-1 space-y-1">
                      {phoneStandardizationResults.examples.slice(0, 2).map((example, index) => (
                        <div key={index} className="bg-white p-2 rounded border">
                          <strong>{example.contact_name}:</strong> {example.primary_result}
                          {example.secondary_result !== "None" && <span> + {example.secondary_result}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowPhoneStandardization(false);
                setPhoneStandardizationResults(null);
                setShowAdvancedPhoneOptions(false);
              }}
              disabled={phoneStandardizationInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePhoneStandardization}
              disabled={phoneStandardizationForm.phoneColumns.length === 0 || phoneStandardizationInProgress}
              className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
            >
              {phoneStandardizationInProgress ? (
                <>
                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Smartphone className="h-4 w-4 mr-1" />
                  {phoneStandardizationForm.phoneColumns.length > 1 ? 'Smart Consolidate' : 'Standardize'} Phone Numbers
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advanced Filtering Dialog */}
      <Dialog open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-blue-600" />
              Advanced Filtering & Segmentation
            </DialogTitle>
            <DialogDescription>
              Create sophisticated filters with multiple conditions and save them as reusable segments for targeted campaigns.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Filter Name and Description */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-gray-700">Filter Name</Label>
                <Input
                  placeholder="e.g., High-Value Tech Leads"
                  value={currentFilter.name}
                  onChange={(e) => setCurrentFilter(prev => ({...prev, name: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700">Description (Optional)</Label>
                <Input
                  placeholder="Describe this segment..."
                  value={currentFilter.description}
                  onChange={(e) => setCurrentFilter(prev => ({...prev, description: e.target.value}))}
                />
              </div>
            </div>

            {/* Logic Operator */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Condition Logic</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="logic"
                    value="AND"
                    checked={currentFilter.logic === "AND"}
                    onChange={(e) => setCurrentFilter(prev => ({...prev, logic: e.target.value}))}
                    className="text-blue-600"
                  />
                  <span className="text-sm"><strong>AND</strong> - All conditions must match</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="logic"
                    value="OR"
                    checked={currentFilter.logic === "OR"}
                    onChange={(e) => setCurrentFilter(prev => ({...prev, logic: e.target.value}))}
                    className="text-blue-600"
                  />
                  <span className="text-sm"><strong>OR</strong> - Any condition can match</span>
                </label>
              </div>
            </div>

            {/* Filter Conditions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-gray-700">Filter Conditions</Label>
                <Button size="sm" variant="outline" onClick={addFilterCondition}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition
                </Button>
              </div>
              
              {currentFilter.conditions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                  <Filter className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No conditions added yet</p>
                  <p className="text-sm">Click "Add Condition" to start building your filter</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {currentFilter.conditions.map((condition, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      {index > 0 && (
                        <span className="text-sm font-medium text-gray-600 px-2">
                          {currentFilter.logic}
                        </span>
                      )}
                      
                      {/* Field Selection */}
                      <Select 
                        value={condition.field} 
                        onValueChange={(value) => updateFilterCondition(index, {field: value})}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Select field" />
                        </SelectTrigger>
                        <SelectContent>
                          {audienceFields.map(field => (
                            <SelectItem key={field} value={field}>
                              {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Operator Selection */}
                      <Select 
                        value={condition.operator} 
                        onValueChange={(value) => updateFilterCondition(index, {operator: value})}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not Equals</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="not_contains">Not Contains</SelectItem>
                          <SelectItem value="starts_with">Starts With</SelectItem>
                          <SelectItem value="ends_with">Ends With</SelectItem>
                          <SelectItem value="greater_than">Greater Than</SelectItem>
                          <SelectItem value="less_than">Less Than</SelectItem>
                          <SelectItem value="exists">Has Value</SelectItem>
                          <SelectItem value="not_exists">Is Empty</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Value Input */}
                      {!['exists', 'not_exists'].includes(condition.operator) && (
                        <Input
                          placeholder="Enter value..."
                          value={condition.value}
                          onChange={(e) => updateFilterCondition(index, {value: e.target.value})}
                          className="flex-1"
                        />
                      )}

                      {/* Remove Condition */}
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => removeFilterCondition(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Results */}
            {filterResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ Filter Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-700">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{filterResults.filter_summary.total_matches}</div>
                    <div className="text-xs">Matching Contacts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{filterResults.filter_summary.conditions_applied}</div>
                    <div className="text-xs">Conditions Applied</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{filterResults.pagination.total_pages}</div>
                    <div className="text-xs">Pages</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{filterResults.filter_summary.logic_operator}</div>
                    <div className="text-xs">Logic Used</div>
                  </div>
                </div>
              </div>
            )}

            {/* Saved Segments */}
            {savedSegments.length > 0 && (
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Saved Segments</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {savedSegments.map((segment) => (
                    <div key={segment.id} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                      <div>
                        <div className="text-sm font-medium text-blue-800">{segment.name}</div>
                        <div className="text-xs text-blue-600">{segment.segment_size || 0} contacts</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setCurrentFilter({
                            name: segment.name,
                            description: segment.description,
                            conditions: segment.conditions,
                            logic: segment.logic
                          });
                        }}
                      >
                        Load
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-between pt-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={clearCurrentFilter}>
                Clear Filter
              </Button>
              {currentFilter.conditions.length > 0 && (
                <Button variant="outline" onClick={() => setShowSaveSegmentDialog(true)}>
                  Save as Segment
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAdvancedFilters(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => applyAdvancedFilter()}
                disabled={currentFilter.conditions.length === 0 || isLoading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {isLoading ? 'Applying...' : 'Apply Filter'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Segment Dialog */}
      <Dialog open={showSaveSegmentDialog} onOpenChange={setShowSaveSegmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save as Segment</DialogTitle>
            <DialogDescription>
              Save this filter configuration as a reusable segment for future campaigns.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Segment Name</Label>
              <Input
                placeholder="e.g., Tech Decision Makers"
                value={currentFilter.name}
                onChange={(e) => setCurrentFilter(prev => ({...prev, name: e.target.value}))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe this segment and its use cases..."
                value={currentFilter.description}
                onChange={(e) => setCurrentFilter(prev => ({...prev, description: e.target.value}))}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSaveSegmentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveCurrentSegment} disabled={!currentFilter.name || isLoading}>
              Save Segment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <Dialog open={showDuplicateDetection} onOpenChange={setShowDuplicateDetection}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              Duplicate Detection & Smart Merging
            </DialogTitle>
            <DialogDescription>
              Identify and merge duplicate contacts using advanced matching algorithms to improve data quality.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Detection Strategy */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Detection Strategy</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="strategy"
                    value="smart"
                    checked={duplicateDetectionSettings.strategy === "smart"}
                    onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, strategy: e.target.value}))}
                    className="text-orange-600"
                  />
                  <div>
                    <div className="text-sm font-medium">🧠 Smart Detection</div>
                    <div className="text-xs text-gray-600">Uses name, email, and phone similarity</div>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="strategy"
                    value="email_only"
                    checked={duplicateDetectionSettings.strategy === "email_only"}
                    onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, strategy: e.target.value}))}
                    className="text-orange-600"
                  />
                  <div>
                    <div className="text-sm font-medium">📧 Email Only</div>
                    <div className="text-xs text-gray-600">Matches only by email address</div>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="strategy"
                    value="phone_only"
                    checked={duplicateDetectionSettings.strategy === "phone_only"}
                    onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, strategy: e.target.value}))}
                    className="text-orange-600"
                  />
                  <div>
                    <div className="text-sm font-medium">📱 Phone Only</div>
                    <div className="text-xs text-gray-600">Matches only by phone number</div>
                  </div>
                </label>
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="strategy"
                    value="name_email"
                    checked={duplicateDetectionSettings.strategy === "name_email"}
                    onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, strategy: e.target.value}))}
                    className="text-orange-600"
                  />
                  <div>
                    <div className="text-sm font-medium">👤 Name + Email</div>
                    <div className="text-xs text-gray-600">Matches by name and email combination</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Similarity Threshold */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Similarity Threshold: {(duplicateDetectionSettings.similarity_threshold * 100).toFixed(0)}%
              </Label>
              <div className="px-3">
                <input
                  type="range"
                  min="0.5"
                  max="1.0"
                  step="0.05"
                  value={duplicateDetectionSettings.similarity_threshold}
                  onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, similarity_threshold: parseFloat(e.target.value)}))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>More Matches (50%)</span>
                  <span>Exact Matches (100%)</span>
                </div>
              </div>
            </div>

            {/* Auto Merge Option */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="auto_merge"
                checked={duplicateDetectionSettings.auto_merge}
                onChange={(e) => setDuplicateDetectionSettings(prev => ({...prev, auto_merge: e.target.checked}))}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <Label htmlFor="auto_merge" className="text-sm">
                Automatically merge obvious duplicates (95%+ similarity)
              </Label>
            </div>

            {/* Detection Results */}
            {duplicateResults && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-3">🔍 Detection Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-orange-700 mb-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-800">{duplicateResults.summary.total_contacts_analyzed}</div>
                    <div className="text-xs">Contacts Analyzed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-800">{duplicateResults.summary.duplicate_groups_found}</div>
                    <div className="text-xs">Duplicate Groups</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-800">{duplicateResults.summary.total_duplicates}</div>
                    <div className="text-xs">Total Duplicates</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-800">{duplicateResults.summary.potential_merges}</div>
                    <div className="text-xs">Potential Merges</div>
                  </div>
                </div>
                
                {duplicateResults.duplicate_groups && duplicateResults.duplicate_groups.length > 0 && (
                  <div>
                    <h5 className="font-medium text-orange-800 mb-2">Sample Duplicate Groups (First 3):</h5>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {duplicateResults.duplicate_groups.slice(0, 3).map((group, index) => (
                        <div key={group.id} className="bg-white p-3 rounded border">
                          <div className="text-xs font-medium text-gray-600 mb-1">Group {index + 1}</div>
                          <div className="text-sm">
                            <strong>Primary:</strong> {group.primary_contact?.full_name || group.primary_contact?.name || 'No Name'} 
                            ({group.primary_contact?.email_address || group.primary_contact?.email || 'No Email'})
                          </div>
                          <div className="text-xs text-gray-600">
                            {group.merge_candidates?.length || 0} duplicates to merge
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDuplicateDetection(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runDuplicateDetection}
              disabled={isLoading}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
            >
              {isLoading ? 'Detecting...' : 'Run Detection'}
            </Button>
            {duplicateResults && duplicateResults.duplicate_groups?.length > 0 && (
              <Button 
                onClick={() => {
                  const groupIds = duplicateResults.duplicate_groups.map(g => g.id);
                  mergeDuplicates(duplicateResults.detection_id, groupIds);
                }}
                disabled={isLoading}
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
              >
                Merge All Duplicates
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Validation Dialog */}
      <Dialog open={showDataValidation} onOpenChange={setShowDataValidation}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Comprehensive Data Validation
            </DialogTitle>
            <DialogDescription>
              Validate and standardize email addresses, phone numbers, and addresses to improve campaign deliverability.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Validation Types */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Validation Types</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'email', name: 'Email Validation', desc: 'Format, domain, and deliverability checks', icon: '📧' },
                  { id: 'phone', name: 'Phone Validation', desc: 'Format standardization and carrier lookup', icon: '📱' },
                  { id: 'address', name: 'Address Validation', desc: 'Address standardization and geocoding', icon: '📍' },
                  { id: 'social_media', name: 'Social Media', desc: 'Social profile URL validation', icon: '🌐' }
                ].map((type) => (
                  <label key={type.id} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={validationSettings.validation_types.includes(type.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValidationSettings(prev => ({...prev, validation_types: [...prev.validation_types, type.id]}));
                        } else {
                          setValidationSettings(prev => ({...prev, validation_types: prev.validation_types.filter(t => t !== type.id)}));
                        }
                      }}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <div className="text-sm font-medium">{type.icon} {type.name}</div>
                      <div className="text-xs text-gray-600">{type.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Batch Size */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Batch Size: {validationSettings.batch_size} contacts
              </Label>
              <div className="px-3">
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="50"
                  value={validationSettings.batch_size}
                  onChange={(e) => setValidationSettings(prev => ({...prev, batch_size: parseInt(e.target.value)}))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>Slower (50)</span>
                  <span>Faster (500)</span>
                </div>
              </div>
            </div>

            {/* Update Records */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="update_records"
                checked={validationSettings.update_records}
                onChange={(e) => setValidationSettings(prev => ({...prev, update_records: e.target.checked}))}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <Label htmlFor="update_records" className="text-sm">
                Automatically update records with corrected data
              </Label>
            </div>

            {/* Validation Results */}
            {validationResults && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3">✅ Validation Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-green-700 mb-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{validationResults.total_contacts}</div>
                    <div className="text-xs">Contacts Analyzed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{validationResults.validated_contacts}</div>
                    <div className="text-xs">Contacts Validated</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-800">{Object.keys(validationResults.field_results || {}).length}</div>
                    <div className="text-xs">Fields Processed</div>
                  </div>
                </div>

                {validationResults.validation_summary && (
                  <div>
                    <h5 className="font-medium text-green-800 mb-2">Field Validation Summary:</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(validationResults.validation_summary).map(([field, stats]) => (
                        <div key={field} className="bg-white p-2 rounded border text-xs">
                          <div className="font-medium">{field.replace(/_/g, ' ').toUpperCase()}</div>
                          <div className="text-green-600">
                            {(stats.validation_rate * 100).toFixed(1)}% valid • {(stats.correction_rate * 100).toFixed(1)}% corrected
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDataValidation(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runDataValidation}
              disabled={validationSettings.validation_types.length === 0 || isLoading}
              className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
            >
              {isLoading ? 'Validating...' : 'Start Validation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Enrichment Dialog */}
      <Dialog open={showDataEnrichment} onOpenChange={setShowDataEnrichment}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Data Enrichment & Enhancement
            </DialogTitle>
            <DialogDescription>
              Automatically enrich your contacts with additional demographic, professional, social, and behavioral insights.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Enrichment Types */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Enrichment Types</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'demographic', name: 'Demographic', desc: 'Age, gender, timezone inference', icon: '👥' },
                  { id: 'professional', name: 'Professional', desc: 'Industry, company size, seniority', icon: '💼' },
                  { id: 'social', name: 'Social Media', desc: 'Social profiles and influence scoring', icon: '📱' },
                  { id: 'behavioral', name: 'Behavioral', desc: 'Engagement patterns and preferences', icon: '🎯' }
                ].map((type) => (
                  <label key={type.id} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={dataEnrichmentSettings.enrichment_types.includes(type.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDataEnrichmentSettings(prev => ({...prev, enrichment_types: [...prev.enrichment_types, type.id]}));
                        } else {
                          setDataEnrichmentSettings(prev => ({...prev, enrichment_types: prev.enrichment_types.filter(t => t !== type.id)}));
                        }
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <div className="text-sm font-medium">{type.icon} {type.name}</div>
                      <div className="text-xs text-gray-600">{type.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Batch Size */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                Batch Size: {dataEnrichmentSettings.batch_size} contacts
              </Label>
              <div className="px-3">
                <input
                  type="range"
                  min="25"
                  max="100"
                  step="25"
                  value={dataEnrichmentSettings.batch_size}
                  onChange={(e) => setDataEnrichmentSettings(prev => ({...prev, batch_size: parseInt(e.target.value)}))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>Conservative (25)</span>
                  <span>Aggressive (100)</span>
                </div>
              </div>
            </div>

            {/* Update Records */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enrichment_update_records"
                checked={dataEnrichmentSettings.update_records}
                onChange={(e) => setDataEnrichmentSettings(prev => ({...prev, update_records: e.target.checked}))}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <Label htmlFor="enrichment_update_records" className="text-sm">
                Automatically update records with enriched data
              </Label>
            </div>

            {/* Enrichment Results */}
            {enrichmentResults && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-3">🚀 Enrichment Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-purple-700 mb-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-800">{enrichmentResults.total_contacts}</div>
                    <div className="text-xs">Contacts Processed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-800">{enrichmentResults.enriched_contacts}</div>
                    <div className="text-xs">Contacts Enriched</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-800">{enrichmentResults.new_fields_added?.length || 0}</div>
                    <div className="text-xs">New Fields Added</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-800">
                      {enrichmentResults.enrichment_summary ? (enrichmentResults.enrichment_summary.enrichment_rate * 100).toFixed(1) : 0}%
                    </div>
                    <div className="text-xs">Success Rate</div>
                  </div>
                </div>

                {enrichmentResults.new_fields_added && enrichmentResults.new_fields_added.length > 0 && (
                  <div>
                    <h5 className="font-medium text-purple-800 mb-2">New Fields Added:</h5>
                    <div className="flex flex-wrap gap-1">
                      {enrichmentResults.new_fields_added.map((field) => (
                        <Badge key={field} variant="secondary" className="text-xs bg-purple-100 text-purple-800">
                          {field.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDataEnrichment(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runDataEnrichment}
              disabled={dataEnrichmentSettings.enrichment_types.length === 0 || isLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              {isLoading ? 'Enriching...' : 'Start Enrichment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Quality Report Dialog */}
      <Dialog open={showQualityReport} onOpenChange={setShowQualityReport}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
              Data Quality Report
            </DialogTitle>
            <DialogDescription>
              Comprehensive analysis of your audience data quality with actionable recommendations for improvement.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {qualityReport ? (
              <>
                {/* Overall Quality Score */}
                <div className="text-center bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
                  <div className="text-4xl font-bold text-indigo-600 mb-2">
                    {(qualityReport.overall_quality_score * 100).toFixed(1)}%
                  </div>
                  <div className="text-lg font-semibold text-indigo-800">Overall Data Quality Score</div>
                  <div className="text-sm text-indigo-600 mt-2">
                    Based on {qualityReport.total_contacts} contacts analyzed
                  </div>
                </div>

                {/* Quality Distribution */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">📊 Quality Distribution</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'excellent', label: 'Excellent', color: 'green', threshold: '90%+' },
                      { key: 'good', label: 'Good', color: 'blue', threshold: '70-89%' },
                      { key: 'fair', label: 'Fair', color: 'yellow', threshold: '50-69%' },
                      { key: 'poor', label: 'Poor', color: 'red', threshold: '<50%' }
                    ].map((quality) => (
                      <div key={quality.key} className={`text-center p-3 bg-${quality.color}-50 border border-${quality.color}-200 rounded-lg`}>
                        <div className={`text-2xl font-bold text-${quality.color}-600`}>
                          {qualityReport.data_quality_distribution[quality.key] || 0}
                        </div>
                        <div className={`text-sm font-medium text-${quality.color}-700`}>{quality.label}</div>
                        <div className={`text-xs text-${quality.color}-600`}>{quality.threshold}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Field Completeness */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">📋 Field Completeness</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                    {Object.entries(qualityReport.field_completeness).map(([field, stats]) => (
                      <div key={field} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-medium">{field.replace(/_/g, ' ').toUpperCase()}</div>
                          <div className="text-xs text-gray-600">
                            {stats.complete_records}/{stats.total_records} complete
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold ${
                            stats.completion_rate >= 0.8 ? 'text-green-600' :
                            stats.completion_rate >= 0.5 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {(stats.completion_rate * 100).toFixed(1)}%
                          </div>
                          <div className={`w-16 h-2 rounded-full ${
                            stats.completion_rate >= 0.8 ? 'bg-green-200' :
                            stats.completion_rate >= 0.5 ? 'bg-yellow-200' : 'bg-red-200'
                          }`}>
                            <div 
                              className={`h-2 rounded-full ${
                                stats.completion_rate >= 0.8 ? 'bg-green-600' :
                                stats.completion_rate >= 0.5 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${stats.completion_rate * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                {qualityReport.recommendations && qualityReport.recommendations.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">💡 Recommendations</h4>
                    <div className="space-y-3">
                      {qualityReport.recommendations.map((rec, index) => (
                        <div key={index} className={`p-3 rounded-lg border ${
                          rec.priority === 'high' ? 'bg-red-50 border-red-200' :
                          rec.priority === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                          'bg-blue-50 border-blue-200'
                        }`}>
                          <div className={`text-sm font-medium ${
                            rec.priority === 'high' ? 'text-red-800' :
                            rec.priority === 'medium' ? 'text-yellow-800' :
                            'text-blue-800'
                          }`}>
                            {rec.priority === 'high' ? '🚨' : rec.priority === 'medium' ? '⚠️' : '💡'} {rec.title}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">{rec.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enrichment Opportunities */}
                {qualityReport.enrichment_opportunities && Object.keys(qualityReport.enrichment_opportunities).length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">🚀 Enrichment Opportunities</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(qualityReport.enrichment_opportunities).map(([field, opportunity]) => (
                        <div key={field} className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="text-sm font-medium text-purple-800">
                            {field.replace(/_/g, ' ').toUpperCase()}
                          </div>
                          <div className="text-xs text-purple-600 mt-1">
                            {opportunity.missing_records} records missing • {opportunity.enrichment_type} enrichment
                          </div>
                          <div className="text-xs text-purple-700 mt-1">
                            <strong>Priority:</strong> {opportunity.priority} • <strong>Potential:</strong> {(opportunity.potential_improvement * 100).toFixed(1)}% improvement
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Loading quality report...</p>
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowQualityReport(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                loadQualityReport();
              }}
              disabled={isLoading}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              {isLoading ? 'Refreshing...' : 'Refresh Report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Connection Wizard Dialog */}
      <Dialog open={showConnectionWizard} onOpenChange={setShowConnectionWizard}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add CRM Connection
            </DialogTitle>
            <DialogDescription>
              Connect a new CRM system to synchronize your customer data automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* CRM Selection */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Select CRM System</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {supportedCrms.map((crm) => (
                  <label 
                    key={crm.id} 
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedCrmType === crm.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="crm_type"
                      value={crm.id}
                      checked={selectedCrmType === crm.id}
                      onChange={(e) => setSelectedCrmType(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      crm.id === 'hubspot' ? 'bg-orange-500' :
                      crm.id === 'zoho' ? 'bg-blue-500' :
                      'bg-gray-500'
                    }`}>
                      {crm.name[0]}
                    </div>
                    <div>
                      <div className="font-semibold">{crm.name}</div>
                      <div className="text-sm text-gray-600">{crm.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Connection Name */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Connection Name</Label>
              <Input
                placeholder="e.g., Main HubSpot Account"
                value={connectionName}
                onChange={(e) => setConnectionName(e.target.value)}
              />
            </div>

            {/* CRM Credentials */}
            {selectedCrmType && (
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  {selectedCrmType === 'hubspot' ? 'HubSpot Access Token' : 'Zoho Access Token'}
                </Label>
                <Input
                  type="password"
                  placeholder={
                    selectedCrmType === 'hubspot' 
                      ? 'Enter your HubSpot private app access token'
                      : 'Enter your Zoho CRM access token'
                  }
                  value={crmCredentials.access_token || ''}
                  onChange={(e) => setCrmCredentials({...crmCredentials, access_token: e.target.value})}
                />
                <p className="text-xs text-gray-600 mt-1">
                  {selectedCrmType === 'hubspot' 
                    ? 'Create a private app in HubSpot Settings → Integrations → Private Apps'
                    : 'Generate token in Zoho CRM Settings → Developer Space → Self Client'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Dialog Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setShowConnectionWizard(false);
              setSelectedCrmType('');
              setConnectionName('');
              setCrmCredentials({});
            }}>
              Cancel
            </Button>
            <Button 
              onClick={createCrmConnection}
              disabled={!selectedCrmType || !connectionName || !crmCredentials.access_token || isLoading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {isLoading ? 'Connecting...' : 'Create Connection'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Undo Operation Confirmation Dialog */}
      <Dialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Undo2 className="h-5 w-5" />
              Confirm Undo Operation
            </DialogTitle>
            <DialogDescription>
              This action will reverse the following operation and cannot be undone itself.
            </DialogDescription>
          </DialogHeader>
          
          {operationToUndo && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">
                    {operationToUndo.operation_type.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <p className="font-medium text-sm">{operationToUndo.operation_name}</p>
                <p className="text-sm text-gray-600 mt-1">{operationToUndo.description}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {operationToUndo.records_count} contacts will be affected
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(operationToUndo.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="undo-reason">Reason for undo (optional)</Label>
                <Textarea
                  id="undo-reason"
                  placeholder="Why are you undoing this operation?"
                  value={undoReason}
                  onChange={(e) => setUndoReason(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUndoDialog(false);
                    setOperationToUndo(null);
                    setUndoReason('');
                  }}
                  disabled={isUndoLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUndoConfirm}
                  variant="destructive"
                  disabled={isUndoLoading}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {isUndoLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      Undoing...
                    </>
                  ) : (
                    <>
                      <Undo2 className="h-4 w-4 mr-1" />
                      Confirm Undo
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Selected Records Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirm Delete Records
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete the selected audience records and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-800">Warning: Permanent Deletion</span>
              </div>
              <p className="text-sm text-red-700">
                You are about to delete <strong>{selectedRecords.size} contact record(s)</strong>.
                This action cannot be undone and the data will be permanently removed from your database.
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={deleteSelectedRecords}
                variant="destructive"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete {selectedRecords.size} Record(s)
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SEO Audit Details Dialog */}
      <Dialog open={showAuditDetails} onOpenChange={setShowAuditDetails}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Search className="h-5 w-5" />
              SEO Audit Details
            </DialogTitle>
            <DialogDescription>
              Comprehensive 60-step SEO analysis results with detailed findings and recommendations
            </DialogDescription>
          </DialogHeader>
          
          {currentSEOAudit && (
            <div className="space-y-6">
              {/* Overall Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold ${
                      currentSEOAudit.overall_score >= 80 ? 'bg-green-100 text-green-800' :
                      currentSEOAudit.overall_score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {currentSEOAudit.overall_score}%
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-blue-900">Overall SEO Score</h3>
                      <p className="text-blue-700">
                        Audit Date: {new Date(currentSEOAudit.audit_date).toLocaleDateString()} | 
                        Duration: {Math.round(currentSEOAudit.audit_duration || 0)}s
                      </p>
                      <p className="text-blue-600">
                        Site: {currentSEOAudit.site_url} | 
                        Issues: {currentSEOAudit.total_issues} | 
                        Critical: {currentSEOAudit.critical_issues.length}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Category Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.crawling_indexing_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.crawling_indexing_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.crawling_indexing_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">Crawling & Indexing</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.web_performance_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.web_performance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.web_performance_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">Web Performance</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.internal_linking_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.internal_linking_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.internal_linking_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">Internal Linking</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.javascript_seo_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.javascript_seo_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.javascript_seo_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">JavaScript SEO</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.structured_data_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.structured_data_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.structured_data_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">Structured Data</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border">
                    <div className={`text-lg font-bold ${
                      currentSEOAudit.international_seo_score >= 80 ? 'text-green-600' :
                      currentSEOAudit.international_seo_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {currentSEOAudit.international_seo_score}%
                    </div>
                    <div className="text-sm font-medium text-gray-700">International SEO</div>
                  </div>
                </div>
              </div>

              {/* Critical Issues */}
              {currentSEOAudit.critical_issues && currentSEOAudit.critical_issues.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Critical Issues ({currentSEOAudit.critical_issues.length})
                  </h4>
                  <ul className="space-y-2">
                    {currentSEOAudit.critical_issues.map((issue, index) => (
                      <li key={index} className="flex items-start gap-2 text-red-700">
                        <span className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-sm">{issue}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Detailed Audit Steps */}
              {currentSEOAudit.audit_steps && currentSEOAudit.audit_steps.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    Detailed Audit Results ({currentSEOAudit.audit_steps.length} checks)
                  </h4>
                  
                  <div className="grid gap-3">
                    {currentSEOAudit.audit_steps.map((step, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`w-3 h-3 rounded-full ${
                                step.status === 'compliant' ? 'bg-green-500' :
                                step.status === 'needs_attention' ? 'bg-yellow-500' :
                                step.status === 'non_compliant' ? 'bg-red-500' : 'bg-gray-500'
                              }`}></span>
                              <h5 className="font-medium text-gray-900">{step.step_name}</h5>
                              <Badge variant="outline" className="text-xs">
                                {step.category.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                            <p className="text-sm text-gray-800">{step.evidence}</p>
                            {step.recommendation && (
                              <div className="mt-2 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                                <p className="text-sm text-blue-800">
                                  <strong>Recommendation:</strong> {step.recommendation}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className={`text-lg font-bold ${
                              (step.score || 0) >= 80 ? 'text-green-600' :
                              (step.score || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {step.score || 0}%
                            </div>
                            <div className="text-xs text-gray-500 capitalize">
                              {step.impact_level} Impact
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Steps */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Next Steps
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><strong>Next Audit:</strong> {currentSEOAudit.next_audit_date ? 
                    new Date(currentSEOAudit.next_audit_date).toLocaleDateString() : 
                    'Schedule recommended'
                  }</p>
                  <p><strong>Monitoring Frequency:</strong> {currentSEOAudit.monitoring_frequency}</p>
                  <p><strong>Focus Areas:</strong> Address critical issues first, then work on performance optimization</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-between gap-2 pt-4">
            <Button
              onClick={() => exportAuditPDF()}
              disabled={isLoading}
              variant="outline"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4 mr-2" />
                  Export PDF
                </>
              )}
            </Button>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAuditDetails(false)}
                variant="outline"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowAuditDetails(false);
                  startSEOAudit();
                }}
                disabled={isAuditRunning}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Run New Audit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header Management Dialog */}
      <Dialog open={showHeaderManager} onOpenChange={setShowHeaderManager}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Header Synonym Manager
            </DialogTitle>
            <DialogDescription>
              Manage learned header mappings, custom fields, and ignored headers from your imports
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Learned Synonyms Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Learned Header Synonyms ({learnedSynonyms.length})
                </CardTitle>
                <CardDescription>
                  AI has learned these header mappings from your training. Edit to improve future imports.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {learnedSynonyms.length > 0 ? (
                  <div className="space-y-3">
                    {learnedSynonyms.map((synonym) => (
                      <div key={synonym.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Original Header</label>
                            <div className="font-medium">{synonym.synonym}</div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Maps To</label>
                            <div className="font-medium text-blue-600">{synonym.canonical_field}</div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Confidence</label>
                            <div className={`font-medium ${synonym.confidence >= 0.9 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {Math.round(synonym.confidence * 100)}%
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingSynonym(synonym)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              if (window.confirm(`Delete mapping "${synonym.synonym}" → "${synonym.canonical_field}"?`)) {
                                deleteSynonym(synonym.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Brain className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No learned synonyms yet. Use the Import Wizard to train the AI!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Custom Fields Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Custom Fields ({headerCustomFields.length})
                </CardTitle>
                <CardDescription>
                  Custom fields you've created for unique data columns
                </CardDescription>
              </CardHeader>
              <CardContent>
                {headerCustomFields.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {headerCustomFields.map((field) => (
                      <div key={field.field_name} className="p-3 border rounded-lg">
                        <div className="font-medium">{field.field_name}</div>
                        <div className="text-sm text-gray-600">Type: {field.field_type}</div>
                        <div className="text-xs text-gray-500">Created: {new Date(field.created_at).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Plus className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No custom fields created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ignored Headers Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Ignored Headers ({ignoredHeaders.length})
                </CardTitle>
                <CardDescription>
                  Headers you've chosen to skip during imports. You can unignore them here to include in future imports.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ignoredHeaders.length > 0 ? (
                  <div className="grid gap-2 md:grid-cols-2">
                    {ignoredHeaders.map((header) => (
                      <div key={header.header} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">{header.header}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (window.confirm(`Remove "${header.header}" from ignored list?`)) {
                              removeIgnoredHeader(header.header);
                            }
                          }}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Unignore
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <X className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No ignored headers</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* New Header Trainer Section - Temporarily disabled for debugging */}
            {/* 
            <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  New-Header Trainer
                </CardTitle>
                <CardDescription>
                  Train the AI to recognize new headers. Choose how to map unknown headers: to existing fields, create new custom fields, or ignore them.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium mb-3">Train New Header Mapping</h4>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      trainNewHeader(
                        formData.get('header_name'),
                        formData.get('mapping_type') === 'existing' ? 'map_to_existing' : 
                        formData.get('mapping_type') === 'custom' ? 'create_custom_field' :
                        'skip',
                        formData.get('mapping_type') === 'existing' ? formData.get('target_field') : 
                        formData.get('mapping_type') === 'custom' ? formData.get('custom_field_name') : null
                      );
                    }} id="header-trainer-form" className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Header Name</label>
                        <Input 
                          name="header_name"
                          placeholder="e.g., Customer_ID, Policy_Number, Date_Of_Birth"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Mapping Action</label>
                        <select 
                          name="mapping_type" 
                          required 
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onChange={(e) => {
                            const existingSection = document.getElementById('existing_field_section');
                            const customSection = document.getElementById('custom_field_section');
                            
                            if (e.target.value === 'existing') {
                              existingSection.classList.remove('hidden');
                              customSection.classList.add('hidden');
                            } else if (e.target.value === 'custom') {
                              existingSection.classList.add('hidden');
                              customSection.classList.remove('hidden');
                            } else {
                              existingSection.classList.add('hidden');
                              customSection.classList.add('hidden');
                            }
                          }}
                        >
                          <option value="">Choose action...</option>
                          <option value="existing">Map to Existing Field</option>
                          <option value="custom">Create New Custom Field</option>
                          <option value="ignore">Skip/Ignore Field</option>
                        </select>
                      </div>
                      
                      <div id="existing_field_section" className="hidden">
                        <label className="block text-sm font-medium mb-1">Target Field</label>
                        <select 
                          name="target_field"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select field...</option>
                          <option value="full_name">Full Name</option>
                          <option value="email_addresses">Email Address</option>
                          <option value="phone_numbers">Phone Number</option>
                          <option value="company_name">Company Name</option>
                          <option value="job_title">Job Title</option>
                          <option value="city">City</option>
                          <option value="state_province">State/Province</option>
                          <option value="country">Country</option>
                        </select>
                      </div>
                      
                      <div id="custom_field_section" className="hidden space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Custom Field Name</label>
                          <Input 
                            name="custom_field_name"
                            placeholder="e.g., Policy Number, Premium Amount"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Field Type</label>
                          <select 
                            name="custom_field_type"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">Yes/No</option>
                          </select>
                        </div>
                      </div>
                      
                      <Button type="submit" className="w-full">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Train AI with This Mapping
                      </Button>
                    </form>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-yellow-800">Training Tips</h5>
                        <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                          <li>• <strong>Map to Existing:</strong> Use when headers are variations of standard fields (e.g., "Email" → "Email Address")</li>
                          <li>• <strong>Create Custom:</strong> Use for domain-specific fields (e.g., "Policy Number", "Premium Amount")</li>
                          <li>• <strong>Skip/Ignore:</strong> Use for irrelevant columns (e.g., "Internal ID", "Temp Notes")</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            */}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Synonym Dialog */}
      <Dialog open={!!editingSynonym} onOpenChange={() => setEditingSynonym(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Header Synonym</DialogTitle>
            <DialogDescription>
              Modify how this header should be mapped in future imports
            </DialogDescription>
          </DialogHeader>
          
          {editingSynonym && (
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              updateSynonym(
                editingSynonym.id,
                formData.get('canonical_field'),
                formData.get('synonym'),
                parseFloat(formData.get('confidence'))
              );
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Original Header</label>
                  <input
                    name="synonym"
                    defaultValue={editingSynonym.synonym}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Maps To Field</label>
                  <Select name="canonical_field" defaultValue={editingSynonym.canonical_field}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_name">📝 Full Name</SelectItem>
                      <SelectItem value="email_addresses">📧 Email Address</SelectItem>
                      <SelectItem value="phone_numbers">📱 Phone Number</SelectItem>
                      <SelectItem value="company_name">🏢 Company</SelectItem>
                      <SelectItem value="job_title">💼 Job Title</SelectItem>
                      <SelectItem value="city">🏙️ City</SelectItem>
                      <SelectItem value="state_province">📍 State/Province</SelectItem>
                      <SelectItem value="country">🌍 Country</SelectItem>
                      <SelectItem value="industry">🏭 Industry</SelectItem>
                      <SelectItem value="custom_tags">🏷️ Tags</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Confidence (0.0 - 1.0)</label>
                  <input
                    name="confidence"
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    defaultValue={editingSynonym.confidence}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit">Update Synonym</Button>
                  <Button type="button" variant="outline" onClick={() => setEditingSynonym(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Enhanced Import Wizard Dialog */}
      <Dialog open={showImportWizard} onOpenChange={setShowImportWizard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Next-Gen Import Wizard
            </DialogTitle>
            <DialogDescription>
              AI-powered contact import with intelligent header mapping and duplicate detection
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Step Indicator */}
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className={`flex items-center space-x-2 ${importStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep === 1 ? 'bg-blue-600 text-white' : importStep > 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  1
                </div>
                <span className="font-medium">Upload</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <div className={`flex items-center space-x-2 ${importStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep === 2 ? 'bg-blue-600 text-white' : importStep > 2 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  2
                </div>
                <span className="font-medium">Map Headers</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <div className={`flex items-center space-x-2 ${importStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep === 3 ? 'bg-blue-600 text-white' : importStep > 3 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  3
                </div>
                <span className="font-medium">Review</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
              <div className={`flex items-center space-x-2 ${importStep >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep === 4 ? 'bg-blue-600 text-white' : importStep > 4 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                  4
                </div>
                <span className="font-medium">Import</span>
              </div>
            </div>
            
            {/* Step 1: File Upload */}
            {importStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 1: Upload Your File</h3>
                <div
                  className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:bg-blue-50 transition-colors"
                  onClick={() => document.getElementById('import-file-input').click()}
                >
                  <Upload className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drop your contact file here or click to browse
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports CSV, Excel (.xlsx, .xls) files up to 100MB
                  </p>
                  <input
                    id="import-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(e.target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* Step 2: Header Mapping */}
            {importStep === 2 && fileAnalysis && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 2: Review Header Mappings</h3>
                
                {/* File Info */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">File:</span>
                      <div className="font-medium">{fileAnalysis.file_info.filename}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Rows:</span>
                      <div className="font-medium">{fileAnalysis.file_info.total_rows.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Valid Contacts:</span>
                      <div className="font-medium text-green-600">{fileAnalysis.data_quality.estimated_valid_contacts}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Duplicates:</span>
                      <div className="font-medium text-orange-600">{fileAnalysis.duplicate_preview.duplicates_in_file}</div>
                    </div>
                  </div>
                </div>
                
                {/* Header Mappings */}
                <div className="space-y-3">
                  {fileAnalysis.mapping_suggestions.filter(suggestion => headerMappings[suggestion.header] !== 'SKIP').map((suggestion, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="font-medium">{suggestion.header}</div>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            suggestion.action === 'auto_map' ? 'bg-green-100 text-green-800' :
                            suggestion.action === 'review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {suggestion.confidence > 0 ? `${Math.round(suggestion.confidence * 100)}% match` : 'Unknown'}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {/* Show Create Field for low confidence headers or new headers */}
                          {(suggestion.action === 'new_header' || suggestion.confidence < 0.70) && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => trainNewHeader(suggestion.header, 'create_new_field')}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Create Field
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleHeaderMapping(suggestion.header, 'SKIP')}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Skip Field
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">Maps to:</span>
                        
                        {/* Check if this header is mapped to a linked section */}
                        {linkedSectionMappings[suggestion.header] ? (
                          <div className="flex items-center gap-2">
                            <div className="px-3 py-2 bg-purple-100 text-purple-800 rounded border text-sm">
                              📦 Linked Section: {fileAnalysis.linked_section_categories?.find(cat => cat.id === linkedSectionMappings[suggestion.header])?.name || linkedSectionMappings[suggestion.header]}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeLinkedSectionMapping(suggestion.header)}
                              className="text-red-600 hover:text-red-700 border-red-200"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Select
                              value={headerMappings[suggestion.header] === 'SKIP' ? 'DONT_MAP' : (headerMappings[suggestion.header] || suggestion.suggested_field || 'DONT_MAP')}
                              onValueChange={(value) => handleHeaderMapping(suggestion.header, value === 'DONT_MAP' ? 'SKIP' : value)}
                            >
                              <SelectTrigger className={`w-64 ${headerMappings[suggestion.header] === 'SKIP' ? 'border-gray-300 text-gray-400' : ''}`}>
                                <SelectValue placeholder={headerMappings[suggestion.header] === 'SKIP' ? 'Field will be skipped' : 'Select field...'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="DONT_MAP">-- Don't Import This Field --</SelectItem>
                                <SelectItem value="full_name">📝 Full Name</SelectItem>
                                <SelectItem value="email_address">📧 Email Address</SelectItem>
                                <SelectItem value="mobile_number">📱 Phone Number</SelectItem>
                                <SelectItem value="company_name">🏢 Company</SelectItem>
                                <SelectItem value="job_title">💼 Job Title</SelectItem>
                                <SelectItem value="city">🏙️ City</SelectItem>
                                <SelectItem value="state_province">📍 State/Province</SelectItem>
                                <SelectItem value="country">🌍 Country</SelectItem>
                                <SelectItem value="industry">🏭 Industry</SelectItem>
                                <SelectItem value="custom_tags">🏷️ Tags</SelectItem>
                              </SelectContent>
                            </Select>
                            {headerMappings[suggestion.header] === 'SKIP' && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <X className="h-4 w-4" />
                                <span>Will be skipped</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      
                      {/* Linked Sections Option - NEW! */}
                      {!linkedSectionMappings[suggestion.header] && (
                        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              id={`linked-${suggestion.header}`}
                              className="h-4 w-4 text-purple-600 rounded border-gray-300"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Show linked section selector
                                  document.getElementById(`linked-selector-${suggestion.header}`).style.display = 'block';
                                } else {
                                  // Hide and reset
                                  document.getElementById(`linked-selector-${suggestion.header}`).style.display = 'none';
                                  removeLinkedSectionMapping(suggestion.header);
                                }
                              }}
                            />
                            <label htmlFor={`linked-${suggestion.header}`} className="text-sm font-medium text-purple-700">
                              📦 Map to Linked Section
                            </label>
                            <span className="text-xs text-purple-600">(For transaction/product data)</span>
                          </div>
                          
                          <div id={`linked-selector-${suggestion.header}`} style={{display: 'none'}} className="ml-6">
                            <Select onValueChange={(value) => handleLinkedSectionMapping(suggestion.header, value)}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select linked section category..." />
                              </SelectTrigger>
                              <SelectContent>
                                {fileAnalysis.linked_section_categories?.map((category) => (
                                  <SelectItem key={category.id} value={category.id}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="text-xs text-gray-500 mt-1">
                              💡 Linked sections store related data separately from core contact info
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Skipped Headers Section */}
                {(() => {
                  const skippedHeaders = fileAnalysis.mapping_suggestions.filter(suggestion => 
                    headerMappings[suggestion.header] === 'SKIP'
                  );
                  
                  if (skippedHeaders.length === 0) return null;
                  
                  return (
                    <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <X className="h-4 w-4 text-gray-500" />
                        <h4 className="font-medium text-gray-700">Skipped Headers ({skippedHeaders.length})</h4>
                      </div>
                      <div className="space-y-2">
                        {skippedHeaders.map((skippedHeader, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white border rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-600">{skippedHeader.header}</span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">Skipped</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => undoSkipField(skippedHeader.header)}
                              className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Undo Skip
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-gray-500">
                        💡 Tip: Use "Undo Skip" to restore any field you skipped by mistake
                      </div>
                    </div>
                  );
                })()}
                
                <div className="flex justify-between pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep(1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setImportStep(3)}
                    disabled={
                      Object.keys(headerMappings).length === 0 && 
                      Object.keys(linkedSectionMappings).length === 0
                    }
                  >
                    Review & Import
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 3: Review & Confirm */}
            {importStep === 3 && fileAnalysis && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 3: Review Import Summary</h3>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Import Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Total Rows:</span>
                          <span className="font-medium">{fileAnalysis.file_info.total_rows}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Valid Contacts:</span>
                          <span className="font-medium text-green-600">{fileAnalysis.data_quality.estimated_valid_contacts}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Auto-merge Duplicates:</span>
                          <span className="font-medium text-blue-600">{fileAnalysis.duplicate_preview.auto_merge_candidates}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Mapped Fields:</span>
                          <span className="font-medium">{Object.values(headerMappings).filter(v => v && v !== 'SKIP' && v !== 'DONT_MAP').length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Linked Sections:</span>
                          <span className="font-medium text-purple-600">{Object.keys(linkedSectionMappings).length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Skipped Fields:</span>
                          <span className="font-medium text-gray-600">{Object.values(headerMappings).filter(v => v === 'SKIP' || v === 'DONT_MAP').length}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Data Preview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-xs">
                        {fileAnalysis.data_preview.sample_rows.slice(0, 3).map((row, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded">
                            {Object.entries(row).slice(0, 3).map(([field, value]) => (
                              <div key={field}>
                                <span className="text-gray-600">{field}:</span> {value}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Linked Sections Summary - NEW! */}
                {Object.keys(linkedSectionMappings).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm text-purple-700">📦 Linked Sections Mapping</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        {Object.entries(linkedSectionMappings).map(([header, sectionType]) => (
                          <div key={header} className="flex justify-between items-center p-2 bg-purple-50 rounded">
                            <span className="font-medium">{header}</span>
                            <span className="text-purple-700 text-xs">
                              {fileAnalysis.linked_section_categories?.find(cat => cat.id === sectionType)?.name || sectionType}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 text-xs text-purple-600">
                        💡 These fields will be stored as linked sections, separate from core contact data
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <div className="flex justify-between pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep(2)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back to Mapping
                  </Button>
                  <Button
                    onClick={startImportProcess}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Start Import
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 4: Import Progress */}
            {importStep === 4 && importProgress && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Step 4: Import in Progress</h3>
                
                <div className="space-y-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${importProgress.progress_percentage}%` }}
                    ></div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      {Math.round(importProgress.progress_percentage)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      {importProgress.stats.rows_processed} of {importProgress.stats.total_rows} rows processed
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-lg font-bold text-green-600">
                        {importProgress.stats.contacts_created}
                      </div>
                      <div className="text-xs text-green-600">Created</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-lg font-bold text-blue-600">
                        {importProgress.stats.contacts_merged}
                      </div>
                      <div className="text-xs text-blue-600">Merged</div>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg">
                      <div className="text-lg font-bold text-yellow-600">
                        {importProgress.stats.contacts_skipped}
                      </div>
                      <div className="text-xs text-yellow-600">Skipped</div>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="text-lg font-bold text-red-600">
                        {importProgress.stats.contacts_invalid}
                      </div>
                      <div className="text-xs text-red-600">Invalid</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
      
      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img 
                src="/logo-black.png" 
                alt="OmniMarket360" 
                className="h-10 w-auto mr-4"
              />
              <div className="text-sm text-gray-600">
                <p>&copy; 2025 OmniMarket360. All rights reserved.</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <p className="flex items-center">
                Product from 
                <span className="font-semibold text-blue-600 ml-1">Efficienza</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Consent Banner */}
      <ConsentBanner />

      {/* Keyword Research Details Dialog */}
      {/* Card Details Dialog */}
      <Dialog open={showCardDetails} onOpenChange={setShowCardDetails}>
        <DialogContent className="max-w-7xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-6 w-6 text-purple-600" />
              {cardDetailsData?.title || 'Details'}
            </DialogTitle>
            <DialogDescription>
              Comprehensive data analysis with advanced table features - Search, Sort, Filter & Export
            </DialogDescription>
          </DialogHeader>
          
          {cardDetailsData && (
            <div className="space-y-6">
              {/* Render Data Tables based on card type */}
              {(cardDetailsType === 'competitors' || cardDetailsType === 'analysis_competitors') && Array.isArray(cardDetailsData.data) && cardDetailsData.data.length > 0 && (
                <DataTableComponent
                  data={cardDetailsData.data}
                  columns={competitorsColumns}
                  title={cardDetailsData.title}
                  totalCount={cardDetailsData.totalCount}
                />
              )}

              {cardDetailsType === 'keywords' && Array.isArray(cardDetailsData.data) && cardDetailsData.data.length > 0 && (
                <DataTableComponent
                  data={cardDetailsData.data}
                  columns={keywordsColumns}
                  title={cardDetailsData.title}
                  totalCount={cardDetailsData.totalCount}
                />
              )}

              {(cardDetailsType === 'high_priority' || cardDetailsType === 'analysis_keywords') && Array.isArray(cardDetailsData.data) && cardDetailsData.data.length > 0 && (
                <DataTableComponent
                  data={cardDetailsData.data}
                  columns={highPriorityColumns}
                  title={cardDetailsData.title}
                  totalCount={cardDetailsData.totalCount}
                />
              )}

              {cardDetailsType === 'content_opportunities' && cardDetailsData.data && (
                <div className="space-y-6">
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <PenTool className="h-5 w-5 text-purple-600" />
                    Content Strategy Opportunities
                  </h4>
                  
                  {cardDetailsData.data.content_strategy && (
                    <div className="grid gap-6">
                      {/* Pillar Content */}
                      {cardDetailsData.data.content_strategy.pillar_content && cardDetailsData.data.content_strategy.pillar_content.length > 0 && (
                        <Card className="border-blue-200 bg-blue-50/30">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-blue-900 text-base">📄 Pillar Content Pages ({cardDetailsData.data.content_strategy.pillar_content.length})</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {cardDetailsData.data.content_strategy.pillar_content.map((content, index) => (
                                <div key={index} className="bg-white rounded-lg border border-blue-200 p-3">
                                  <div className="font-medium text-gray-900">{content.title || content.topic || `Pillar Content ${index + 1}`}</div>
                                  {content.description && (
                                    <div className="text-sm text-gray-600 mt-1">{content.description}</div>
                                  )}
                                  {content.keywords && Array.isArray(content.keywords) && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {content.keywords.slice(0, 5).map((keyword, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                          {keyword}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Product Pages */}
                      {cardDetailsData.data.content_strategy.product_pages && cardDetailsData.data.content_strategy.product_pages.length > 0 && (
                        <Card className="border-green-200 bg-green-50/30">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-green-900 text-base">🛍️ Product Pages ({cardDetailsData.data.content_strategy.product_pages.length})</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {cardDetailsData.data.content_strategy.product_pages.map((content, index) => (
                                <div key={index} className="bg-white rounded-lg border border-green-200 p-3">
                                  <div className="font-medium text-gray-900">{content.title || content.topic || `Product Page ${index + 1}`}</div>
                                  {content.description && (
                                    <div className="text-sm text-gray-600 mt-1">{content.description}</div>
                                  )}
                                  {content.keywords && Array.isArray(content.keywords) && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {content.keywords.slice(0, 5).map((keyword, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                          {keyword}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Supporting Content */}
                      {cardDetailsData.data.content_strategy.supporting_content && cardDetailsData.data.content_strategy.supporting_content.length > 0 && (
                        <Card className="border-purple-200 bg-purple-50/30">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-purple-900 text-base">📝 Supporting Blog Content ({cardDetailsData.data.content_strategy.supporting_content.length})</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="grid gap-2 max-h-80 overflow-y-auto">
                              {cardDetailsData.data.content_strategy.supporting_content.map((content, index) => (
                                <div key={index} className="bg-white rounded border border-purple-200 p-2">
                                  <div className="font-medium text-sm text-gray-900">{content.title || content.topic || `Blog Post ${index + 1}`}</div>
                                  {content.description && (
                                    <div className="text-xs text-gray-600 mt-1">{content.description}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Research Report Display */}
              {cardDetailsType === 'research_report' && cardDetailsData.data && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Search className="h-6 w-6 text-blue-600" />
                      Keyword Research Intelligence Report
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Comprehensive analysis of keyword opportunities, competitor insights, and strategic recommendations
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-blue-600">
                          {cardDetailsData.data.keyword_scorecards?.length || 0}
                        </div>
                        <div className="text-sm text-gray-600">Keywords Analyzed</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-green-600">
                          {cardDetailsData.data.priority_action_plan?.length || 0}
                        </div>
                        <div className="text-sm text-gray-600">Priority Actions</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-purple-600">
                          {cardDetailsData.data.strategic_recommendations?.length || 0}
                        </div>
                        <div className="text-sm text-gray-600">Strategies</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center shadow-sm">
                        <div className="text-2xl font-bold text-orange-600">
                          {cardDetailsData.data.competitor_insights?.length || 0}
                        </div>
                        <div className="text-sm text-gray-600">Competitors</div>
                      </div>
                    </div>
                  </div>

                  {/* Keyword Scorecards */}
                  {cardDetailsData.data.keyword_scorecards && cardDetailsData.data.keyword_scorecards.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Target className="h-5 w-5 text-blue-600" />
                        Keyword Analysis & Scoring
                      </h4>
                      <div className="space-y-3">
                        {cardDetailsData.data.keyword_scorecards.slice(0, 10).map((keyword, index) => (
                          <div key={index} className="bg-gray-50 border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-semibold text-gray-900">{keyword.keyword}</h5>
                              <span className={`px-2 py-1 rounded text-sm font-medium ${
                                (keyword.overall_score || 0) >= 80 ? 'bg-green-100 text-green-800' :
                                (keyword.overall_score || 0) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                Score: {keyword.overall_score || 0}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                              <div><strong>Volume:</strong> {keyword.search_volume || 'N/A'}</div>
                              <div><strong>Difficulty:</strong> {keyword.difficulty || 'N/A'}</div>
                              <div><strong>Intent:</strong> {keyword.intent || 'N/A'}</div>
                              <div><strong>CPC:</strong> ${keyword.cpc || 'N/A'}</div>
                            </div>
                            {keyword.strategic_value && (
                              <p className="text-sm text-blue-700 mt-2 bg-blue-50 p-2 rounded">
                                <strong>Strategic Value:</strong> {keyword.strategic_value}
                              </p>
                            )}
                          </div>
                        ))}
                        {cardDetailsData.data.keyword_scorecards.length > 10 && (
                          <div className="text-center text-gray-500 text-sm">
                            ... and {cardDetailsData.data.keyword_scorecards.length - 10} more keywords
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Priority Action Plan */}
                  {cardDetailsData.data.priority_action_plan && cardDetailsData.data.priority_action_plan.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Priority Action Plan
                      </h4>
                      <div className="space-y-3">
                        {cardDetailsData.data.priority_action_plan.map((action, index) => (
                          <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                                {index + 1}
                              </span>
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900 mb-1">{action.action}</h5>
                                <p className="text-sm text-gray-700 mb-2">{action.description}</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className={`px-2 py-1 rounded ${
                                    action.priority === 'high' ? 'bg-red-100 text-red-800' :
                                    action.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {action.priority} priority
                                  </span>
                                  {action.expected_impact && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {action.expected_impact} impact
                                    </span>
                                  )}
                                  {action.timeline && (
                                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                      {action.timeline}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strategic Recommendations */}
                  {cardDetailsData.data.strategic_recommendations && cardDetailsData.data.strategic_recommendations.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-purple-600" />
                        Strategic Recommendations
                      </h4>
                      <div className="space-y-4">
                        {cardDetailsData.data.strategic_recommendations.map((recommendation, index) => (
                          <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <h5 className="font-semibold text-gray-900 mb-2">{recommendation.strategy}</h5>
                            <p className="text-sm text-gray-700 mb-3">{recommendation.rationale}</p>
                            {recommendation.implementation_steps && (
                              <div className="bg-white p-3 rounded border-l-4 border-purple-400">
                                <h6 className="font-medium text-gray-900 mb-1">Implementation Steps:</h6>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {recommendation.implementation_steps.map((step, stepIndex) => (
                                    <li key={stepIndex} className="flex items-start gap-2">
                                      <span className="text-purple-600 mt-0.5">•</span>
                                      {step}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Competitor Insights */}
                  {cardDetailsData.data.competitor_insights && cardDetailsData.data.competitor_insights.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="h-5 w-5 text-orange-600" />
                        Competitor Intelligence
                      </h4>
                      <div className="space-y-4">
                        {cardDetailsData.data.competitor_insights.map((insight, index) => (
                          <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-semibold text-gray-900">{insight.competitor}</h5>
                              {insight.authority_score && (
                                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
                                  Authority: {insight.authority_score}
                                </span>
                              )}
                            </div>
                            {insight.strength_areas && (
                              <div className="mb-2">
                                <span className="text-sm font-medium text-gray-700">Strength Areas: </span>
                                <span className="text-sm text-gray-600">{insight.strength_areas.join(', ')}</span>
                              </div>
                            )}
                            {insight.gap_opportunities && (
                              <div className="bg-white p-2 rounded border-l-4 border-green-400">
                                <span className="text-sm font-medium text-gray-700">Gap Opportunities: </span>
                                <span className="text-sm text-green-700">{insight.gap_opportunities.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Export Actions */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Download className="h-5 w-5 text-blue-600" />
                      Export Options
                    </h4>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={() => {
                          if (cardDetailsData.session_id) {
                            exportKeywordResearch(cardDetailsData.session_id);
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export Keywords CSV
                      </Button>
                      <Button
                        onClick={() => {
                          if (cardDetailsData.session_id) {
                            exportKeywordResearchPDF(cardDetailsData.session_id);
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <FileDown className="h-4 w-4 mr-1" />
                        Export PDF Report
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* No Data Message */}
              {(!cardDetailsData.data || (Array.isArray(cardDetailsData.data) && cardDetailsData.data.length === 0)) && (
                <div className="text-center text-gray-500 py-12">
                  <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
                  <p>No data found for this analysis type. Please run the analysis again or check your research session.</p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-6 border-t">
            <Button
              onClick={() => setShowCardDetails(false)}
              variant="outline"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-orange-600" />
              Keyword Data Migration
            </DialogTitle>
            <DialogDescription>
              Migrate your existing keyword data to the new Unified Intelligence Hub
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {migrationStatus && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2">Migration Summary</h4>
                <div className="space-y-2 text-sm text-orange-800">
                  <div className="flex justify-between">
                    <span>Old SEO Keywords:</span>
                    <span className="font-medium">{migrationStatus.user_specific.old_seo_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Research Keywords:</span>
                    <span className="font-medium">{migrationStatus.user_specific.research_count}</span>
                  </div>
                  <div className="flex justify-between border-t border-orange-300 pt-2">
                    <span className="font-semibold">Total to Migrate:</span>
                    <span className="font-bold">{migrationStatus.user_specific.old_seo_count + migrationStatus.user_specific.research_count}</span>
                  </div>
                </div>
              </div>
            )}

            {migrationProgress && (
              <div className="space-y-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${migrationProgress.progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 text-center">
                  {migrationProgress.message}
                </p>
                {migrationProgress.stats && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      ✅ Successfully migrated {migrationProgress.stats.seo_keywords_migrated + migrationProgress.stats.research_keywords_migrated} keywords!
                    </p>
                  </div>
                )}
              </div>
            )}

            {!migrationProgress && (
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-600">
                  This will consolidate all your keyword data into the new unified system with enhanced features and better organization.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowMigrationDialog(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={runKeywordMigration}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Start Migration
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Keyword Entry Dialog */}
      <Dialog open={showManualKeywordDialog} onOpenChange={setShowManualKeywordDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Manual Keyword Intelligence Entry
            </DialogTitle>
            <DialogDescription>
              Add your strategic keywords with AI-powered intelligence analysis. Combine your business expertise with our genius keyword system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Progress Display */}
            {manualKeywordProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="w-full bg-blue-200 rounded-full h-2 mb-4">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${manualKeywordProgress.progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-blue-700 text-center mb-2">
                  {manualKeywordProgress.message}
                </p>
                
                {manualKeywordProgress.stats && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                    <h4 className="font-semibold text-green-900 mb-2">Intelligence Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                      <div>High Priority: {manualKeywordProgress.stats.high_priority_count || 0}</div>
                      <div>Content Ops: {manualKeywordProgress.stats.content_opportunities_count || 0}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {!manualKeywordProgress && (
              <>
                {/* Bulk Keywords Input */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Keywords (One per line) *
                  </label>
                  <textarea
                    value={manualKeywordForm.keywords}
                    onChange={(e) => setManualKeywordForm(prev => ({ ...prev, keywords: e.target.value }))}
                    placeholder="premium indian catering&#10;corporate lunch delivery&#10;authentic regional cuisine&#10;organic food catering services"
                    className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isProcessingManualKeywords}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter each keyword on a new line. Add 1-20 keywords for optimal processing.
                  </p>
                </div>
                
                {/* Priority Hint */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Strategic Priority Hint
                  </label>
                  <select
                    value={manualKeywordForm.priority}
                    onChange={(e) => setManualKeywordForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isProcessingManualKeywords}
                  >
                    <option value="high">High Priority - Core business focus</option>
                    <option value="medium">Medium Priority - Strategic opportunity</option>
                    <option value="low">Low Priority - Future consideration</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    This helps the AI understand the strategic importance of these keywords to your business.
                  </p>
                </div>
                
                {/* Business Context */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Business Context (Optional)
                  </label>
                  <textarea
                    value={manualKeywordForm.businessContext}
                    onChange={(e) => setManualKeywordForm(prev => ({ ...prev, businessContext: e.target.value }))}
                    placeholder="These keywords represent our premium service offerings that competitors often miss. They have high conversion potential based on customer feedback..."
                    className="w-full h-16 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isProcessingManualKeywords}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Explain why these keywords matter to your business. This enhances AI analysis accuracy.
                  </p>
                </div>
                
                {/* Tags */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Tags (Optional)
                  </label>
                  <input
                    type="text"
                    value={manualKeywordForm.tags}
                    onChange={(e) => setManualKeywordForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="premium-services, high-conversion, competitor-gap"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isProcessingManualKeywords}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated tags for organizing your keywords (e.g., category, intent, priority).
                  </p>
                </div>
                
                {/* Intelligence Analysis Toggle */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="enableIntelligence"
                      checked={manualKeywordForm.enableIntelligence}
                      onChange={(e) => setManualKeywordForm(prev => ({ ...prev, enableIntelligence: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isProcessingManualKeywords}
                    />
                    <label htmlFor="enableIntelligence" className="text-sm font-medium text-blue-900">
                      Enable AI Intelligence Analysis
                    </label>
                  </div>
                  <p className="text-xs text-blue-700 mt-2 ml-6">
                    Run full strategic analysis including business value scoring, competitive intelligence, content opportunities, and success probability predictions.
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => setShowManualKeywordDialog(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={isProcessingManualKeywords}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={processManualKeywords}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={isProcessingManualKeywords || !manualKeywordForm.keywords.trim()}
                  >
                    {isProcessingManualKeywords ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Brain className="h-4 w-4 mr-2" />
                        Analyze & Add Keywords
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Single Keyword Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Keyword
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the keyword "{keywordToDelete?.keyword}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <Trash2 className="h-4 w-4" />
                <span className="font-medium">This keyword will be permanently deleted</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => setShowDeleteConfirmDialog(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => deleteUnifiedKeyword(keywordToDelete?.id)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Details Modal */}
      <Dialog open={showAnalyticsModal} onOpenChange={setShowAnalyticsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              {analyticsModalData?.title || 'Analytics Details'}
            </DialogTitle>
            <DialogDescription>
              {analyticsModalData?.explanation || 'Detailed breakdown of analytics metrics'}
            </DialogDescription>
          </DialogHeader>
          
          {analyticsModalData && (
            <div className="space-y-6">
              {/* Explanation Card */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">What This Metric Means</h4>
                <p className="text-sm text-gray-700">{analyticsModalData.explanation}</p>
              </div>

              {/* Details Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">Detailed Information</h4>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {analyticsModalData.details?.[0]?.map((header, index) => (
                          <th key={index} className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analyticsModalData.details?.slice(1).map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700">
                              {cellIndex === 1 ? (
                                <span className="font-semibold text-blue-900">{cell}</span>
                              ) : (
                                cell
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recommendations Section */}
              {analyticsModalData.type === 'high_priority' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 mb-2">💡 Strategic Recommendations</h4>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>• Focus content creation on these high-priority keywords first</li>
                    <li>• These represent the best ROI opportunities in your portfolio</li>
                    <li>• Consider creating comprehensive content clusters around these terms</li>
                  </ul>
                </div>
              )}

              {analyticsModalData.type === 'content_opportunities' && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-orange-900 mb-2">🎯 Action Items</h4>
                  <ul className="text-sm text-orange-800 space-y-1">
                    <li>• Create content targeting these identified gaps</li>
                    <li>• Analyze competitor content quality and create superior alternatives</li>
                    <li>• Monitor ranking progress for new content pieces</li>
                  </ul>
                </div>
              )}

              {analyticsModalData.type === 'growth_rate' && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">📈 Growth Insights</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Positive growth indicates active keyword research and expansion</li>
                    <li>• Consistent growth shows systematic approach to SEO strategy</li>
                    <li>• Track growth rate to measure research effectiveness</li>
                  </ul>
                </div>
              )}

              {/* Export Section */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">
                  <strong>{analyticsModalData.type}</strong> analytics data
                </div>
                <Button
                  onClick={() => {
                    // Export analytics details
                    if (analyticsModalData.details) {
                      const csvContent = analyticsModalData.details.map(row => 
                        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
                      ).join('\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${analyticsModalData.type}_analytics_${new Date().toISOString().split('T')[0]}.csv`;
                      link.style.display = 'none';
                      
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      toast.success('Analytics data exported successfully!');
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Details
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <Button onClick={() => setShowAnalyticsModal(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Research Metrics Details Modal */}
      <Dialog open={showMetricsModal} onOpenChange={setShowMetricsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              {metricsModalData?.title || 'Metrics Breakdown'}
            </DialogTitle>
            <DialogDescription>
              {metricsModalData?.summary || 'Detailed breakdown of research metrics'}
            </DialogDescription>
          </DialogHeader>
          
          {metricsModalData && (
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">{metricsModalData.summary}</p>
              </div>

              {/* Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium text-gray-900">Detailed Breakdown</h4>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        {metricsModalData.headers?.map((header, index) => (
                          <th key={index} className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {metricsModalData.data?.length > 0 ? (
                        metricsModalData.data.map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={metricsModalData.headers?.length || 1} className="px-4 py-8 text-center text-gray-500">
                            No data available for this metric
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Export Section */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">
                  <strong>{metricsModalData.data?.length || 0}</strong> records shown
                </div>
                <Button
                  onClick={() => {
                    // Export CSV functionality
                    if (metricsModalData.data && metricsModalData.headers) {
                      const csvContent = [
                        metricsModalData.headers.join(','),
                        ...metricsModalData.data.map(row => 
                          row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
                        )
                      ].join('\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `${metricsModalData.type}_breakdown_${new Date().toISOString().split('T')[0]}.csv`;
                      link.style.display = 'none';
                      
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      toast.success('Metrics data exported successfully!');
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex justify-end pt-4">
            <Button onClick={() => setShowMetricsModal(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Multiple Keywords Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Multiple Keywords
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedKeywords.length} selected keywords? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <Trash2 className="h-4 w-4" />
                <span className="font-medium">
                  {selectedKeywords.length} keywords will be permanently deleted
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => setShowBulkDeleteDialog(false)}
              variant="outline"
              className="flex-1"
              disabled={isDeletingKeywords}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteBulkUnifiedKeywords}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeletingKeywords}
            >
              {isDeletingKeywords ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedKeywords.length} Keywords
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dashboard Keyword Details Modal */}
      <Dialog open={showKeywordDetailsModal} onOpenChange={setShowKeywordDetailsModal}>
        <DialogContent className="max-w-7xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-6 w-6 text-purple-600" />
              {detailedBreakdownData?.title || 'Keyword Details'}
            </DialogTitle>
            <DialogDescription>
              Comprehensive data analysis with advanced table features - Search, Sort, Filter & Export
            </DialogDescription>
          </DialogHeader>
          
          {detailedBreakdownData && (
            <div className="space-y-6">
              {/* Export Button */}
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    // CSV Export functionality
                    const headers = Object.keys(detailedBreakdownData.details[0] || {});
                    const csvContent = [
                      headers.join(','),
                      ...detailedBreakdownData.details.map(row => 
                        headers.map(header => `"${row[header] || ''}"`).join(',')
                      )
                    ].join('\n');
                    
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${keywordDetailsType}_${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('CSV export completed successfully');
                  }}
                  size="sm"
                  variant="outline"
                  className="border-green-300 text-green-700 hover:bg-green-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Data Table */}
              {detailedBreakdownData.details && detailedBreakdownData.details.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {detailedBreakdownData.title} ({detailedBreakdownData.totalCount} total)
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {detailedBreakdownData.description}
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {detailedBreakdownData.details[0] && Object.keys(detailedBreakdownData.details[0]).map(header => (
                            <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {header.replace('_', ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {detailedBreakdownData.details.slice(0, 50).map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {Object.entries(row).map(([key, value], cellIndex) => (
                              <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {value || 'N/A'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {detailedBreakdownData.details.length > 50 && (
                    <div className="px-6 py-3 bg-gray-50 border-t text-sm text-gray-600">
                      Showing first 50 of {detailedBreakdownData.totalCount} total items. Export CSV for complete data.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enhanced Column Manager Dialog */}
      <Dialog open={showEnhancedColumnManager} onOpenChange={setShowEnhancedColumnManager}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Manage Table Columns
            </DialogTitle>
            <DialogDescription>
              Choose which columns to display in the enhanced contacts table, including linked section data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Group columns by category */}
            {['contact', 'demographic', 'geographic', 'professional', 'linked_sections', 'scoring', 'system'].map(category => {
              const categoryColumns = availableColumns.filter(col => col.category === category);
              const categoryNames = {
                contact: '👤 Contact Information',
                demographic: '🏷️ Demographics',
                geographic: '🌍 Geographic',
                professional: '💼 Professional',
                linked_sections: '📦 Linked Sections',
                scoring: '📊 Scoring & Engagement', 
                system: '⚙️ System Fields'
              };
              
              return (
                <div key={category} className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <h4 className="font-medium text-sm">{categoryNames[category]}</h4>
                    <Badge variant="outline" className="text-xs">
                      {categoryColumns.filter(col => enhancedVisibleColumns.has(col.key)).length}/{categoryColumns.length}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {categoryColumns.map(column => (
                      <div key={column.key} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`col-${column.key}`}
                          checked={enhancedVisibleColumns.has(column.key)}
                          onChange={() => toggleColumnVisibility(column.key)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`col-${column.key}`} className="text-sm font-medium flex-1 cursor-pointer">
                          {column.label}
                        </label>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            column.type === 'linked_section' ? 'bg-purple-50 text-purple-700 border-purple-200' : ''
                          }`}
                        >
                          {column.type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                {enhancedVisibleColumns.size} of {availableColumns.length} columns selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Select all columns
                    setEnhancedVisibleColumns(new Set(availableColumns.map(c => c.key)));
                  }}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Reset to default essential columns
                    setEnhancedVisibleColumns(new Set([
                      'full_name', 'email_addresses', 'phone_numbers', 'company_name', 'data_source', 'total_score', 'category'
                    ]));
                  }}
                >
                  Reset to Default
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Select only linked sections
                    const linkedSectionCols = availableColumns.filter(c => c.category === 'linked_sections').map(c => c.key);
                    setEnhancedVisibleColumns(new Set([
                      'full_name', 'email_addresses', 'phone_numbers', // Essential contact fields
                      ...linkedSectionCols // All linked section fields
                    ]));
                  }}
                  className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  📦 Focus on Linked Sections
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enhanced Linked Sections Dialog */}
      <Dialog open={showLinkedSections} onOpenChange={setShowLinkedSections}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5" />
              Contact Profile - {selectedContactForSections?.full_name}
            </DialogTitle>
            <DialogDescription>
              Complete contact profile with linked sections data across all industries
            </DialogDescription>
          </DialogHeader>
          
          {selectedContactForSections && (
            <div className="space-y-6">
              {/* Enhanced Contact Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm font-medium text-gray-600">📧 Email</p>
                    <p className="text-sm font-semibold">{selectedContactForSections.email_addresses?.[0] || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">📱 Phone</p>
                    <p className="text-sm font-semibold">{selectedContactForSections.phone_numbers?.[0] || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">🏢 Company</p>
                    <p className="text-sm font-semibold">{selectedContactForSections.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">🎯 Score</p>
                    <Badge variant={selectedContactForSections.total_score >= 80 ? 'default' : selectedContactForSections.total_score >= 60 ? 'secondary' : 'outline'}>
                      {selectedContactForSections.total_score || 0}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">📍 Location</p>
                    <p className="text-sm font-semibold">
                      {[selectedContactForSections.city, selectedContactForSections.state_province, selectedContactForSections.country]
                        .filter(Boolean).join(', ') || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">🏷️ Tags</p>
                    <p className="text-sm font-semibold">{selectedContactForSections.custom_tags?.join(', ') || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Dynamic Linked Sections Tabs */}
              <Tabs defaultValue="purchase_history" className="w-full">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="purchase_history">📦 Purchase History</TabsTrigger>
                  <TabsTrigger value="product_preferences">🎯 Preferences</TabsTrigger>
                  <TabsTrigger value="employment_data">🏢 Employment</TabsTrigger>
                  <TabsTrigger value="financial_info">🏦 Financial</TabsTrigger>
                  <TabsTrigger value="insurance_policies">🛡️ Insurance</TabsTrigger>
                  <TabsTrigger value="custom_section">🎨 Custom</TabsTrigger>
                </TabsList>
                
                {/* Dynamic Tab Content */}
                {['purchase_history', 'product_preferences', 'employment_data', 'financial_info', 'insurance_policies', 'custom_section'].map(sectionKey => {
                  const sectionData = contactLinkedSections[sectionKey]?.entries || [];
                  const sectionNames = {
                    purchase_history: '📦 Purchase History',
                    product_preferences: '🎯 Product Preferences',
                    employment_data: '🏢 Employment Data',
                    financial_info: '🏦 Financial Information',
                    insurance_policies: '🛡️ Insurance Policies',
                    custom_section: '🎨 Custom Section'
                  };
                  
                  return (
                    <TabsContent key={sectionKey} value={sectionKey} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium">{sectionNames[sectionKey]}</h3>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                          onClick={() => handleAddValue(sectionKey)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Value
                        </Button>
                      </div>
                      
                      {sectionData.length > 0 ? (
                        <div className="space-y-3">
                          {sectionData.map((entry, index) => (
                            <div key={index} className="bg-white border border-purple-100 rounded-lg p-4 hover:shadow-sm transition-shadow group">
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="text-sm font-medium text-gray-700">Entry #{index + 1}</h4>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button
                                    size="sm"
                                    variant="ghost" 
                                    onClick={() => handleEditEntry(sectionKey, entry)}
                                    className="h-6 w-6 p-0 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      if (window.confirm('Are you sure you want to delete this entry?')) {
                                        deleteLinkedSectionEntry(selectedContactForSections.id, sectionKey, entry.id);
                                      }
                                    }}
                                    className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                {Object.entries(entry).filter(([key]) => !['id', 'imported_at', 'created_at', 'updated_at', 'source', 'batch_id', 'created_by', 'updated_by'].includes(key)).map(([key, value]) => (
                                  <div key={key}>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      {key.replace(/_/g, ' ')}
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">
                                      {String(value)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                              {(entry.imported_at || entry.created_at) && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <p className="text-xs text-gray-400">
                                    {entry.created_at ? `Added: ${new Date(entry.created_at).toLocaleDateString()}` : `Imported: ${new Date(entry.imported_at).toLocaleDateString()}`} • Source: {entry.source || 'import'}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">{sectionNames[sectionKey].split(' ')[0]}</span>
                          </div>
                          <p className="text-lg font-medium">No {sectionNames[sectionKey].toLowerCase()} data</p>
                          <p className="text-sm">Click "Add Value" to manually add data to this section</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-4 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                            onClick={() => handleAddValue(sectionKey)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add First Entry
                          </Button>
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Linked Section Entry Dialog */}
      <Dialog open={showAddLinkedSectionEntry} onOpenChange={setShowAddLinkedSectionEntry}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {editingEntry ? 'Edit' : 'Add'} {selectedSectionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Entry
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the information below' : 'Fill in the information below to add a new entry'}
            </DialogDescription>
          </DialogHeader>
          
          {sectionTemplate && (
            <div className="space-y-4">
              {/* Dynamic Form Fields */}
              {sectionTemplate.required_fields.concat(sectionTemplate.optional_fields).map(fieldName => {
                const fieldType = sectionTemplate.field_types[fieldName];
                const fieldOptions = sectionTemplate.field_options?.[fieldName];
                const isRequired = sectionTemplate.required_fields.includes(fieldName);
                
                return (
                  <div key={fieldName} className="space-y-2">
                    <Label htmlFor={fieldName} className="text-sm font-medium">
                      {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    
                    {fieldType === 'select' && fieldOptions ? (
                      <Select 
                        value={newEntryData[fieldName] || ''} 
                        onValueChange={(value) => setNewEntryData(prev => ({ ...prev, [fieldName]: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${fieldName.replace(/_/g, ' ')}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {fieldOptions.map(option => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : fieldType === 'textarea' ? (
                      <Textarea
                        id={fieldName}
                        value={newEntryData[fieldName] || ''}
                        onChange={(e) => setNewEntryData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                        placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
                        rows={3}
                      />
                    ) : fieldType === 'date' ? (
                      <Input
                        id={fieldName}
                        type="date"
                        value={newEntryData[fieldName] || ''}
                        onChange={(e) => setNewEntryData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                      />
                    ) : fieldType === 'number' || fieldType === 'currency' ? (
                      <Input
                        id={fieldName}
                        type="number"
                        step={fieldType === 'currency' ? '0.01' : '1'}
                        value={newEntryData[fieldName] || ''}
                        onChange={(e) => setNewEntryData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                        placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
                      />
                    ) : fieldType === 'tags' ? (
                      <Input
                        id={fieldName}
                        value={newEntryData[fieldName] || ''}
                        onChange={(e) => setNewEntryData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                        placeholder="Enter tags separated by commas"
                      />
                    ) : (
                      <Input
                        id={fieldName}
                        value={newEntryData[fieldName] || ''}
                        onChange={(e) => setNewEntryData(prev => ({ ...prev, [fieldName]: e.target.value }))}
                        placeholder={`Enter ${fieldName.replace(/_/g, ' ')}`}
                      />
                    )}
                  </div>
                );
              })}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => {
                    if (editingEntry) {
                      updateLinkedSectionEntry(
                        selectedContactForSections.id, 
                        selectedSectionType, 
                        editingEntry.id, 
                        newEntryData
                      );
                    } else {
                      addLinkedSectionEntry(
                        selectedContactForSections.id, 
                        selectedSectionType, 
                        newEntryData
                      );
                    }
                  }}
                  disabled={isLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingEntry ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {editingEntry ? 'Update Entry' : 'Add Entry'}
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAddLinkedSectionEntry(false);
                    setNewEntryData({});
                    setEditingEntry(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Wrapper App component with Settings Provider
const App = () => {
  return (
    <SettingsProvider>
      <AppContent />
    </SettingsProvider>
  );
};

// ===== STEP 3: GOALS & CHALLENGES =====

const Step3Goals = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 3: Goals & Challenges</CardTitle>
      <CardDescription>Understand their objectives, motivations, and pain points</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Primary Goals *</Label>
        <div className="space-y-2 mt-2">
          {(formData.goals || []).map((goal, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input 
                value={goal}
                onChange={(e) => {
                  const newGoals = [...(formData.goals || [])];
                  newGoals[index] = e.target.value;
                  updateFormData('goals', newGoals);
                }}
                placeholder={`Goal ${index + 1}`}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const newGoals = (formData.goals || []).filter((_, i) => i !== index);
                  updateFormData('goals', newGoals);
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button 
            variant="outline" 
            onClick={() => {
              const newGoals = [...(formData.goals || []), ''];
              updateFormData('goals', newGoals);
            }}
          >
            Add Goal
          </Button>
        </div>
      </div>
      
      <div>
        <Label>Motivations *</Label>
        <Textarea 
          value={formData.motivations}
          onChange={(e) => updateFormData('motivations', e.target.value)}
          placeholder="What drives and motivates them? (e.g., career growth, recognition, problem-solving)"
          className="min-h-[100px] mt-1"
        />
      </div>
      
      <div>
        <Label>Pain Points *</Label>
        <Textarea 
          value={formData.pain_points}
          onChange={(e) => updateFormData('pain_points', e.target.value)}
          placeholder="What frustrates them? What challenges do they face? (e.g., budget constraints, time pressure)"
          className="min-h-[100px] mt-1"
        />
      </div>
      
      <div>
        <Label>Success Metrics</Label>
        <Input 
          value={formData.success_metrics}
          onChange={(e) => updateFormData('success_metrics', e.target.value)}
          placeholder="How do they measure success? (e.g., ROI, team performance, customer satisfaction)"
          className="mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 4: BEHAVIORAL TRAITS =====

const Step4Behavior = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 4: Behavioral Traits</CardTitle>
      <CardDescription>Understand how they think, decide, and communicate</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Personality Type</Label>
          <Select 
            value={formData.behavior.personality_type || ''} 
            onValueChange={(value) => updateFormData('behavior.personality_type', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select personality type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="analytical">Analytical</SelectItem>
              <SelectItem value="creative">Creative</SelectItem>
              <SelectItem value="driver">Driver</SelectItem>
              <SelectItem value="expressive">Expressive</SelectItem>
              <SelectItem value="amiable">Amiable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Decision Making Style</Label>
          <Select 
            value={formData.behavior.decision_making || ''} 
            onValueChange={(value) => updateFormData('behavior.decision_making', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select decision style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="data-driven">Data-Driven</SelectItem>
              <SelectItem value="intuitive">Intuitive</SelectItem>
              <SelectItem value="consensus-builder">Consensus Builder</SelectItem>
              <SelectItem value="quick-decisive">Quick & Decisive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Communication Style</Label>
          <Select 
            value={formData.behavior.communication_style || ''} 
            onValueChange={(value) => updateFormData('behavior.communication_style', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select communication style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct">Direct</SelectItem>
              <SelectItem value="diplomatic">Diplomatic</SelectItem>
              <SelectItem value="collaborative">Collaborative</SelectItem>
              <SelectItem value="formal">Formal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Tech Comfort Level</Label>
          <Select 
            value={formData.behavior.tech_comfort || ''} 
            onValueChange={(value) => updateFormData('behavior.tech_comfort', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select tech comfort level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
              <SelectItem value="expert">Expert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label>Decision Factors</Label>
        <Textarea 
          value={formData.behavior.decision_factors}
          onChange={(e) => updateFormData('behavior.decision_factors', e.target.value)}
          placeholder="What factors influence their purchasing decisions? (e.g., price, quality, brand reputation)"
          className="min-h-[80px] mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 5: CONTENT & CHANNELS =====

const Step5Content = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 5: Content & Channels</CardTitle>
      <CardDescription>How they consume and engage with content</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Preferred Content Types</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {wizardOptions.content_channels?.content_types?.map(type => (
            <div key={type} className="flex items-center space-x-2">
              <input 
                type="checkbox"
                id={`content-${type}`}
                checked={(formData.content_channels.content_types || []).includes(type)}
                onChange={(e) => {
                  const current = formData.content_channels.content_types || [];
                  const updated = e.target.checked 
                    ? [...current, type]
                    : current.filter(t => t !== type);
                  updateFormData('content_channels.content_types', updated);
                }}
                className="rounded border-gray-300"
              />
              <Label htmlFor={`content-${type}`} className="text-sm">{type}</Label>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Social Media Platforms</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {wizardOptions.content_channels?.channels?.map(platform => (
            <div key={platform} className="flex items-center space-x-2">
              <input 
                type="checkbox"
                id={`platform-${platform}`}
                checked={(formData.content_channels.social_platforms || []).includes(platform)}
                onChange={(e) => {
                  const current = formData.content_channels.social_platforms || [];
                  const updated = e.target.checked 
                    ? [...current, platform]
                    : current.filter(p => p !== platform);
                  updateFormData('content_channels.social_platforms', updated);
                }}
                className="rounded border-gray-300"
              />
              <Label htmlFor={`platform-${platform}`} className="text-sm">{platform}</Label>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Content Consumption Habits</Label>
        <Textarea 
          value={formData.content_channels.consumption_habits}
          onChange={(e) => updateFormData('content_channels.consumption_habits', e.target.value)}
          placeholder="When and how do they consume content? (e.g., morning news, commute podcasts)"
          className="min-h-[80px] mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 6: BUYING JOURNEY =====

const Step6Journey = ({ formData, updateFormData, wizardOptions }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 6: Buying Journey</CardTitle>
      <CardDescription>Map their path from awareness to purchase decision</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Awareness Stage Behavior</Label>
        <Textarea 
          value={formData.journey.awareness}
          onChange={(e) => updateFormData('journey.awareness', e.target.value)}
          placeholder="How do they typically discover new solutions? What triggers their interest?"
          className="min-h-[80px] mt-1"
        />
      </div>
      
      <div>
        <Label>Consideration Stage Behavior</Label>
        <Textarea 
          value={formData.journey.consideration}
          onChange={(e) => updateFormData('journey.consideration', e.target.value)}
          placeholder="How do they research and evaluate options? What information do they seek?"
          className="min-h-[80px] mt-1"
        />
      </div>
      
      <div>
        <Label>Decision Stage Behavior</Label>
        <Textarea 
          value={formData.journey.decision}
          onChange={(e) => updateFormData('journey.decision', e.target.value)}
          placeholder="How do they make final purchasing decisions? What seals the deal?"
          className="min-h-[80px] mt-1"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Decision Timeline</Label>
          <Select 
            value={formData.journey.decision_timeline || ''} 
            onValueChange={(value) => updateFormData('journey.decision_timeline', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select decision timeline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Immediate (within days)</SelectItem>
              <SelectItem value="short">Short-term (1-4 weeks)</SelectItem>
              <SelectItem value="medium">Medium-term (1-3 months)</SelectItem>
              <SelectItem value="long">Long-term (3+ months)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Budget Authority</Label>
          <Select 
            value={formData.journey.budget_authority || ''} 
            onValueChange={(value) => updateFormData('journey.budget_authority', value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select budget authority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full Authority</SelectItem>
              <SelectItem value="partial">Partial Authority</SelectItem>
              <SelectItem value="influencer">Influencer</SelectItem>
              <SelectItem value="none">No Authority</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label>Common Objections</Label>
        <Textarea 
          value={formData.journey.objections}
          onChange={(e) => updateFormData('journey.objections', e.target.value)}
          placeholder="What concerns or objections do they typically have?"
          className="min-h-[80px] mt-1"
        />
      </div>
    </CardContent>
  </div>
);

// ===== STEP 7: VALUE PROPOSITION =====

const Step7ValueProp = ({ formData, updateFormData }) => (
  <div>
    <CardHeader>
      <CardTitle>Step 7: Value Proposition & Messaging</CardTitle>
      <CardDescription>Define how to communicate value effectively to this persona</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <Label>Core Value Proposition *</Label>
        <Textarea 
          value={formData.value_prop}
          onChange={(e) => updateFormData('value_prop', e.target.value)}
          placeholder="What's the primary value your product/service offers to this persona?"
          className="min-h-[100px] mt-1"
        />
      </div>
      
      <div>
        <Label>Key Benefits</Label>
        <div className="space-y-2 mt-2">
          {(formData.key_benefits || []).map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input 
                value={benefit}
                onChange={(e) => {
                  const newBenefits = [...(formData.key_benefits || [])];
                  newBenefits[index] = e.target.value;
                  updateFormData('key_benefits', newBenefits);
                }}
                placeholder={`Key benefit ${index + 1}`}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const newBenefits = (formData.key_benefits || []).filter((_, i) => i !== index);
                  updateFormData('key_benefits', newBenefits);
                }}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button 
            variant="outline" 
            onClick={() => {
              const newBenefits = [...(formData.key_benefits || []), ''];
              updateFormData('key_benefits', newBenefits);
            }}
          >
            Add Benefit
          </Button>
        </div>
      </div>
      
      <div>
        <Label>Messaging Tone</Label>
        <Select 
          value={formData.messaging_tone || ''} 
          onValueChange={(value) => updateFormData('messaging_tone', value)}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select messaging tone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="friendly">Friendly</SelectItem>
            <SelectItem value="authoritative">Authoritative</SelectItem>
            <SelectItem value="conversational">Conversational</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>CTA Preferences</Label>
        <Input 
          value={formData.cta_preferences}
          onChange={(e) => updateFormData('cta_preferences', e.target.value)}
          placeholder="What call-to-action style works best? (e.g., 'Learn More', 'Get Started', 'Book Demo')"
          className="mt-1"
        />
      </div>
      
      <div>
        <Label>Tags</Label>
        <Input 
          value={(formData.tags || []).join(', ')}
          onChange={(e) => {
            const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
            updateFormData('tags', tags);
          }}
          placeholder="Add tags separated by commas (e.g., marketing, B2B, tech-savvy)"
          className="mt-1"
        />
      </div>
    </CardContent>
  </div>
);

export default App;